import React, { useState, useRef, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import { supabase } from './supabase';
import ReactGA from 'react-ga4';
import { BorderBeam } from './components/BorderBeam';
import { AnimatedGradientText } from './components/AnimatedGradientText';
import { ShimmerButton } from './components/ShimmerButton';
import { BlurFade } from './components/BlurFade';
import { WavyBackground } from './components/ui/wavy-background';

ReactGA.initialize('G-34EYP3ZVZQ');
ReactGA.send({ hitType: 'pageview', page: window.location.pathname });

// ── Palette ────────────────────────────────────────────────────────────────────
const P = {
  paper:       '#F6F2EA',
  surface:     '#FFFFFF',
  border:      '#E6E0D6',
  ink:         '#1D1D1F',
  muted:       '#8B8F97',
  accent:      '#3C8CFF',
  accentHover: '#1F6BFF',
  accentLight: 'rgba(123,211,255,0.18)',
  success:     '#0F766E',
  warning:     '#B45309',
  error:       '#B42318',
};

const MOBILE_BREAKPOINT = 640;
const TABLET_BREAKPOINT = 1024;

const PROGRESS_PROFILE_KEY = 'yte_progress_profile_v1';
const PROGRESS_SPEED_MULTIPLIER = 0.25; // 4x faster visual progress
const MOBILE_BOTTOM_NAV_HEIGHT = 64;
const MOBILE_BOTTOM_NAV_LIFT = 0;
const DEFAULT_PROGRESS_PROFILE = Object.freeze({
  url: { avgMs: 60000, samples: 0 },
  upload: { avgMs: 140000, samples: 0 },
});

const clampNumber = (value, min, max) => Math.max(min, Math.min(max, value));
const scaleProgressMs = (ms) => Math.round(ms * PROGRESS_SPEED_MULTIPLIER);

const cloneDefaultProgressProfile = () => ({
  url: { ...DEFAULT_PROGRESS_PROFILE.url },
  upload: { ...DEFAULT_PROGRESS_PROFILE.upload },
});

function readProgressProfile() {
  const fallback = cloneDefaultProgressProfile();
  if (typeof window === 'undefined') return fallback;
  try {
    const raw = JSON.parse(localStorage.getItem(PROGRESS_PROFILE_KEY) || '{}');
    const next = cloneDefaultProgressProfile();
    ['url', 'upload'].forEach((mode) => {
      const avgMs = Number(raw?.[mode]?.avgMs);
      const samples = Number(raw?.[mode]?.samples);
      if (Number.isFinite(avgMs) && avgMs > 0) next[mode].avgMs = Math.round(avgMs);
      if (Number.isFinite(samples) && samples >= 0) next[mode].samples = Math.round(samples);
    });
    return next;
  } catch {
    return fallback;
  }
}

function writeProgressProfile(profile) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(PROGRESS_PROFILE_KEY, JSON.stringify(profile));
  } catch {}
}

const LANGUAGES = [
  { code: 'en',      label: 'English' },
  { code: 'es',      label: 'Spanish' },
  { code: 'fr',      label: 'French' },
  { code: 'de',      label: 'German' },
  { code: 'it',      label: 'Italian' },
  { code: 'pt',      label: 'Portuguese' },
  { code: 'ru',      label: 'Russian' },
  { code: 'zh-Hans', label: 'Chinese (Simplified)' },
  { code: 'zh-Hant', label: 'Chinese (Traditional)' },
  { code: 'ja',      label: 'Japanese' },
  { code: 'ko',      label: 'Korean' },
  { code: 'ar',      label: 'Arabic' },
  { code: 'hi',      label: 'Hindi' },
  { code: 'tr',      label: 'Turkish' },
  { code: 'nl',      label: 'Dutch' },
  { code: 'pl',      label: 'Polish' },
];

const DEMO_CHIPS = [
  'Summarize the video',
  'What are the key points?',
  'What questions does this video answer?',
  'What is the main argument?',
];

const SUMMARY_DEMO_PREVIEWS = [
  {
    bullets: [
      'Speaker explains the core thesis in under 2 minutes.',
      'Main takeaway: shorter feedback loops improve outcomes.',
      'Action item: test one change per week and compare results.',
    ],
    metrics: ['3 key points', '2m read', '4 chapters'],
  },
  {
    bullets: [
      'Video compares three strategies with real examples.',
      'Most effective pattern: clear constraints before execution.',
      'Recommended next step: create a checklist for repeatability.',
    ],
    metrics: ['4 key points', '90s read', '5 chapters'],
  },
  {
    bullets: [
      'Host breaks down mistakes that caused poor retention.',
      'Best insight: pairing visuals with timestamps boosts recall.',
      'Practical move: summarize each section into one sentence.',
    ],
    metrics: ['3 key points', '75s read', '3 chapters'],
  },
];

const FLASHCARD_DEMO_PREVIEWS = [
  {
    topic: 'Core Idea',
    question: 'What is the main argument of the video?',
    answer: 'Fast feedback loops produce better decisions and more consistent outcomes.',
  },
  {
    topic: 'Method',
    question: 'What 3-step framework is recommended?',
    answer: 'Define a metric, test one change, then compare against a baseline.',
  },
  {
    topic: 'Application',
    question: 'How should you apply this in practice?',
    answer: 'Run weekly experiments and summarize each result into one actionable takeaway.',
  },
];

const CHAT_DEMO_PREVIEWS = [
  {
    questionOne: 'What is the main thesis in this lecture?',
    answerOne: 'The speaker argues shorter feedback cycles produce better long-term decisions.',
    timestampOne: '15:35',
    questionTwo: 'Do they give a concrete framework?',
    answerTwo: 'Yes. They suggest a 3-step loop: choose one metric, test weekly, compare to baseline.',
    timestampTwo: '22:10',
  },
  {
    questionOne: 'Why does retention drop after section 2?',
    answerOne: 'The transcript notes jargon spikes and fewer examples, which lowers comprehension.',
    timestampOne: '09:42',
    questionTwo: 'What fix do they recommend?',
    answerTwo: 'Pair each concept with one practical case study and a recap checkpoint.',
    timestampTwo: '12:28',
  },
  {
    questionOne: 'What claim is best supported by evidence?',
    answerOne: 'Their strongest claim is that constrained experiments outperform broad planning.',
    timestampOne: '18:04',
    questionTwo: 'Any actionable next step for a student?',
    answerTwo: 'Create a weekly experiment log and review outcomes every Friday.',
    timestampTwo: '24:16',
  },
];

const STUDY_GUIDE_DEMO_PREVIEWS = [
  {
    overview: 'Overview: convert the lecture into a focused weekly revision plan.',
    objectives: [
      'Objective: identify the 3 highest-impact ideas.',
      'Objective: map each idea to one practical use case.',
    ],
    reviewQuestion: 'Review: Which assumption changed most after the evidence section?',
  },
  {
    overview: 'Overview: break a long transcript into clear modules for recall.',
    objectives: [
      'Objective: summarize each module in one sentence.',
      'Objective: compare the speaker framework against your current workflow.',
    ],
    reviewQuestion: 'Review: What tradeoff appears when speed is prioritized over depth?',
  },
  {
    overview: 'Overview: synthesize the core argument into exam-ready notes.',
    objectives: [
      'Objective: define key terms before analyzing claims.',
      'Objective: connect each claim to one cited source timestamp.',
    ],
    reviewQuestion: 'Review: Which piece of evidence is strongest and why?',
  },
];

const ACADEMIC_DEMO_PREVIEWS = [
  {
    reference: '[1] Smith et al. (2024) - Learning Loops in Applied Research',
    claim: 'Claim: weekly constrained experiments improved retention by 18%.',
    glossary: ['Baseline', 'Confounder', 'Effect Size'],
  },
  {
    reference: '[2] Patel & Nguyen (2023) - Evidence-Grounded Learning Design',
    claim: 'Claim: citation-linked summaries reduced review time without lowering accuracy.',
    glossary: ['Cohort', 'Intervention', 'Variance'],
  },
  {
    reference: '[3] Jensen (2025) - Retrieval Practice Across Long-Form Video',
    claim: 'Claim: timestamped prompts increased long-term recall in follow-up tests.',
    glossary: ['Retention', 'Recall', 'Signal Noise'],
  },
];

const BRAND_NAME = 'ScribeSnap';
const BRAND_LOGO_SRC = '/logo-wordmark.png';
const FOOTER_LOGO_SRC = '/scribesnap_wordmark_footer.svg';
const CANONICAL_APP_ORIGIN = 'https://scribesnap.ai';
const CANONICAL_APP_HOST = new URL(CANONICAL_APP_ORIGIN).hostname.toLowerCase();
const LOCAL_DEV_HOSTS = new Set(['localhost', '127.0.0.1']);
const PLATFORM_BRAND = {
  youtube:     { icon: '#FF0000', stat: '#1F6BFF', bg: 'rgba(123,211,255,0.22)', bgSoft: 'rgba(123,211,255,0.18)' },
  vimeo:       { icon: '#1AB7EA', stat: '#3C8CFF', bg: 'rgba(60,140,255,0.16)',  bgSoft: 'rgba(60,140,255,0.14)' },
  tiktok:      { icon: '#010101', stat: '#FF0050', bg: 'rgba(255,0,80,0.10)',    bgSoft: 'rgba(255,0,80,0.08)'   },
  twitter:     { icon: '#000000', stat: '#1D9BF0', bg: 'rgba(29,155,240,0.12)', bgSoft: 'rgba(29,155,240,0.09)' },
  instagram:   { icon: '#E1306C', stat: '#C13584', bg: 'rgba(193,53,132,0.12)', bgSoft: 'rgba(193,53,132,0.09)' },
  dailymotion: { icon: '#00B4F0', stat: '#0065A3', bg: 'rgba(0,101,163,0.12)',  bgSoft: 'rgba(0,101,163,0.09)'  },
  facebook:    { icon: '#1877F2', stat: '#1877F2', bg: 'rgba(24,119,242,0.12)', bgSoft: 'rgba(24,119,242,0.09)' },
  loom:        { icon: '#625DF5', stat: '#625DF5', bg: 'rgba(98,93,245,0.12)',  bgSoft: 'rgba(98,93,245,0.09)'  },
  wistia:      { icon: '#54ABCC', stat: '#54ABCC', bg: 'rgba(84,171,204,0.12)', bgSoft: 'rgba(84,171,204,0.09)' },
};

const HERO_TRUST_LOGOS = ['youtube', 'tiktok', 'instagram', 'twitter', 'vimeo', 'loom'];

function isInAppBrowser() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Instagram|FBAN|FBAV|FB_IAB|FB4A|FBIOS|Twitter|TikTok|Snapchat|Pinterest|LinkedIn|WhatsApp|Line\/|KAKAOTALK|MicroMessenger/i.test(ua);
}

function getAuthRedirectUrl() {
  if (typeof window === 'undefined') return CANONICAL_APP_ORIGIN;
  // Keep OAuth callback on the same host that started auth so PKCE verifier
  // stays in same-origin storage.
  return window.location.origin;
}

function getPasswordResetRedirectUrl() {
  const base = getAuthRedirectUrl();
  try {
    const u = new URL(base);
    u.searchParams.set('reset', '1');
    return u.toString();
  } catch {
    return `${base}?reset=1`;
  }
}

const AUTH_URL_KEYS = [
  'access_token',
  'refresh_token',
  'expires_at',
  'expires_in',
  'token_type',
  'provider_token',
  'provider_refresh_token',
  'code',
  'token_hash',
  'type',
  'mode',
  'action',
  'reset',
  'error',
  'error_code',
  'error_description',
  'state',
  'sb',
];

function getAuthUrlState() {
  if (typeof window === 'undefined') {
    return {
      hasAuthParams: false,
      hasBlockingAuthParams: false,
      isRecovery: false,
      tokenHash: '',
      authCode: '',
    };
  }
  const searchParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
  const getParam = (key) => searchParams.get(key) || hashParams.get(key) || '';

  const hasAuthParams = AUTH_URL_KEYS.some((k) => searchParams.has(k) || hashParams.has(k));
  const hasBlockingAuthParams = [
    'access_token',
    'refresh_token',
    'code',
    'token_hash',
    'provider_token',
    'provider_refresh_token',
  ].some((k) => searchParams.has(k) || hashParams.has(k));
  const type = getParam('type').toLowerCase();
  const mode = getParam('mode').toLowerCase();
  const action = getParam('action').toLowerCase();
  const resetFlag = getParam('reset').toLowerCase();
  const tokenHash = getParam('token_hash');
  const authCode = getParam('code');
  const isRecovery = type === 'recovery' || mode === 'recovery' || mode === 'reset' || action === 'reset_password' || resetFlag === '1' || resetFlag === 'true';

  return { hasAuthParams, hasBlockingAuthParams, isRecovery, tokenHash, authCode };
}

function cleanupAuthUrl() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const hashParams = new URLSearchParams((url.hash || '').replace(/^#/, ''));

  let changed = false;
  AUTH_URL_KEYS.forEach((k) => {
    if (url.searchParams.has(k)) {
      url.searchParams.delete(k);
      changed = true;
    }
    if (hashParams.has(k)) {
      hashParams.delete(k);
      changed = true;
    }
  });
  if (!changed) return;

  const search = url.searchParams.toString();
  const hash = hashParams.toString();
  const nextUrl = `${url.pathname}${search ? `?${search}` : ''}${hash ? `#${hash}` : ''}`;
  window.history.replaceState({}, document.title, nextUrl);
}

// ── Helpers ───────────────────────────────────────────────────────────────────
// Returns { platform, id, url } or null
function parseVideoUrl(input) {
  if (!input) return null;
  const trimmed = input.trim();

  try {
    const u = new URL(trimmed);
    const h = u.hostname.replace(/^www\./, '');

    // Vimeo
    if (h.includes('vimeo.com')) {
      const m = u.pathname.match(/\/(\d+)/);
      if (m) return { platform: 'vimeo', id: m[1], url: `https://vimeo.com/${m[1]}` };
    }

    // TikTok — extract numeric video ID for embed
    if (h === 'tiktok.com' || h === 'vm.tiktok.com' || h === 'vt.tiktok.com') {
      const m = u.pathname.match(/\/video\/(\d+)/);
      return { platform: 'tiktok', id: m ? m[1] : trimmed, url: trimmed };
    }

    // Twitter / X
    if (h === 'twitter.com' || h === 'x.com' || h === 't.co')
      return { platform: 'twitter', id: trimmed, url: trimmed };

    // Instagram
    if (h === 'instagram.com')
      return { platform: 'instagram', id: trimmed, url: trimmed };

    // Dailymotion — extract video ID for embed
    if (h === 'dailymotion.com' || h === 'dai.ly') {
      const m = u.pathname.match(/\/(?:video\/)?([a-zA-Z0-9]+)/);
      return { platform: 'dailymotion', id: m ? m[1] : trimmed, url: trimmed };
    }

    // Facebook
    if (h === 'facebook.com' || h === 'fb.watch' || h === 'fb.com')
      return { platform: 'facebook', id: trimmed, url: trimmed };

    // Loom — extract UUID for embed
    if (h === 'loom.com' || h === 'www.loom.com') {
      const m = u.pathname.match(/\/(?:share|embed)\/([a-zA-Z0-9]+)/);
      return { platform: 'loom', id: m ? m[1] : trimmed, url: trimmed };
    }

    // Wistia / Brightcove
    if (h.includes('wistia.com') || h === 'wi.st' || h.includes('brightcove.com'))
      return { platform: 'wistia', id: trimmed, url: trimmed };

    // YouTube
    let id = u.searchParams.get('v');
    if (!id && h === 'youtu.be') id = u.pathname.slice(1).split('?')[0];
    if (!id) {
      const m = u.pathname.match(/\/(shorts|embed|v)\/([a-zA-Z0-9_-]{11})/);
      if (m) id = m[2];
    }
    if (id && /^[a-zA-Z0-9_-]{11}$/.test(id))
      return { platform: 'youtube', id, url: `https://youtube.com/watch?v=${id}` };
  } catch {}

  // YouTube bare ID
  if (/^[a-zA-Z0-9_-]{11}$/.test(trimmed))
    return { platform: 'youtube', id: trimmed, url: `https://youtube.com/watch?v=${trimmed}` };

  return null;
}

function formatTime(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function formatVideoDuration(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m} min ${s} sec`;
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

async function fetchVideoMeta(platform, canonicalUrl) {
  try {
    const res = await fetch(`/api/meta?platform=${encodeURIComponent(platform)}&url=${encodeURIComponent(canonicalUrl)}`);
    if (!res.ok) return { title: '', channel: '', thumbnail: null };
    return await res.json();
  } catch {}
  return { title: '', channel: '', thumbnail: null };
}

// ── Icons ─────────────────────────────────────────────────────────────────────
const DownloadIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
    <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
  </svg>
);
const ChevronIcon = ({ size = 13, dir = 'down' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ transform: dir === 'up' ? 'rotate(180deg)' : dir === 'left' ? 'rotate(90deg)' : 'none' }}>
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);
const CopyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);
const CheckIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const SpinnerIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
    style={{ animation: 'spin 0.8s linear infinite' }}>
    <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
  </svg>
);
const BugIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 2l1.88 1.88"/><path d="M14.12 3.88 16 2"/>
    <path d="M9 7.13v-1a3.003 3.003 0 1 1 6 0v1"/>
    <path d="M12 20c-3.3 0-6-2.7-6-6v-3a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v3c0 3.3-2.7 6-6 6z"/>
    <path d="M12 20v-9"/><path d="M6.53 9C4.6 8.8 3 7.1 3 5"/><path d="M6 13H2"/><path d="M3 21c0-2.1 1.7-3.9 4-4"/>
    <path d="M20.97 5c0 2.1-1.6 3.8-3.5 4"/><path d="M22 13h-4"/><path d="M17 17c2.3.1 4 1.9 4 4"/>
  </svg>
);
// ScribeSnap wave logo animated loading indicator
const ScribeSnapWaveLoader = ({ width = 140, color }) => (
  <svg viewBox="0 0 512 512" width={width} height={Math.round(width * 0.35)} style={{ display: 'block', overflow: 'visible' }}>
    <defs>
      {!color && (
        <linearGradient id="ss-wave-grad" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#7bd3ff" />
          <stop offset="55%" stopColor="#3c8cff" />
          <stop offset="100%" stopColor="#1f6bff" />
        </linearGradient>
      )}
    </defs>
    <path
      d="M38.96,254.18c36.17,0,36.17-58.43,72.35-58.43s36.17,147.48,72.35,147.48,36.17-239.3,72.35-239.3,36.17,333.91,72.35,333.91,36.17-264.35,72.35-264.35,36.17,80.7,72.35,80.7"
      pathLength="1"
      fill="none"
      stroke={color || 'url(#ss-wave-grad)'}
      strokeWidth="26"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ animation: 'ss-wave-draw 2.4s cubic-bezier(0.5,0.1,0.5,1) infinite' }}
    />
    <circle cx="13.91" cy="254.18" r="13.91" fill={color || '#7bd3ff'} />
    <circle cx="498.09" cy="254.18" r="13.91" fill={color || '#1f6bff'} />
  </svg>
);
const YouTubeIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={PLATFORM_BRAND.youtube.icon}>
    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
  </svg>
);
const VimeoIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={PLATFORM_BRAND.vimeo.icon}>
    <path d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.409 0-2.578-1.294-3.553-3.881L5.322 11.4C4.603 8.816 3.834 7.522 3.01 7.522c-.179 0-.806.378-1.881 1.132L0 7.197c1.185-1.044 2.351-2.084 3.501-3.128C5.08 2.701 6.266 1.984 7.055 1.91c1.867-.18 3.016 1.1 3.447 3.838.465 2.953.789 4.789.971 5.507.539 2.45 1.131 3.674 1.776 3.674.502 0 1.256-.796 2.265-2.385 1.004-1.589 1.54-2.797 1.612-3.628.144-1.371-.395-2.061-1.614-2.061-.574 0-1.167.121-1.777.391 1.186-3.868 3.434-5.757 6.762-5.637 2.473.06 3.628 1.664 3.48 4.807z"/>
  </svg>
);
const TikTokIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={PLATFORM_BRAND.tiktok.icon}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.78 1.52V6.75a4.85 4.85 0 0 1-1.01-.06z"/>
  </svg>
);
const TwitterIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={PLATFORM_BRAND.twitter.icon}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.259 5.631 5.905-5.631zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);
const InstagramIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={PLATFORM_BRAND.instagram.icon}>
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/>
  </svg>
);
const DailymotionIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill={PLATFORM_BRAND.dailymotion.icon} aria-label="Dailymotion">
    <path d="M18.755 15.083c-2.339 0-4.135 1.901-4.135 4.147 0 2.339 1.796 4.115 4.339 4.115l-0.021 0.005c2.12 0 3.901-1.751 3.901-4.057 0-2.396-1.781-4.209-4.084-4.209zM0 0v32h32v-32zM27.588 27.745h-4.765v-1.881c-1.468 1.439-2.963 1.959-4.953 1.959-2.031 0-3.776-0.661-5.239-1.979-1.933-1.704-2.933-3.964-2.933-6.584 0-2.4 0.933-4.552 2.683-6.229 1.557-1.531 3.459-2.307 5.579-2.307 2.025 0 3.588 0.683 4.708 2.12v-7.303l4.921-1.020 0.005 0.005h-0.005z"/>
  </svg>
);
const FacebookIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={PLATFORM_BRAND.facebook.icon}>
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);
const LoomIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={PLATFORM_BRAND.loom.icon}>
    <path d="M22.001 10.802h-5.773l4.101-2.394-1.196-2.07-4.108 2.397 2.394-4.108-2.07-1.196-2.394 4.101V1.759h-2.388v5.773L8.173 3.43 6.104 4.628l2.394 4.108-4.108-2.397-1.196 2.07 4.101 2.394H1.523v2.389h5.772l-4.101 2.393 1.196 2.071 4.108-2.397-2.394 4.107 2.07 1.196 2.394-4.1v5.772h2.388v-5.773l2.394 4.101 2.07-1.196-2.394-4.107 4.107 2.397 1.197-2.071-4.102-2.393h5.773v-2.389z"/>
  </svg>
);
const WistiaIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={PLATFORM_BRAND.wistia.icon}>
    <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm-1.25 16.518l-4.5-4.319 1.396-1.435 3.078 2.937 6.105-6.437 1.429 1.403-7.508 7.851z"/>
  </svg>
);
const PlatformIcon = ({ platform, size = 18 }) => {
  if (platform === 'youtube')     return <YouTubeIcon size={size} />;
  if (platform === 'vimeo')       return <VimeoIcon size={size} />;
  if (platform === 'tiktok')      return <TikTokIcon size={size} />;
  if (platform === 'twitter')     return <TwitterIcon size={size} />;
  if (platform === 'instagram')   return <InstagramIcon size={size} />;
  if (platform === 'dailymotion') return <DailymotionIcon size={size} />;
  if (platform === 'facebook')    return <FacebookIcon size={size} />;
  if (platform === 'loom')        return <LoomIcon size={size} />;
  if (platform === 'wistia')      return <WistiaIcon size={size} />;
  // Generic video icon fallback
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2"/><polygon points="8 21 16 21 12 17"/>
    </svg>
  );
};
const GitHubIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0 0 24 12c0-6.63-5.37-12-12-12z"/>
  </svg>
);
const LinkedInIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);
const BrandLogo = ({ height = 36 }) => (
  <img
    src={BRAND_LOGO_SRC}
    alt={BRAND_NAME}
    style={{ height, width: 'auto', display: 'block' }}
  />
);

// ── Referral Promo Modal ───────────────────────────────────────────────────────
const ReferralPromoModal = ({ user, onClose }) => {
  const [copied, setCopied] = React.useState(false);
  const refLink = `${window.location.origin}?ref=${user.id}`;
  const shareText = `Check out ScribeSnap — it extracts YouTube transcripts in seconds! Sign up with my link and we both get +3 free credits: ${refLink}`;
  const tweetText = `I use ScribeSnap to get YouTube transcripts instantly 🎬 Try it free — use my invite link and we both get bonus credits 👉 ${refLink}`;
  const emailSubject = 'Try ScribeSnap — free YouTube transcript tool';
  const copyLink = () => { navigator.clipboard.writeText(refLink).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); }); };
  const shareWA    = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank', 'noopener,noreferrer');
  const shareX     = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`, '_blank', 'noopener,noreferrer');
  const shareEmail = () => window.open(`mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(shareText)}`, '_blank', 'noopener,noreferrer');

  const shareBtn = (onClick, border, bg, hoverBg, color, icon, label) => (
    <button onClick={onClick} style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
      borderRadius: 10, border, background: bg, color,
      fontWeight: 600, fontSize: 13, cursor: 'pointer', transition: 'all 0.15s', flex: 1,
    }}
      onMouseEnter={e => { e.currentTarget.style.background = hoverBg; }}
      onMouseLeave={e => { e.currentTarget.style.background = bg; }}
    >{icon}{label}</button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(29,29,31,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: '100%', maxWidth: 420, background: P.surface, borderRadius: 20, border: `1px solid ${P.border}`, boxShadow: '0 24px 80px rgba(29,29,31,0.2)', overflow: 'hidden', animation: 'fadeUp 0.3s ease' }}>
        <div style={{ height: 4, background: 'linear-gradient(90deg, #3C8CFF, #5B9FFF)' }} />
        <div style={{ padding: '22px 22px 26px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{ width: 42, height: 42, borderRadius: 12, background: 'rgba(60,140,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3C8CFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/>
                  <line x1="12" y1="22" x2="12" y2="7"/>
                  <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
                  <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
                </svg>
              </div>
              <div>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: P.ink }}>Get more free credits</h2>
                <p style={{ margin: '2px 0 0', fontSize: 12, color: P.muted }}>Invite friends · earn +3 credits per signup</p>
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: P.muted, fontSize: 20, lineHeight: 1, padding: '0 2px', transition: 'color 0.15s', flexShrink: 0 }}
              onMouseEnter={e => { e.currentTarget.style.color = P.ink; }}
              onMouseLeave={e => { e.currentTarget.style.color = P.muted; }}
            >×</button>
          </div>

          <div style={{ background: P.paper, borderRadius: 10, border: `1px solid ${P.border}`, padding: '9px 12px', marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: P.muted, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 3 }}>Your invite link</div>
            <div style={{ fontSize: 12, color: P.ink, wordBreak: 'break-all' }}>{refLink}</div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            {shareBtn(shareWA,
              '1px solid rgba(37,211,102,0.3)', 'rgba(37,211,102,0.07)', 'rgba(37,211,102,0.14)', '#128C7E',
              <svg width="15" height="15" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>,
              'WhatsApp'
            )}
            {shareBtn(shareX,
              '1px solid rgba(0,0,0,0.13)', 'rgba(0,0,0,0.04)', 'rgba(0,0,0,0.09)', P.ink,
              <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>,
              'Share on X'
            )}
            {shareBtn(shareEmail,
              `1px solid ${P.border}`, P.paper, P.surface, P.ink,
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2,4 12,13 22,4"/></svg>,
              'Email'
            )}
            {shareBtn(copyLink,
              copied ? '1px solid rgba(15,118,110,0.3)' : `1px solid ${P.border}`,
              copied ? 'rgba(15,118,110,0.08)' : P.paper,
              copied ? 'rgba(15,118,110,0.08)' : P.surface,
              copied ? '#0F766E' : P.ink,
              copied
                ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>,
              copied ? 'Copied!' : 'Copy link'
            )}
          </div>
          <p style={{ margin: 0, fontSize: 11, color: P.muted, textAlign: 'center' }}>
            Both you and your friend get <strong style={{ color: P.accent }}>+3 free credits</strong> automatically on signup
          </p>
        </div>
      </div>
    </div>
  );
};

// ── Credits ───────────────────────────────────────────────────────────────────
const CREDITS_FREE = 2;   // not signed in
const CREDITS_MAX  = 20;  // signed in
const CREDITS_PERIOD_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function initCredits() {
  try {
    const max = CREDITS_FREE;
    let stored = JSON.parse(localStorage.getItem('yte_credits') || 'null');
    if (!stored || typeof stored.resetAt !== 'number' || Date.now() > stored.resetAt) {
      stored = { used: 0, resetAt: Date.now() + CREDITS_PERIOD_MS };
    }
    if (stored.used > max) stored = { ...stored, used: max };
    stored = { ...stored, tierMax: max, userId: null };
    localStorage.setItem('yte_credits', JSON.stringify(stored));
    return stored;
  } catch {
    return { used: 0, resetAt: Date.now() + CREDITS_PERIOD_MS, tierMax: CREDITS_FREE, userId: null };
  }
}

const CreditsWidget = ({ credits, onUpgrade, user, onShowReferralPromo }) => {
  const [open, setOpen] = React.useState(false);
  const [refCopied, setRefCopied] = React.useState(false);
  const ref = React.useRef(null);
  const refLink = user ? `${window.location.origin}?ref=${user.id}` : '';
  const copyRefLink = () => {
    if (!refLink) return;
    navigator.clipboard.writeText(refLink).then(() => {
      setRefCopied(true);
      setTimeout(() => setRefCopied(false), 2000);
    });
  };
  const shareWA    = () => window.open(`https://wa.me/?text=${encodeURIComponent(`Check out ScribeSnap — it extracts YouTube transcripts in seconds! Sign up with my link and we both get +3 free credits: ${refLink}`)}`, '_blank', 'noopener,noreferrer');
  const shareX     = () => window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(`I use ScribeSnap to get YouTube transcripts instantly 🎬 Try it free — use my invite link and we both get bonus credits 👉 ${refLink}`)}`, '_blank', 'noopener,noreferrer');
  const shareEmail = () => window.open(`mailto:?subject=${encodeURIComponent('Try ScribeSnap — free YouTube transcript tool')}&body=${encodeURIComponent(`Check out ScribeSnap — it extracts YouTube transcripts in seconds! Sign up with my link and we both get +3 free credits: ${refLink}`)}`, '_blank', 'noopener,noreferrer');
  const used = credits?.used ?? 0;
  const resetAt = credits?.resetAt ?? (Date.now() + CREDITS_PERIOD_MS);
  const tierMax = credits?.tierMax || (credits?.userId ? CREDITS_MAX : CREDITS_FREE);
  const daysLeft = Math.max(0, Math.ceil((resetAt - Date.now()) / 86400000));
  const pct = Math.min(100, (used / tierMax) * 100);
  const nearLimit = used >= tierMax * 0.8;
  const isGuest = !credits?.userId;

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '5px 10px', borderRadius: 8,
          border: `1px solid ${nearLimit ? 'rgba(180,83,9,0.3)' : P.border}`,
          background: nearLimit ? 'rgba(180,83,9,0.06)' : P.paper,
          cursor: 'pointer', transition: 'all 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = nearLimit ? 'rgba(180,83,9,0.1)' : P.surface; }}
        onMouseLeave={e => { e.currentTarget.style.background = nearLimit ? 'rgba(180,83,9,0.06)' : P.paper; }}
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill={nearLimit ? P.warning : P.accent}>
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
        </svg>
        <span style={{ fontSize: 12, fontWeight: 600, color: nearLimit ? P.warning : P.ink, fontVariantNumeric: 'tabular-nums' }}>
          {used} / {tierMax}
        </span>
      </button>

      {open && (
        <div className="fade-up" style={{
          position: 'absolute', right: 0, top: 'calc(100% + 8px)',
          width: 290, background: P.surface, border: `1px solid ${P.border}`,
          borderRadius: 14, boxShadow: '0 8px 32px rgba(29,29,31,0.12)',
          padding: '14px 16px', zIndex: 200,
        }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: P.ink, marginBottom: 6 }}>
            Free Credits
          </div>

          {/* Progress bar */}
          <div style={{ height: 5, borderRadius: 999, background: P.border, overflow: 'hidden', marginBottom: 8 }}>
            <div style={{
              height: '100%', width: `${pct}%`, borderRadius: 999,
              background: nearLimit ? P.warning : P.accent,
              transition: 'width 0.4s ease',
            }} />
          </div>

          <div style={{ fontSize: 12, color: P.muted, marginBottom: 4 }}>
            <span style={{ color: P.ink, fontWeight: 600 }}>{used} of {tierMax}</span> used
          </div>
          <div style={{ fontSize: 11, color: P.muted }}>
            Resets in <span style={{ fontWeight: 600, color: P.ink }}>{daysLeft} day{daysLeft !== 1 ? 's' : ''}</span>
          </div>
          {isGuest && (
            <div style={{ marginTop: 10, fontSize: 11, color: P.muted, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span>Sign in to get <strong style={{ color: P.ink }}>20 credits</strong> per 7-day period.</span>
              {used >= tierMax && (
                <button
                  onClick={onUpgrade}
                  style={{
                    alignSelf: 'flex-start',
                    padding: '6px 10px',
                    borderRadius: 8,
                    border: `1px solid ${P.accent}`,
                    background: P.accent,
                    color: 'white',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = P.accentHover; }}
                  onMouseLeave={e => { e.currentTarget.style.background = P.accent; }}
                >
                  Create free account for 20 credits
                </button>
              )}
            </div>
          )}
          {!isGuest && refLink && (
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${P.border}` }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
                <div style={{ width: 26, height: 26, borderRadius: 7, background: 'rgba(60,140,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3C8CFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                    <circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: P.ink }}>Invite friends, earn credits</div>
              </div>
              {/* Badge */}
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 20, background: 'rgba(60,140,255,0.09)', marginBottom: 7 }}>
                <svg width="9" height="9" viewBox="0 0 24 24" fill="#3C8CFF"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#3C8CFF' }}>+3 free credits per friend who signs up</span>
              </div>
              {/* Description */}
              <p style={{ margin: '0 0 9px', fontSize: 11, color: P.muted, lineHeight: 1.5 }}>
                Share your personal link. Every friend who creates an account using it gets a bonus — and so do you.
              </p>
              {/* Link box */}
              <div style={{ background: P.paper, border: `1px solid ${P.border}`, borderRadius: 8, padding: '6px 9px', marginBottom: 7, fontSize: 11, color: P.muted, wordBreak: 'break-all', lineHeight: 1.4 }}>
                {refLink}
              </div>
              {/* Copy button */}
              <button onClick={copyRefLink} style={{
                width: '100%', padding: '7px 0', borderRadius: 8, marginBottom: 7,
                border: `1px solid ${refCopied ? 'rgba(15,118,110,0.3)' : 'rgba(60,140,255,0.3)'}`,
                background: refCopied ? 'rgba(15,118,110,0.08)' : 'rgba(60,140,255,0.07)',
                color: refCopied ? '#0F766E' : P.accent,
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                transition: 'all 0.15s',
              }}>
                {refCopied
                  ? <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>Copied!</>
                  : <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2 2v1"/></svg>Copy link</>
                }
              </button>
              {/* Social share row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 6 }}>
                {[
                  { label: 'WhatsApp', color: '#128C7E', bg: 'rgba(37,211,102,0.07)', border: 'rgba(37,211,102,0.3)', fn: shareWA,
                    icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg> },
                  { label: 'Share on X', color: P.ink, bg: 'rgba(0,0,0,0.04)', border: 'rgba(0,0,0,0.13)', fn: shareX,
                    icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
                  { label: 'Email', color: P.ink, bg: P.paper, border: P.border, fn: shareEmail,
                    icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2,4 12,13 22,4"/></svg> },
                ].map(({ label, color, bg, border, fn, icon }) => (
                  <button key={label} onClick={fn} style={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                    padding: '7px 4px', borderRadius: 8, border: `1px solid ${border}`,
                    background: bg, color, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(0.93)'; }}
                    onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}
                  >{icon}{label}</button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Shared helpers ────────────────────────────────────────────────���───────────
const AuthLogo = () => (
  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
    <BrandLogo height={40} />
  </div>
);

const modalOverlay = {
  position: 'fixed', inset: 0, zIndex: 1000,
  background: 'rgba(29,29,31,0.5)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
};
const modalCard = {
  width: '100%', maxWidth: 420,
  background: P.surface, borderRadius: 20,
  border: `1px solid ${P.border}`,
  boxShadow: '0 24px 80px rgba(29,29,31,0.2)',
  padding: '32px 32px 28px',
  position: 'relative',
};
const friendlyError = (msg = '') => {
  if (msg.includes('Email not confirmed')) return 'Please confirm your email first. Check your inbox for the confirmation link.';
  if (msg.includes('Invalid login credentials')) return 'Incorrect email or password. Please try again.';
  if (msg.includes('User already registered')) return 'An account with this email already exists. Try signing in instead.';
  if (msg.includes('Password should be at least')) return 'Password must be at least 6 characters.';
  if (msg.includes('rate limit') || msg.includes('too many')) return 'Too many attempts. Please wait a moment and try again.';
  if (msg.includes('network') || msg.includes('fetch')) return 'Network error. Please check your connection.';
  return msg || 'Something went wrong. Please try again.';
};

const funnyTranscriptError = (msg = '') => {
  const m = msg.toLowerCase();
  if (m.includes('rate') || m.includes('too many requests') || m.includes('429'))
    return "YouTube's bouncer cut us off. Give it a minute, then try again.";
  if (m.includes('private') || m.includes('members only') || m.includes('members-only'))
    return "That video has trust issues. It's private or members-only — nothing we can do here.";
  if (m.includes('copyright'))
    return "A copyright lawyer got there first. This one's locked down.";
  if (m.includes('unavailable'))
    return "The video didn't show up. Classic. Double-check the URL and try again.";
  if (m.includes('no captions') || m.includes('no captions found'))
    return "This video went caption-free and AI transcription isn't set up. Nothing to grab, sadly.";
  if (m.includes('audio file too large') || m.includes('too large'))
    return "That video is way too long. Even we have limits. Try a shorter one.";
  if (m.includes('timed out') || m.includes('timeout'))
    return "Still loading... just kidding, we gave up. The video might be hiding. Try again.";
  if (m.includes('connection') || m.includes('network') || m.includes('fetch'))
    return "Connection ghosted us. Were you on a train? Try again.";
  if (m.includes('failed to process'))
    return "We got the transcript and immediately dropped it. Very smooth. Try again.";
  if (m.includes('invalid') && m.includes('url'))
    return "That URL looks suspicious. Try YouTube, Vimeo, TikTok, X, Instagram, Dailymotion, Facebook, Loom, or Wistia.";
  if (m.includes('invalid') && m.includes('vimeo'))
    return "That Vimeo URL didn't pass the vibe check. Try pasting it again.";
  if (m.includes('failed to fetch') || m.includes('failed to download'))
    return "The transcript played hard to get. Try again.";
  return msg || "Something went sideways. Give it another shot.";
};

// ── AuthModal ─────────────────────────────────────────────────────────────────
const AuthModal = ({ onClose, onAuthSuccess, initialTab = 'signin' }) => {
  const [screen, setScreen]     = React.useState(initialTab); // 'signin'|'signup'|'forgot'|'pending'
  const [email, setEmail]       = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm]   = React.useState('');
  const [username, setUsername] = React.useState('');
  const [loading, setLoading]   = React.useState(false);
  const [error, setError]       = React.useState('');
  const [resendLoading, setResendLoading] = React.useState(false);
  const [resendDone, setResendDone]       = React.useState(false);
  const [linkCopied, setLinkCopied]       = React.useState(false);
  const overlayRef = React.useRef(null);

  React.useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Poll for email confirmation so cross-device activation works
  React.useEffect(() => {
    if (screen !== 'pending' || !email || !password) return;
    const id = setInterval(async () => {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (!error) {
        clearInterval(id);
        onClose();
      }
    }, 5000);
    return () => clearInterval(id);
  }, [screen, email, password, onClose]);

  const inputStyle = {
    width: '100%', padding: '11px 13px', borderRadius: 10,
    border: `1px solid ${P.border}`, background: P.paper,
    fontSize: 14, color: P.ink, outline: 'none', transition: 'border-color 0.15s',
    fontFamily: 'inherit',
  };

  const switchScreen = (s) => { setScreen(s); setError(''); setResendDone(false); };

  const handleSignIn = async (e) => {
    e.preventDefault(); setError('');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) { setError('Please enter a valid email address.'); return; }
    setLoading(true);
    try {
      const { data, error: err } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (err) { setError(friendlyError(err.message)); return; }
      if (data?.user) { onAuthSuccess(data.user); onClose(); }
    } finally { setLoading(false); }
  };

  const handleSignUp = async (e) => {
    e.preventDefault(); setError('');
    const trimmedUser = username.trim();
    const trimmedEmail = email.trim().toLowerCase();
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    if (!emailRegex.test(trimmedEmail)) { setError('Please enter a valid email address (e.g. you@example.com).'); return; }
    // Block obvious disposable/fake domains
    const blockedDomains = ['mailinator.com','guerrillamail.com','trashmail.com','tempmail.com','yopmail.com','sharklasers.com','throwam.com','dispostable.com','maildrop.cc','fakeinbox.com'];
    const emailDomain = trimmedEmail.split('@')[1] || '';
    if (blockedDomains.includes(emailDomain)) { setError('Please use a real email address.'); return; }
    if (!trimmedUser) { setError('Please choose a username.'); return; }
    if (trimmedUser.length < 2) { setError('Username must be at least 2 characters.'); return; }
    if (!/^[a-zA-Z0-9_.-]+$/.test(trimmedUser)) { setError('Username can only contain letters, numbers, _, . and -'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    // Require at least "Fair" strength
    const pwScore = [password.length>=8, password.length>=12, /[A-Z]/.test(password), /[a-z]/.test(password), /[0-9]/.test(password), /[^A-Za-z0-9]/.test(password)].filter(Boolean).length;
    if (pwScore <= 2) { setError('Password is too weak. Add uppercase letters, numbers, or symbols.'); return; }
    setLoading(true);
    try {
      // Check username availability against profiles table
      const { data: existing } = await supabase
        .from('profiles')
        .select('id')
        .ilike('username', trimmedUser)
        .maybeSingle();
      if (existing) { setError('Username already taken. Please choose another.'); return; }

      const signUpPendingRef = localStorage.getItem('yte_pending_ref');
      const { error: err } = await supabase.auth.signUp({
        email, password,
        options: { data: { full_name: trimmedUser, username: trimmedUser.toLowerCase(), ...(signUpPendingRef ? { referred_by: signUpPendingRef } : {}) } },
      });
      if (err) { setError(friendlyError(err.message)); return; }
      setScreen('pending');
    } finally { setLoading(false); }
  };

  const handleForgot = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const { error: err } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: getPasswordResetRedirectUrl(),
      });
      if (err) { setError(friendlyError(err.message)); return; }
      setScreen('forgotSent');
    } finally { setLoading(false); }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await supabase.auth.resend({ type: 'signup', email });
      setResendDone(true);
    } finally { setResendLoading(false); }
  };

  const CloseBtn = () => (
    <button onClick={onClose} style={{
      position: 'absolute', top: 12, right: 12, width: 40, height: 40,
      borderRadius: 10, background: 'none', border: 'none', cursor: 'pointer',
      color: P.muted, fontSize: 20, lineHeight: 1,
      display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.background = P.paper; e.currentTarget.style.color = P.ink; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = P.muted; }}
    >×</button>
  );

  const SubmitBtn = ({ label }) => (
    <button type="submit" disabled={loading} style={{
      marginTop: 4, padding: '11px 0', minHeight: 44, borderRadius: 10, border: 'none',
      background: loading ? 'rgba(60,140,255,0.55)' : P.accent,
      color: 'white', fontSize: 14, fontWeight: 600,
      cursor: loading ? 'not-allowed' : 'pointer', transition: 'background 0.15s',
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%',
    }}
      onMouseEnter={e => { if (!loading) e.currentTarget.style.background = P.accentHover; }}
      onMouseLeave={e => { if (!loading) e.currentTarget.style.background = P.accent; }}
    >{loading && <SpinnerIcon size={14} />}{label}</button>
  );

  const ErrorBox = ({ msg }) => msg ? (
    <div style={{ padding: '9px 12px', borderRadius: 8, background: 'rgba(180,35,24,0.07)', border: `1px solid rgba(180,35,24,0.18)`, fontSize: 13, color: P.error, lineHeight: 1.5 }}>{msg}</div>
  ) : null;

  // ── Email pending screen ────────────────────────��──────────────────────────
  if (screen === 'pending') return (
    <div ref={overlayRef} onClick={e => { if (e.target === overlayRef.current) onClose(); }} style={modalOverlay}>
      <div className="fade-up" style={modalCard}>
        <CloseBtn />
        <AuthLogo />
        <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>📬</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: P.ink, marginBottom: 8 }}>Check your inbox</div>
          <div style={{ fontSize: 13, color: P.muted, lineHeight: 1.6, marginBottom: 20 }}>
            We sent a confirmation link to <strong style={{ color: P.ink }}>{email}</strong>.<br />
            Click it to activate your account, then come back and sign in.
          </div>
          {resendDone
            ? <div style={{ fontSize: 13, color: P.success, marginBottom: 12 }}>Resent! Check your spam folder if needed.</div>
            : <button onClick={handleResend} disabled={resendLoading} style={{ background: 'none', border: 'none', cursor: resendLoading ? 'default' : 'pointer', fontSize: 13, color: P.accent, fontWeight: 600, marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6, margin: '0 auto 12px' }}>
                {resendLoading && <SpinnerIcon size={12} />} Didn't get it? Resend
              </button>
          }
          <button onClick={() => switchScreen('signin')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: P.muted }}>
            ← Back to sign in
          </button>
        </div>
      </div>
    </div>
  );

  // ── Forgot sent screen ────────────────────────────────────────────────────
  if (screen === 'forgotSent') return (
    <div ref={overlayRef} onClick={e => { if (e.target === overlayRef.current) onClose(); }} style={modalOverlay}>
      <div className="fade-up" style={modalCard}>
        <CloseBtn />
        <AuthLogo />
        <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🔑</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: P.ink, marginBottom: 8 }}>Reset link sent</div>
          <div style={{ fontSize: 13, color: P.muted, lineHeight: 1.6, marginBottom: 20 }}>
            We sent a password reset link to <strong style={{ color: P.ink }}>{email}</strong>.<br />
            Click it to set a new password.
          </div>
          <button onClick={() => switchScreen('signin')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: P.muted }}>← Back to sign in</button>
        </div>
      </div>
    </div>
  );

  // ── Forgot password screen ────────────────────────────────────────────────
  if (screen === 'forgot') return (
    <div ref={overlayRef} onClick={e => { if (e.target === overlayRef.current) onClose(); }} style={modalOverlay}>
      <div className="fade-up" style={modalCard}>
        <CloseBtn />
        <AuthLogo />
        <div style={{ fontSize: 18, fontWeight: 700, color: P.ink, marginBottom: 4 }}>Reset password</div>
        <div style={{ fontSize: 13, color: P.muted, marginBottom: 20 }}>Enter your email and we'll send you a reset link.</div>
        <form onSubmit={handleForgot} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: P.ink, display: 'block', marginBottom: 5 }}>Email</label>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" style={inputStyle}
              onFocus={e => { e.target.style.borderColor = P.accent; }} onBlur={e => { e.target.style.borderColor = P.border; }} />
          </div>
          <ErrorBox msg={error} />
          <SubmitBtn label="Send reset link" />
        </form>
        <p style={{ textAlign: 'center', fontSize: 12, color: P.muted, marginTop: 16, marginBottom: 0 }}>
          <button onClick={() => switchScreen('signin')} style={{ background: 'none', border: 'none', color: P.accent, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>← Back to sign in</button>
        </p>
      </div>
    </div>
  );

  // ── Sign in / Sign up screen ──────────────────────────────────────────────
  const isSignUp = screen === 'signup';
  return (
    <div ref={overlayRef} onClick={e => { if (e.target === overlayRef.current) onClose(); }} style={modalOverlay}>
      <div className="fade-up" style={modalCard}>
        <CloseBtn />
        <AuthLogo />

        {/* Tabs */}
        <div style={{ display: 'flex', marginBottom: 24, background: P.paper, borderRadius: 10, padding: 3, border: `1px solid ${P.border}` }}>
          {[['signin', 'Sign in'], ['signup', 'Create account']].map(([key, label]) => (
            <button key={key} onClick={() => switchScreen(key)} style={{
              flex: 1, padding: '10px 0', minHeight: 40, borderRadius: 8, border: 'none',
              background: screen === key ? P.surface : 'transparent',
              color: screen === key ? P.ink : P.muted,
              fontSize: 13, fontWeight: screen === key ? 600 : 400,
              cursor: 'pointer', transition: 'all 0.15s',
              boxShadow: screen === key ? '0 1px 4px rgba(29,29,31,0.08)' : 'none',
            }}>{label}</button>
          ))}
        </div>

        <form onSubmit={isSignUp ? handleSignUp : handleSignIn} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {isSignUp && (() => {
            const adjs = ['swift','dark','silent','bright','wild','bold','calm','sharp','keen','cool','neo','cyber','solar','lunar','nova','prime','pixel','sonic','turbo','neon','royal','iron','ghost','storm','vivid'];
            const nouns = ['eagle','wolf','fox','panda','tiger','hawk','coder','ninja','rider','blade','spark','pulse','drift','nexus','orbit','vault','scout','pilot','forge','creek','peak','comet','flare','prism','craft'];
            const seps = ['_','.',''];
            const generateUsername = () => {
              const adj  = adjs[Math.floor(Math.random() * adjs.length)];
              const noun = nouns[Math.floor(Math.random() * nouns.length)];
              const sep  = seps[Math.floor(Math.random() * seps.length)];
              const num  = Math.random() > 0.5 ? Math.floor(Math.random() * 90 + 10) : '';
              return `${adj}${sep}${noun}${num}`;
            };
            return (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: P.ink }}>Username</label>
                  <button type="button" onClick={() => setUsername(generateUsername())}
                    style={{ background: 'none', border: 'none', fontSize: 11, color: P.muted, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: 4, transition: 'color 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.color = P.accent; }}
                    onMouseLeave={e => { e.currentTarget.style.color = P.muted; }}
                  >🎲 Random username</button>
                </div>
                <input type="text" required value={username} onChange={e => setUsername(e.target.value)} placeholder="yourname" style={inputStyle}
                  onFocus={e => { e.target.style.borderColor = P.accent; }} onBlur={e => { e.target.style.borderColor = P.border; }} />
              </div>
            );
          })()}
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: P.ink, display: 'block', marginBottom: 5 }}>Email</label>
            <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" style={inputStyle}
              onFocus={e => { e.target.style.borderColor = P.accent; }} onBlur={e => { e.target.style.borderColor = P.border; }} />
          </div>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: P.ink }}>Password</label>
              {!isSignUp && (
                <button type="button" onClick={() => switchScreen('forgot')}
                  style={{ background: 'none', border: 'none', fontSize: 12, color: P.muted, cursor: 'pointer', padding: '6px 2px', minHeight: 32, display: 'inline-flex', alignItems: 'center', transition: 'color 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.color = P.accent; }}
                  onMouseLeave={e => { e.currentTarget.style.color = P.muted; }}
                >Forgot password?</button>
              )}
            </div>
            <input type="password" required value={password} onChange={e => setPassword(e.target.value)}
              placeholder={isSignUp ? 'Min. 6 characters' : '••••••••'} style={inputStyle}
              onFocus={e => { e.target.style.borderColor = P.accent; }} onBlur={e => { e.target.style.borderColor = P.border; }} />
            {isSignUp && password.length > 0 && (() => {
              // Strength scoring
              let score = 0;
              if (password.length >= 8)  score++;
              if (password.length >= 12) score++;
              if (/[A-Z]/.test(password)) score++;
              if (/[a-z]/.test(password)) score++;
              if (/[0-9]/.test(password)) score++;
              if (/[^A-Za-z0-9]/.test(password)) score++;
              const level = score <= 2 ? 0 : score <= 3 ? 1 : score <= 4 ? 2 : 3;
              const labels  = ['Weak',   'Fair',    'Good',    'Strong'];
              const colors  = ['#B42318','#B45309', '#0F766E', '#166534'];
              const widths  = ['25%',    '50%',     '75%',     '100%'];
              return (
                <div style={{ marginTop: 8 }}>
                  {/* Bar */}
                  <div style={{ height: 4, borderRadius: 999, background: P.border, overflow: 'hidden', marginBottom: 5 }}>
                    <div style={{ height: '100%', width: widths[level], borderRadius: 999, background: colors[level], transition: 'width 0.3s ease, background 0.3s ease' }} />
                  </div>
                  {/* Label + generator link */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 11, fontWeight: 600, color: colors[level] }}>{labels[level]} password</span>
                    <a href="https://pwasecurity.org/" target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 11, color: P.muted, textDecoration: 'none', transition: 'color 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.color = P.accent; }}
                      onMouseLeave={e => { e.currentTarget.style.color = P.muted; }}
                    >🔐 Generate secure password</a>
                  </div>
                </div>
              );
            })()}
            {isSignUp && password.length === 0 && (
              <div style={{ marginTop: 5, textAlign: 'right' }}>
                <a href="https://pwasecurity.org/" target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 11, color: P.muted, textDecoration: 'none', transition: 'color 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.color = P.accent; }}
                  onMouseLeave={e => { e.currentTarget.style.color = P.muted; }}
                >🔐 Generate secure password</a>
              </div>
            )}
          </div>
          {isSignUp && (
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: P.ink, display: 'block', marginBottom: 5 }}>Repeat password</label>
              <input type="password" required value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat your password" style={inputStyle}
                onFocus={e => { e.target.style.borderColor = P.accent; }} onBlur={e => { e.target.style.borderColor = P.border; }} />
            </div>
          )}
          <ErrorBox msg={error} />
          <SubmitBtn label={isSignUp ? 'Create account' : 'Sign in'} />
        </form>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0 14px' }}>
          <div style={{ flex: 1, height: 1, background: P.border }} />
          <span style={{ fontSize: 12, color: P.muted }}>or</span>
          <div style={{ flex: 1, height: 1, background: P.border }} />
        </div>

        {/* Google button — replaced with copy-link CTA in in-app browsers */}
        {isInAppBrowser() ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: 8,
              padding: '10px 12px', borderRadius: 10,
              background: 'rgba(180,91,24,0.08)', border: '1px solid rgba(180,91,24,0.22)',
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}>
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <span style={{ fontSize: 12, color: '#B45309', lineHeight: 1.5 }}>
                Google sign-in doesn't work in in-app browsers. Open this page in Safari or Chrome first.
              </span>
            </div>
            <button
              onClick={() => {
                navigator.clipboard.writeText(window.location.href).then(() => {
                  setLinkCopied(true);
                  setTimeout(() => setLinkCopied(false), 2500);
                });
              }}
              style={{
                width: '100%', padding: '10px 0', minHeight: 44, borderRadius: 10,
                border: `1px solid ${P.border}`, background: P.paper,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                fontSize: 14, fontWeight: 600, color: linkCopied ? P.success : P.ink,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = P.surface; }}
              onMouseLeave={e => { e.currentTarget.style.background = P.paper; }}
            >
              {linkCopied ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Link copied!
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                  Copy link to open in browser
                </>
              )}
            </button>
          </div>
        ) : (
        <button
          onClick={async () => {
            setError('');
            const primaryRedirect = getAuthRedirectUrl();
            let { error: err } = await supabase.auth.signInWithOAuth({
              provider: 'google',
              options: { redirectTo: primaryRedirect },
            });
            // Fallback for environments where only canonical domain is allowlisted.
            if (
              err &&
              !LOCAL_DEV_HOSTS.has(window.location.hostname.toLowerCase()) &&
              primaryRedirect !== CANONICAL_APP_ORIGIN &&
              /(redirect|allow|invalid.*url|not allowed)/i.test(err.message || '')
            ) {
              const retry = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: { redirectTo: CANONICAL_APP_ORIGIN },
              });
              err = retry.error;
            }
            if (err) setError(friendlyError(err.message));
          }}
          style={{
            width: '100%', padding: '10px 0', minHeight: 44, borderRadius: 10,
            border: `1px solid ${P.border}`, background: P.paper,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            fontSize: 14, fontWeight: 600, color: P.ink, cursor: 'pointer',
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = P.surface; e.currentTarget.style.borderColor = '#aaa'; }}
          onMouseLeave={e => { e.currentTarget.style.background = P.paper; e.currentTarget.style.borderColor = P.border; }}
        >
          {/* Google "G" logo */}
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
            <path fill="none" d="M0 0h48v48H0z"/>
          </svg>
          Continue with Google
        </button>
        )}

        <p style={{ textAlign: 'center', fontSize: 12, color: P.muted, marginTop: 16, marginBottom: 0 }}>
          {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
          <button onClick={() => switchScreen(isSignUp ? 'signin' : 'signup')}
            style={{ background: 'none', border: 'none', color: P.accent, fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '6px 2px', minHeight: 32, display: 'inline-flex', alignItems: 'center' }}
          >{isSignUp ? 'Sign in' : 'Create one'}</button>
        </p>
      </div>
    </div>
  );
};

// ── PasswordResetModal ──────────────────────��─────��───────────────────────────
const PasswordResetModal = ({ onClose }) => {
  const [password, setPassword] = React.useState('');
  const [confirm, setConfirm]   = React.useState('');
  const [loading, setLoading]   = React.useState(false);
  const [error, setError]       = React.useState('');
  const [done, setDone]         = React.useState(false);

  const inputStyle = {
    width: '100%', padding: '10px 13px', borderRadius: 10,
    border: `1px solid ${P.border}`, background: P.paper,
    fontSize: 14, color: P.ink, outline: 'none', transition: 'border-color 0.15s', fontFamily: 'inherit',
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError('');
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ password });
      if (err) { setError(err.message); return; }
      setDone(true);
      setTimeout(onClose, 2000);
    } finally { setLoading(false); }
  };

  return (
    <div style={modalOverlay}>
      <div className="fade-up" style={modalCard}>
        <AuthLogo />
        {done ? (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✅</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: P.ink }}>Password updated!</div>
            <div style={{ fontSize: 13, color: P.muted, marginTop: 8 }}>Redirecting you back…</div>
          </div>
        ) : (
          <>
            <div style={{ fontSize: 18, fontWeight: 700, color: P.ink, marginBottom: 4 }}>Set new password</div>
            <div style={{ fontSize: 13, color: P.muted, marginBottom: 18 }}>Choose a new password for your account.</div>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: P.ink, display: 'block', marginBottom: 5 }}>New password</label>
                <input type="password" required value={password} onChange={e => setPassword(e.target.value)} placeholder="Min. 6 characters" style={inputStyle}
                  onFocus={e => { e.target.style.borderColor = P.accent; }} onBlur={e => { e.target.style.borderColor = P.border; }} />
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, color: P.ink, display: 'block', marginBottom: 5 }}>Confirm password</label>
                <input type="password" required value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat new password" style={inputStyle}
                  onFocus={e => { e.target.style.borderColor = P.accent; }} onBlur={e => { e.target.style.borderColor = P.border; }} />
              </div>
              <div style={{ marginTop: -2, textAlign: 'right' }}>
                <a href="https://pwasecurity.org/#" target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 11, color: P.muted, textDecoration: 'none', transition: 'color 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.color = P.accent; }}
                  onMouseLeave={e => { e.currentTarget.style.color = P.muted; }}
                >🔐 Generate secure password</a>
              </div>
              {error && <div style={{ padding: '9px 12px', borderRadius: 8, background: 'rgba(180,35,24,0.07)', border: `1px solid rgba(180,35,24,0.18)`, fontSize: 13, color: P.error }}>{error}</div>}
              <button type="submit" disabled={loading} style={{
                marginTop: 4, padding: '11px 0', minHeight: 44, borderRadius: 10, border: 'none',
                background: loading ? 'rgba(60,140,255,0.55)' : P.accent,
                color: 'white', fontSize: 14, fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              }}
                onMouseEnter={e => { if (!loading) e.currentTarget.style.background = P.accentHover; }}
                onMouseLeave={e => { if (!loading) e.currentTarget.style.background = P.accent; }}
              >{loading && <SpinnerIcon size={14} />}Update password</button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

// ── Dashboard ─────────────────────────────────────────────────────────────────
const Dashboard = ({ user, credits, history, setHistory, onBack, onSignOut, onLoadTranscript, lang }) => {
  const [tab, setTab] = React.useState('overview');
  // ON ICE: const [prefLangSaved, setPrefLangSaved] = React.useState(false);
  const [copyRefDone, setCopyRefDone] = React.useState(false);
  const [showAllHistory, setShowAllHistory] = React.useState(false);

  // Profile editing
  const [editingName, setEditingName] = React.useState(false);
  const [nameInput, setNameInput] = React.useState(
    user.user_metadata?.username || user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || ''
  );
  const [nameSaving, setNameSaving] = React.useState(false);
  const [nameSaved, setNameSaved] = React.useState(false);
  const [nameError, setNameError] = React.useState('');

  // Inline password change (multi-step)
  const [pwStep, setPwStep] = React.useState('idle'); // idle | form | confirm | loading | success | error
  const [pwCurrent, setPwCurrent] = React.useState('');
  const [pwNew, setPwNew] = React.useState('');
  const [pwConfirm, setPwConfirm] = React.useState('');
  const [pwError, setPwError] = React.useState('');

  // Confirmations
  const [signOutConfirm, setSignOutConfirm] = React.useState(false);
  const [clearConfirm, setClearConfirm] = React.useState(false);
  const [clearDone, setClearDone] = React.useState(false);

  // Delete account flow
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);
  const [deleteStep, setDeleteStep] = React.useState('reason'); // 'reason' | 'countdown' | 'typing' | 'deleting' | 'error'
  const [deleteReason, setDeleteReason] = React.useState('');
  const [deleteTyped, setDeleteTyped] = React.useState('');
  const [deleteCountdown, setDeleteCountdown] = React.useState(8);
  const [deleteError, setDeleteError] = React.useState('');

  React.useEffect(() => {
    if (deleteStep !== 'countdown' || deleteCountdown <= 0) return;
    const t = setTimeout(() => setDeleteCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [deleteStep, deleteCountdown]);

  // Extra preferences — read from Supabase user_metadata first, fall back to localStorage
  const prefKey = (k) => user ? `yte_pref_${k}_${user.id}` : `yte_pref_${k}`;
  const cloudPrefs = user.user_metadata?.prefs || {};
  const [prefTimestamps, setPrefTimestamps] = React.useState(() => {
    if ('timestamps' in cloudPrefs) return cloudPrefs.timestamps;
    try { return localStorage.getItem(prefKey('timestamps')) !== 'false'; } catch { return true; }
  });
  const [prefAutoCopy, setPrefAutoCopy] = React.useState(() => {
    if ('autocopy' in cloudPrefs) return cloudPrefs.autocopy;
    try { return localStorage.getItem(prefKey('autocopy')) === 'true'; } catch { return false; }
  });
  const [prefFormat, setPrefFormat] = React.useState(() => {
    if (cloudPrefs.format) return cloudPrefs.format;
    try { return localStorage.getItem(prefKey('format')) || 'plain'; } catch { return 'plain'; }
  });

  // Ref tracks latest prefs so rapid changes don't lose earlier updates (stale closure fix)
  const latestPrefsRef = React.useRef(user.user_metadata?.prefs || {});

  const savePrefsToCloud = async (updates) => {
    try {
      const merged = { ...latestPrefsRef.current, ...updates };
      latestPrefsRef.current = merged;
      await supabase.auth.updateUser({ data: { prefs: merged } });
    } catch (_) { /* localStorage still holds the value */ }
  };

  const refLink = `${window.location.origin}?ref=${user.id}`;
  const copyRefLink = () => {
    navigator.clipboard.writeText(refLink).then(() => {
      setCopyRefDone(true);
      setTimeout(() => setCopyRefDone(false), 2000);
    });
  };
  // ON ICE: const saveLangPref = (newLang) => {
  //   const key = user ? `yte_lang_${user.id}` : 'yte_lang';
  //   localStorage.setItem(key, newLang);
  //   savePrefsToCloud({ lang: newLang });
  // };

  const used = credits?.used ?? 0;
  const tierMax = credits?.tierMax || CREDITS_MAX;
  const resetAt = credits?.resetAt ?? (Date.now() + CREDITS_PERIOD_MS);
  const daysLeft = Math.max(0, Math.ceil((resetAt - Date.now()) / 86400000));
  const pct = Math.min(100, (used / tierMax) * 100);
  const remaining = Math.max(0, tierMax - used);
  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '—';
  const initial = (user.email || '?')[0].toUpperCase();
  const displayName = user.user_metadata?.username || user.user_metadata?.full_name || user.user_metadata?.name || user.email?.split('@')[0] || 'User';
  const latest = history[0] || null;
  const latestWc = latest && latest.transcript ? latest.transcript.trim().split(/\s+/).length : 0;

  // Per-day usage stats from history
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayCounts = [0, 0, 0, 0, 0, 0, 0];
  history.forEach(h => { if (h.date) dayCounts[new Date(h.date).getDay()]++; });
  const maxCount = Math.max(...dayCounts);
  const mostUsedDayIdx = maxCount > 0 ? dayCounts.indexOf(maxCount) : -1;
  const mostUsedDay = mostUsedDayIdx >= 0 ? `${dayNames[mostUsedDayIdx]} (${maxCount})` : '—';
  const avgPerDay = history.length > 0 ? (history.length / 7).toFixed(1) : '0.0';

  const CONTINUE_ACTIONS = [
    { label: 'Summarize', target: 'summary' },
    { label: 'Flashcards', target: 'flashcards' },
    { label: 'Study guide', target: 'study-guide' },
  ];
  const DS = {
    color: {
      surface: '#FFFFFF',
      border: '#E6E0D6',
      text: '#1D1D1F',
      muted: '#6F7480',
      accent: '#3C8CFF',
      accentHover: '#1F6BFF',
      accentSoft: 'rgba(60,140,255,0.1)',
    },
    radius: {
      card: 14,
      button: 8,
    },
  };

  const openTranscript = (entry, target = 'transcript') => {
    if (!entry) return;
    onLoadTranscript(entry, target);
    onBack();
  };

  const onShareReferralWhatsApp = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(`Check out ScribeSnap — it extracts YouTube transcripts in seconds! Sign up with my link and we both get +3 free credits: ${refLink}`)}`,
      '_blank', 'noopener,noreferrer'
    );
  };
  const onShareReferralX = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent('I use ScribeSnap to get YouTube transcripts instantly 🎬 Try it free — use my invite link and we both get +3 bonus credits 👉')}&url=${encodeURIComponent(refLink)}`,
      '_blank', 'noopener,noreferrer'
    );
  };
  const onShareReferralInstagram = () => {
    navigator.clipboard.writeText(`Check out ScribeSnap — it extracts YouTube transcripts in seconds! Sign up with my link and we both get +3 free credits: ${refLink}`);
    window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
  };
  const onShareReferralEmail = () => {
    window.location.href = `mailto:?subject=${encodeURIComponent('Try ScribeSnap — free YouTube transcript tool')}&body=${encodeURIComponent(`Check out ScribeSnap — it extracts YouTube transcripts in seconds! Sign up with my link and we both get +3 free credits: ${refLink}`)}`;
  };

  // Save display name via Supabase
  const saveDisplayName = async () => {
    if (!nameInput.trim()) return;
    setNameSaving(true);
    setNameError('');
    try {
      const { error } = await supabase.auth.updateUser({
        data: { username: nameInput.trim(), full_name: nameInput.trim() },
      });
      if (error) { setNameError(error.message); return; }
      setEditingName(false);
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 3000);
    } catch (e) {
      setNameError(e.message || 'Failed to save name.');
    } finally {
      setNameSaving(false);
    }
  };

  // Inline password change
  const handlePasswordChange = async () => {
    if (pwNew !== pwConfirm) { setPwError('New passwords do not match.'); return; }
    if (pwNew.length < 6) { setPwError('Password must be at least 6 characters.'); return; }
    setPwStep('loading');
    setPwError('');
    try {
      // Re-authenticate to verify current password
      const { error: reAuthErr } = await supabase.auth.signInWithPassword({ email: user.email, password: pwCurrent });
      if (reAuthErr) { setPwError('Current password is incorrect.'); setPwStep('confirm'); return; }
      const { error: updateErr } = await supabase.auth.updateUser({ password: pwNew });
      if (updateErr) { setPwError(updateErr.message); setPwStep('confirm'); return; }
      setPwStep('success');
      setTimeout(() => { setPwStep('idle'); setPwCurrent(''); setPwNew(''); setPwConfirm(''); setPwError(''); }, 3000);
    } catch (e) {
      setPwError(e.message || 'Failed to update password.');
      setPwStep('confirm');
    }
  };

  // Clear history
  const handleClearHistory = () => {
    const key = user ? `yte_history_${user.id}` : 'yte_history';
    localStorage.removeItem(key);
    if (setHistory) setHistory([]);
    setClearDone(true);
    setTimeout(() => { setClearConfirm(false); setClearDone(false); }, 2000);
  };

  // Delete account
  const handleDeleteAccount = async () => {
    setDeleteStep('deleting');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch('/api/delete-account', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session?.access_token}` },
        body: JSON.stringify({ reason: deleteReason }),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        setDeleteError(d.error || 'Something went wrong. Please try again.');
        setDeleteStep('error');
        return;
      }
      await onSignOut();
    } catch (err) {
      setDeleteError('Network error. Please try again.');
      setDeleteStep('error');
    }
  };

  // Export history as JSON
  const exportHistory = () => {
    const data = JSON.stringify(history, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `scribesnap-history-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const weekSegments = Array.from({ length: 7 }, (_, i) => i);
  const activeDay = Math.min(6, Math.round((pct / 100) * 6));

  const renderContinueCard = ({ mobile = false }) => {
    if (mobile) {
      if (!latest) return null;
      return (
        <div style={{ background: DS.color.surface, border: `1px solid ${DS.color.border}`, borderRadius: DS.radius.card, overflow: 'hidden' }}>
          <div style={{ padding: '9px 12px 8px', borderBottom: `1px solid ${DS.color.border}` }}>
            <span style={{ fontSize: 14, fontWeight: 700, color: DS.color.text }}>Continue where you left off</span>
          </div>
          <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 60, height: 34, borderRadius: 7, overflow: 'hidden', flexShrink: 0, background: 'rgba(60,140,255,0.06)', border: '1px solid rgba(60,140,255,0.1)' }}>
              {latest.thumbnail && <img src={latest.thumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={e => { e.target.style.display = 'none'; }} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: DS.color.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{latest.title || latest.id}</div>
              <div style={{ fontSize: 12, color: DS.color.muted }}>{latestWc > 0 ? `${latestWc.toLocaleString()} words · ` : ''}{timeAgo(latest.date)}</div>
            </div>
            <button onClick={() => openTranscript(latest)} style={{ flexShrink: 0, border: 'none', borderRadius: 7, background: DS.color.accent, color: 'white', fontSize: 12, fontWeight: 700, padding: '5px 11px', cursor: 'pointer' }}>Open</button>
          </div>
          <div style={{ padding: '0 12px 9px', display: 'flex', gap: 5 }}>
            {CONTINUE_ACTIONS.map(({ label, target }) => (
              <button
                key={target}
                onClick={() => openTranscript(latest, target)}
                style={{ flex: 1, border: `1px solid ${DS.color.border}`, background: '#F6F2EA', color: DS.color.muted, fontSize: 12, fontWeight: 600, padding: '5px 2px', borderRadius: 7, cursor: 'pointer', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      );
    }

    return (
      <section className="ds-card ds-continue">
        <div className="ds-continue-header">
          <h2 className="ds-side-title">Continue where you left off</h2>
        </div>
        {latest ? (
          <>
            <div className="ds-continue-thumb" onClick={() => openTranscript(latest)}>
              {latest.thumbnail ? (
                <img src={latest.thumbnail} alt={latest.title || 'Video'} loading="lazy" />
              ) : (
                <div style={{ color: 'rgba(60,140,255,0.3)' }}>
                  <PlatformIcon platform={latest.platform || 'youtube'} size={48} />
                </div>
              )}
              <div className="ds-continue-play">
                <div className="ds-continue-play-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="#1D1D1F" style={{ marginLeft: 2 }}>
                    <polygon points="5 3 19 12 5 21 5 3"/>
                  </svg>
                </div>
              </div>
            </div>
            <div className="ds-continue-body">
              <p className="ds-continue-video-title">{latest.title || latest.id}</p>
              <p className="ds-continue-meta">
                <PlatformIcon platform={latest.platform || 'youtube'} size={12} />
                {latest.channel || (latest.platform ? latest.platform.charAt(0).toUpperCase() + latest.platform.slice(1) : 'YouTube')}
                {latestWc > 0 && <span style={{ color: '#C0BAB3' }}>·</span>}
                {latestWc > 0 && `${latestWc.toLocaleString()} words`}
              </p>
              <button className="ds-continue-open-btn" onClick={() => openTranscript(latest)}>Open</button>
              <div className="ds-continue-quick">
                {CONTINUE_ACTIONS.map(({ label, target }) => (
                  <button key={target} className="ds-continue-quick-btn" onClick={() => openTranscript(latest, target)}>{label}</button>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="ds-continue-empty">
            <p style={{ margin: 0 }}>Extract a transcript to see it here.</p>
            <button
              style={{ marginTop: 12, border: 'none', borderRadius: 10, background: '#3C8CFF', color: 'white', fontSize: 13, fontWeight: 600, padding: '8px 16px', cursor: 'pointer' }}
              onClick={onBack}
            >Extract transcript</button>
          </div>
        )}
      </section>
    );
  };

  const renderReferralCard = ({ mobile = false }) => {
    if (mobile) {
      return (
        <div style={{ background: DS.color.surface, border: `1px solid ${DS.color.border}`, borderRadius: DS.radius.card, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: DS.radius.button, background: DS.color.accentSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3C8CFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: DS.color.text }}>Invite friends, earn credits</div>
              <div style={{ fontSize: 12, color: DS.color.muted }}>+3 free credits per friend who signs up</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <div style={{ flex: 1, minWidth: 0, background: '#F6F2EA', border: `1px solid ${DS.color.border}`, borderRadius: DS.radius.button, padding: '7px 10px', fontSize: 12, color: DS.color.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{refLink}</div>
            <button onClick={copyRefLink} style={{ flexShrink: 0, border: 'none', borderRadius: DS.radius.button, background: copyRefDone ? '#0F766E' : DS.color.accent, color: 'white', fontSize: 12, fontWeight: 700, padding: '7px 12px', cursor: 'pointer', transition: 'background 0.2s' }}>{copyRefDone ? '✓ Copied' : 'Copy'}</button>
          </div>
        </div>
      );
    }

    return (
      <section className="ds-card ds-referral">
        <div className="ds-referral-head">
          <div className="ds-referral-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
              <circle cx="9" cy="7" r="4"/>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
            </svg>
          </div>
          <div>
            <h2 className="ds-referral-title">Invite friends, earn credits</h2>
            <p className="ds-referral-subtitle">Share your link · both get rewarded</p>
          </div>
        </div>
        <div className="ds-referral-badge">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
          +3 free credits per friend who signs up
        </div>
        <div className="ds-referral-body">
          <div className="ds-referral-link-row">
            <div className="ds-referral-link-box">{refLink}</div>
            <button className={`ds-referral-copy-btn${copyRefDone ? ' done' : ''}`} onClick={copyRefLink}>
              {copyRefDone ? '✓ Copied!' : 'Copy link'}
            </button>
          </div>
          <div className="ds-referral-share-row">
            <button className="ds-referral-share-btn" onClick={onShareReferralWhatsApp}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z"/></svg>
              WhatsApp
            </button>
            <button className="ds-referral-share-btn" onClick={onShareReferralX}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="#1D1D1F"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              Share on X
            </button>
            <button className="ds-referral-share-btn" onClick={onShareReferralInstagram}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" stroke="#E1306C">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
              </svg>
              Instagram
            </button>
            <button className="ds-referral-share-btn" onClick={onShareReferralEmail}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#3C8CFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><polyline points="2,4 12,13 22,4"/></svg>
              Email
            </button>
          </div>
          {((user?.user_metadata?.referral_count || 0) > 0 || (user?.user_metadata?.referral_bonus || 0) > 0) && (
            <div className="ds-referral-stats">
              <div className="ds-referral-stat">
                <span className="ds-referral-stat-value">{user?.user_metadata?.referral_count || 0}</span>
                <span className="ds-referral-stat-label">friends joined</span>
              </div>
              <div className="ds-referral-stat">
                <span className="ds-referral-stat-value">+{user?.user_metadata?.referral_bonus || 0}</span>
                <span className="ds-referral-stat-label">credits earned</span>
              </div>
            </div>
          )}
        </div>
      </section>
    );
  };

  const renderMobileOverview = () => {
    const metricCards = [
      {
        key: 'credits',
        value: `${used} / ${tierMax}`,
        label: 'Credits used',
        iconBg: 'rgba(60,140,255,0.12)',
        iconColor: '#3C8CFF',
        icon: <svg width="14" height="14" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="2"/><path d="M9 5.5v3.5l2 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
      },
      {
        key: 'saved',
        value: `${history.length}`,
        label: 'Transcripts saved',
        iconBg: 'rgba(15,118,110,0.12)',
        iconColor: '#0F766E',
        icon: <svg width="14" height="14" viewBox="0 0 18 18" fill="none"><rect x="2" y="3" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M5 7h8M5 10h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>,
      },
      {
        key: 'reset',
        value: `${daysLeft}`,
        label: 'Days until reset',
        iconBg: 'rgba(180,83,9,0.12)',
        iconColor: '#B45309',
        icon: <svg width="14" height="14" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="2"/><path d="M9 5v4.5l2.5 1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>,
      },
    ];

    return (
      <div className="ds-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 6 }}>
        <div className="ds-mobile-metrics">
          {metricCards.map((card, idx) => (
            <div
              key={card.key}
              className={`ds-mobile-metric${idx === 2 ? ' is-wide' : ''}`}
              style={{
                background: '#FFFFFF',
                border: '1px solid #E6E0D6',
                borderRadius: 12,
                padding: '10px 11px',
              }}
            >
              <div style={{ width: 28, height: 28, borderRadius: 8, background: card.iconBg, color: card.iconColor, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>{card.icon}</div>
              <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', color: '#1D1D1F', lineHeight: 1 }}>{card.value}</div>
              <div style={{ marginTop: 4, fontSize: 12, color: '#8B8F97', fontWeight: 600 }}>{card.label}</div>
            </div>
          ))}
        </div>

        <div style={{ background: '#FFFFFF', border: '1px solid #E6E0D6', borderRadius: 12, padding: '10px 12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1D1D1F' }}>Weekly credits</span>
            <span style={{ fontSize: 12, color: '#6F7480' }}>resets in {daysLeft}d</span>
          </div>
          <div style={{ height: 6, borderRadius: 999, background: '#E6E0D6', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${pct}%`, borderRadius: 999, background: pct >= 80 ? '#B45309' : '#3C8CFF', transition: 'width 0.4s ease' }} />
          </div>
          <div style={{ marginTop: 5, fontSize: 12, color: '#6F7480' }}>{remaining} credits remaining</div>
        </div>

        {renderContinueCard({ mobile: true })}

        <div style={{ background: '#FFFFFF', border: '1px solid #E6E0D6', borderRadius: 12, overflow: 'hidden' }}>
          <div style={{ padding: '9px 12px 8px', borderBottom: '1px solid #E6E0D6' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1D1D1F' }}>Recent transcripts</span>
          </div>
          {history.length === 0 ? (
            <div style={{ padding: '16px 12px', textAlign: 'center' }}>
              <p style={{ margin: '0 0 10px', fontSize: 13, color: '#8B8F97' }}>No transcripts yet.</p>
              <button onClick={onBack} style={{ border: 'none', borderRadius: 9, background: '#3C8CFF', color: 'white', fontSize: 13, fontWeight: 600, padding: '7px 16px', cursor: 'pointer' }}>Extract one</button>
            </div>
          ) : (
            <>
              {(showAllHistory ? history : history.slice(0, 4)).map((h, idx) => {
                const wc = h.transcript ? h.transcript.trim().split(/\s+/).length : 0;
                const title = h.title || h.id;
                return (
                  <div key={`m-${h.id}-${idx}`} onClick={() => openTranscript(h)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderTop: '1px solid #E6E0D6', cursor: 'pointer' }}>
                    <div style={{ width: 62, height: 36, borderRadius: 6, overflow: 'hidden', flexShrink: 0, background: 'rgba(60,140,255,0.06)', border: '1px solid rgba(60,140,255,0.1)' }}>
                      {h.thumbnail && <img src={h.thumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={e => { e.target.style.display = 'none'; }} />}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#1D1D1F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
                      <div style={{ fontSize: 11, color: '#6F7480', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wc > 0 ? `${wc.toLocaleString()} words · ` : ''}{timeAgo(h.date)}</div>
                    </div>
                  </div>
                );
              })}
              {history.length > 4 && (
                <div style={{ padding: '8px 12px 10px', borderTop: '1px solid #E6E0D6' }}>
                  <button onClick={() => setShowAllHistory(v => !v)} style={{ border: 'none', background: 'none', color: '#3C8CFF', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                    {showAllHistory ? 'Show less' : `+ View all ${history.length} transcripts`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {renderReferralCard({ mobile: true })}
      </div>
    );
  };

  return (
    <div className="ds-shell">
      <style>{`
        .ds-shell {
          min-height: 100vh;
          padding-top: 56px;
          background: #F6F2EA;
          position: relative;
          overflow-x: hidden;
        }
        /* ── Sidebar layout ── */
        .ds-layout { display: flex; min-height: calc(100vh - 56px); overflow-x: hidden; }
        .ds-sidebar {
          width: 224px; flex-shrink: 0; background: #FFFFFF;
          border-right: 1px solid #E6E0D6; display: flex; flex-direction: column;
          position: sticky; top: 56px; height: calc(100vh - 56px); overflow-y: auto;
        }
        .ds-sidebar-user { padding: 26px 20px 18px; border-bottom: 1px solid #E6E0D6; }
        .ds-sidebar-avatar {
          width: 50px; height: 50px; border-radius: 50%;
          background: #3C8CFF; color: white; font-size: 21px; font-weight: 700;
          display: flex; align-items: center; justify-content: center;
          margin-bottom: 11px; box-shadow: 0 4px 14px rgba(60,140,255,0.28);
        }
        .ds-sidebar-name { font-size: 14px; font-weight: 700; color: #1D1D1F; margin: 0 0 2px; letter-spacing: -0.01em; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .ds-sidebar-email { font-size: 11.5px; color: #8B8F97; margin: 0 0 10px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
        .ds-sidebar-plan { display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 999px; background: rgba(60,140,255,0.09); color: #3C8CFF; font-size: 11px; font-weight: 700; }
        .ds-sidebar-nav { flex: 1; padding: 10px; display: flex; flex-direction: column; gap: 2px; }
        .ds-nav-btn {
          display: flex; align-items: center; gap: 10px; width: 100%; padding: 9px 12px;
          border: none; border-radius: 10px; background: transparent; color: #8B8F97;
          font-size: 14px; font-weight: 500; cursor: pointer; text-align: left; transition: all 0.15s ease;
        }
        .ds-nav-btn:hover { background: #F6F2EA; color: #1D1D1F; }
        .ds-nav-btn.is-active { background: rgba(60,140,255,0.08); color: #3C8CFF; font-weight: 600; }
        .ds-nav-btn svg { flex-shrink: 0; opacity: 0.7; }
        .ds-nav-btn.is-active svg { opacity: 1; }
        .ds-sidebar-footer { padding: 10px; border-top: 1px solid #E6E0D6; }
        .ds-back-btn {
          display: flex; align-items: center; gap: 8px; width: 100%; padding: 9px 12px;
          border: none; border-radius: 10px; background: transparent; color: #8B8F97;
          font-size: 13px; font-weight: 500; cursor: pointer; text-align: left; transition: all 0.15s ease;
        }
        .ds-back-btn:hover { background: #F6F2EA; color: #1D1D1F; }
        /* ── Main content ── */
        .ds-main { flex: 1; min-width: 0; padding: 36px 44px 72px; overflow-y: auto; }
        .ds-main-header { margin-bottom: 26px; }
        .ds-main-title { font-size: 26px; font-weight: 800; color: #1D1D1F; letter-spacing: -0.03em; margin: 0 0 4px; }
        .ds-main-sub { font-size: 14px; color: #8B8F97; margin: 0; }
        /* ── Stats row ── */
        .ds-stats-row { display: grid; grid-template-columns: repeat(3,1fr); gap: 14px; margin-bottom: 24px; }
        .ds-stat-card {
          background: #FFFFFF; border: 1px solid #E6E0D6; border-radius: 16px;
          padding: 18px 20px; transition: transform 0.18s ease, box-shadow 0.18s ease;
          min-height: 0;
          height: auto;
        }
        .ds-stat-card:hover { transform: translateY(-2px); box-shadow: 0 6px 22px rgba(29,29,31,0.07); }
        .ds-stat-icon { width: 32px; height: 32px; border-radius: 9px; background: rgba(60,140,255,0.08); color: #3C8CFF; display: flex; align-items: center; justify-content: center; margin-bottom: 12px; }
        .ds-stat-val { font-size: 30px; font-weight: 800; color: #1D1D1F; letter-spacing: -0.03em; line-height: 1; margin-bottom: 4px; }
        .ds-stat-lbl { font-size: 13px; color: #8B8F97; font-weight: 500; }
        /* ── Section card ── */
        .ds-section { background: #FFFFFF; border: 1px solid #E6E0D6; border-radius: 18px; margin-bottom: 18px; overflow: hidden; }
        .ds-section-head { display: flex; align-items: center; justify-content: space-between; padding: 18px 22px 14px; border-bottom: 1px solid #E6E0D6; }
        .ds-section-title { font-size: 15px; font-weight: 700; color: #1D1D1F; letter-spacing: -0.01em; margin: 0; }
        /* ── Credit bar ── */
        .ds-credit-track { height: 8px; border-radius: 999px; background: #E6E0D6; overflow: hidden; margin: 14px 22px 0; }
        .ds-credit-fill { height: 100%; border-radius: 999px; background: linear-gradient(90deg,#5B9BD5 0%,#3C8CFF 60%,#1F6BFF 100%); transition: width 0.5s cubic-bezier(0.4,0,0.2,1); }
        .ds-credit-meta { display: flex; justify-content: space-between; align-items: center; padding: 10px 22px 18px; font-size: 13px; color: #8B8F97; }
        /* ── Transcript rows ── */
        .ds-hist-row { display: flex; align-items: center; gap: 12px; padding: 11px 20px; border-bottom: 1px solid rgba(230,224,214,0.6); transition: background 0.12s ease; cursor: pointer; position: relative; animation: ds-row-in 0.3s ease both; }
        .ds-hist-row:first-of-type { border-top: none; }
        .ds-hist-row:last-of-type { border-bottom: none; }
        .ds-hist-row::before { content: ''; position: absolute; left: 0; top: 8px; bottom: 8px; width: 3px; border-radius: 0 2px 2px 0; background: #3C8CFF; transform: scaleY(0); transition: transform 0.2s cubic-bezier(0.34,1.56,0.64,1); transform-origin: center; }
        .ds-hist-row:hover::before { transform: scaleY(1); }
        .ds-hist-row:hover { background: rgba(60,140,255,0.035); }
        .ds-hist-thumb { width: 88px; height: 50px; border-radius: 8px; overflow: hidden; flex-shrink: 0; background: rgba(60,140,255,0.06); border: 1px solid rgba(60,140,255,0.1); display: flex; align-items: center; justify-content: center; position: relative; transition: box-shadow 0.15s ease; }
        .ds-hist-row:hover .ds-hist-thumb { box-shadow: 0 3px 10px rgba(60,140,255,0.18); border-color: rgba(60,140,255,0.25); }
        .ds-hist-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; transition: transform 0.25s ease; }
        .ds-hist-row:hover .ds-hist-thumb img { transform: scale(1.05); }
        .ds-hist-info { flex: 1; min-width: 0; }
        .ds-hist-title { font-size: 13px; font-weight: 600; color: #1D1D1F; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; margin-bottom: 3px; }
        .ds-hist-meta { font-size: 11.5px; color: #8B8F97; display: flex; align-items: center; gap: 4px; }
        .ds-open-btn { flex-shrink: 0; border: none; border-radius: 8px; padding: 5px 12px; background: #3C8CFF; color: white; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.15s ease; box-shadow: 0 2px 8px rgba(60,140,255,0.22); font-family: inherit; }
        .ds-open-btn:hover { background: #1F6BFF; box-shadow: 0 4px 12px rgba(60,140,255,0.32); }
        .ds-view-all-btn { display: block; width: 100%; padding: 10px 20px; border: none; border-top: 1px solid rgba(230,224,214,0.7); background: rgba(246,242,234,0.3); color: #8B8F97; font-size: 12px; font-weight: 600; cursor: pointer; text-align: left; transition: color 0.12s ease; font-family: inherit; }
        .ds-view-all-btn:hover { color: #3C8CFF; }
        /* ── Overview 2-col grid ── */
        .ds-overview-mobile { display: none; }
        .ds-overview-desktop { display: block; }
        .ds-mobile-metrics { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
        .ds-mobile-metric.is-wide { grid-column: 1 / -1; }
        .ds-overview-grid { display: grid; grid-template-columns: minmax(0,1.6fr) minmax(270px,1fr); gap: 18px; align-items: start; }
        /* ── Settings 2-col grid ── */
        .ds-settings-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
        .ds-settings-card-2 { background: #FFFFFF; border: 1px solid #E6E0D6; border-radius: 18px; padding: 22px; }
        .ds-settings-card-title { font-size: 16px; font-weight: 700; color: #1D1D1F; margin: 0 0 16px; letter-spacing: -0.01em; }
        /* ── Fade animation ── */
        .ds-fade-in { animation: dsFadeIn 0.2s ease both; }
        @keyframes dsFadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        /* ── Mobile header ── */
        .ds-mobile-header { display: none; align-items: center; gap: 10px; padding: 12px 16px; background: #FFFFFF; border-bottom: 1px solid #E6E0D6; position: sticky; top: 56px; z-index: 10; }
        /* ── Mobile bottom tabs ── */
        .ds-mobile-tabs { display: none; position: fixed; bottom: 0; left: 0; right: 0; background: #FFFFFF; border-top: 1px solid #E6E0D6; padding-bottom: env(safe-area-inset-bottom, 0); z-index: 50; }
        .ds-mobile-tabs-inner { display: flex; height: 56px; }
        .ds-mob-tab { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px; border: none; background: transparent; color: #8B8F97; font-size: 10px; font-weight: 500; cursor: pointer; transition: color 0.15s ease; }
        .ds-mob-tab.is-active { color: #3C8CFF; font-weight: 700; }
        /* ── Responsive ── */
        @media (max-width: 900px) {
          .ds-sidebar { display: none; }
          .ds-layout { display: block; min-height: auto; }
          .ds-overview-mobile { display: block; }
          .ds-overview-desktop { display: none; }
          .ds-mobile-header { position: static; top: auto; z-index: auto; }
          .ds-main {
            padding: 16px 16px calc(106px + env(safe-area-inset-bottom, 0px));
            overflow-x: hidden;
            overflow-y: visible;
            -webkit-overflow-scrolling: auto;
          }
          .ds-overview-grid { grid-template-columns: 1fr; gap: 14px; }
          .ds-stats-row { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-bottom: 16px; }
          .ds-settings-grid { grid-template-columns: 1fr; gap: 12px; }
          .ds-settings-card-2 { padding: 18px; border-radius: 14px; }
          .ds-section { border-radius: 14px; margin-bottom: 14px; }
          .ds-section-head { flex-wrap: wrap; align-items: flex-start; gap: 8px; padding: 14px 16px 12px; }
          .ds-credit-track { margin: 10px 16px 0; }
          .ds-credit-meta { padding: 8px 16px 14px; flex-wrap: wrap; gap: 6px 12px; }
          .ds-hist-row { padding: 11px 16px; gap: 10px; align-items: flex-start; flex-wrap: wrap; }
          .ds-hist-info { min-width: min(190px, 100%); flex: 1 1 190px; }
          .ds-hist-meta { flex-wrap: wrap; }
          .ds-open-btn { margin-left: auto; min-height: 32px; }
          .ds-view-all-btn { padding: 11px 16px; }
          .ds-continue, .ds-referral { margin-bottom: 14px; }
          .ds-referral-link-row { flex-wrap: wrap; }
          .ds-mobile-header { display: flex !important; }
          .ds-mobile-tabs { display: block !important; }
        }
        @media (max-width: 600px) {
          .ds-main { padding: 12px 12px calc(100px + env(safe-area-inset-bottom, 0px)); }
          .ds-stats-row { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; margin-bottom: 12px; }
          .ds-stats-row .ds-stat-card:last-child { grid-column: 1 / -1; }
          .ds-stat-card { padding: 12px; border-radius: 12px; }
          .ds-stat-icon { width: 26px; height: 26px; margin-bottom: 8px; }
          .ds-stat-val { font-size: 22px; margin-bottom: 3px; }
          .ds-stat-lbl { font-size: 12px; line-height: 1.25; }
          .ds-section { margin-bottom: 10px; border-radius: 12px; }
          .ds-hist-row { padding: 10px 12px; }
          .ds-hist-thumb { width: 72px; height: 40px; border-radius: 7px; }
          .ds-hist-info { min-width: 0; flex: 1 1 100%; }
          .ds-hist-title { white-space: normal; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
          .ds-hist-meta { font-size: 11px; gap: 4px; }
          .ds-open-btn { display: none; }
          .ds-settings-card-2 { padding: 14px; }
          .ds-settings-card-title { font-size: 15px; margin-bottom: 12px; }
          .ds-setting-row { flex-direction: column !important; align-items: flex-start !important; gap: 8px !important; }
          .ds-setting-value { max-width: 100%; text-align: left; white-space: normal; overflow-wrap: anywhere; }
          .ds-settings-row-actions { width: 100%; }
          .ds-settings-row-actions .ds-settings-save-btn,
          .ds-settings-row-actions .ds-settings-cancel-btn { flex: 1 1 0; text-align: center; }
          .ds-settings-confirm-row { flex-wrap: wrap; }
          .ds-settings-confirm-row .ds-settings-save-btn,
          .ds-settings-confirm-row .ds-settings-cancel-btn { flex: 1 1 100%; text-align: center; }
          .ds-referral-link-row { flex-direction: column; }
          .ds-referral-copy-btn { width: 100%; padding: 9px 12px; }
          .ds-referral-share-row { grid-template-columns: 1fr; }
          .ds-referral-badge { max-width: 100%; white-space: normal; line-height: 1.35; }
          .ds-continue-quick { flex-wrap: wrap; }
          .ds-continue-quick-btn { flex: 1 1 calc(50% - 5px); min-width: 120px; }
        }
        @media (max-width: 520px) {
          .ds-main { padding: 10px 10px calc(96px + env(safe-area-inset-bottom, 0px)); }
          .ds-mobile-header { padding: 10px 12px; }
          .ds-stat-card { padding: 10px; }
          .ds-stat-icon { width: 24px; height: 24px; margin-bottom: 7px; }
          .ds-stat-val { font-size: 20px; }
          .ds-stat-lbl { font-size: 11px; }
          .ds-section-head { padding: 12px; }
          .ds-section-title { font-size: 14px; }
          .ds-credit-track { margin: 8px 12px 0; height: 7px; }
          .ds-credit-meta { padding: 8px 12px 12px; font-size: 12px; }
          .ds-hist-row { padding: 9px 10px; gap: 8px; }
          .ds-hist-thumb { width: 64px; height: 36px; }
          .ds-hist-title { font-size: 12px; }
          .ds-hist-meta { font-size: 10.5px; }
          .ds-view-all-btn { padding: 10px 12px; font-size: 12px; }
          .ds-referral-body { padding: 12px; }
          .ds-referral-banner { padding: 13px 12px 11px; }
          .ds-referral-title { font-size: 13.5px; }
        }
        @media (max-width: 420px) {
          .ds-mobile-metrics { grid-template-columns: 1fr; }
          .ds-mobile-metric.is-wide { grid-column: auto; }
          .ds-mobile-header { padding: 10px 12px; }
          .ds-main { padding: 10px 10px calc(96px + env(safe-area-inset-bottom, 0px)); }
          .ds-stats-row { grid-template-columns: 1fr; }
          .ds-stats-row .ds-stat-card:last-child { grid-column: auto; }
          .ds-stat-card { padding: 10px; }
          .ds-stat-val { font-size: 21px; }
          .ds-section-head { padding: 12px 12px 10px; }
          .ds-credit-track { margin: 8px 12px 0; }
          .ds-credit-meta { padding: 8px 12px 12px; font-size: 12px; }
          .ds-hist-row { padding: 9px 10px; }
          .ds-hist-thumb { width: 64px; height: 36px; }
          .ds-hist-title { font-size: 12px; }
          .ds-hist-meta { font-size: 10.5px; }
          .ds-settings-card-2 { padding: 12px; }
          .ds-settings-btn { min-height: 40px; }
          .ds-referral-title { font-size: 13.5px; }
          .ds-referral-share-btn { min-height: 38px; }
          .ds-continue-quick-btn { min-width: 100%; }
        }
        @media (min-width: 1300px) { .ds-main { padding: 40px 60px 80px; } }
        .ds-wrap {
          max-width: 1200px;
          margin: 0 auto;
          padding: 34px 24px 56px;
          position: relative;
          z-index: 1;
        }
        /* ── Top nav ── */
        .ds-topnav {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 22px;
        }
        .ds-topnav-back {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border: none;
          background: none;
          color: ${DS.color.muted};
          cursor: pointer;
          padding: 6px 8px 6px 2px;
          transition: color 0.15s;
          flex-shrink: 0;
        }
        .ds-topnav-back:hover { color: #1D1D1F; }
        .ds-topnav-title {
          margin: 0;
          font-size: 16px;
          font-weight: 700;
          color: ${DS.color.text};
          letter-spacing: -0.01em;
        }
        .ds-grid {
          display: grid;
          grid-template-columns: minmax(0, 1.85fr) minmax(318px, 1fr);
          gap: 18px;
          align-items: start;
        }
        .ds-card {
          background: ${DS.color.surface};
          border: 1px solid ${DS.color.border};
          border-radius: 18px;
          box-shadow: 0 2px 12px rgba(29,29,31,0.06);
        }
        .ds-profile {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 18px 22px;
          margin-bottom: 12px;
        }
        .ds-avatar {
          width: 62px;
          height: 62px;
          border-radius: 50%;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          font-weight: 700;
          color: white;
          background: #3C8CFF;
          box-shadow: 0 6px 16px rgba(60,140,255,0.25);
        }
        .ds-profile-name {
          font-size: clamp(20px, 2.4vw, 30px);
          font-weight: 700;
          letter-spacing: -0.02em;
          color: #1D1D1F;
          margin: 0 0 2px;
        }
        .ds-profile-email {
          font-size: 14px;
          color: #6F7480;
          margin: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .ds-profile-meta {
          margin-left: auto;
          text-align: right;
          flex-shrink: 0;
        }
        .ds-profile-meta-label {
          margin: 0 0 3px;
          font-size: 11px;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: #6F7480;
          font-weight: 600;
        }
        .ds-profile-meta-value {
          margin: 0;
          font-size: 15px;
          color: #1D1D1F;
          font-weight: 700;
        }
        /* ── Section tabs (white card, blue active) ── */
        .ds-section-tabs {
          display: flex;
          align-items: center;
          gap: 4px;
          padding: 5px;
          margin-bottom: 14px;
          background: #FFFFFF;
          border: 1px solid #E6E0D6;
          border-radius: 18px;
          box-shadow: 0 2px 12px rgba(29,29,31,0.06);
        }
        .ds-section-tab {
          flex: 1;
          border: none;
          border-radius: 11px;
          padding: 8px;
          background: transparent;
          color: #6F7480;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.15s;
          text-align: center;
        }
        .ds-section-tab:hover { color: #1D1D1F; }
        .ds-section-tab.is-active {
          color: #3C8CFF;
          font-weight: 700;
          background: rgba(60,140,255,0.07);
        }
        .ds-overview-col {
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .ds-stats {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 12px;
        }
        .ds-stat {
          position: relative;
          overflow: hidden;
          padding: 16px 16px 14px;
          border-radius: 16px;
          border: 1px solid #E6E0D6;
          background: #FFFFFF;
          box-shadow: 0 2px 10px rgba(29,29,31,0.05);
        }
        .ds-stat::before {
          content: '';
          position: absolute;
          inset: -16px;
          opacity: 0.8;
          pointer-events: none;
        }
        .ds-stat.ds-stat-primary::before {
          background: linear-gradient(130deg, rgba(60,140,255,0.1) 0%, rgba(60,140,255,0.06) 100%);
        }
        .ds-stat.ds-stat-neutral::before {
          background: linear-gradient(140deg, rgba(60,140,255,0.05) 0%, rgba(60,140,255,0.02) 100%);
        }
        .ds-stat.ds-stat-youtube::before {
          background: linear-gradient(145deg, rgba(255,0,0,0.05) 0%, rgba(60,140,255,0.06) 100%);
        }
        .ds-stat.ds-stat-vimeo::before {
          background: linear-gradient(140deg, rgba(26,183,234,0.07) 0%, rgba(60,140,255,0.05) 100%);
        }
        .ds-stat-head {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          gap: 6px;
          color: #3C8CFF;
          margin-bottom: 8px;
        }
        .ds-stat-value {
          position: relative;
          z-index: 1;
          margin: 0 0 2px;
          font-size: 28px;
          line-height: 1.05;
          letter-spacing: -0.02em;
          color: #1D1D1F;
          font-weight: 700;
        }
        .ds-stat-label {
          position: relative;
          z-index: 1;
          margin: 0 0 7px;
          font-size: 13px;
          color: #1D1D1F;
          font-weight: 600;
        }
        .ds-stat-sub {
          position: relative;
          z-index: 1;
          margin: 0;
          font-size: 13px;
          color: #6F7480;
        }
        .ds-usage {
          padding: 16px 18px 14px;
        }
        .ds-usage-head {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 10px;
          gap: 10px;
        }
        .ds-usage-title {
          margin: 0;
          font-size: 16px;
          color: #1D1D1F;
          font-weight: 700;
          letter-spacing: -0.01em;
        }
        .ds-usage-switch {
          display: inline-flex;
          border-radius: 10px;
          padding: 3px;
          background: rgba(60,140,255,0.07);
          border: 1px solid rgba(60,140,255,0.18);
          gap: 3px;
        }
        .ds-usage-switch button {
          border: none;
          border-radius: 8px;
          padding: 5px 10px;
          background: transparent;
          color: #8B8F97;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .ds-usage-switch button.is-active {
          background: white;
          color: #1D1D1F;
        }
        .ds-usage-track {
          height: 9px;
          border-radius: 999px;
          background: #E6E0D6;
          overflow: hidden;
        }
        .ds-usage-bar {
          height: 100%;
          border-radius: 999px;
          background: linear-gradient(90deg, #5B9BD5 0%, #3C8CFF 60%, #1F6BFF 100%);
          transition: width 0.45s ease;
        }
        .ds-usage-meta {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 12px;
        }
        .ds-usage-meta strong {
          color: #1D1D1F;
          font-size: 22px;
        }
        .ds-usage-days {
          display: flex;
          align-items: end;
          gap: 7px;
        }
        .ds-usage-day {
          width: 13px;
          border-radius: 4px 4px 3px 3px;
          background: rgba(60,140,255,0.14);
          transition: all 0.2s ease;
        }
        .ds-usage-day.is-active {
          background: #3C8CFF;
        }
        .ds-usage-daystats {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px solid #E6E0D6;
          font-size: 12px;
          color: #8B8F97;
        }
        @keyframes ds-row-in {
          from { opacity: 0; transform: translateY(6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .ds-list-card { padding: 0; overflow: hidden; position: relative; }
        .ds-list-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 10px;
          padding: 14px 18px 13px;
          border-bottom: 1px solid rgba(230,224,214,0.8);
          background: linear-gradient(to bottom, rgba(246,242,234,0.6), transparent);
        }
        .ds-list-title {
          margin: 0;
          font-size: 15px;
          color: #1D1D1F;
          font-weight: 700;
          letter-spacing: -0.01em;
          display: flex;
          align-items: center;
          gap: 7px;
        }
        .ds-list-title-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 26px;
          height: 26px;
          border-radius: 7px;
          background: rgba(60,140,255,0.10);
          color: #3C8CFF;
          flex-shrink: 0;
        }
        .ds-list-count {
          font-size: 11px;
          font-weight: 700;
          color: #3C8CFF;
          background: rgba(60,140,255,0.10);
          border: 1px solid rgba(60,140,255,0.18);
          padding: 2px 9px;
          border-radius: 999px;
          letter-spacing: 0.02em;
        }
        .ds-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 11px 18px;
          border-bottom: 1px solid rgba(230,224,214,0.5);
          transition: background 0.15s ease;
          cursor: pointer;
          position: relative;
          animation: ds-row-in 0.3s ease both;
        }
        .ds-row:last-of-type { border-bottom: none; }
        .ds-row::before {
          content: '';
          position: absolute;
          left: 0; top: 8px; bottom: 8px;
          width: 3px;
          border-radius: 0 2px 2px 0;
          background: #3C8CFF;
          transform: scaleY(0);
          transition: transform 0.2s cubic-bezier(0.34,1.56,0.64,1);
          transform-origin: center;
        }
        .ds-row:hover { background: rgba(60,140,255,0.035); }
        .ds-row:hover::before { transform: scaleY(1); }
        .ds-thumb {
          width: 96px;
          height: 54px;
          border-radius: 9px;
          overflow: hidden;
          flex-shrink: 0;
          background: rgba(200,190,170,0.18);
          border: 1px solid rgba(180,170,150,0.25);
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          transition: box-shadow 0.15s ease;
        }
        .ds-row:hover .ds-thumb {
          box-shadow: 0 4px 14px rgba(60,140,255,0.18);
          border-color: rgba(60,140,255,0.25);
        }
        .ds-thumb::after {
          content: '';
          position: absolute;
          inset: 0;
          background: rgba(60,140,255,0.0);
          transition: background 0.15s ease;
          pointer-events: none;
          z-index: 1;
        }
        .ds-row:hover .ds-thumb::after { background: rgba(60,140,255,0.1); }
        .ds-thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
          transition: transform 0.25s ease;
        }
        .ds-row:hover .ds-thumb img { transform: scale(1.05); }
        .ds-row-title {
          margin: 0 0 3px;
          font-size: 13px;
          color: #1D1D1F;
          font-weight: 600;
          letter-spacing: -0.01em;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .ds-row-meta {
          margin: 0;
          font-size: 11.5px;
          color: #8B8F97;
          display: flex;
          align-items: center;
          gap: 4px;
          flex-wrap: wrap;
        }
        .ds-row-meta-dot {
          display: inline-block;
          width: 3px;
          height: 3px;
          border-radius: 50%;
          background: #C8C4BC;
          flex-shrink: 0;
        }
        .ds-row-actions {
          margin-left: auto;
          display: inline-flex;
          gap: 6px;
          flex-shrink: 0;
        }
        .ds-row-btn {
          border: 1px solid #E6E0D6;
          border-radius: 8px;
          padding: 5px 12px;
          min-width: 56px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
          font-family: inherit;
        }
        .ds-row-btn.primary {
          background: #3C8CFF;
          color: white;
          border-color: transparent;
          box-shadow: 0 2px 8px rgba(60,140,255,0.25);
        }
        .ds-row-btn.primary:hover {
          background: #1F6BFF;
          box-shadow: 0 4px 12px rgba(60,140,255,0.35);
        }
        .ds-row-btn.ghost {
          background: rgba(255,255,255,0.7);
          color: #1D1D1F;
          border-color: #E6E0D6;
        }
        .ds-row-btn.ghost:hover {
          border-color: rgba(29,29,31,0.28);
          background: rgba(255,255,255,0.95);
        }
        .ds-list-footer {
          border-top: 1px solid rgba(230,224,214,0.7);
          padding: 10px 18px 10px;
          background: rgba(246,242,234,0.3);
        }
        .ds-link-btn {
          border: none;
          background: none;
          color: #8B8F97;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 0;
          font-family: inherit;
          transition: color 0.15s ease;
        }
        .ds-link-btn:hover { color: #3C8CFF; }
        .ds-side {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        /* ── Continue where you left off ── */
        .ds-continue { padding: 0; overflow: hidden; }
        .ds-continue-header { padding: 13px 14px 0; }
        .ds-continue-body { padding: 9px 14px 13px; }
        .ds-side-title {
          margin: 0;
          font-size: 14px;
          line-height: 1.2;
          color: #1D1D1F;
          font-weight: 700;
          letter-spacing: -0.01em;
        }
        .ds-continue-thumb {
          position: relative;
          width: 100%;
          aspect-ratio: 16/9;
          overflow: hidden;
          background: rgba(60,140,255,0.08);
          margin: 10px 0 0;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .ds-continue-thumb img {
          width: 100%; height: 100%; object-fit: cover; display: block;
        }
        .ds-continue-play {
          position: absolute; inset: 0;
          display: flex; align-items: center; justify-content: center;
          background: rgba(0,0,0,0.22);
          transition: background 0.15s;
        }
        .ds-continue-thumb:hover .ds-continue-play { background: rgba(0,0,0,0.4); }
        .ds-continue-play-icon {
          width: 40px; height: 40px;
          background: rgba(255,255,255,0.92);
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 3px 10px rgba(0,0,0,0.18);
        }
        .ds-continue-video-title {
          margin: 0 0 2px;
          font-size: 14px;
          font-weight: 700;
          color: #1D1D1F;
          letter-spacing: -0.01em;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .ds-continue-meta {
          margin: 0 0 9px;
          font-size: 12px;
          color: #6F7480;
          display: flex; align-items: center; gap: 4px; flex-wrap: wrap;
        }
        .ds-continue-open-btn {
          width: 100%;
          border: none;
          border-radius: 9px;
          background: rgba(60,140,255,0.13);
          color: #3C8CFF;
          font-size: 14px;
          font-weight: 700;
          padding: 9px 12px;
          cursor: pointer;
          transition: background 0.15s, transform 0.15s;
          margin-bottom: 7px;
        }
        .ds-continue-open-btn:hover { background: rgba(60,140,255,0.22); transform: translateY(-1px); }
        .ds-continue-quick {
          display: flex;
          gap: 5px;
        }
        .ds-continue-quick-btn {
          flex: 1;
          border: 1px solid #E6E0D6;
          border-radius: 7px;
          padding: 5px 2px;
          background: white;
          color: #8B8F97;
          font-size: 11px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s;
          text-align: center;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .ds-continue-quick-btn:hover { border-color: rgba(60,140,255,0.3); color: #3C8CFF; background: rgba(60,140,255,0.04); }
        .ds-continue-empty {
          padding: 20px 14px 14px;
          text-align: center;
          color: #8B8F97;
          font-size: 13px;
        }
        /* ── Share card ── */
        .ds-share { padding: 13px 14px; }
        .ds-share-head {
          display: flex; align-items: center; gap: 8px; margin-bottom: 4px;
        }
        .ds-share-icon {
          width: 26px; height: 26px; border-radius: 8px;
          background: rgba(60,140,255,0.08); color: #3C8CFF;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .ds-share-title {
          margin: 0; font-size: 14px; font-weight: 700; color: #1D1D1F;
        }
        .ds-share-desc {
          margin: 0 0 8px; font-size: 12px; color: #8B8F97; line-height: 1.4;
        }
        .ds-share-item {
          display: flex; align-items: center; gap: 9px;
          padding: 7px 0; border: none; border-bottom: 1px solid #E6E0D6;
          background: none; width: 100%; text-align: left; cursor: pointer;
          font-size: 13px; color: #1D1D1F; font-weight: 500;
          transition: color 0.15s;
        }
        .ds-share-item:last-child { border-bottom: none; padding-bottom: 0; }
        .ds-share-item:hover { color: #3C8CFF; }
        .ds-share-item-icon {
          width: 26px; height: 26px; border-radius: 7px;
          background: rgba(29,29,31,0.04);
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0; color: #8B8F97;
        }
        .ds-share-item:hover .ds-share-item-icon { color: #3C8CFF; background: rgba(60,140,255,0.08); }
        /* ── Referral card ── */
        .ds-referral {
          padding: 0; overflow: hidden;
        }
        .ds-referral-head {
          display: flex; align-items: flex-start; gap: 12px;
          padding: 20px 20px 0;
        }
        .ds-referral-icon {
          width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0;
          background: rgba(60,140,255,0.08); color: #3C8CFF;
          display: flex; align-items: center; justify-content: center;
        }
        .ds-referral-title {
          margin: 0 0 2px; font-size: 14px; font-weight: 700; color: #1D1D1F; letter-spacing: -0.01em;
        }
        .ds-referral-subtitle {
          margin: 0; font-size: 12px; color: #8B8F97;
        }
        .ds-referral-badge {
          display: inline-flex; align-items: center; gap: 5px;
          margin: 12px 20px 0;
          background: rgba(60,140,255,0.07); color: #3C8CFF;
          border: 1px solid rgba(60,140,255,0.16);
          border-radius: 999px; padding: 4px 11px 4px 8px;
          font-size: 11px; font-weight: 600;
        }
        .ds-referral-body {
          padding: 12px 20px 20px;
        }
        .ds-referral-link-row {
          display: flex; gap: 6px; align-items: stretch; margin-bottom: 9px;
        }
        .ds-referral-link-box {
          flex: 1; min-width: 0;
          background: #F6F2EA; border: 1px solid #E6E0D6; border-radius: 9px;
          padding: 8px 10px; font-size: 11px; color: #8B8F97;
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
          font-family: monospace;
        }
        .ds-referral-copy-btn {
          border: none; border-radius: 9px;
          background: #3C8CFF; color: white;
          font-size: 12px; font-weight: 700;
          padding: 8px 14px; cursor: pointer; flex-shrink: 0;
          transition: background 0.15s; white-space: nowrap;
        }
        .ds-referral-copy-btn:hover { background: #1F6BFF; }
        .ds-referral-copy-btn.done { background: #0F766E; }
        .ds-referral-share-row {
          display: grid; grid-template-columns: 1fr 1fr; gap: 6px;
        }
        .ds-referral-share-btn {
          display: flex; align-items: center; justify-content: center; gap: 7px;
          padding: 8px 6px; border-radius: 9px; font-size: 12px; font-weight: 500;
          cursor: pointer; transition: all 0.12s;
          border: 1px solid #E6E0D6; background: #FFFFFF; color: #1D1D1F;
        }
        .ds-referral-share-btn:hover { background: #F6F2EA; border-color: #D0CAC0; }
        .ds-referral-stats {
          display: flex; gap: 0; margin-top: 14px;
          border-top: 1px solid #E6E0D6; padding-top: 14px;
        }
        .ds-referral-stat {
          flex: 1; text-align: center;
          border-right: 1px solid #E6E0D6;
        }
        .ds-referral-stat:last-child { border-right: none; }
        .ds-referral-stat-value {
          display: block; font-size: 18px; font-weight: 800; color: #1D1D1F; line-height: 1;
        }
        .ds-referral-stat-label {
          display: block; font-size: 11px; color: #8B8F97; margin-top: 3px;
        }
        .ds-empty {
          text-align: center;
          padding: 42px 18px;
          color: #8B8F97;
        }
        .ds-empty h3 {
          margin: 0 0 8px;
          color: #1D1D1F;
          font-size: 20px;
        }
        .ds-empty p {
          margin: 0;
          font-size: 14px;
          line-height: 1.5;
        }
        .ds-empty button {
          margin-top: 14px;
          border: none;
          border-radius: 10px;
          background: #1D1D1F;
          color: white;
          font-size: 14px;
          font-weight: 600;
          padding: 10px 18px;
          cursor: pointer;
        }
        .ds-settings {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: 14px;
        }
        .ds-settings .ds-card {
          padding: 18px;
        }
        .ds-settings-title {
          margin: 0 0 13px;
          font-size: 17px;
          color: #1D1D1F;
          font-weight: 700;
        }
        .ds-setting-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
          padding: 8px 0;
          border-bottom: 1px solid #E6E0D6;
        }
        .ds-setting-row:last-child {
          border-bottom: none;
          padding-bottom: 0;
        }
        .ds-setting-label { color: #6F7480; font-size: 13px; }
        .ds-setting-value {
          color: #1D1D1F;
          font-size: 13px;
          font-weight: 600;
          text-align: right;
          max-width: 66%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
        .ds-settings-btn {
          width: 100%;
          border-radius: 10px;
          padding: 9px 11px;
          border: 1px solid #E6E0D6;
          background: white;
          color: #1D1D1F;
          font-size: 13px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .ds-settings-btn:hover {
          border-color: rgba(60,140,255,0.3);
          background: rgba(60,140,255,0.06);
        }
        .ds-settings-btn.danger {
          border-color: rgba(180,35,24,0.26);
          color: #B42318;
          background: rgba(180,35,24,0.06);
        }
        .ds-settings-btn.danger:hover {
          background: rgba(180,35,24,0.12);
        }
        .ds-settings-help {
          margin: -2px 0 4px;
          font-size: 12px;
          line-height: 1.45;
          color: #6F7480;
        }
        .ds-security-feedback {
          margin-top: 2px;
          border-radius: 10px;
          padding: 10px 12px;
          font-size: 12px;
          line-height: 1.45;
          border: 1px solid transparent;
        }
        .ds-security-feedback.loading {
          color: #3C8CFF;
          background: rgba(60,140,255,0.08);
          border-color: rgba(60,140,255,0.22);
        }
        .ds-security-feedback.success {
          color: #0F766E;
          background: rgba(15,118,110,0.1);
          border-color: rgba(15,118,110,0.26);
        }
        .ds-security-feedback.error {
          color: #B42318;
          background: rgba(180,35,24,0.08);
          border-color: rgba(180,35,24,0.24);
        }
        /* ── Settings extras ── */
        .ds-settings-input {
          width: 100%; padding: 7px 10px; border-radius: 8px;
          border: 1px solid #E6E0D6; background: #F6F2EA;
          font-size: 13px; color: #1D1D1F; outline: none;
          transition: border-color 0.15s; font-family: inherit; box-sizing: border-box;
        }
        .ds-settings-input:focus { border-color: rgba(60,140,255,0.5); }
        .ds-settings-form { margin-top: 10px; display: flex; flex-direction: column; gap: 8px; }
        .ds-settings-form-label { font-size: 11px; font-weight: 600; color: #8B8F97; margin-bottom: 3px; display: block; }
        .ds-settings-row-actions { display: flex; gap: 6px; align-items: center; flex-wrap: wrap; }
        .ds-settings-edit-btn {
          padding: 4px 10px; border-radius: 6px; border: 1px solid #E6E0D6;
          background: white; font-size: 12px; font-weight: 600; color: #8B8F97;
          cursor: pointer; transition: all 0.15s; white-space: nowrap;
        }
        .ds-settings-edit-btn:hover { border-color: rgba(60,140,255,0.3); color: #3C8CFF; }
        .ds-settings-save-btn {
          padding: 4px 10px; border-radius: 6px; border: none;
          background: #3C8CFF; font-size: 12px; font-weight: 600;
          color: white; cursor: pointer; transition: background 0.15s; white-space: nowrap;
        }
        .ds-settings-save-btn:hover { background: #1F6BFF; }
        .ds-settings-save-btn:disabled { opacity: 0.55; cursor: not-allowed; }
        .ds-settings-cancel-btn {
          padding: 4px 10px; border-radius: 6px; border: 1px solid #E6E0D6;
          background: white; font-size: 12px; font-weight: 600;
          color: #8B8F97; cursor: pointer; white-space: nowrap;
        }
        .ds-settings-confirm-box {
          margin-top: 8px; padding: 12px 14px; border-radius: 10px;
          background: rgba(180,35,24,0.05); border: 1px solid rgba(180,35,24,0.18);
        }
        .ds-settings-confirm-box.neutral {
          background: rgba(60,140,255,0.05); border-color: rgba(60,140,255,0.18);
        }
        .ds-settings-confirm-text { font-size: 13px; color: #1D1D1F; margin: 0 0 10px; line-height: 1.4; }
        .ds-settings-confirm-row { display: flex; gap: 8px; }
        /* Toggle switch */
        .ds-toggle-wrap { display: flex; align-items: center; gap: 8px; }
        .ds-toggle {
          position: relative; width: 36px; height: 20px; flex-shrink: 0;
          display: inline-block; cursor: pointer;
        }
        .ds-toggle input { opacity: 0; width: 0; height: 0; position: absolute; }
        .ds-toggle-track {
          position: absolute; inset: 0; border-radius: 20px;
          background: #D5CFC7; transition: background 0.2s; cursor: pointer;
        }
        .ds-toggle input:checked ~ .ds-toggle-track { background: #3C8CFF; }
        .ds-toggle-track::after {
          content: ''; position: absolute; width: 14px; height: 14px;
          border-radius: 50%; background: white; top: 3px; left: 3px;
          transition: transform 0.2s;
        }
        .ds-toggle input:checked ~ .ds-toggle-track::after { transform: translateX(16px); }
        .ds-settings-feedback {
          margin-top: 6px; border-radius: 8px; padding: 8px 11px;
          font-size: 12px; line-height: 1.45; border: 1px solid transparent;
        }
        .ds-settings-feedback.success { color: #0F766E; background: rgba(15,118,110,0.09); border-color: rgba(15,118,110,0.24); }
        .ds-settings-feedback.error { color: #B42318; background: rgba(180,35,24,0.07); border-color: rgba(180,35,24,0.22); }
        .ds-settings-feedback.loading { color: #3C8CFF; background: rgba(60,140,255,0.07); border-color: rgba(60,140,255,0.2); }
        @media (max-width: 1130px) {
          .ds-grid { grid-template-columns: 1fr; }
          .ds-side { order: 2; }
        }
        @media (max-width: 930px) {
          .ds-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .ds-settings { grid-template-columns: 1fr; }
        }
        @media (max-width: 760px) {
          .ds-wrap { padding: 16px 14px 44px; max-width: 100%; overflow-x: hidden; }
          .ds-topnav { margin-bottom: 14px; }
          .ds-profile {
            flex-direction: column;
            align-items: center;
            text-align: center;
            padding: 18px 16px;
            gap: 6px;
          }
          .ds-profile-meta { margin-left: 0; text-align: center; }
          .ds-profile-email { white-space: normal; overflow-wrap: anywhere; max-width: 100%; }
          .ds-section-tabs { overflow-x: auto; }
          .ds-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .ds-usage-head { flex-wrap: wrap; }
          .ds-thumb { width: 72px; height: 40px; }
        }
        @media (max-width: 639px) {
          .ds-wrap { padding: 10px 10px 60px; overflow-x: hidden; }
          /* Compact horizontal profile: [avatar] [name + email] */
          .ds-profile {
            flex-direction: row;
            align-items: center;
            text-align: left;
            padding: 10px 12px;
            gap: 10px;
            margin-bottom: 8px;
          }
          .ds-profile-meta { display: none; }
          .ds-profile-info { flex: 1; min-width: 0; overflow: hidden; }
          .ds-profile-name { font-size: 15px !important; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-bottom: 1px; }
          .ds-profile-email { font-size: 12px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
          .ds-avatar { width: 38px; height: 38px; font-size: 15px; flex-shrink: 0; }
          .ds-section-tab { font-size: 13px; padding: 7px 10px; }
          /* Hide weekly usage card — credits pill covers the same info */
          .ds-usage { display: none; }
          .ds-usage-days { display: none; }
          /* 2x2 stats grid, compact */
          .ds-stats { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
          .ds-stat { padding: 12px 12px 10px; border-radius: 13px; }
          .ds-stat-value { font-size: 22px; }
          .ds-stat-label { font-size: 12px; }
          .ds-stat-sub { font-size: 11px; }
          .ds-thumb { width: 64px; height: 36px; }
          .ds-credits-mobile { display: block !important; }
        }
        /* ── Hide old layout wrappers ── */
        .ds-wrap { display: none !important; }
      `}</style>

      {/* ── Mobile sticky header (hidden on desktop) ── */}
      <div className="ds-mobile-header">
        <button onClick={onBack} style={{ border: 'none', background: 'none', cursor: 'pointer', color: P.muted, padding: '4px 4px 4px 0', display: 'flex', alignItems: 'center' }}>
          <ChevronIcon size={16} dir="left" />
        </button>
        <div style={{ width: 34, height: 34, borderRadius: '50%', background: P.accent, color: 'white', fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{initial}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: P.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
          <div style={{ fontSize: 11, color: P.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
        </div>
      </div>

      {/* ═══ UNIFIED LAYOUT ═══ */}
      <div className="ds-layout">
        {/* ── Sidebar (desktop only, hidden on mobile via CSS) ── */}
        <aside className="ds-sidebar">
          <div className="ds-sidebar-user">
            <div className="ds-sidebar-avatar">{initial}</div>
            <p className="ds-sidebar-name">{displayName}</p>
            <p className="ds-sidebar-email">{user.email}</p>
            <span className="ds-sidebar-plan">Free · {tierMax} credits / 7d</span>
          </div>
          <nav className="ds-sidebar-nav">
            {[
              { key: 'overview', label: 'Overview', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="1" y="1" width="6" height="6" rx="1.5" fill="currentColor"/><rect x="9" y="1" width="6" height="6" rx="1.5" fill="currentColor"/><rect x="1" y="9" width="6" height="6" rx="1.5" fill="currentColor"/><rect x="9" y="9" width="6" height="6" rx="1.5" fill="currentColor"/></svg> },
              { key: 'settings', label: 'Settings', icon: <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="2.5" stroke="currentColor" strokeWidth="1.5"/><path d="M8 1v2M8 13v2M1 8h2M13 8h2M2.93 2.93l1.41 1.41M11.66 11.66l1.41 1.41M2.93 13.07l1.41-1.41M11.66 4.34l1.41-1.41" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg> },
            ].map(({ key, label, icon }) => (
              <button key={key} className={`ds-nav-btn${tab === key ? ' is-active' : ''}`} onClick={() => setTab(key)}>
                {icon}{label}
              </button>
            ))}
          </nav>
          <div className="ds-sidebar-footer">
            <button className="ds-back-btn" onClick={onBack}>
              <ChevronIcon size={13} dir="left" />Back to app
            </button>
          </div>
        </aside>

        {/* ── Main content ── */}
        <main className="ds-main">
          {tab === 'overview' && (
            <>
              <div className="ds-overview-mobile">
                {renderMobileOverview()}
              </div>
              <div className="ds-overview-desktop">
                <div key="overview" className="ds-fade-in ds-overview-grid">
                {/* Left column: stats + credits + history */}
                <div>
                  <div className="ds-stats-row">
                    <div className="ds-stat-card">
                      <div className="ds-stat-icon" style={{ background: 'rgba(60,140,255,0.1)', color: '#3C8CFF' }}>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="2"/><path d="M9 5.5v3.5l2 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                      </div>
                      <div className="ds-stat-val">{used}<span style={{ fontSize: 13, fontWeight: 500, color: '#8B8F97' }}> / {tierMax}</span></div>
                      <div className="ds-stat-lbl">Credits used</div>
                    </div>
                    <div className="ds-stat-card">
                      <div className="ds-stat-icon" style={{ background: 'rgba(15,118,110,0.1)', color: '#0F766E' }}>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="3" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M5 7h8M5 10h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                      </div>
                      <div className="ds-stat-val">{history.length}</div>
                      <div className="ds-stat-lbl">Transcripts saved</div>
                    </div>
                    <div className="ds-stat-card">
                      <div className="ds-stat-icon" style={{ background: 'rgba(180,83,9,0.1)', color: '#B45309' }}>
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><circle cx="9" cy="9" r="7" stroke="currentColor" strokeWidth="2"/><path d="M9 5v4.5l2.5 1.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>
                      </div>
                      <div className="ds-stat-val">{daysLeft}</div>
                      <div className="ds-stat-lbl">Days until reset</div>
                    </div>
                  </div>

                  <div className="ds-section" style={{ marginBottom: 18 }}>
                    <div className="ds-section-head">
                      <h2 className="ds-section-title">Weekly credits</h2>
                      <span style={{ fontSize: 12, color: '#8B8F97' }}>Resets in <strong style={{ color: '#1D1D1F' }}>{daysLeft}d</strong></span>
                    </div>
                    <div style={{ padding: '0 22px 18px' }}>
                      <div className="ds-credit-track">
                        <div className="ds-credit-fill" style={{ width: `${pct}%`, background: pct >= 80 ? '#B45309' : '#3C8CFF' }} />
                      </div>
                      <div className="ds-credit-meta">
                        <span>{used} / {tierMax} used</span>
                        <span>{remaining} remaining</span>
                      </div>
                    </div>
                  </div>

                  <div className="ds-section">
                    <div className="ds-section-head">
                      <h2 className="ds-section-title" style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 6, background: 'rgba(60,140,255,0.10)', color: '#3C8CFF', flexShrink: 0 }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        </span>
                        Recent transcripts
                      </h2>
                      {history.length > 0 && (
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#3C8CFF', background: 'rgba(60,140,255,0.10)', padding: '2px 9px', borderRadius: 999, border: '1px solid rgba(60,140,255,0.18)' }}>{history.length}</span>
                      )}
                    </div>
                    {history.length === 0 ? (
                      <div style={{ padding: '32px 22px', textAlign: 'center' }}>
                        <p style={{ margin: '0 0 12px', color: '#8B8F97', fontSize: 14 }}>No transcripts yet.</p>
                        <button onClick={onBack} style={{ border: 'none', borderRadius: 10, background: '#3C8CFF', color: 'white', fontSize: 13, fontWeight: 600, padding: '8px 20px', cursor: 'pointer' }}>Extract one</button>
                      </div>
                    ) : (
                      <>
                        {(showAllHistory ? history : history.slice(0, 4)).map((h, idx) => {
                          const wc = h.transcript ? h.transcript.trim().split(/\s+/).length : 0;
                          const title = h.title || h.id;
                          const channel = h.channel || (h.platform ? h.platform.charAt(0).toUpperCase() + h.platform.slice(1) : 'YouTube');
                          return (
                            <div key={`${h.id}-${idx}`} className="ds-hist-row" style={{ animationDelay: `${idx * 0.06}s` }} onClick={() => openTranscript(h)}>
                              <div className="ds-hist-thumb">
                                {h.thumbnail ? (
                                  <img src={h.thumbnail} alt={title} loading="lazy" onError={e => { e.target.style.display = 'none'; }} />
                                ) : (
                                  <PlatformIcon platform={h.platform || 'youtube'} size={22} />
                                )}
                              </div>
                              <div className="ds-hist-info">
                                <p className="ds-hist-title">{title}</p>
                                <p className="ds-hist-meta">
                                  <PlatformIcon platform={h.platform || 'youtube'} size={11} />
                                  {channel}
                                  {wc > 0 && <><span className="ds-row-meta-dot" />{wc.toLocaleString()} words</>}
                                  <span className="ds-row-meta-dot" />{timeAgo(h.date)}
                                </p>
                              </div>
                              <button className="ds-open-btn" onClick={e => { e.stopPropagation(); openTranscript(h); }}>Open</button>
                            </div>
                          );
                        })}
                        {history.length > 4 && (
                          <button className="ds-view-all-btn" onClick={() => setShowAllHistory(v => !v)}>
                            {showAllHistory ? 'Show less' : `+ View all ${history.length} transcripts`}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Right column: continue + referral */}
                <div>
                  {renderContinueCard({ mobile: false })}
                  {renderReferralCard({ mobile: false })}
                </div>
              </div>
              </div>
            </>
          )}

          {tab === 'settings' && (
            <div key="settings" className="ds-fade-in ds-settings-grid">
              {/* ── Card 1: Profile ── */}
              <div className="ds-settings-card-2">
                <h3 className="ds-settings-card-title">Profile</h3>
                <div className="ds-setting-row" style={{ alignItems: 'flex-start', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                    <span className="ds-setting-label">Display name</span>
                    {!editingName && (
                      <div className="ds-settings-row-actions">
                        {nameSaved && <span style={{ fontSize: 12, color: P.success, fontWeight: 600 }}>Saved ✓</span>}
                        <button className="ds-settings-edit-btn" onClick={() => setEditingName(true)}>Edit</button>
                      </div>
                    )}
                  </div>
                  {editingName ? (
                    <div style={{ width: '100%' }}>
                      <input className="ds-settings-input" value={nameInput} onChange={e => setNameInput(e.target.value)} placeholder="Your display name" autoFocus onKeyDown={e => { if (e.key === 'Enter') saveDisplayName(); if (e.key === 'Escape') { setEditingName(false); setNameError(''); } }} />
                      {nameError && <div className="ds-settings-feedback error" style={{ marginTop: 4 }}>{nameError}</div>}
                      <div className="ds-settings-row-actions" style={{ marginTop: 6 }}>
                        <button className="ds-settings-save-btn" onClick={saveDisplayName} disabled={nameSaving}>{nameSaving ? 'Saving…' : 'Save name'}</button>
                        <button className="ds-settings-cancel-btn" onClick={() => { setEditingName(false); setNameError(''); }}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <span className="ds-setting-value" style={{ textAlign: 'left' }}>{displayName}</span>
                  )}
                </div>
                <div className="ds-setting-row">
                  <span className="ds-setting-label">Email</span>
                  <span className="ds-setting-value">{user.email}</span>
                </div>
                <div className="ds-setting-row">
                  <span className="ds-setting-label">Member since</span>
                  <span className="ds-setting-value">{memberSince}</span>
                </div>
                <div className="ds-setting-row">
                  <span className="ds-setting-label">Current plan</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'rgba(60,140,255,0.1)', color: '#3C8CFF' }}>Free</span>
                    <span className="ds-setting-value" style={{ maxWidth: 'none' }}>{tierMax} credits / 7 days</span>
                  </div>
                </div>
              </div>

              {/* ── Card 2: Preferences ── */}
              <div className="ds-settings-card-2">
                <h3 className="ds-settings-card-title">Preferences</h3>
                <div className="ds-setting-row" style={{ alignItems: 'center', opacity: 0.45 }}>
                  <span className="ds-setting-label">Transcript language</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <select value={lang} disabled style={{ fontSize: 13, padding: '5px 10px', borderRadius: 8, border: `1px solid ${P.border}`, background: P.paper, color: P.ink, cursor: 'not-allowed', outline: 'none' }}>
                      {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                    </select>
                    <span style={{ fontSize: 11, color: P.muted, fontWeight: 500, fontStyle: 'italic' }}>Coming soon</span>
                  </div>
                </div>
                <div className="ds-setting-row" style={{ alignItems: 'center' }}>
                  <span className="ds-setting-label">Default export format</span>
                  <select value={prefFormat} onChange={e => { setPrefFormat(e.target.value); try { localStorage.setItem(prefKey('format'), e.target.value); } catch {} savePrefsToCloud({ format: e.target.value }); }} style={{ fontSize: 13, padding: '5px 10px', borderRadius: 8, border: `1px solid ${P.border}`, background: P.paper, color: P.ink, cursor: 'pointer', outline: 'none' }}>
                    <option value="plain">Plain text (.txt)</option>
                    <option value="srt">Subtitles (.srt)</option>
                    <option value="pdf">Document (.pdf)</option>
                  </select>
                </div>
                <div className="ds-setting-row" style={{ alignItems: 'center' }}>
                  <div>
                    <span className="ds-setting-label">Show timestamps</span>
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: '#9B9490' }}>Display time markers in transcripts</p>
                  </div>
                  <label className="ds-toggle">
                    <input type="checkbox" checked={prefTimestamps} onChange={e => { setPrefTimestamps(e.target.checked); try { localStorage.setItem(prefKey('timestamps'), String(e.target.checked)); } catch {} savePrefsToCloud({ timestamps: e.target.checked }); }} />
                    <span className="ds-toggle-track" />
                  </label>
                </div>
                <div className="ds-setting-row" style={{ alignItems: 'center' }}>
                  <div>
                    <span className="ds-setting-label">Auto-copy transcript</span>
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: '#9B9490' }}>Copy to clipboard after extraction</p>
                  </div>
                  <label className="ds-toggle">
                    <input type="checkbox" checked={prefAutoCopy} onChange={e => { setPrefAutoCopy(e.target.checked); try { localStorage.setItem(prefKey('autocopy'), String(e.target.checked)); } catch {} savePrefsToCloud({ autocopy: e.target.checked }); }} />
                    <span className="ds-toggle-track" />
                  </label>
                </div>
              </div>

              {/* ── Card 3: Security ── */}
              <div className="ds-settings-card-2" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <h3 className="ds-settings-card-title" style={{ marginBottom: 2 }}>Security</h3>
                {pwStep === 'idle' && (
                  <button className="ds-settings-btn" onClick={() => { setPwStep('form'); setPwError(''); }}>Change password</button>
                )}
                {(pwStep === 'form' || pwStep === 'confirm') && (
                  <div className="ds-settings-form">
                    <div>
                      <label className="ds-settings-form-label">Current password</label>
                      <input className="ds-settings-input" type="password" value={pwCurrent} onChange={e => setPwCurrent(e.target.value)} placeholder="Enter current password" />
                    </div>
                    <div>
                      <label className="ds-settings-form-label">New password</label>
                      <input className="ds-settings-input" type="password" value={pwNew} onChange={e => setPwNew(e.target.value)} placeholder="Min. 6 characters" />
                    </div>
                    <div>
                      <label className="ds-settings-form-label">Confirm new password</label>
                      <input className="ds-settings-input" type="password" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)} placeholder="Repeat new password" />
                    </div>
                    {pwError && <div className="ds-settings-feedback error">{pwError}</div>}
                    {pwStep === 'form' && (
                      <div className="ds-settings-row-actions">
                        <button className="ds-settings-save-btn" style={{ flex: 1 }} onClick={() => { if (!pwCurrent) { setPwError('Enter your current password.'); return; } if (pwNew.length < 6) { setPwError('New password must be at least 6 characters.'); return; } if (pwNew !== pwConfirm) { setPwError('New passwords do not match.'); return; } setPwError(''); setPwStep('confirm'); }}>Continue</button>
                        <button className="ds-settings-cancel-btn" onClick={() => { setPwStep('idle'); setPwCurrent(''); setPwNew(''); setPwConfirm(''); setPwError(''); }}>Cancel</button>
                      </div>
                    )}
                    {pwStep === 'confirm' && (
                      <div className="ds-settings-confirm-box neutral">
                        <p className="ds-settings-confirm-text">Are you sure you want to change your password? You'll stay signed in on this device.</p>
                        <div className="ds-settings-confirm-row">
                          <button className="ds-settings-save-btn" style={{ flex: 1 }} onClick={handlePasswordChange}>Yes, change it</button>
                          <button className="ds-settings-cancel-btn" onClick={() => setPwStep('form')}>Go back</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {pwStep === 'loading' && <div className="ds-settings-feedback loading">Updating your password…</div>}
                {pwStep === 'success' && <div className="ds-settings-feedback success">Password updated successfully ✓</div>}
                <div style={{ borderTop: `1px solid ${P.border}`, paddingTop: 10, marginTop: 2 }}>
                  {!signOutConfirm ? (
                    <button className="ds-settings-btn danger" onClick={() => setSignOutConfirm(true)}>Sign out of account</button>
                  ) : (
                    <div className="ds-settings-confirm-box">
                      <p className="ds-settings-confirm-text">Are you sure you want to sign out?</p>
                      <div className="ds-settings-confirm-row">
                        <button className="ds-settings-save-btn" style={{ flex: 1, background: P.error }} onClick={onSignOut}>Yes, sign out</button>
                        <button className="ds-settings-cancel-btn" onClick={() => setSignOutConfirm(false)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
                <div style={{ borderTop: `1px solid ${P.border}`, paddingTop: 10 }}>
                  <button onClick={() => { setShowDeleteModal(true); setDeleteStep('reason'); setDeleteReason(''); setDeleteTyped(''); setDeleteCountdown(8); setDeleteError(''); }} className="ds-settings-btn danger">Delete account</button>
                </div>
              </div>

              {/* ── Card 4: Data & Storage ── */}
              <div className="ds-settings-card-2" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <h3 className="ds-settings-card-title" style={{ marginBottom: 2 }}>Data & Storage</h3>
                <p className="ds-settings-help">Your transcripts are stored locally on this device.</p>
                <div className="ds-setting-row" style={{ border: 'none', padding: '4px 0' }}>
                  <span className="ds-setting-label">Saved transcripts</span>
                  <span className="ds-setting-value">{history.length} / 10</span>
                </div>
                <button className="ds-settings-btn" onClick={exportHistory} disabled={history.length === 0} style={{ opacity: history.length === 0 ? 0.45 : 1 }}>Export history as JSON</button>
                {!clearConfirm ? (
                  <button className="ds-settings-btn danger" onClick={() => setClearConfirm(true)} disabled={history.length === 0} style={{ opacity: history.length === 0 ? 0.45 : 1 }}>Clear all history</button>
                ) : (
                  <div className="ds-settings-confirm-box">
                    {clearDone ? (
                      <div className="ds-settings-feedback success" style={{ margin: 0 }}>History cleared ✓</div>
                    ) : (
                      <>
                        <p className="ds-settings-confirm-text">This will permanently delete all {history.length} saved transcript{history.length !== 1 ? 's' : ''}. This cannot be undone.</p>
                        <div className="ds-settings-confirm-row">
                          <button className="ds-settings-save-btn" style={{ flex: 1, background: P.error }} onClick={handleClearHistory}>Yes, clear all</button>
                          <button className="ds-settings-cancel-btn" onClick={() => setClearConfirm(false)}>Cancel</button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          {false && (
          <div>

          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain', padding: '12px 14px', paddingBottom: 'max(80px, calc(env(safe-area-inset-bottom, 0px) + 60px))', display: 'flex', flexDirection: 'column', gap: 10 }}>

            {/* ── Overview ── */}
            {tab === 'overview' && (
              <>
                {/* Credits bar */}
                <div style={{ background: '#FFFFFF', border: '1px solid #E6E0D6', borderRadius: 14, padding: '9px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#1D1D1F' }}>{used} / {tierMax} credits used</span>
                    <span style={{ fontSize: 12, color: '#6F7480' }}>resets in {daysLeft}d</span>
                  </div>
                  <div style={{ height: 5, borderRadius: 999, background: '#E6E0D6', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, borderRadius: 999, background: pct >= 80 ? '#B45309' : '#3C8CFF', transition: 'width 0.4s ease' }} />
                  </div>
                  <div style={{ marginTop: 4, fontSize: 12, color: '#6F7480' }}>{remaining} credits remaining</div>
                </div>

                {renderContinueCard({ mobile: true })}

                {/* Recent transcripts */}
                <div style={{ background: '#FFFFFF', border: '1px solid #E6E0D6', borderRadius: 14, overflow: 'hidden' }}>
                  <div style={{ padding: '9px 12px 8px', borderBottom: '1px solid #E6E0D6' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#1D1D1F' }}>Recent Transcripts</span>
                  </div>
                  {history.length === 0 ? (
                    <div style={{ padding: '16px 12px', textAlign: 'center' }}>
                      <p style={{ margin: '0 0 10px', fontSize: 13, color: '#8B8F97' }}>No transcripts yet.</p>
                      <button onClick={onBack} style={{ border: 'none', borderRadius: 9, background: '#3C8CFF', color: 'white', fontSize: 13, fontWeight: 600, padding: '7px 16px', cursor: 'pointer' }}>Extract one</button>
                    </div>
                  ) : (
                    <>
                      {(showAllHistory ? history : history.slice(0, 3)).map((h, idx) => {
                        const wc = h.transcript ? h.transcript.trim().split(/\s+/).length : 0;
                        const title = h.title || h.id;
                        return (
                          <div key={`m-${h.id}-${idx}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', borderTop: '1px solid #E6E0D6' }}>
                            <div style={{ width: 54, height: 30, borderRadius: 6, overflow: 'hidden', flexShrink: 0, background: 'rgba(60,140,255,0.06)', border: '1px solid rgba(60,140,255,0.1)' }}>
                              {h.thumbnail && <img src={h.thumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} onError={e => { e.target.style.display = 'none'; }} />}
                            </div>
                            <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: '#1D1D1F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{title}</div>
                              <div style={{ fontSize: 12, color: '#6F7480' }}>{wc > 0 ? `${wc.toLocaleString()} words · ` : ''}{timeAgo(h.date)}</div>
                            </div>
                            <button onClick={() => openTranscript(h)} style={{ flexShrink: 0, border: 'none', borderRadius: 7, background: '#3C8CFF', color: 'white', fontSize: 11, fontWeight: 700, padding: '5px 10px', cursor: 'pointer' }}>Open</button>
                          </div>
                        );
                      })}
                      {history.length > 3 && (
                        <div style={{ padding: '8px 12px 10px', borderTop: '1px solid #E6E0D6' }}>
                          <button onClick={() => setShowAllHistory(v => !v)} style={{ border: 'none', background: 'none', color: '#3C8CFF', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: 0 }}>
                            {showAllHistory ? 'Show less' : `+ View all ${history.length} transcripts`}
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {renderReferralCard({ mobile: true })}
              </>
            )}

            {/* ── Settings ── */}
            {tab === 'settings' && (
              <>
                {/* Profile */}
                <div style={{ background: '#FFFFFF', border: '1px solid #E6E0D6', borderRadius: 14, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#8B8F97', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>Profile</span>
                  {/* Display name */}
                  <div style={{ paddingBottom: 10, marginBottom: 10, borderBottom: '1px solid #E6E0D6' }}>
                    <div style={{ fontSize: 11, color: '#8B8F97', marginBottom: 4 }}>Display name</div>
                    {!editingName ? (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 14, fontWeight: 600, color: '#1D1D1F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{displayName}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                          {nameSaved && <span style={{ fontSize: 12, color: P.success, fontWeight: 600 }}>Saved ✓</span>}
                          <button onClick={() => setEditingName(true)} style={{ border: '1px solid #E6E0D6', background: 'white', color: '#8B8F97', fontSize: 12, fontWeight: 600, padding: '4px 10px', borderRadius: 6, cursor: 'pointer' }}>Edit</button>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <input className="ds-settings-input" value={nameInput} onChange={e => setNameInput(e.target.value)} placeholder="Your display name" autoFocus onKeyDown={e => { if (e.key === 'Enter') saveDisplayName(); if (e.key === 'Escape') { setEditingName(false); setNameError(''); } }} />
                        {nameError && <div className="ds-settings-feedback error" style={{ marginTop: 4 }}>{nameError}</div>}
                        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                          <button className="ds-settings-save-btn" onClick={saveDisplayName} disabled={nameSaving}>{nameSaving ? 'Saving…' : 'Save'}</button>
                          <button className="ds-settings-cancel-btn" onClick={() => { setEditingName(false); setNameError(''); }}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                  {/* Email */}
                  <div style={{ paddingBottom: 10, marginBottom: 10, borderBottom: '1px solid #E6E0D6' }}>
                    <div style={{ fontSize: 11, color: '#8B8F97', marginBottom: 3 }}>Email</div>
                    <div style={{ fontSize: 14, color: '#1D1D1F', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
                  </div>
                  {/* Member since */}
                  <div style={{ paddingBottom: 10, marginBottom: 10, borderBottom: '1px solid #E6E0D6' }}>
                    <div style={{ fontSize: 11, color: '#8B8F97', marginBottom: 3 }}>Member since</div>
                    <div style={{ fontSize: 14, color: '#1D1D1F' }}>{memberSince}</div>
                  </div>
                  {/* Plan */}
                  <div>
                    <div style={{ fontSize: 11, color: '#8B8F97', marginBottom: 3 }}>Plan</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'rgba(60,140,255,0.1)', color: '#3C8CFF' }}>Free</span>
                      <span style={{ fontSize: 14, color: '#1D1D1F' }}>{tierMax} credits / 7 days</span>
                    </div>
                  </div>
                </div>

                {/* Preferences */}
                <div style={{ background: '#FFFFFF', border: '1px solid #E6E0D6', borderRadius: 14, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 0 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#8B8F97', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 10 }}>Preferences</span>
                  {/* Export format */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, paddingBottom: 12, marginBottom: 12, borderBottom: '1px solid #E6E0D6' }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#1D1D1F' }}>Export format</div>
                    <select value={prefFormat} onChange={e => { setPrefFormat(e.target.value); try { localStorage.setItem(prefKey('format'), e.target.value); } catch {} savePrefsToCloud({ format: e.target.value }); }} style={{ fontSize: 13, padding: '5px 8px', borderRadius: 8, border: '1px solid #E6E0D6', background: '#F6F2EA', color: '#1D1D1F', cursor: 'pointer', outline: 'none' }}>
                      <option value="plain">Plain text</option>
                      <option value="srt">Subtitles (.srt)</option>
                      <option value="pdf">Document (.pdf)</option>
                    </select>
                  </div>
                  {[
                    { label: 'Show timestamps', sub: 'Time markers in transcript', checked: prefTimestamps, onChange: e => { setPrefTimestamps(e.target.checked); try { localStorage.setItem(prefKey('timestamps'), String(e.target.checked)); } catch {} savePrefsToCloud({ timestamps: e.target.checked }); } },
                    { label: 'Auto-copy transcript', sub: 'Copy to clipboard after extraction', checked: prefAutoCopy, onChange: e => { setPrefAutoCopy(e.target.checked); try { localStorage.setItem(prefKey('autocopy'), String(e.target.checked)); } catch {} savePrefsToCloud({ autocopy: e.target.checked }); } },
                  ].map((pref, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, paddingTop: i > 0 ? 12 : 0, marginTop: i > 0 ? 12 : 0, borderTop: i > 0 ? '1px solid #E6E0D6' : 'none' }}>
                      <div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1D1D1F' }}>{pref.label}</div>
                        <div style={{ fontSize: 11, color: '#9B9490' }}>{pref.sub}</div>
                      </div>
                      <label className="ds-toggle">
                        <input type="checkbox" checked={pref.checked} onChange={pref.onChange} />
                        <span className="ds-toggle-track" />
                      </label>
                    </div>
                  ))}
                </div>

                {/* Security */}
                <div style={{ background: '#FFFFFF', border: '1px solid #E6E0D6', borderRadius: 14, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#8B8F97', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Security</span>
                  {pwStep === 'idle' && (
                    <button className="ds-settings-btn" onClick={() => { setPwStep('form'); setPwError(''); }}>Change password</button>
                  )}
                  {(pwStep === 'form' || pwStep === 'confirm') && (
                    <div className="ds-settings-form">
                      <div>
                        <label className="ds-settings-form-label">Current password</label>
                        <input className="ds-settings-input" type="password" value={pwCurrent} onChange={e => setPwCurrent(e.target.value)} placeholder="Enter current password" />
                      </div>
                      <div>
                        <label className="ds-settings-form-label">New password</label>
                        <input className="ds-settings-input" type="password" value={pwNew} onChange={e => setPwNew(e.target.value)} placeholder="Min. 6 characters" />
                      </div>
                      <div>
                        <label className="ds-settings-form-label">Confirm new password</label>
                        <input className="ds-settings-input" type="password" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)} placeholder="Repeat new password" />
                      </div>
                      {pwError && <div className="ds-settings-feedback error">{pwError}</div>}
                      {pwStep === 'form' && (
                        <div className="ds-settings-row-actions">
                          <button className="ds-settings-save-btn" style={{ flex: 1 }} onClick={() => { if (!pwCurrent) { setPwError('Enter your current password.'); return; } if (pwNew.length < 6) { setPwError('New password must be at least 6 characters.'); return; } if (pwNew !== pwConfirm) { setPwError('New passwords do not match.'); return; } setPwError(''); setPwStep('confirm'); }}>Continue</button>
                          <button className="ds-settings-cancel-btn" onClick={() => { setPwStep('idle'); setPwCurrent(''); setPwNew(''); setPwConfirm(''); setPwError(''); }}>Cancel</button>
                        </div>
                      )}
                      {pwStep === 'confirm' && (
                        <div className="ds-settings-confirm-box neutral">
                          <p className="ds-settings-confirm-text">Are you sure you want to change your password?</p>
                          <div className="ds-settings-confirm-row">
                            <button className="ds-settings-save-btn" style={{ flex: 1 }} onClick={handlePasswordChange}>Yes, change it</button>
                            <button className="ds-settings-cancel-btn" onClick={() => setPwStep('form')}>Go back</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  {pwStep === 'loading' && <div className="ds-settings-feedback loading">Updating your password…</div>}
                  {pwStep === 'success' && <div className="ds-settings-feedback success">Password updated successfully ✓</div>}
                </div>

                {/* Data & Storage */}
                <div style={{ background: '#FFFFFF', border: '1px solid #E6E0D6', borderRadius: 14, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#8B8F97', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Data & Storage</span>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <span style={{ color: '#8B8F97' }}>Saved transcripts</span>
                    <span style={{ fontWeight: 600, color: '#1D1D1F' }}>{history.length} / 10</span>
                  </div>
                  <button className="ds-settings-btn" onClick={exportHistory} disabled={history.length === 0} style={{ opacity: history.length === 0 ? 0.45 : 1 }}>Export history as JSON</button>
                  {!clearConfirm ? (
                    <button className="ds-settings-btn danger" onClick={() => setClearConfirm(true)} disabled={history.length === 0} style={{ opacity: history.length === 0 ? 0.45 : 1 }}>Clear all history</button>
                  ) : (
                    <div className="ds-settings-confirm-box">
                      {clearDone ? (
                        <div className="ds-settings-feedback success" style={{ margin: 0 }}>History cleared ✓</div>
                      ) : (
                        <>
                          <p className="ds-settings-confirm-text">This will permanently delete all {history.length} saved transcript{history.length !== 1 ? 's' : ''}. This cannot be undone.</p>
                          <div className="ds-settings-confirm-row">
                            <button className="ds-settings-save-btn" style={{ flex: 1, background: P.error }} onClick={handleClearHistory}>Yes, clear all</button>
                            <button className="ds-settings-cancel-btn" onClick={() => setClearConfirm(false)}>Cancel</button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Sign out */}
                <div style={{ background: '#FFFFFF', border: '1px solid #E6E0D6', borderRadius: 14, padding: '12px 14px' }}>
                  {!signOutConfirm ? (
                    <button onClick={() => setSignOutConfirm(true)} style={{ width: '100%', border: '1px solid rgba(180,35,24,0.26)', background: 'rgba(180,35,24,0.06)', color: '#B42318', fontSize: 13, fontWeight: 600, padding: '10px', borderRadius: 10, cursor: 'pointer' }}>Sign out</button>
                  ) : (
                    <div>
                      <p style={{ margin: '0 0 10px', fontSize: 13, color: '#1D1D1F' }}>Are you sure you want to sign out?</p>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button className="ds-settings-save-btn" style={{ flex: 1, background: P.error }} onClick={onSignOut}>Yes, sign out</button>
                        <button className="ds-settings-cancel-btn" onClick={() => setSignOutConfirm(false)}>Cancel</button>
                      </div>
                    </div>
                  )}
                </div>
                {/* Danger zone */}
                <div style={{ background: '#FFFFFF', border: '1px solid #E6E0D6', borderRadius: 14, padding: '12px 14px' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#8B8F97', letterSpacing: '0.06em', textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>Danger zone</span>
                  <button onClick={() => { setShowDeleteModal(true); setDeleteStep('reason'); setDeleteReason(''); setDeleteTyped(''); setDeleteCountdown(8); setDeleteError(''); }} style={{ width: '100%', border: '1px solid rgba(180,35,24,0.3)', background: 'rgba(180,35,24,0.05)', color: '#B42318', fontSize: 13, fontWeight: 600, padding: '10px', borderRadius: 10, cursor: 'pointer', textAlign: 'left' }}>Delete account</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
        </main>
      </div>

      {/* Mobile bottom tabs */}
      <div className="ds-mobile-tabs">
        <div style={{ display: 'flex', padding: '8px 16px' }}>
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'settings', label: 'Settings' },
          ].map(({ key, label }) => (
            <button key={key} className={`ds-mob-tab${tab === key ? ' is-active' : ''}`} onClick={() => setTab(key)} style={{ flex: 1, border: 'none', background: 'none', fontSize: 13, fontWeight: tab === key ? 700 : 500, color: tab === key ? '#3C8CFF' : '#8B8F97', padding: '6px', cursor: 'pointer' }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {false && (
      <div>
        <nav></nav>
        <div className="ds-grid">
          <main>
            <section className="ds-card ds-profile">
              <div className="ds-avatar">{initial}</div>
              <div className="ds-profile-info" style={{ minWidth: 0 }}>
                <h1 className="ds-profile-name">{displayName}</h1>
                <p className="ds-profile-email">{user.email}</p>
              </div>
              <div className="ds-profile-meta">
                <p className="ds-profile-meta-label">Member since</p>
                <p className="ds-profile-meta-value">{memberSince}</p>
              </div>
            </section>

            {/* Credits pill — mobile only, overview tab only */}
            {tab === 'overview' && (
              <div className="ds-credits-mobile" style={{ display: 'none', margin: '8px 0', padding: '10px 14px', background: '#FFFFFF', border: '1px solid #E6E0D6', borderRadius: 14, boxShadow: '0 2px 8px rgba(29,29,31,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#1D1D1F' }}>Free Credits</span>
                  <span style={{ fontSize: 12, color: '#8B8F97' }}>{used} / {tierMax} used · resets in {daysLeft}d</span>
                </div>
                <div style={{ height: 5, borderRadius: 999, background: '#E6E0D6', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, borderRadius: 999, background: pct >= 80 ? '#B45309' : '#3C8CFF', transition: 'width 0.4s ease' }} />
                </div>
              </div>
            )}

            {/* Inline section tabs */}
            <div className="ds-section-tabs">
              {[['overview', 'Overview'], ['settings', 'Settings']].map(([key, label]) => (
                <button key={key} className={`ds-section-tab ${tab === key ? 'is-active' : ''}`} onClick={() => setTab(key)}>
                  {label}
                </button>
              ))}
            </div>

            {tab === 'overview' && (
              <div className="ds-overview-col">
                <section className="ds-card ds-usage">
                  <div className="ds-usage-head">
                    <h2 className="ds-usage-title">Weekly usage</h2>
                    <span style={{ fontSize: 13, color: '#8B8F97', fontWeight: 500 }}>
                      Resets in <strong style={{ color: '#1D1D1F' }}>{daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong>
                    </span>
                  </div>
                  <div className="ds-usage-track">
                    <div className="ds-usage-bar" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="ds-usage-meta">
                    <div>
                      <strong>{used} / {tierMax}</strong>{' '}
                      <span style={{ color: '#8B8F97', fontSize: 14 }}>used</span>
                    </div>
                    <div className="ds-usage-days">
                      {weekSegments.map(i => (
                        <span
                          key={i}
                          className={`ds-usage-day ${i <= activeDay ? 'is-active' : ''}`}
                          style={{ height: `${8 + (i === activeDay ? 18 : i <= activeDay ? 12 : 6)}px` }}
                        />
                      ))}
                      <span style={{ marginLeft: 6, fontSize: 13, color: '#8B8F97' }}>{remaining} remaining</span>
                    </div>
                  </div>
                  <div className="ds-usage-daystats">
                    <span>Most used day: <strong style={{ color: '#1D1D1F' }}>{mostUsedDay}</strong></span>
                    <span>Avg per day: <strong style={{ color: '#1D1D1F' }}>{avgPerDay}</strong></span>
                  </div>
                </section>

                <section className="ds-card ds-list-card" style={{ position: 'relative' }}>
                  <BorderBeam size={340} duration={14} delay={2} colorFrom="rgba(60,140,255,0.55)" colorTo="rgba(123,211,255,0)" borderWidth={1.5} />
                  <div className="ds-list-head">
                    <h2 className="ds-list-title">
                      <span className="ds-list-title-icon">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                        </svg>
                      </span>
                      Recent Transcripts
                    </h2>
                    {history.length > 0 && (
                      <span className="ds-list-count">{history.length}</span>
                    )}
                  </div>

                  {history.length === 0 ? (
                    <div className="ds-empty">
                      <h3>No transcripts yet</h3>
                      <p>Extract your first transcript and it will appear here.</p>
                      <button onClick={onBack}>Extract transcript</button>
                    </div>
                  ) : (
                    <>
                      {(showAllHistory ? history : history.slice(0, 4)).map((h, idx) => {
                        const wc = h.transcript ? h.transcript.trim().split(/\s+/).length : 0;
                        const title = h.title || h.id;
                        const channel = h.channel || (h.platform ? h.platform.charAt(0).toUpperCase() + h.platform.slice(1) : 'YouTube');
                        return (
                          <div key={`${h.id}-${idx}`} className="ds-row" style={{ animationDelay: `${idx * 0.06}s` }} onClick={() => openTranscript(h)}>
                            <div className="ds-thumb">
                              {h.thumbnail ? (
                                <img src={h.thumbnail} alt={title} loading="lazy" />
                              ) : (
                                <PlatformIcon platform={h.platform || 'youtube'} size={28} />
                              )}
                            </div>
                            <div className="ds-row-main" style={{ minWidth: 0, flex: 1 }}>
                              <p className="ds-row-title">{title}</p>
                              <p className="ds-row-meta">
                                <PlatformIcon platform={h.platform || 'youtube'} size={11} />
                                {channel}
                                {wc > 0 && <><span className="ds-row-meta-dot" />{wc.toLocaleString()} words</>}
                                <span className="ds-row-meta-dot" />{timeAgo(h.date)}
                              </p>
                            </div>
                            <div className="ds-row-actions">
                              <button className="ds-row-btn primary" onClick={e => { e.stopPropagation(); openTranscript(h); }}>Open</button>
                            </div>
                          </div>
                        );
                      })}
                      {history.length > 4 && (
                        <div className="ds-list-footer">
                          <button className="ds-link-btn" onClick={() => setShowAllHistory(v => !v)}>
                            {showAllHistory ? 'Show less' : `+ View all ${history.length} transcripts`} <ChevronIcon size={11} dir={showAllHistory ? 'up' : 'right'} />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </section>

              </div>
            )}

            {tab === 'settings' && (
              <section className="ds-settings">

                {/* ── Card 1: Profile ── */}
                <div className="ds-card">
                  <h3 className="ds-settings-title">Profile</h3>

                  {/* Display name */}
                  <div className="ds-setting-row" style={{ alignItems: 'flex-start', flexDirection: 'column', gap: 6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                      <span className="ds-setting-label">Display name</span>
                      {!editingName && (
                        <div className="ds-settings-row-actions">
                          {nameSaved && <span style={{ fontSize: 12, color: P.success, fontWeight: 600 }}>Saved ✓</span>}
                          <button className="ds-settings-edit-btn" onClick={() => setEditingName(true)}>Edit</button>
                        </div>
                      )}
                    </div>
                    {editingName ? (
                      <div style={{ width: '100%' }}>
                        <input
                          className="ds-settings-input"
                          value={nameInput}
                          onChange={e => setNameInput(e.target.value)}
                          placeholder="Your display name"
                          autoFocus
                          onKeyDown={e => { if (e.key === 'Enter') saveDisplayName(); if (e.key === 'Escape') { setEditingName(false); setNameError(''); } }}
                        />
                        {nameError && <div className="ds-settings-feedback error" style={{ marginTop: 4 }}>{nameError}</div>}
                        <div className="ds-settings-row-actions" style={{ marginTop: 6 }}>
                          <button className="ds-settings-save-btn" onClick={saveDisplayName} disabled={nameSaving}>{nameSaving ? 'Saving…' : 'Save name'}</button>
                          <button className="ds-settings-cancel-btn" onClick={() => { setEditingName(false); setNameError(''); }}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <span className="ds-setting-value" style={{ textAlign: 'left', maxWidth: '100%' }}>{displayName}</span>
                    )}
                  </div>

                  {/* Email */}
                  <div className="ds-setting-row">
                    <span className="ds-setting-label">Email</span>
                    <span className="ds-setting-value">{user.email}</span>
                  </div>

                  {/* Member since */}
                  <div className="ds-setting-row">
                    <span className="ds-setting-label">Member since</span>
                    <span className="ds-setting-value">{memberSince}</span>
                  </div>

                  {/* Plan */}
                  <div className="ds-setting-row">
                    <span className="ds-setting-label">Current plan</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'rgba(60,140,255,0.1)', color: '#3C8CFF' }}>Free</span>
                      <span className="ds-setting-value" style={{ maxWidth: 'none' }}>{tierMax} credits / 7 days</span>
                    </div>
                  </div>
                </div>

                {/* ── Card 2: Preferences ── */}
                <div className="ds-card">
                  <h3 className="ds-settings-title">Preferences</h3>

                  {/* Transcript language — ON ICE, disabled until feature is stable */}
                  <div className="ds-setting-row" style={{ alignItems: 'center', opacity: 0.45 }}>
                    <span className="ds-setting-label">Transcript language</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <select
                        value={lang}
                        disabled
                        style={{ fontSize: 13, padding: '5px 10px', borderRadius: 8, border: `1px solid ${P.border}`, background: P.paper, color: P.ink, cursor: 'not-allowed', outline: 'none' }}
                      >
                        {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
                      </select>
                      <span style={{ fontSize: 11, color: P.muted, fontWeight: 500, fontStyle: 'italic' }}>Coming soon</span>
                    </div>
                  </div>

                  {/* Default export format */}
                  <div className="ds-setting-row" style={{ alignItems: 'center' }}>
                    <span className="ds-setting-label">Default export format</span>
                    <select
                      value={prefFormat}
                      onChange={e => {
                        setPrefFormat(e.target.value);
                        try { localStorage.setItem(prefKey('format'), e.target.value); } catch {}
                        savePrefsToCloud({ format: e.target.value });
                      }}
                      style={{ fontSize: 13, padding: '5px 10px', borderRadius: 8, border: `1px solid ${P.border}`, background: P.paper, color: P.ink, cursor: 'pointer', outline: 'none' }}
                    >
                      <option value="plain">Plain text (.txt)</option>
                      <option value="srt">Subtitles (.srt)</option>
                      <option value="pdf">Document (.pdf)</option>
                    </select>
                  </div>

                  {/* Show timestamps toggle */}
                  <div className="ds-setting-row" style={{ alignItems: 'center' }}>
                    <div>
                      <span className="ds-setting-label">Show timestamps</span>
                      <p style={{ margin: '2px 0 0', fontSize: 11, color: '#9B9490' }}>Display time markers in transcripts</p>
                    </div>
                    <label className="ds-toggle">
                      <input
                        type="checkbox"
                        checked={prefTimestamps}
                        onChange={e => {
                          setPrefTimestamps(e.target.checked);
                          try { localStorage.setItem(prefKey('timestamps'), String(e.target.checked)); } catch {}
                          savePrefsToCloud({ timestamps: e.target.checked });
                        }}
                      />
                      <span className="ds-toggle-track" />
                    </label>
                  </div>

                  {/* Auto-copy toggle */}
                  <div className="ds-setting-row" style={{ alignItems: 'center' }}>
                    <div>
                      <span className="ds-setting-label">Auto-copy transcript</span>
                      <p style={{ margin: '2px 0 0', fontSize: 11, color: '#9B9490' }}>Copy to clipboard after extraction</p>
                    </div>
                    <label className="ds-toggle">
                      <input
                        type="checkbox"
                        checked={prefAutoCopy}
                        onChange={e => {
                          setPrefAutoCopy(e.target.checked);
                          try { localStorage.setItem(prefKey('autocopy'), String(e.target.checked)); } catch {}
                          savePrefsToCloud({ autocopy: e.target.checked });
                        }}
                      />
                      <span className="ds-toggle-track" />
                    </label>
                  </div>
                </div>

                {/* ── Card 3: Security ── */}
                <div className="ds-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <h3 className="ds-settings-title" style={{ marginBottom: 2 }}>Security</h3>

                  {pwStep === 'idle' && (
                    <button className="ds-settings-btn" onClick={() => { setPwStep('form'); setPwError(''); }}>Change password</button>
                  )}

                  {(pwStep === 'form' || pwStep === 'confirm') && (
                    <div className="ds-settings-form">
                      <div>
                        <label className="ds-settings-form-label">Current password</label>
                        <input className="ds-settings-input" type="password" value={pwCurrent} onChange={e => setPwCurrent(e.target.value)} placeholder="Enter current password" />
                      </div>
                      <div>
                        <label className="ds-settings-form-label">New password</label>
                        <input className="ds-settings-input" type="password" value={pwNew} onChange={e => setPwNew(e.target.value)} placeholder="Min. 6 characters" />
                      </div>
                      <div>
                        <label className="ds-settings-form-label">Confirm new password</label>
                        <input className="ds-settings-input" type="password" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)} placeholder="Repeat new password" />
                      </div>
                      {pwError && <div className="ds-settings-feedback error">{pwError}</div>}

                      {pwStep === 'form' && (
                        <div className="ds-settings-row-actions">
                          <button className="ds-settings-save-btn" style={{ flex: 1 }} onClick={() => {
                            if (!pwCurrent) { setPwError('Enter your current password.'); return; }
                            if (pwNew.length < 6) { setPwError('New password must be at least 6 characters.'); return; }
                            if (pwNew !== pwConfirm) { setPwError('New passwords do not match.'); return; }
                            setPwError(''); setPwStep('confirm');
                          }}>Continue</button>
                          <button className="ds-settings-cancel-btn" onClick={() => { setPwStep('idle'); setPwCurrent(''); setPwNew(''); setPwConfirm(''); setPwError(''); }}>Cancel</button>
                        </div>
                      )}

                      {pwStep === 'confirm' && (
                        <div className="ds-settings-confirm-box neutral">
                          <p className="ds-settings-confirm-text">Are you sure you want to change your password? You'll stay signed in on this device.</p>
                          <div className="ds-settings-confirm-row">
                            <button className="ds-settings-save-btn" style={{ flex: 1 }} onClick={handlePasswordChange}>Yes, change it</button>
                            <button className="ds-settings-cancel-btn" onClick={() => setPwStep('form')}>Go back</button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {pwStep === 'loading' && <div className="ds-settings-feedback loading">Updating your password…</div>}
                  {pwStep === 'success' && <div className="ds-settings-feedback success">Password updated successfully ✓</div>}

                  <div style={{ borderTop: `1px solid ${P.border}`, paddingTop: 10, marginTop: 2 }}>
                    {!signOutConfirm ? (
                      <button className="ds-settings-btn danger" onClick={() => setSignOutConfirm(true)}>Sign out of account</button>
                    ) : (
                      <div className="ds-settings-confirm-box">
                        <p className="ds-settings-confirm-text">Are you sure you want to sign out?</p>
                        <div className="ds-settings-confirm-row">
                          <button className="ds-settings-save-btn" style={{ flex: 1, background: P.error }} onClick={onSignOut}>Yes, sign out</button>
                          <button className="ds-settings-cancel-btn" onClick={() => setSignOutConfirm(false)}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div style={{ borderTop: `1px solid ${P.border}`, paddingTop: 10, marginTop: 6 }}>
                    <button onClick={() => { setShowDeleteModal(true); setDeleteStep('reason'); setDeleteReason(''); setDeleteTyped(''); setDeleteCountdown(8); setDeleteError(''); }} className="ds-settings-btn danger">Delete account</button>
                  </div>
                </div>

                {/* ── Card 4: Data & Storage ── */}
                <div className="ds-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <h3 className="ds-settings-title" style={{ marginBottom: 2 }}>Data & Storage</h3>
                  <p className="ds-settings-help">Your transcripts are stored locally on this device.</p>

                  <div className="ds-setting-row" style={{ border: 'none', padding: '4px 0' }}>
                    <span className="ds-setting-label">Saved transcripts</span>
                    <span className="ds-setting-value">{history.length} / 10</span>
                  </div>

                  <button
                    className="ds-settings-btn"
                    onClick={exportHistory}
                    disabled={history.length === 0}
                    style={{ opacity: history.length === 0 ? 0.45 : 1 }}
                  >Export history as JSON</button>

                  {!clearConfirm ? (
                    <button
                      className="ds-settings-btn danger"
                      onClick={() => setClearConfirm(true)}
                      disabled={history.length === 0}
                      style={{ opacity: history.length === 0 ? 0.45 : 1 }}
                    >Clear all history</button>
                  ) : (
                    <div className="ds-settings-confirm-box">
                      {clearDone ? (
                        <div className="ds-settings-feedback success" style={{ margin: 0 }}>History cleared ✓</div>
                      ) : (
                        <>
                          <p className="ds-settings-confirm-text">This will permanently delete all {history.length} saved transcript{history.length !== 1 ? 's' : ''}. This cannot be undone.</p>
                          <div className="ds-settings-confirm-row">
                            <button className="ds-settings-save-btn" style={{ flex: 1, background: P.error }} onClick={handleClearHistory}>Yes, clear all</button>
                            <button className="ds-settings-cancel-btn" onClick={() => setClearConfirm(false)}>Cancel</button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>

              </section>
            )}
          </main>

        </div>
      </div>
      )}

      {/* ── Delete Account Modal ── */}
      {showDeleteModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: P.paper, borderRadius: 20, padding: '28px 24px', maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', position: 'relative' }}>
            {deleteStep !== 'deleting' && (
              <button onClick={() => setShowDeleteModal(false)} style={{ position: 'absolute', top: 14, right: 14, background: 'none', border: 'none', cursor: 'pointer', color: P.muted, fontSize: 18, lineHeight: 1, padding: 4, borderRadius: 6 }} title="Close">✕</button>
            )}

            {/* Step 1: Why are you leaving? */}
            {deleteStep === 'reason' && (
              <>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <div style={{ fontSize: 30, marginBottom: 8 }}>👋</div>
                  <h2 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: P.ink }}>Before you go...</h2>
                  <p style={{ margin: 0, fontSize: 13, color: P.muted }}>Why are you leaving? It only takes a second.</p>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                  {["I don't use it anymore", "It's too expensive", "Missing features I need", "Privacy concerns", "Found something better", "Just testing it out"].map(reason => (
                    <button key={reason} onClick={() => setDeleteReason(reason)} style={{ textAlign: 'left', padding: '10px 14px', borderRadius: 10, cursor: 'pointer', border: `1.5px solid ${deleteReason === reason ? P.accent : P.border}`, background: deleteReason === reason ? P.accentLight : P.surface, color: deleteReason === reason ? P.accent : P.ink, fontSize: 13, fontWeight: deleteReason === reason ? 600 : 400, transition: 'all 0.15s' }}>{reason}</button>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setShowDeleteModal(false)} style={{ flex: 1, padding: '10px', border: `1px solid ${P.border}`, borderRadius: 10, background: 'transparent', color: P.muted, fontSize: 13, cursor: 'pointer' }}>Never mind</button>
                  <button disabled={!deleteReason} onClick={() => { setDeleteCountdown(8); setDeleteStep('countdown'); }} style={{ flex: 1, padding: '10px', borderRadius: 10, fontSize: 13, fontWeight: 600, border: 'none', background: deleteReason ? P.ink : P.border, color: deleteReason ? P.paper : P.muted, opacity: deleteReason ? 1 : 0.5, cursor: deleteReason ? 'pointer' : 'not-allowed', transition: 'all 0.15s' }}>Continue</button>
                </div>
              </>
            )}

            {/* Step 2: Countdown warning */}
            {deleteStep === 'countdown' && (
              <>
                <div style={{ textAlign: 'center', marginBottom: 18 }}>
                  <div style={{ fontSize: 30, marginBottom: 8 }}>⚠️</div>
                  <h2 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: P.error }}>This cannot be undone</h2>
                  <p style={{ margin: 0, fontSize: 13, color: P.muted }}>Deleting your account will permanently remove:</p>
                </div>
                <div style={{ background: 'rgba(180,35,24,0.06)', border: '1px solid rgba(180,35,24,0.15)', borderRadius: 12, padding: '12px 14px', marginBottom: 18 }}>
                  {['Your account and login', 'All saved preferences', 'Your referral credits', 'Access to all features'].map(item => (
                    <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 13, color: P.ink }}><span style={{ color: P.error, fontSize: 11 }}>✕</span>{item}</div>
                  ))}
                </div>
                <button disabled={deleteCountdown > 0} onClick={() => { setDeleteTyped(''); setDeleteStep('typing'); }} style={{ width: '100%', padding: '11px', borderRadius: 10, fontSize: 13, fontWeight: 600, border: 'none', background: deleteCountdown > 0 ? P.border : 'rgba(180,35,24,0.1)', color: deleteCountdown > 0 ? P.muted : P.error, marginBottom: 8, cursor: deleteCountdown > 0 ? 'not-allowed' : 'pointer', transition: 'all 0.3s' }}>{deleteCountdown > 0 ? `I understand, continue (${deleteCountdown}s)` : 'I understand, continue →'}</button>
                <button onClick={() => setDeleteStep('reason')} style={{ width: '100%', padding: '9px', border: `1px solid ${P.border}`, borderRadius: 10, background: 'transparent', color: P.muted, fontSize: 13, cursor: 'pointer' }}>Go back</button>
              </>
            )}

            {/* Step 3: Type to confirm */}
            {deleteStep === 'typing' && (
              <>
                <div style={{ textAlign: 'center', marginBottom: 18 }}>
                  <h2 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: P.ink }}>Final confirmation</h2>
                  <p style={{ margin: 0, fontSize: 13, color: P.muted }}>Type <strong style={{ color: P.ink }}>delete my account</strong> to confirm.</p>
                </div>
                <input value={deleteTyped} onChange={e => setDeleteTyped(e.target.value)} placeholder="delete my account" style={{ width: '100%', padding: '10px 12px', borderRadius: 10, fontSize: 13, border: `1.5px solid ${deleteTyped.toLowerCase() === 'delete my account' ? P.error : P.border}`, background: P.surface, color: P.ink, outline: 'none', boxSizing: 'border-box', marginBottom: 14 }} />
                <button disabled={deleteTyped.toLowerCase() !== 'delete my account'} onClick={handleDeleteAccount} style={{ width: '100%', padding: '11px', borderRadius: 10, fontSize: 13, fontWeight: 700, border: 'none', background: deleteTyped.toLowerCase() === 'delete my account' ? P.error : P.border, color: deleteTyped.toLowerCase() === 'delete my account' ? 'white' : P.muted, marginBottom: 8, cursor: deleteTyped.toLowerCase() === 'delete my account' ? 'pointer' : 'not-allowed', transition: 'all 0.15s' }}>Delete my account permanently</button>
                <button onClick={() => setDeleteStep('countdown')} style={{ width: '100%', padding: '9px', border: `1px solid ${P.border}`, borderRadius: 10, background: 'transparent', color: P.muted, fontSize: 13, cursor: 'pointer' }}>Go back</button>
              </>
            )}

            {/* Deleting… */}
            {deleteStep === 'deleting' && (
              <div style={{ textAlign: 'center', padding: '24px 0' }}>
                <p style={{ margin: 0, fontSize: 14, color: P.muted }}>Deleting your account…</p>
              </div>
            )}

            {/* Error */}
            {deleteStep === 'error' && (
              <>
                <div style={{ textAlign: 'center', marginBottom: 18 }}>
                  <div style={{ fontSize: 30, marginBottom: 8 }}>😕</div>
                  <h2 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: P.ink }}>Something went wrong</h2>
                  <p style={{ margin: 0, fontSize: 13, color: P.muted }}>{deleteError}</p>
                </div>
                <button onClick={() => setDeleteStep('typing')} style={{ width: '100%', padding: '10px', border: `1px solid ${P.border}`, borderRadius: 10, background: 'transparent', color: P.ink, fontSize: 13, cursor: 'pointer' }}>Try again</button>
              </>
            )}

          </div>
        </div>
      )}

    </div>
  );
};

// ── UserMenu ──────────────────────────────────────────────────────────────────
const UserMenu = ({ user, onSignOut, onDashboard }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const initial = (user.email || '?')[0].toUpperCase();

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const menuItem = (icon, label, onClick, danger = false) => (
    <button onClick={() => { setOpen(false); onClick(); }} style={{
      display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 16px',
      background: 'none', border: 'none', cursor: 'pointer',
      fontSize: 13, color: danger ? P.muted : P.muted, textAlign: 'left', transition: 'all 0.15s',
    }}
      onMouseEnter={e => { e.currentTarget.style.background = P.paper; e.currentTarget.style.color = danger ? P.error : P.ink; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = P.muted; }}
    >{icon}{label}</button>
  );

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(v => !v)} title={user.email} style={{
        width: 32, height: 32, borderRadius: '50%',
        background: P.accent, border: 'none', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 13, fontWeight: 700, color: 'white', transition: 'background 0.15s',
      }}
        onMouseEnter={e => { e.currentTarget.style.background = P.accentHover; }}
        onMouseLeave={e => { e.currentTarget.style.background = P.accent; }}
      >{initial}</button>

      {open && (
        <div className="fade-up" style={{
          position: 'absolute', right: 0, top: 'calc(100% + 8px)',
          width: 220, background: P.surface, border: `1px solid ${P.border}`,
          borderRadius: 14, boxShadow: '0 8px 32px rgba(29,29,31,0.12)',
          padding: '8px 0', zIndex: 200, overflow: 'hidden',
        }}>
          {/* User info */}
          <div style={{ padding: '8px 16px 10px', borderBottom: `1px solid ${P.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: P.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'white', flexShrink: 0 }}>{initial}</div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: P.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email?.split('@')[0]}</div>
                <div style={{ fontSize: 11, color: P.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
              </div>
            </div>
          </div>

          {menuItem(
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
            'Dashboard', onDashboard
          )}
          <div style={{ height: 1, background: P.border, margin: '4px 0' }} />
          {menuItem(
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
            'Sign out', onSignOut, true
          )}
        </div>
      )}
    </div>
  );
};

// ── Navbar ────────────────────────────────────────────────────────────────────
const Navbar = ({ onAskAI, hasTranscript, credits, user, onSignIn, onSignOut, onDashboard, onHome, onShowReferralPromo, isMobile, onReportBug }) => (
  <nav style={{
    position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
    height: 56, display: 'flex', alignItems: 'center',
    padding: isMobile ? '0 16px' : '0 28px',
    background: P.surface,
    borderBottom: `1px solid ${P.border}`,
  }}>
    <a
      href="/"
      onClick={(e) => { e.preventDefault(); onHome?.(); }}
      aria-label={`Go to ${BRAND_NAME} home`}
      style={{ display: 'flex', alignItems: 'center', gap: 9, textDecoration: 'none', minHeight: 44 }}
    >
      <img
        src={FOOTER_LOGO_SRC}
        alt={BRAND_NAME}
        style={{ height: 40, width: 'auto', display: 'block' }}
      />
      {hasTranscript && (
        <span style={{
          display: 'inline-flex', alignItems: 'center',
          padding: '2px 8px', borderRadius: 999,
          background: P.accentLight, color: P.accent,
          fontSize: 10, fontWeight: 700,
          letterSpacing: '0.06em', textTransform: 'uppercase',
        }}>Studio</span>
      )}
    </a>

    <div style={{ flex: 1 }} />

    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button onClick={onReportBug} title="Report a Bug"
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 8, border: 'none', background: 'transparent', color: P.muted, cursor: 'pointer', transition: 'all 0.15s' }}
        onMouseEnter={e => { e.currentTarget.style.background = P.paper; e.currentTarget.style.color = P.ink; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = P.muted; }}
      ><BugIcon size={16} /></button>
      {!isMobile && <CreditsWidget credits={credits} onUpgrade={() => onSignIn('signup')} user={user} onShowReferralPromo={onShowReferralPromo} />}
      {!isMobile && <div style={{ width: 1, height: 18, background: P.border }} />}
      {user ? (
        <UserMenu user={user} onSignOut={onSignOut} onDashboard={onDashboard} />
      ) : (
        <button
          onClick={onSignIn}
          style={{
            marginLeft: 2,
            padding: isMobile ? '0 16px' : '7px 16px',
            minHeight: isMobile ? 44 : undefined,
            lineHeight: 1,
            borderRadius: 8, border: 'none',
            background: P.accent, color: 'white',
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
            transition: 'background 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = P.accentHover; }}
          onMouseLeave={e => { e.currentTarget.style.background = P.accent; }}
        >Sign in</button>
      )}
    </div>
  </nav>
);

// ── Main App ──────────────────────────────────────────────────────────────────
const App = () => {
  const [videoUrl, setVideoUrl]           = useState('');
  const [inputMode, setInputMode]         = useState('url');   // 'url' | 'upload'
  const [uploadFile, setUploadFile]       = useState(null);
  const [uploadDragOver, setUploadDragOver] = useState(false);
  const [lang, setLang]                   = useState(() => localStorage.getItem('yte_lang') || 'en');
  const [transcript, setTranscript]       = useState('');
  const [segments, setSegments]           = useState([]);
  const [transcriptSource, setTranscriptSource] = useState('');
  const [currentVideoId, setCurrentVideoId] = useState(null);
  const [currentPlatform, setCurrentPlatform] = useState('youtube');
  const [currentThumbnail, setCurrentThumbnail] = useState(null);
  const [loading, setLoading]             = useState(false);
  const [loadingMsg, setLoadingMsg]       = useState('');
  const [loadingPercent, setLoadingPercent] = useState(0);
  const [loadingStage, setLoadingStage]   = useState('');
  const [langRefetching, setLangRefetching] = useState(false);
  const [langRefetchMsg, setLangRefetchMsg] = useState('');
  const [isTranslated, setIsTranslated]   = useState(false);
  const [error, setError]                 = useState('');
  const [copied, setCopied]               = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [search, setSearch]               = useState('');
  const [history, setHistory]             = useState(() => {
    try { return JSON.parse(localStorage.getItem('yte_history') || '[]'); } catch { return []; }
  });
  const [credits, setCredits] = useState(initCredits);
  const [showBookmarkBanner, setShowBookmarkBanner] = useState(false);
  const [showReferralBanner, setShowReferralBanner] = useState(false);
  const [showReferralPromo, setShowReferralPromo] = useState(false);
  const [user, setUser]                   = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authInitialTab, setAuthInitialTab] = useState('signin');
  const [view, setView]                   = useState('app');   // 'app' | 'dashboard'
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [summary, setSummary]             = useState('');
  const [summarizing, setSummarizing]     = useState(false);
  const [summaryCopied, setSummaryCopied] = useState(false);
  const [showTimestamps, setShowTimestamps] = useState(true);
  const [showTopics, setShowTopics]         = useState(false);
  const [showQA, setShowQA]               = useState(false);
  const [qaQuestion, setQaQuestion]       = useState('');
  const [qaMessages, setQaMessages]       = useState([]);
  const [qaLoading, setQaLoading]         = useState(false);
  const [timeline, setTimeline]           = useState(null);  // [{title, startSeconds, summary}]
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [flashcards, setFlashcards]               = useState([]);       // [{question, answer, topic}]
  const [flashcardsLoading, setFlashcardsLoading] = useState(false);
  const [showFlashcardModal, setShowFlashcardModal] = useState(false);
  const [flashcardIndex, setFlashcardIndex]       = useState(0);
  const [flashcardFlipped, setFlashcardFlipped]   = useState(false);
  const [flashcardKnown, setFlashcardKnown]       = useState(new Set());
  const [flashcardsMoreLoading, setFlashcardsMoreLoading] = useState(false);
  const [flashcardsExhausted, setFlashcardsExhausted]     = useState(false);
  const [flashcardsExhaustedReason, setFlashcardsExhaustedReason] = useState('');
  const [flashcardsTabPulse, setFlashcardsTabPulse]       = useState(false);
  const [expandedCards, setExpandedCards]         = useState(new Set());  // indices expanded in tab view
  const [studyGuide, setStudyGuide]               = useState(null);     // {overview, objectives, keyConcepts, sections, reviewQuestions}
  const [studyGuideLoading, setStudyGuideLoading] = useState(false);
  const [studyGuideFull, setStudyGuideFull]       = useState(false);
  const [academicInsights, setAcademicInsights]               = useState(null); // {references, claims, glossary, researchGaps}
  const [academicInsightsLoading, setAcademicInsightsLoading] = useState(false);
  const [academicInsightsFull, setAcademicInsightsFull]       = useState(false);
  const [discover, setDiscover]                               = useState(null); // {keywords, videos, papers}
  const [discoverLoading, setDiscoverLoading]                 = useState(false);
  const [activeLogo, setActiveLogo]               = useState('youtube');
  const [logoFlip, setLogoFlip]                   = useState('idle');    // 'idle' | 'out' | 'in'
  const [summaryDemoIdx, setSummaryDemoIdx]       = useState(0);
  const [flashcardDemoIdx, setFlashcardDemoIdx]   = useState(0);
  const [chatDemoIdx, setChatDemoIdx]             = useState(0);
  const [studyGuideDemoIdx, setStudyGuideDemoIdx] = useState(0);
  const [academicDemoIdx, setAcademicDemoIdx]     = useState(0);
  const [workflowHoverType, setWorkflowHoverType] = useState('');
  const [sgQuestion, setSgQuestion]               = useState('');
  const [sgMessages, setSgMessages]               = useState([]);       // [{role, text, isError?}]
  const [sgLoading, setSgLoading]                 = useState(false);
  const [activeTab, setActiveTab]         = useState('transcript'); // 'transcript' | 'timeline' | 'editor' | 'summary' | 'study-guide'
  const [pendingHistoryAction, setPendingHistoryAction] = useState(null); // 'transcript' | 'summary' | 'flashcards' | 'study-guide'
  const [currentTitle, setCurrentTitle]   = useState('');
  const [currentChannel, setCurrentChannel] = useState('');
  const [selectedSegment, setSelectedSegment] = useState(null);
  const [playingSegment, setPlayingSegment] = useState(null);
  const [exportToggle, setExportToggle]   = useState(false);
  const [showBugModal, setShowBugModal]           = useState(false);
  const [bugForm, setBugForm]                     = useState({ category: 'bug', description: '', steps: '', email: '' });
  const [bugSubmitting, setBugSubmitting]         = useState(false);
  const [bugStatus, setBugStatus]                 = useState(null); // null | 'success' | 'error'

  // ── Responsive layout state ──────────────────────────────────────────────
  const [windowWidth, setWindowWidth]         = useState(() => window.innerWidth);
  const [mobilePanel, setMobilePanel]         = useState('transcript'); // 'transcript'|'ai'|'insights'|'history'
  const [sidebarTab, setSidebarTab]           = useState('ai'); // 'ai'|'insights'|'summary'|'flashcards'|'study-guide'
  const [sidebarTabOrder, setSidebarTabOrder] = useState(['ai', 'insights', 'summary', 'flashcards', 'study-guide', 'academic', 'discover']);
  const [activeTabOrder, setActiveTabOrder]   = useState(['transcript', 'editor', 'flashcards', 'summary', 'study-guide', 'academic', 'discover']);
  const [dragTabKey, setDragTabKey]           = useState(null);
  const [dragOverKey, setDragOverKey]         = useState(null);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [recentOpen, setRecentOpen]               = useState(true);
  const [hideMobileNavForFooter, setHideMobileNavForFooter] = useState(false);
  const [isMobileKeyboardOpen, setIsMobileKeyboardOpen] = useState(false);
  const [mobileKeyboardInset, setMobileKeyboardInset] = useState(0);
  const isMobile  = windowWidth < MOBILE_BREAKPOINT;
  const isTablet  = windowWidth >= MOBILE_BREAKPOINT && windowWidth < TABLET_BREAKPOINT;
  const isDesktop = windowWidth >= TABLET_BREAKPOINT;

  const aiCacheRef         = useRef({});  // keyed by videoId → saved AI state
  const mobileNavRef       = useRef(null);
  const footerRef          = useRef(null);
  const downloadMenuRef    = useRef(null);
  const qaInputRef         = useRef(null);
  const playerRef          = useRef(null);
  const segmentRefs        = useRef({});
  const transcriptListRef  = useRef(null);
  const playingSegmentRef  = useRef(null);
  const ytPlayerRef        = useRef(null);
  const ytPlayerDivRef     = useRef(null);
  const timeIntervalRef    = useRef(null);
  const extractionRequestLockRef = useRef(false);
  const segmentsRef        = useRef([]);
  const urlInputRef     = useRef(null);
  const fileInputRef    = useRef(null);
  const qaRef           = useRef(null);
  const chatMessagesRef = useRef(null);
  const recoveryIntentRef = useRef(false);
  const progressTickerRef = useRef(null);
  const progressRunRef = useRef(null); // { mode, startedAt, estimateMs }
  const serverProgressRef = useRef(0);
  const progressProfileRef = useRef(null);
  if (!progressProfileRef.current) progressProfileRef.current = readProgressProfile();

  useEffect(() => {
    let raf;
    const onResize = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setWindowWidth(window.innerWidth));
    };
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); cancelAnimationFrame(raf); };
  }, []);

  useEffect(() => () => {
    if (progressTickerRef.current) clearInterval(progressTickerRef.current);
  }, []);

  useEffect(() => {
    if (!isMobile || !transcript) {
      setHideMobileNavForFooter(false);
      return;
    }
    const footerEl = footerRef.current;
    if (!footerEl || typeof IntersectionObserver === 'undefined') {
      setHideMobileNavForFooter(false);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      setHideMobileNavForFooter(entry.isIntersecting);
    }, { threshold: 0.08 });
    observer.observe(footerEl);
    return () => observer.disconnect();
  }, [isMobile, transcript]);

  useEffect(() => {
    if (!isMobile || typeof window === 'undefined') {
      setIsMobileKeyboardOpen(false);
      setMobileKeyboardInset(0);
      return;
    }

    const isTextEntryElement = (el) => {
      if (!el || !(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || el.isContentEditable;
    };

    const vv = window.visualViewport;
    let maxViewportHeight = vv?.height || window.innerHeight;
    let blurRaf = 0;

    const updateKeyboardState = () => {
      const activeEl = document.activeElement;
      const hasInputFocus = isTextEntryElement(activeEl);
      const viewportHeight = vv?.height || window.innerHeight;
      const viewportTop = vv?.offsetTop || 0;
      if (viewportHeight > maxViewportHeight) maxViewportHeight = viewportHeight;
      const shrinkAmount = Math.max(0, maxViewportHeight - viewportHeight);
      const occludedByKeyboard = Math.max(0, window.innerHeight - (viewportTop + viewportHeight));
      const focusedInComposer = activeEl === qaInputRef.current || activeEl?.closest?.('[data-composer="true"]');
      const keyboardLikelyOpen = hasInputFocus && (vv ? (shrinkAmount > 70 || occludedByKeyboard > 70 || focusedInComposer) : true);
      setIsMobileKeyboardOpen(keyboardLikelyOpen);
      setMobileKeyboardInset(keyboardLikelyOpen ? Math.round(Math.max(shrinkAmount, occludedByKeyboard)) : 0);
    };

    const handleFocusIn = () => updateKeyboardState();
    const handleFocusOut = () => {
      cancelAnimationFrame(blurRaf);
      blurRaf = requestAnimationFrame(updateKeyboardState);
    };
    const handleOrientationChange = () => {
      maxViewportHeight = vv?.height || window.innerHeight;
      updateKeyboardState();
    };

    vv?.addEventListener('resize', updateKeyboardState);
    vv?.addEventListener('scroll', updateKeyboardState);
    window.addEventListener('focusin', handleFocusIn);
    window.addEventListener('focusout', handleFocusOut);
    window.addEventListener('orientationchange', handleOrientationChange);
    updateKeyboardState();

    return () => {
      vv?.removeEventListener('resize', updateKeyboardState);
      vv?.removeEventListener('scroll', updateKeyboardState);
      window.removeEventListener('focusin', handleFocusIn);
      window.removeEventListener('focusout', handleFocusOut);
      window.removeEventListener('orientationchange', handleOrientationChange);
      cancelAnimationFrame(blurRaf);
    };
  }, [isMobile]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const host = window.location.hostname.toLowerCase();
    if (host !== `www.${CANONICAL_APP_HOST}`) return;
    const target = `${CANONICAL_APP_ORIGIN}${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.replace(target);
  }, []);

  useEffect(() => {
    document.title = currentTitle
      ? `ScribeSnap Studio — ${currentTitle}`
      : 'ScribeSnap | Free YouTube Transcript Extractor with AI Summaries';
  }, [currentTitle]);

  useEffect(() => {
    if (getAuthUrlState().hasBlockingAuthParams) return;
    if (transcript) {
      window.history.replaceState({}, '', '/studio');
      // Noindex the studio so Google ignores it (no content for crawlers)
      if (!document.querySelector('meta[data-studio-noindex]')) {
        const m = document.createElement('meta');
        m.name = 'robots'; m.content = 'noindex'; m.dataset.studioNoindex = '1';
        document.head.appendChild(m);
      }
    } else {
      window.history.replaceState({}, '', '/');
      document.querySelector('meta[data-studio-noindex]')?.remove();
    }
  }, [transcript, user]);

  // Remove the SEO loading overlay once React has mounted
  useEffect(() => { document.getElementById('app-loader')?.remove(); }, []);

  useEffect(() => {
    const handler = (e) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target))
        setShowDownloadMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Keep segmentsRef in sync so intervals can read latest value without stale closure
  useEffect(() => { segmentsRef.current = segments; }, [segments]);

  // Study guide fullscreen — Escape to close
  useEffect(() => {
    if (!studyGuideFull) return;
    const handler = (e) => { if (e.key === 'Escape') setStudyGuideFull(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [studyGuideFull]);

  // Academic insights fullscreen — Escape to close
  useEffect(() => {
    if (!academicInsightsFull) return;
    const handler = (e) => { if (e.key === 'Escape') setAcademicInsightsFull(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [academicInsightsFull]);

  // Bug report modal — Escape to close
  useEffect(() => {
    if (!showBugModal) return;
    const handler = (e) => { if (e.key === 'Escape') { setShowBugModal(false); setBugStatus(null); } };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showBugModal]);

  // Flashcard modal keyboard navigation
  useEffect(() => {
    if (!showFlashcardModal) return;
    const handler = (e) => {
      if (e.key === 'ArrowRight') fcNext();
      else if (e.key === 'ArrowLeft') fcPrev();
      else if (e.key === ' ') { e.preventDefault(); setFlashcardFlipped(f => !f); }
      else if (e.key === 'Escape') closeFlashcardModal();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showFlashcardModal, flashcardIndex, flashcards.length]);

  // Reset playing segment when transcript changes
  useEffect(() => {
    setPlayingSegment(null);
    playingSegmentRef.current = null;
  }, [segments]);

  // Flip-clock logo alternation — only when no URL is typed
  useEffect(() => {
    if (parseVideoUrl(videoUrl)?.platform) return;
    const FLIP_DURATION = 220;
    const id = setInterval(() => {
      setLogoFlip('out');
      const t1 = setTimeout(() => {
        const LOGO_CYCLE = ['youtube', 'tiktok', 'loom', 'vimeo', 'dailymotion', 'instagram'];
        setActiveLogo(p => { const i = LOGO_CYCLE.indexOf(p); return LOGO_CYCLE[(i + 1) % LOGO_CYCLE.length]; });
        setLogoFlip('in');
        const t2 = setTimeout(() => setLogoFlip('idle'), FLIP_DURATION);
        return () => clearTimeout(t2);
      }, FLIP_DURATION);
      return () => clearTimeout(t1);
    }, 2800);
    return () => clearInterval(id);
  }, [videoUrl]);

  // Landing card animation: cycle realistic summary snippets
  useEffect(() => {
    if (transcript || workflowHoverType !== 'summaries') return;
    const id = setInterval(() => {
      setSummaryDemoIdx((i) => (i + 1) % SUMMARY_DEMO_PREVIEWS.length);
    }, 3200);
    return () => clearInterval(id);
  }, [transcript, workflowHoverType]);

  // Landing card animation: cycle realistic flashcard Q/A snippets
  useEffect(() => {
    if (transcript || workflowHoverType !== 'flashcards') return;
    const id = setInterval(() => {
      setFlashcardDemoIdx((i) => (i + 1) % FLASHCARD_DEMO_PREVIEWS.length);
    }, 3800);
    return () => clearInterval(id);
  }, [transcript, workflowHoverType]);

  // Landing card animation: cycle realistic AI chat Q/A snippets
  useEffect(() => {
    if (transcript || workflowHoverType !== 'evidence') return;
    const id = setInterval(() => {
      setChatDemoIdx((i) => (i + 1) % CHAT_DEMO_PREVIEWS.length);
    }, 6800);
    return () => clearInterval(id);
  }, [transcript, workflowHoverType]);

  // Landing card animation: cycle study guide snippets
  useEffect(() => {
    if (transcript || workflowHoverType !== 'study-guide') return;
    const id = setInterval(() => {
      setStudyGuideDemoIdx((i) => (i + 1) % STUDY_GUIDE_DEMO_PREVIEWS.length);
    }, 5600);
    return () => clearInterval(id);
  }, [transcript, workflowHoverType]);

  // Landing card animation: cycle academic snippets
  useEffect(() => {
    if (transcript || workflowHoverType !== 'academic') return;
    const id = setInterval(() => {
      setAcademicDemoIdx((i) => (i + 1) % ACADEMIC_DEMO_PREVIEWS.length);
    }, 6000);
    return () => clearInterval(id);
  }, [transcript, workflowHoverType]);

  // Reset workflow demo previews to static first frame when no card is hovered
  useEffect(() => {
    if (transcript || workflowHoverType) return;
    setSummaryDemoIdx(0);
    setFlashcardDemoIdx(0);
    setChatDemoIdx(0);
    setStudyGuideDemoIdx(0);
    setAcademicDemoIdx(0);
  }, [transcript, workflowHoverType]);

  // YouTube IFrame API — proper SDK approach for reliable time tracking
  useEffect(() => {
    if (!currentVideoId || currentPlatform === 'vimeo') return;

    // Cleanup previous player + interval
    clearInterval(timeIntervalRef.current);
    if (ytPlayerRef.current) {
      try { ytPlayerRef.current.destroy(); } catch (e) {}
      ytPlayerRef.current = null;
    }

    const createPlayer = () => {
      const wrapper = ytPlayerDivRef.current;
      if (!wrapper || !window.YT?.Player) return;
      // YT.Player replaces its target element with an <iframe>.
      // We must give it an inner div (not the React-managed wrapper) so React
      // doesn't destroy the iframe on the next re-render (e.g. when playingSegment updates).
      wrapper.innerHTML = '';
      const innerDiv = document.createElement('div');
      wrapper.appendChild(innerDiv);
      ytPlayerRef.current = new window.YT.Player(innerDiv, {
        videoId: currentVideoId,
        width: '100%',
        height: '100%',
        playerVars: { rel: 0, modestbranding: 1 },
        events: {
          onStateChange: (e) => {
            clearInterval(timeIntervalRef.current);
            if (e.data === 1 /* PLAYING */) {
              timeIntervalRef.current = setInterval(() => {
                const t = ytPlayerRef.current?.getCurrentTime?.();
                if (typeof t !== 'number') return;
                const segs = segmentsRef.current;
                if (!segs.length) return;
                let idx = 0;
                for (let i = 0; i < segs.length; i++) {
                  if (segs[i].seconds <= t) idx = i;
                  else break;
                }
                if (idx !== playingSegmentRef.current) {
                  playingSegmentRef.current = idx;
                  setPlayingSegment(idx);
                  const el = segmentRefs.current[idx];
                  const listEl = transcriptListRef.current;
                  if (el && listEl) {
                    const elRect = el.getBoundingClientRect();
                    const listRect = listEl.getBoundingClientRect();
                    const relativeTop = elRect.top - listRect.top + listEl.scrollTop;
                    const scrollTarget = relativeTop - listEl.clientHeight / 3;
                    listEl.scrollTo({ top: Math.max(0, scrollTarget), behavior: 'smooth' });
                  }
                }
              }, 500);
            }
          },
        },
      });
    };

    if (window.YT?.Player) {
      createPlayer();
    } else {
      // Load the YouTube IFrame API script once
      if (!document.getElementById('yt-iframe-api-script')) {
        const tag = document.createElement('script');
        tag.id = 'yt-iframe-api-script';
        tag.src = 'https://www.youtube.com/iframe_api';
        document.head.appendChild(tag);
      }
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (prev) prev();
        createPlayer();
      };
    }

    return () => {
      clearInterval(timeIntervalRef.current);
      if (ytPlayerRef.current) {
        try { ytPlayerRef.current.destroy(); } catch (e) {}
        ytPlayerRef.current = null;
      }
      if (ytPlayerDivRef.current) ytPlayerDivRef.current.innerHTML = '';
    };
  }, [currentVideoId, currentPlatform]);

  useEffect(() => {
    if (localStorage.getItem('yte_bookmark_dismissed')) return;
    const t = setTimeout(() => setShowBookmarkBanner(true), 4000);
    return () => clearTimeout(t);
  }, []);

  // Show referral promo after 5 min for signed-in users who haven't referred anyone yet
  useEffect(() => {
    if (!user) return;
    if ((user.user_metadata?.referral_count || 0) > 0) return;
    const t = setTimeout(() => setShowReferralPromo(true), 5 * 60 * 1000);
    return () => clearTimeout(t);
  }, [user]);

  // ── Supabase auth session ──────────────────────────────────────────────────
  useEffect(() => {
    const authState = getAuthUrlState();
    recoveryIntentRef.current = authState.isRecovery;

    // Capture ?ref= referral param before cleanupAuthUrl removes it
    const pendingRefParam = new URLSearchParams(window.location.search).get('ref');
    if (pendingRefParam) {
      localStorage.setItem('yte_pending_ref', pendingRefParam);
    }

    let mounted = true;
    const bootstrapAuth = async () => {
      // Explicit PKCE code exchange fallback. detectSessionInUrl usually handles
      // this automatically, but some callback timing/path cases can miss.
      if (!authState.isRecovery && authState.authCode) {
        try {
          await supabase.auth.exchangeCodeForSession(authState.authCode);
        } catch (err) {
          // Non-fatal: getSession/onAuthStateChange can still recover.
          console.warn('[auth] code exchange fallback failed:', err?.message || err);
        }
      }

      // Handle recovery links that arrive as token_hash + type=recovery.
      if (authState.isRecovery && authState.tokenHash) {
        const { data, error } = await supabase.auth.verifyOtp({
          type: 'recovery',
          token_hash: authState.tokenHash,
        });
        if (!error && data?.session?.user) {
          if (!mounted) return;
          setUser(data.session.user);
          setShowPasswordReset(true);
          recoveryIntentRef.current = false;
          cleanupAuthUrl();
          return;
        }
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!mounted) return;
      setUser(session?.user ?? null);
      if (recoveryIntentRef.current && session?.user) {
        setShowPasswordReset(true);
        recoveryIntentRef.current = false;
      }
      // Avoid stripping auth callback params before session exchange finishes.
      if (session?.user || !authState.hasBlockingAuthParams) cleanupAuthUrl();
      // Show referral invite banner if visitor arrived via a ref link and isn't signed in
      if (!session?.user && localStorage.getItem('yte_pending_ref')) {
        setShowReferralBanner(true);
      }
    };
    bootstrapAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setShowPasswordReset(true);
        recoveryIntentRef.current = false;
        cleanupAuthUrl();
        return;
      }

      const shouldOpenRecoveryModal = (
        recoveryIntentRef.current &&
        !!session?.user &&
        (event === 'SIGNED_IN' || event === 'INITIAL_SESSION')
      );
      setUser(session?.user ?? null);
      if (shouldOpenRecoveryModal) {
        setShowPasswordReset(true);
        recoveryIntentRef.current = false;
      }
      if (session?.user || !getAuthUrlState().hasBlockingAuthParams) cleanupAuthUrl();

      // Hide referral banner once signed in
      if (event === 'SIGNED_IN' && session?.user) setShowReferralBanner(false);

      // Auto-claim referral bonus on first sign-in
      if (event === 'SIGNED_IN' && session?.user) {
        const claimRef = localStorage.getItem('yte_pending_ref');
        if (claimRef && claimRef !== session.user.id) {
          try {
            const resp = await fetch('/api/referral/claim', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
              body: JSON.stringify({ referrer_id: claimRef }),
            });
            // On success or known terminal error, clear the pending ref
            if (resp.ok || resp.status === 409 || resp.status === 400 || resp.status === 404) {
              localStorage.removeItem('yte_pending_ref');
            }
            if (resp.ok) {
              // Refresh user metadata so referral_bonus is reflected immediately
              const { data: { user: refreshed } } = await supabase.auth.getUser();
              if (refreshed) setUser(refreshed);
            }
          } catch (_) { /* network error — will retry next sign-in */ }
        }

        // Restore credits if this is a re-registration after account deletion
        try {
          const restoreResp = await fetch('/api/restore-account', {
            method: 'POST',
            headers: { Authorization: `Bearer ${session.access_token}` },
          });
          if (restoreResp.ok) {
            const d = await restoreResp.json();
            if (d.restored) {
              const { data: { user: refreshed } } = await supabase.auth.getUser();
              if (refreshed) setUser(refreshed);
            }
          }
        } catch (_) { /* best-effort */ }
      }
    });
    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Load user-specific lang preference on login/logout — cloud takes priority over localStorage
  useEffect(() => {
    const cloudLang = user?.user_metadata?.prefs?.lang;
    if (cloudLang) { setLang(cloudLang); return; }
    const key = user ? `yte_lang_${user.id}` : 'yte_lang';
    const saved = localStorage.getItem(key);
    if (saved) setLang(saved);
  }, [user]);

  // ON ICE — language translation disabled; restore when feature is ready
  // const saveLangPref = (newLang) => {
  //   const key = user ? `yte_lang_${user.id}` : 'yte_lang';
  //   localStorage.setItem(key, newLang);
  // };

  // Re-init credits keyed by user when auth changes
  useEffect(() => {
    const anonKey = 'yte_credits';
    const key = user ? `yte_credits_${user.id}` : anonKey;
    try {
      let stored = JSON.parse(localStorage.getItem(key) || 'null');

      // If signed in and no user-specific credits yet, migrate anon credits once
      if (user && !stored) {
        const anon = JSON.parse(localStorage.getItem(anonKey) || 'null');
        if (anon && typeof anon.resetAt === 'number' && Date.now() <= anon.resetAt) {
          stored = anon;
          localStorage.setItem(key, JSON.stringify(stored));
        }
      }

      const referralBonus = user?.user_metadata?.referral_bonus || 0;
      const tierMax = user ? CREDITS_MAX + referralBonus : CREDITS_FREE;

      if (!stored || typeof stored.resetAt !== 'number' || Date.now() > stored.resetAt) {
        stored = { used: 0, resetAt: Date.now() + CREDITS_PERIOD_MS };
      }

      if (stored.used > tierMax) stored = { ...stored, used: tierMax };

      stored = { ...stored, tierMax, userId: user ? user.id : null };
      localStorage.setItem(key, JSON.stringify(stored));
      setCredits(stored);
    } catch {
      const referralBonus = user?.user_metadata?.referral_bonus || 0;
      setCredits({ used: 0, resetAt: Date.now() + CREDITS_PERIOD_MS, tierMax: user ? CREDITS_MAX + referralBonus : CREDITS_FREE, userId: user ? user.id : null });
    }
  }, [user]);

  // Re-init history keyed by user when auth changes
  useEffect(() => {
    const key = user ? `yte_history_${user.id}` : 'yte_history';
    try { setHistory(JSON.parse(localStorage.getItem(key) || '[]')); } catch { setHistory([]); }
  }, [user]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUser(null); setView('app');
  };

  const dismissBookmarkBanner = () => {
    setShowBookmarkBanner(false);
    localStorage.setItem('yte_bookmark_dismissed', '1');
  };

  const incrementCredits = () => {
    setCredits(prev => {
      const bonus = user?.user_metadata?.referral_bonus || 0;
      const max = user ? CREDITS_MAX + bonus : CREDITS_FREE;
      const next = { ...prev, used: Math.min(max, prev.used + 1), tierMax: max, userId: user?.id ?? null };
      const key = user ? `yte_credits_${user.id}` : 'yte_credits';
      localStorage.setItem(key, JSON.stringify(next));
      return next;
    });
  };

  // Sync local credit state from the server's authoritative response.
  // Called when the server includes a `credits` object in the SSE done event.
  const syncCreditsFromServer = (serverCredits) => {
    if (!serverCredits || !user) return;
    setCredits(prev => {
      const next = {
        ...prev,
        used: serverCredits.used,
        tierMax: serverCredits.tier_max,
        resetAt: new Date(serverCredits.reset_at).getTime(),
        userId: user.id,
      };
      localStorage.setItem(`yte_credits_${user.id}`, JSON.stringify(next));
      return next;
    });
  };

  // Returns the current Supabase session access token, or null if not signed in.
  const getAuthToken = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      return session?.access_token ?? null;
    } catch { return null; }
  };

  const getEstimatedProgressMs = (mode) => {
    const fallback = mode === 'upload'
      ? DEFAULT_PROGRESS_PROFILE.upload.avgMs
      : DEFAULT_PROGRESS_PROFILE.url.avgMs;
    const min = mode === 'upload' ? 15000 : 6000;
    const max = mode === 'upload' ? 105000 : 60000;
    const avgMs = Number(progressProfileRef.current?.[mode]?.avgMs);
    const baseMs = Number.isFinite(avgMs) ? avgMs : fallback;
    return clampNumber(scaleProgressMs(baseMs), min, max);
  };

  const learnProgressDuration = (mode, durationMs) => {
    if (!Number.isFinite(durationMs) || durationMs <= 0) return;
    const boundedDuration = clampNumber(durationMs, 5000, 600000);
    const currentProfile = progressProfileRef.current || cloneDefaultProgressProfile();
    const prev = currentProfile[mode] || {
      avgMs: mode === 'upload' ? DEFAULT_PROGRESS_PROFILE.upload.avgMs : DEFAULT_PROGRESS_PROFILE.url.avgMs,
      samples: 0,
    };
    const alpha = prev.samples < 5 ? 0.35 : 0.22;
    const nextProfile = {
      ...currentProfile,
      [mode]: {
        avgMs: Math.round((prev.avgMs * (1 - alpha)) + (boundedDuration * alpha)),
        samples: clampNumber((prev.samples || 0) + 1, 1, 50),
      },
    };
    progressProfileRef.current = nextProfile;
    writeProgressProfile(nextProfile);
  };

  const stopProgressTicker = () => {
    if (progressTickerRef.current) {
      clearInterval(progressTickerRef.current);
      progressTickerRef.current = null;
    }
  };

  const beginSmoothLoading = (mode, message, stage) => {
    stopProgressTicker();
    const estimateMs = getEstimatedProgressMs(mode);
    progressRunRef.current = { mode, startedAt: Date.now(), estimateMs };
    serverProgressRef.current = 5;
    setLoading(true);
    setLoadingMsg(message);
    setLoadingPercent(5);
    setLoadingStage(stage);

    progressTickerRef.current = setInterval(() => {
      const run = progressRunRef.current;
      if (!run) return;
      const elapsed = Date.now() - run.startedAt;
      const ratio = clampNumber(elapsed / run.estimateMs, 0, 1);
      const eased = 1 - Math.pow(1 - ratio, 0.9);
      const baseProgress = 5 + (eased * 89); // 5% -> ~94% over estimated duration
      const overtimePct = Math.min(4, Math.max(0, elapsed - run.estimateMs) / 15000);
      const syntheticProgress = Math.min(98, baseProgress + overtimePct);
      const floor = serverProgressRef.current || 0;
      const next = Math.max(floor, syntheticProgress);
      setLoadingPercent(prev => Math.min(98, Math.max(prev, Math.round(next))));
    }, 250);
  };

  const applyServerProgress = (payload) => {
    if (!payload || typeof payload !== 'object') return;
    const { message, percent, stage } = payload;
    if (message) setLoadingMsg(message);
    if (stage) {
      setLoadingStage(stage);
      const run = progressRunRef.current;
      if (run && stage === 'whisper') {
        run.estimateMs = Math.max(run.estimateMs, scaleProgressMs(run.mode === 'upload' ? 160000 : 90000));
      } else if (run && stage === 'audio') {
        run.estimateMs = Math.max(run.estimateMs, scaleProgressMs(run.mode === 'upload' ? 140000 : 70000));
      }
    }
    if (Number.isFinite(percent)) {
      const bounded = clampNumber(percent, 0, 99);
      serverProgressRef.current = Math.max(serverProgressRef.current, bounded);
      setLoadingPercent(prev => Math.max(prev, Math.round(serverProgressRef.current)));
    }
  };

  const finishSmoothLoading = (wasSuccessful) => {
    const run = progressRunRef.current;
    stopProgressTicker();
    if (wasSuccessful && run) {
      learnProgressDuration(run.mode, Date.now() - run.startedAt);
    }
    progressRunRef.current = null;
    serverProgressRef.current = 0;
  };

  const handleInputFocus = async () => {
    if (videoUrl) return;
    try {
      const text = await navigator.clipboard.readText();
      if (text && (text.includes('youtube.com') || text.includes('youtu.be'))) {
        setVideoUrl(text.trim());
      }
    } catch {}
  };

  const saveToHistory = (entry) => {
    setHistory(prev => {
      const next = [entry, ...prev.filter(h => h.id !== entry.id)].slice(0, 10);
      const key = user ? `yte_history_${user.id}` : 'yte_history';
      localStorage.setItem(key, JSON.stringify(next));
      return next;
    });
  };

  const deleteFromHistory = (id, e) => {
    e.stopPropagation();
    setHistory(prev => {
      const next = prev.filter(h => h.id !== id);
      const key = user ? `yte_history_${user.id}` : 'yte_history';
      localStorage.setItem(key, JSON.stringify(next));
      return next;
    });
  };

  const loadFromHistory = (entry, target = 'transcript') => {
    // Save current video's AI state before switching
    if (currentVideoId) {
      aiCacheRef.current[currentVideoId] = {
        summary, timeline, showQA, qaMessages,
        flashcards, flashcardsExhausted, flashcardsExhaustedReason, expandedCards,
        studyGuide, sgMessages, activeTab,
        academicInsights, discover,
      };
    }

    const platform = entry.platform || 'youtube';
    setVideoUrl(entry.url || (platform === 'vimeo' ? `https://vimeo.com/${entry.id}` : `https://youtube.com/watch?v=${entry.id}`));
    setTranscript(entry.transcript);
    setSegments(entry.segments || []);
    setTranscriptSource(entry.source || '');
    setCurrentVideoId(entry.id);
    setCurrentPlatform(platform);
    setCurrentThumbnail(entry.thumbnail || null);
    setCurrentTitle(entry.title || '');
    setCurrentChannel(entry.channel || '');
    setError(''); setSearch('');

    // Reset all loading states
    setSummarizing(false);
    setFlashcardsLoading(false);
    setStudyGuideLoading(false);
    setAcademicInsightsLoading(false);
    setDiscoverLoading(false);

    // Restore cached AI state for this video, or clear if none
    const cached = aiCacheRef.current[entry.id];
    setSummary(cached?.summary ?? '');
    setTimeline(cached?.timeline ?? null);
    setShowQA(cached?.showQA ?? false);
    setQaMessages(cached?.qaMessages ?? []);
    setFlashcards(cached?.flashcards ?? []);
    setFlashcardsExhausted(cached?.flashcardsExhausted ?? false);
    setFlashcardsExhaustedReason(cached?.flashcardsExhaustedReason ?? '');
    setExpandedCards(cached?.expandedCards ?? new Set());
    setStudyGuide(cached?.studyGuide ?? null);
    setSgMessages(cached?.sgMessages ?? []);
    setAcademicInsights(cached?.academicInsights ?? null);
    setAcademicInsightsFull(false);
    setDiscover(cached?.discover ?? null);
    setStudyGuideFull(false);
    setShowFlashcardModal(false);
    setFlashcardIndex(0);
    setFlashcardFlipped(false);
    setFlashcardKnown(new Set());
    setActiveTab(target === 'transcript' ? (cached?.activeTab ?? 'transcript') : 'transcript');
    setSidebarTab('ai');
    setMobilePanel('transcript'); setHistoryDrawerOpen(false);
    setPendingHistoryAction(target);
  };

  const resetAll = () => {
    setVideoUrl(''); setTranscript(''); setSegments([]); setIsTranslated(false);
    setTranscriptSource(''); setCurrentVideoId(null); setCurrentPlatform('youtube'); setCurrentThumbnail(null); setError(''); setSearch('');
    setSummary(''); setShowTimestamps(true); setShowTopics(false); setShowQA(false);
    setQaQuestion(''); setQaMessages([]);
    setTimeline(null);
    setFlashcards([]); setFlashcardsExhausted(false); setFlashcardsExhaustedReason(''); setExpandedCards(new Set()); setStudyGuide(null); setSgMessages([]); setStudyGuideFull(false); setShowFlashcardModal(false);
    setAcademicInsights(null); setAcademicInsightsLoading(false); setAcademicInsightsFull(false);
    setDiscover(null); setDiscoverLoading(false);
    setActiveTab('transcript');
    setPendingHistoryAction(null);
    setCurrentTitle(''); setCurrentChannel('');
    setSelectedSegment(null); setExportToggle(false);
    setMobilePanel('transcript'); setHistoryDrawerOpen(false);
  };

  const goHome = () => {
    setView('app');
    setShowAuthModal(false);
    setShowPasswordReset(false);
    resetAll();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const startNewSearch = () => {
    goHome();
    setTimeout(() => {
      urlInputRef.current?.focus();
    }, 260);
  };

  const askQuestion = async (overrideQ) => {
    const q = (overrideQ || qaQuestion).trim();
    if (!q || qaLoading) return;
    const history = qaMessages; // snapshot before state update
    setShowQA(true);
    setQaMessages(prev => [...prev, { role: 'user', text: q }]);
    setQaQuestion('');
    setQaLoading(true);
    try {
      const token = await getAuthToken();
      const res = await fetch('/api/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ transcript: segments.some(s => s.seconds > 0) ? '' : transcript, segments: segments.some(s => s.seconds > 0) ? segments.slice(0, 800) : [], question: q, history }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (res.status === 401) { setQaMessages(prev => prev.slice(0, -1)); setQaQuestion(q); setShowAuthModal(true); setAuthInitialTab('signup'); return; }
        throw new Error(data.details || data.error || `Server error ${res.status}`);
      }
      // Stream tokens — accumulate locally and update last AI message each time
      let accumulated = '';
      let placeholderAdded = false;
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop();
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;
          const payload = trimmed.slice(5).trim();
          if (payload === '[DONE]') break;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.token) {
              accumulated += parsed.token;
              if (!placeholderAdded) {
                placeholderAdded = true;
                setQaLoading(false);
                setQaMessages(prev => [...prev, { role: 'ai', text: accumulated }]);
              } else {
                const snap = accumulated;
                setQaMessages(prev => {
                  const msgs = [...prev];
                  if (msgs.length > 0 && msgs[msgs.length - 1].role === 'ai') {
                    msgs[msgs.length - 1] = { ...msgs[msgs.length - 1], text: snap };
                  }
                  return msgs;
                });
              }
            }
          } catch (e) {
            if (e.message !== 'Unexpected end of JSON input') throw e;
          }
        }
      }
    } catch (err) {
      setQaMessages(prev => {
        const msgs = [...prev];
        if (msgs.length > 0 && msgs[msgs.length - 1].role === 'ai' && msgs[msgs.length - 1].text === '') {
          msgs[msgs.length - 1] = { role: 'ai', text: `Error: ${err.message}`, isError: true };
          return msgs;
        }
        return [...msgs, { role: 'ai', text: `Error: ${err.message}`, isError: true }];
      });
    } finally {
      setQaLoading(false);
      setTimeout(() => qaInputRef.current?.focus(), 50);
    }
  };

  // Auto-scroll chat messages container to bottom (direct scrollTop, no page scroll)
  useEffect(() => {
    const el = chatMessagesRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [qaMessages, qaLoading]);

  const getTranscript = async (langOverride) => {
    if (extractionRequestLockRef.current) return;
    const _used = credits?.used ?? 0;
    const _max = credits?.tierMax ?? (user ? CREDITS_MAX : CREDITS_FREE);
    if (_used >= _max) return;
    const parsed = parseVideoUrl(videoUrl);
    if (!parsed) { setError(funnyTranscriptError('invalid url')); return; }
    const { platform, id: videoId, url: videoCanonical } = parsed;
    const langToUse = langOverride || lang;
    extractionRequestLockRef.current = true;

    setError(''); setTranscript(''); setTranscriptSource('');
    setSegments([]); setCurrentVideoId(null); setCurrentPlatform(platform); setCurrentThumbnail(null); setSearch('');
    setSummary(''); setTimeline(null); setShowTopics(false);
    setFlashcards([]); setFlashcardsExhausted(false); setFlashcardsExhaustedReason(''); setExpandedCards(new Set()); setStudyGuide(null); setSgMessages([]); setStudyGuideFull(false); setQaMessages([]); setShowQA(false);
    setAcademicInsights(null); setAcademicInsightsLoading(false); setAcademicInsightsFull(false);
    setMobilePanel('transcript'); setHistoryDrawerOpen(false);
    setPendingHistoryAction(null);
    beginSmoothLoading('url', 'Looking for subtitles…', 'subtitles');

    // Use fetch-streaming so we can send auth headers and inspect non-2xx statuses.
    const apiUrl = platform === 'youtube'
      ? `/api/transcript?videoId=${videoId}&lang=${langToUse}`
      : `/api/transcript?platform=${encodeURIComponent(platform)}&url=${encodeURIComponent(videoCanonical)}&lang=${langToUse}`;

    const clearLoading = () => {
      finishSmoothLoading(false);
      setLoading(false); setLoadingMsg(''); setLoadingPercent(0); setLoadingStage('');
    };

    const controller = new AbortController();
    let streamSettled = false; // true once any terminal event (done/error/timeout) has been handled
    const killTimer = setTimeout(() => {
      if (streamSettled) return; streamSettled = true;
      controller.abort();
      setError(funnyTranscriptError('timed out'));
      clearLoading();
    }, 180000);

    try {
      const token = await getAuthToken();
      const headers = token
        ? { 'Authorization': `Bearer ${token}`, 'Accept': 'text/event-stream' }
        : { 'Accept': 'text/event-stream' };

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
        signal: controller.signal,
      });

      if (response.status === 402) {
        const body = await response.json().catch(() => ({}));
        if (body.used !== undefined) syncCreditsFromServer(body);
        streamSettled = true;
        setError('You\'ve used all your credits for this period. Sign in or wait for your credits to reset.');
        clearLoading();
        return;
      }

      if (!response.ok || !response.body) {
        let msg = `Server error ${response.status}`;
        const body = await response.json().catch(() => null);
        if (body?.error) msg = body.details ? `${body.error}: ${body.details}` : body.error;
        streamSettled = true;
        setError(funnyTranscriptError(msg));
        clearLoading();
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      streamLoop:
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const messages = buffer.split(/\r?\n\r?\n/);
        buffer = messages.pop() || '';

        for (const raw of messages) {
          if (!raw.trim()) continue;
          const lines = raw.split(/\r?\n/);
          let event = 'message';
          const dataLines = [];
          for (const line of lines) {
            if (line.startsWith('event:')) event = line.slice(6).trim();
            else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim());
          }
          if (!dataLines.length) continue;

          let data;
          try { data = JSON.parse(dataLines.join('\n')); } catch { continue; }

          if (event === 'progress') {
            applyServerProgress(data);
            continue;
          }

          if (event === 'done') {
            streamSettled = true;
            try {
              const platformLabel = platform.charAt(0).toUpperCase() + platform.slice(1);
              const fallbackTitle = data.title || videoId;
              const fallbackChannel = data.channel || platformLabel;
              const fallbackThumb = data.thumbnail || (platform === 'youtube' ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` : null);

              setTranscript(data.transcript);
              setSegments(data.segments || []);
              setIsTranslated(false); // never show banner on initial load
              setTranscriptSource(data.source || '');
              setCurrentVideoId(videoId);
              setCurrentThumbnail(fallbackThumb);
              setCurrentTitle(fallbackTitle);
              setCurrentChannel(fallbackChannel);
              finishSmoothLoading(true);
              setLoadingPercent(100);
              // Prefer server-authoritative credit count; fall back to local increment.
              if (data.credits) syncCreditsFromServer(data.credits);
              else incrementCredits();
              // After 3rd extraction, nudge signed-in users without referrals to share
              if (user && (credits.used + 1) === 3 && !(user.user_metadata?.referral_count)) {
                setTimeout(() => setShowReferralPromo(true), 1500);
              }
              saveToHistory({
                id: videoId, platform, transcript: data.transcript, segments: data.segments || [],
                source: data.source || '', date: new Date().toISOString(),
                thumbnail: fallbackThumb, title: fallbackTitle, channel: fallbackChannel, url: videoCanonical,
              });

              // Metadata can be slow; load it in the background so transcript renders immediately.
              fetchVideoMeta(platform, videoCanonical).then((meta) => {
                if (!meta) return;
                const nextTitle = meta.title || fallbackTitle;
                const nextChannel = meta.channel || fallbackChannel;
                const nextThumb = meta.thumbnail || fallbackThumb;
                setCurrentTitle(nextTitle);
                setCurrentChannel(nextChannel);
                setCurrentThumbnail(nextThumb);
                // Update the history entry with the real title/channel/thumbnail
                saveToHistory({
                  id: videoId, platform, transcript: data.transcript, segments: data.segments || [],
                  source: data.source || '', date: new Date().toISOString(),
                  thumbnail: nextThumb, title: nextTitle, channel: nextChannel, url: videoCanonical,
                });
              }).catch(() => {});
            } catch {
              setError(funnyTranscriptError('failed to process'));
            } finally {
              setTimeout(clearLoading, 600);
            }
            break streamLoop;
          }

          if (event === 'out_of_credits') {
            streamSettled = true;
            if (data.used !== undefined) syncCreditsFromServer(data);
            setError('You\'ve used all your credits for this period. Sign in or wait for your credits to reset.');
            clearLoading();
            break streamLoop;
          }

          if (event === 'transcript_error') {
            streamSettled = true;
            setError(funnyTranscriptError(data.details ? `${data.error}: ${data.details}` : (data.error || 'failed to fetch')));
            clearLoading();
            break streamLoop;
          }
        }
      }

      if (!streamSettled) {
        streamSettled = true;
        setError(funnyTranscriptError('connection'));
        clearLoading();
      }
    } catch (err) {
      if (streamSettled || err?.name === 'AbortError') return;
      streamSettled = true;
      setError(funnyTranscriptError('connection'));
      clearLoading();
    } finally {
      clearTimeout(killTimer);
      extractionRequestLockRef.current = false;
    }
  };

  const getTranscriptFromUpload = async (file) => {
    if (extractionRequestLockRef.current) return;
    const _used = credits?.used ?? 0;
    const _max  = credits?.tierMax ?? (user ? CREDITS_MAX : CREDITS_FREE);
    if (_used >= _max) return;
    if (!file) { setError('Please select a file first.'); return; }

    const MAX_BYTES = 500 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      setError('That file is too big (max 500 MB). Please trim or compress the video first.');
      return;
    }
    extractionRequestLockRef.current = true;

    setError(''); setTranscript(''); setTranscriptSource('');
    setSegments([]); setCurrentVideoId(null); setCurrentPlatform('upload'); setCurrentThumbnail(null); setSearch('');
    setSummary(''); setTimeline(null); setShowTopics(false);
    setFlashcards([]); setFlashcardsExhausted(false); setFlashcardsExhaustedReason(''); setExpandedCards(new Set()); setStudyGuide(null); setSgMessages([]); setStudyGuideFull(false); setQaMessages([]); setShowQA(false);
    setAcademicInsights(null); setAcademicInsightsLoading(false); setAcademicInsightsFull(false);
    setMobilePanel('transcript'); setHistoryDrawerOpen(false);
    setPendingHistoryAction(null);
    beginSmoothLoading('upload', 'Uploading file…', 'upload');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = await getAuthToken();
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const response = await fetch('/api/transcript/upload', { method: 'POST', headers, body: formData });
      if (response.status === 402) {
        // Out of credits — server returned JSON 402 before SSE started
        const body = await response.json().catch(() => ({}));
        if (body.used !== undefined) syncCreditsFromServer(body);
        setError('You\'ve used all your credits for this period. Sign in or wait for your credits to reset.');
        finishSmoothLoading(false);
        setLoading(false); setLoadingMsg(''); setLoadingPercent(0); setLoadingStage('');
        return;
      }
      if (!response.ok || !response.body) throw new Error(`Server error ${response.status}`);

      const reader  = response.body.getReader();
      const decoder = new TextDecoder();
      let   buffer  = '';

      const killTimer = setTimeout(() => {
        reader.cancel();
        setError('Transcription timed out. Try a shorter file.');
        finishSmoothLoading(false);
        setLoading(false); setLoadingMsg(''); setLoadingPercent(0); setLoadingStage('');
      }, 180000);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const messages = buffer.split(/\r?\n\r?\n/);
        buffer = messages.pop();
        for (const raw of messages) {
          if (!raw.trim()) continue;
          const lines     = raw.split(/\r?\n/);
          const eventLine = lines.find(l => l.startsWith('event:'));
          const dataLine  = lines.find(l => l.startsWith('data:'));
          if (!dataLine) continue;
          const event = eventLine ? eventLine.slice(6).trim() : 'message';
          let data;
          try { data = JSON.parse(dataLine.slice(5).trim()); } catch { continue; }

          if (event === 'progress') {
            applyServerProgress(data);
          } else if (event === 'done') {
            clearTimeout(killTimer);
            const fileName = file.name.replace(/\.[^/.]+$/, '');
            const syntheticId = `upload_${Date.now()}`;
            setTranscript(data.transcript);
            setSegments(data.segments || []);
            setIsTranslated(false);
            setTranscriptSource(data.source || 'whisper');
            setCurrentVideoId(syntheticId);
            setCurrentThumbnail(null);
            setCurrentTitle(fileName);
            setCurrentChannel('Local file');
            finishSmoothLoading(true);
            setLoadingPercent(100);
            if (data.credits) syncCreditsFromServer(data.credits);
            else incrementCredits();
            saveToHistory({
              id: syntheticId, platform: 'upload',
              transcript: data.transcript, segments: data.segments || [],
              source: 'whisper', date: new Date().toISOString(),
              thumbnail: null, title: fileName, channel: 'Local file', url: null,
            });
            setTimeout(() => {
              finishSmoothLoading(false);
              setLoading(false); setLoadingMsg(''); setLoadingPercent(0); setLoadingStage('');
            }, 600);
          } else if (event === 'transcript_error') {
            clearTimeout(killTimer);
            setError(data.error || 'Transcription failed.');
            finishSmoothLoading(false);
            setLoading(false); setLoadingMsg(''); setLoadingPercent(0); setLoadingStage('');
          }
        }
      }
    } catch (err) {
      setError('Upload failed — ' + (err.message || 'please try again.'));
      finishSmoothLoading(false);
      setLoading(false); setLoadingMsg(''); setLoadingPercent(0); setLoadingStage('');
    } finally {
      extractionRequestLockRef.current = false;
    }
  };

  // ON ICE — language translation disabled; restore refetchWithLang when feature is ready
  // const refetchWithLang = (newLang) => {
  //   const parsed = parseVideoUrl(videoUrl);
  //   if (!parsed) return;
  //   const { platform, id: videoId, url: videoCanonical } = parsed;
  //   setLangRefetching(true);
  //   setLangRefetchMsg('');
  //   setSearch(''); setSummary(''); setTimeline(null); setShowTopics(false); setFlashcards([]); setFlashcardsExhausted(false); setFlashcardsExhaustedReason(''); setExpandedCards(new Set()); setStudyGuide(null); setSgMessages([]); setStudyGuideFull(false); setQaMessages([]);
  //   const apiUrl = platform === 'vimeo'
  //     ? `/api/transcript?platform=vimeo&url=${encodeURIComponent(videoCanonical)}&lang=${newLang}`
  //     : `/api/transcript?videoId=${videoId}&lang=${newLang}`;
  //   const es = new EventSource(apiUrl);
  //   const killTimer = setTimeout(() => { es.close(); setLangRefetching(false); setLangRefetchMsg(''); }, 120000);
  //   es.addEventListener('progress', (e) => {
  //     try { const { message } = JSON.parse(e.data); if (message) setLangRefetchMsg(message); } catch {}
  //   });
  //   es.addEventListener('done', async (e) => {
  //     clearTimeout(killTimer); es.close();
  //     try {
  //       const data = JSON.parse(e.data);
  //       const seen = new Set();
  //       const segs = (data.segments || []).filter(s => s.text && !seen.has(s.text) && seen.add(s.text));
  //       if (segs.length > 0) {
  //         setSegments(segs);
  //         setTranscript(data.transcript || segs.map(s => s.text).join(' '));
  //         setIsTranslated(data.translated || false);
  //         setSelectedSegment(null); setPlayingSegment(null); playingSegmentRef.current = null;
  //       }
  //     } catch {}
  //     setLangRefetching(false); setLangRefetchMsg('');
  //   });
  //   es.addEventListener('error', () => { clearTimeout(killTimer); es.close(); setLangRefetching(false); setLangRefetchMsg(''); });
  //   es.onerror = () => { if (es.readyState === EventSource.CLOSED) return; clearTimeout(killTimer); es.close(); setLangRefetching(false); setLangRefetchMsg(''); };
  // };

  const dlName = (ext) => {
    const safe = (currentTitle || 'transcript').replace(/[/\\?%*:|"<>]/g, '-').trim().slice(0, 80);
    return `ScribeSnap.ai - ${safe}.${ext}`;
  };

  const downloadTxt = () => {
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([transcript], { type: 'text/plain' })),
      download: dlName('txt'),
    });
    a.click(); URL.revokeObjectURL(a.href); setShowDownloadMenu(false);
  };

  const downloadPdf = () => {
    const doc = new jsPDF(); const m = 15; doc.setFontSize(12);
    doc.text(doc.splitTextToSize(transcript, doc.internal.pageSize.getWidth() - m * 2), m, m);
    doc.save(dlName('pdf')); setShowDownloadMenu(false);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(transcript).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  const copyAsMarkdown = () => {
    const tsLink = (s) => currentPlatform === 'vimeo'
      ? `https://vimeo.com/${currentVideoId}#t=${s.seconds}s`
      : `https://youtube.com/watch?v=${currentVideoId}&t=${s.seconds}s`;
    const md = segments.length > 0
      ? segments.map(s => `**[${formatTime(s.seconds)}](${tsLink(s)})** ${s.text}`).join('\n\n')
      : transcript;
    navigator.clipboard.writeText(md).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
    setShowDownloadMenu(false);
  };

  const formatSrtTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const ms = Math.round((seconds % 1) * 1000);
    return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')},${String(ms).padStart(3,'0')}`;
  };

  const downloadSrt = () => {
    if (!segments.length) return;
    const srt = segments.map((seg, i) => {
      const start = formatSrtTime(seg.seconds);
      const end = formatSrtTime(segments[i + 1] ? segments[i + 1].seconds - 0.05 : seg.seconds + 5);
      return `${i + 1}\n${start} --> ${end}\n${seg.text.trim()}\n`;
    }).join('\n');
    const a = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(new Blob([srt], { type: 'text/srt' })),
      download: dlName('srt'),
    });
    a.click(); URL.revokeObjectURL(a.href);
  };

  const seekToTime = (seconds) => {
    if (currentPlatform !== 'vimeo' && ytPlayerRef.current?.seekTo) {
      ytPlayerRef.current.seekTo(seconds, true);
    } else {
      playerRef.current?.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'seekTo', args: [seconds, true] }),
        '*'
      );
    }
  };

  // Renders a single line of AI chat text, turning [M:SS] timestamps into clickable links
  // that seek the video AND scroll the transcript panel to that segment
  const renderChatLine = (line) => {
    const parts = line.split(/(\[\d+:\d{2}(?::\d{2})?\])/g);
    if (parts.length === 1) return line;
    return parts.map((part, i) => {
      const m = part.match(/^\[(\d+):(\d{2})(?::(\d{2}))?\]$/);
      if (m) {
        const secs = m[3]
          ? parseInt(m[1]) * 3600 + parseInt(m[2]) * 60 + parseInt(m[3])
          : parseInt(m[1]) * 60 + parseInt(m[2]);
        const handleTimestampClick = () => {
          // Seek the video
          seekToTime(secs);
          // Find the nearest segment index
          if (segments.length > 0) {
            let idx = 0;
            for (let j = 0; j < segments.length; j++) {
              if (segments[j].seconds <= secs) idx = j;
              else break;
            }
            // Highlight the segment
            setSelectedSegment(idx);
            // Scroll the transcript panel to that segment
            const el = segmentRefs.current[idx];
            const listEl = transcriptListRef.current;
            if (el && listEl) {
              const elRect = el.getBoundingClientRect();
              const listRect = listEl.getBoundingClientRect();
              const relativeTop = elRect.top - listRect.top + listEl.scrollTop;
              listEl.scrollTo({ top: Math.max(0, relativeTop - listEl.clientHeight / 3), behavior: 'smooth' });
            }
          }
        };
        // Format as MM:SS for display (strip the brackets)
        const label = part.slice(1, -1);
        return (
          <span key={i} onClick={handleTimestampClick} title="Jump to this point in the video and transcript"
            style={{ color: P.accent, fontFamily: 'monospace', fontWeight: 700, fontSize: '0.88em', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 3 }}>
            {label}
          </span>
        );
      }
      return <React.Fragment key={i}>{part}</React.Fragment>;
    });
  };

  const AiBubbleAvatar = ({ size = 26, iconSize = 15, radius = 7 }) => (
    <div style={{
      width: size, height: size, borderRadius: radius, flexShrink: 0, marginTop: 1,
      background: 'linear-gradient(135deg, rgba(60,140,255,0.13) 0%, rgba(60,140,255,0.05) 100%)',
      border: '1.5px solid rgba(60,140,255,0.2)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <img src="/scribesnap_icon_wave.svg" alt="AI" style={{ width: iconSize, height: iconSize }} />
    </div>
  );

  const summarize = async () => {
    setSummarizing(true); setSummary('');
    if (isDesktop) setActiveTab('summary');
    else if (isMobile) setMobilePanel('summary');
    else setSidebarTab('summary');
    try {
      const token = await getAuthToken();
      const res = await fetch('/api/summarize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ transcript, platform: currentPlatform }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(`Server error ${res.status}`); }
      if (!res.ok) { if (res.status === 401) { setShowAuthModal(true); setAuthInitialTab('signup'); return; } throw new Error(data.details || data.error || 'Failed to summarize'); }
      setSummary(data.summary);
      setActiveTab('summary');
      setSidebarTab('summary');
      setMobilePanel('summary');
    } catch (err) { setSummary(`Error: ${err.message}`); }
    finally { setSummarizing(false); }
  };

  const generateTimeline = async () => {
    if (timelineLoading) return;
    setTimelineLoading(true); setTimeline(null);
    try {
      const token = await getAuthToken();
      const res = await fetch('/api/timeline', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ transcript, segments }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(`Server error ${res.status}`); }
      if (!res.ok) { if (res.status === 401) { setShowAuthModal(true); setAuthInitialTab('signup'); return; } throw new Error(data.error || 'Failed to generate timeline'); }
      setTimeline(data.sections || []);
    } catch (err) {
      setTimeline([{ title: 'Error', startSeconds: 0, summary: err.message, _error: true }]);
    } finally { setTimelineLoading(false); }
  };


  const submitBugReport = async () => {
    if (!bugForm.description.trim() || bugSubmitting) return;
    setBugSubmitting(true); setBugStatus(null);
    try {
      const res = await fetch('/api/report-bug', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: bugForm.category,
          description: bugForm.description,
          steps: bugForm.steps,
          userEmail: bugForm.email,
          url: window.location.href,
          userAgent: navigator.userAgent,
        }),
      });
      if (res.ok) {
        setBugStatus('success');
        setTimeout(() => { setShowBugModal(false); setBugForm({ category: 'bug', description: '', steps: '', email: '' }); setBugStatus(null); }, 2200);
      } else {
        console.error('[bug-report] server returned', res.status, res.statusText);
        setBugStatus('error');
      }
    } catch (err) {
      console.error('[bug-report] fetch threw:', err);
      setBugStatus('error');
    }
    setBugSubmitting(false);
  };

  const generateFlashcards = async () => {
    if (flashcardsLoading) return;
    setFlashcardsLoading(true); setFlashcards([]); setFlashcardsExhausted(false); setFlashcardsExhaustedReason('');
    if (isDesktop) setActiveTab('flashcards');
    else if (isMobile) setMobilePanel('flashcards');
    else setSidebarTab('flashcards');
    try {
      const token = await getAuthToken();
      const res = await fetch('/api/flashcards', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ transcript }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(`Server error ${res.status}`); }
      if (!res.ok) { if (res.status === 401) { setShowAuthModal(true); setAuthInitialTab('signup'); return; } throw new Error(data.error || 'Failed to generate flashcards'); }
      if (data.noMore) {
        setFlashcardsExhausted(true);
        setFlashcardsExhaustedReason(data.reason || 'This content doesn\'t have educational concepts suitable for flashcards.');
        setActiveTab('flashcards');
        setSidebarTab('flashcards');
        setMobilePanel('flashcards');
        return;
      }
      const cards = (data.flashcards || []).filter(c => c && c.question && c.answer);
      setFlashcards(cards);
      setFlashcardIndex(0); setFlashcardFlipped(false); setFlashcardKnown(new Set()); setExpandedCards(new Set());
      if (cards.length > 0) {
        setActiveTab('flashcards');
        setSidebarTab('flashcards');
        setMobilePanel('flashcards');
        setFlashcardsTabPulse(true);
        setTimeout(() => setFlashcardsTabPulse(false), 1400);
      }
    } catch (err) { setFlashcards([]); }
    finally { setFlashcardsLoading(false); }
  };

  const generateMoreFlashcards = async () => {
    if (flashcardsMoreLoading || flashcardsExhausted) return;
    setFlashcardsMoreLoading(true);
    try {
      const existingQuestions = flashcards.map(c => c.question);
      const token = await getAuthToken();
      const res = await fetch('/api/flashcards', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ transcript, existingQuestions }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(`Server error ${res.status}`); }
      if (!res.ok) throw new Error(data.error || 'Failed to generate more flashcards');
      if (data.noMore) {
        setFlashcardsExhausted(true);
        setFlashcardsExhaustedReason(data.reason || 'All key concepts from this transcript are already covered.');
        return;
      }
      const existingQNorm = new Set(flashcards.map(c => c.question.trim().toLowerCase()));
      const newCards = (data.flashcards || [])
        .filter(c => c && c.question && c.answer)
        .filter(c => !existingQNorm.has(c.question.trim().toLowerCase()));
      if (newCards.length === 0) {
        setFlashcardsExhausted(true);
        setFlashcardsExhaustedReason('All key concepts from this transcript are already covered.');
        return;
      }
      setFlashcards(prev => [...prev, ...newCards]);
    } catch (err) { /* silent */ }
    finally { setFlashcardsMoreLoading(false); }
  };

  const generateStudyGuide = async () => {
    if (studyGuideLoading) return;
    setStudyGuideLoading(true); setStudyGuide(null);
    if (isDesktop) setActiveTab('study-guide');
    else if (isMobile) setMobilePanel('study-guide');
    else setSidebarTab('study-guide');
    try {
      const token = await getAuthToken();
      const res = await fetch('/api/study-guide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ transcript }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(`Server error ${res.status}`); }
      if (!res.ok) { if (res.status === 401) { setShowAuthModal(true); setAuthInitialTab('signup'); return; } throw new Error(data.error || 'Failed to generate study guide'); }
      setStudyGuide(data);
      setActiveTab('study-guide');
      setSidebarTab('study-guide');
      setMobilePanel('study-guide');
    } catch (err) { setStudyGuide({ _error: err.message }); }
    finally { setStudyGuideLoading(false); }
  };

  const generateAcademicInsights = async () => {
    if (academicInsightsLoading) return;
    setAcademicInsightsLoading(true); setAcademicInsights(null);
    if (isDesktop) setActiveTab('academic');
    else if (isMobile) setMobilePanel('academic');
    else setSidebarTab('academic');
    try {
      const token = await getAuthToken();
      const res = await fetch('/api/academic-insights', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ transcript }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(`Server error ${res.status}`); }
      if (!res.ok) { if (res.status === 401) { setShowAuthModal(true); setAuthInitialTab('signup'); return; } throw new Error(data.error || 'Failed to generate academic insights'); }
      setAcademicInsights(data);
      setActiveTab('academic');
      setSidebarTab('academic');
      setMobilePanel('academic');
    } catch (err) { setAcademicInsights({ _error: err.message }); }
    finally { setAcademicInsightsLoading(false); }
  };

  const generateDiscover = async () => {
    if (discoverLoading) return;
    setDiscoverLoading(true); setDiscover(null);
    if (isDesktop) setActiveTab('discover');
    else setSidebarTab('discover');
    try {
      const token = await getAuthToken();
      const res = await fetch('/api/discover', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ transcript, videoId: currentVideoId, title: currentTitle }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); } catch { throw new Error(`Server error ${res.status}`); }
      if (!res.ok) { if (res.status === 401) { setShowAuthModal(true); setAuthInitialTab('signup'); return; } throw new Error(data.error || 'Failed to discover related content'); }
      setDiscover(data);
    } catch (err) { setDiscover({ _error: err.message }); }
    finally { setDiscoverLoading(false); }
  };

  useEffect(() => {
    if (!pendingHistoryAction || !currentVideoId || !transcript) return;

    if (pendingHistoryAction === 'summary') {
      if (summary) {
        setActiveTab('summary');
        setSidebarTab('summary');
        setMobilePanel('summary');
      } else if (!summarizing) {
        summarize();
      }
      setPendingHistoryAction(null);
      return;
    }

    if (pendingHistoryAction === 'flashcards') {
      if (flashcards.length > 0 || flashcardsExhausted) {
        setActiveTab('flashcards');
        setSidebarTab('flashcards');
        setMobilePanel('flashcards');
      } else if (!flashcardsLoading) {
        generateFlashcards();
      }
      setPendingHistoryAction(null);
      return;
    }

    if (pendingHistoryAction === 'study-guide') {
      if (studyGuide && !studyGuide._error) {
        setActiveTab('study-guide');
        setSidebarTab('study-guide');
        setMobilePanel('study-guide');
      } else if (!studyGuideLoading) {
        generateStudyGuide();
      }
      setPendingHistoryAction(null);
      return;
    }

    setActiveTab('transcript');
    setSidebarTab('ai');
    setMobilePanel('transcript');
    setPendingHistoryAction(null);
  }, [
    pendingHistoryAction,
    currentVideoId,
    transcript,
    summary,
    summarizing,
    flashcards.length,
    flashcardsExhausted,
    flashcardsLoading,
    studyGuide,
    studyGuideLoading,
  ]);

  const pauseVideo = () => {
    if (currentPlatform === 'vimeo') {
      playerRef.current?.contentWindow?.postMessage(JSON.stringify({ method: 'pause' }), '*');
    } else {
      ytPlayerRef.current?.pauseVideo?.();
    }
  };
  const resumeVideo = () => {
    if (currentPlatform === 'vimeo') {
      playerRef.current?.contentWindow?.postMessage(JSON.stringify({ method: 'play' }), '*');
    } else {
      ytPlayerRef.current?.playVideo?.();
    }
  };

  const openFlashcardModal = () => {
    pauseVideo();
    setFlashcardIndex(0); setFlashcardFlipped(false);
    setShowFlashcardModal(true);
  };
  const closeFlashcardModal = () => {
    setShowFlashcardModal(false);
  };

  const fcNext = () => {
    setFlashcardFlipped(false);
    setTimeout(() => setFlashcardIndex(i => Math.min(i + 1, flashcards.length - 1)), 150);
  };
  const fcPrev = () => {
    setFlashcardFlipped(false);
    setTimeout(() => setFlashcardIndex(i => Math.max(i - 1, 0)), 150);
  };
  const fcMarkKnown = (known) => {
    setFlashcardKnown(prev => {
      const next = new Set(prev);
      if (known) next.add(flashcardIndex); else next.delete(flashcardIndex);
      return next;
    });
    if (flashcardIndex < flashcards.length - 1) fcNext();
  };

  const askSgQuestion = async (overrideQ) => {
    const q = (overrideQ || sgQuestion).trim();
    if (!q || sgLoading) return;
    const history = sgMessages; // snapshot before state update
    setSgMessages(prev => [...prev, { role: 'user', text: q }]);
    setSgQuestion('');
    setSgLoading(true);
    const hasSegs = segments.some(s => s.seconds > 0);
    try {
      const token = await getAuthToken();
      const res = await fetch('/api/ask', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ transcript: hasSegs ? '' : transcript, segments: hasSegs ? segments.slice(0, 800) : [], question: q, history }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.details || data.error || `Server error ${res.status}`);
      }
      // Stream tokens — accumulate and update last AI message
      let accumulated = '';
      let placeholderAdded = false;
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop();
        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data:')) continue;
          const payload = trimmed.slice(5).trim();
          if (payload === '[DONE]') break;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.token) {
              accumulated += parsed.token;
              if (!placeholderAdded) {
                placeholderAdded = true;
                setSgLoading(false);
                setSgMessages(prev => [...prev, { role: 'ai', text: accumulated }]);
              } else {
                const snap = accumulated;
                setSgMessages(prev => {
                  const msgs = [...prev];
                  if (msgs.length > 0 && msgs[msgs.length - 1].role === 'ai') {
                    msgs[msgs.length - 1] = { ...msgs[msgs.length - 1], text: snap };
                  }
                  return msgs;
                });
              }
            }
          } catch (e) {
            if (e.message !== 'Unexpected end of JSON input') throw e;
          }
        }
      }
    } catch (err) {
      setSgMessages(prev => [...prev, { role: 'ai', text: `Error: ${err.message}`, isError: true }]);
    } finally { setSgLoading(false); }
  };

  const highlightText = (text) => {
    if (!search.trim()) return text;
    const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    return text.split(regex).map((part, i) =>
      regex.test(part) ? <mark key={i} style={{ background: '#FEF08A', borderRadius: 2, padding: '0 1px' }}>{part}</mark> : part
    );
  };

  const wordCount    = transcript ? transcript.trim().split(/\s+/).length : 0;
  const charCount    = transcript ? transcript.length : 0;
  const readingMins  = wordCount > 0 ? Math.max(1, Math.round(wordCount / 200)) : 0;
  const matchCount   = search.trim() && transcript
    ? (transcript.match(new RegExp(search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi')) || []).length
    : 0;
  const summaryPreview = SUMMARY_DEMO_PREVIEWS[summaryDemoIdx % SUMMARY_DEMO_PREVIEWS.length];
  const flashcardPreview = FLASHCARD_DEMO_PREVIEWS[flashcardDemoIdx % FLASHCARD_DEMO_PREVIEWS.length];
  const chatPreview = CHAT_DEMO_PREVIEWS[chatDemoIdx % CHAT_DEMO_PREVIEWS.length];
  const studyGuidePreview = STUDY_GUIDE_DEMO_PREVIEWS[studyGuideDemoIdx % STUDY_GUIDE_DEMO_PREVIEWS.length];
  const academicPreview = ACADEMIC_DEMO_PREVIEWS[academicDemoIdx % ACADEMIC_DEMO_PREVIEWS.length];

  const onNavAskAI = () => {
    if (transcript) {
      setShowQA(true);
      setTimeout(() => { qaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); qaInputRef.current?.focus(); }, 100);
    } else {
      urlInputRef.current?.focus();
    }
  };

  // shared pill button style
  const pillBtn = (active) => ({
    display: 'flex', alignItems: 'center', gap: 5,
    width: '100%', justifyContent: 'center', marginTop: 8,
    background: active ? P.paper : P.surface,
    border: `1px solid ${P.border}`, borderRadius: 10, padding: '9px 14px',
    color: active ? P.ink : P.muted, fontSize: 12, fontWeight: 600, cursor: 'pointer',
    transition: 'all 0.15s',
  });

  // ── Shared sidebar content (used in desktop column, tablet drawer, mobile panel) ──
  const renderSidebarContent = () => (
    <>
      {/* Export header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 16px 14px' }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: P.ink }}>Export</span>
      </div>

      {/* Export format list */}
      <div style={{ padding: '0 10px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[
          { label: 'Text', selected: true, fn: downloadTxt, icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg> },
          { label: 'PDF', selected: false, fn: downloadPdf, icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="M10 11h4"/><path d="M10 15h4"/></svg> },
          { label: 'SRT', selected: false, fn: downloadSrt, disabled: segments.length === 0, icon: <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> },
        ].map(item => (
          <button key={item.label} onClick={item.disabled ? undefined : item.fn} style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 10,
            border: 'none', textAlign: 'left', width: '100%', cursor: item.disabled ? 'default' : 'pointer',
            background: item.selected ? P.accentLight : 'transparent', transition: 'background 0.12s',
            opacity: item.disabled ? 0.4 : 1,
          }}
            onMouseEnter={e => { if (!item.disabled && !item.selected) e.currentTarget.style.background = 'rgba(29,29,31,0.05)'; }}
            onMouseLeave={e => { if (!item.selected) e.currentTarget.style.background = 'transparent'; }}
          >
            <span style={{ color: item.selected ? P.accent : P.muted, display: 'flex', alignItems: 'center', flexShrink: 0 }}>{item.icon}</span>
            <span style={{ fontSize: 13, fontWeight: item.selected ? 600 : 500, color: item.selected ? P.accent : P.ink }}>{item.label}</span>
          </button>
        ))}
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: P.border, margin: '0 16px' }} />

      {/* Insights header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '14px 16px 8px' }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: P.ink }}>Insights</span>
      </div>

      {/* Insights rows */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px 12px', display: 'flex', flexDirection: 'column' }}>
        {[
          { title: 'AI Summaries', sub: 'Bullet point summaries', color: P.accent, bg: 'rgba(60,140,255,0.1)',
            icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
            onClick: summary ? () => { setActiveTab('summary'); setSidebarTab('summary'); setMobilePanel('summary'); setHistoryDrawerOpen(false); } : summarize, active: !!summary, loading: summarizing },
          { title: 'Flash Cards', sub: 'Q&A cards with flip mode', color: P.warning, bg: 'rgba(180,83,9,0.1)',
            icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
            onClick: flashcards.length > 0 ? () => { setActiveTab('flashcards'); setSidebarTab('flashcards'); setMobilePanel('flashcards'); setHistoryDrawerOpen(false); } : generateFlashcards, active: flashcards.length > 0, loading: flashcardsLoading },
          { title: 'Study Guide', sub: 'Objectives, concepts & review', color: P.success, bg: 'rgba(15,118,110,0.1)',
            icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>,
            onClick: studyGuide && !studyGuide._error ? () => { setActiveTab('study-guide'); setSidebarTab('study-guide'); setMobilePanel('study-guide'); setHistoryDrawerOpen(false); } : generateStudyGuide, active: !!studyGuide && !studyGuide._error, loading: studyGuideLoading },
          { title: 'Academic', sub: 'References, claims & glossary', color: '#7C3AED', bg: 'rgba(124,58,237,0.1)',
            icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>,
            onClick: academicInsights && !academicInsights._error ? () => { setActiveTab('academic'); setSidebarTab('academic'); setMobilePanel('academic'); setHistoryDrawerOpen(false); } : generateAcademicInsights, active: !!academicInsights && !academicInsights._error, loading: academicInsightsLoading },
          { title: 'Discover', sub: 'Related videos & papers', color: '#C2410C', bg: 'rgba(194,65,12,0.1)',
            icon: <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>,
            onClick: discover && !discover._error ? () => { setActiveTab('discover'); setSidebarTab('discover'); setMobilePanel('discover'); setHistoryDrawerOpen(false); } : generateDiscover, active: !!discover && !discover._error, loading: discoverLoading },
        ].map(item => (
          <div key={item.title} onClick={item.loading ? undefined : item.onClick}
            style={{ display: 'flex', alignItems: 'center', gap: 13, padding: '12px 10px', borderRadius: 11, cursor: 'pointer', transition: 'background 0.12s', marginBottom: 3 }}
            onMouseEnter={e => { e.currentTarget.style.background = P.surface; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
          >
            <div style={{ width: 42, height: 42, borderRadius: 12, background: item.active ? item.bg : item.bg.replace('0.1', '0.07'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: item.color }}>
              {item.loading ? <SpinnerIcon size={14} /> : item.icon}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: item.active ? item.color : P.ink }}>{item.title}</div>
              <div style={{ fontSize: 11.5, color: P.muted, marginTop: 1 }}>{item.sub}</div>
            </div>
            {item.active
              ? <div style={{ width: 8, height: 8, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
              : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={P.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.4 }}><polyline points="9 18 15 12 9 6"/></svg>
            }
          </div>
        ))}
        {studyGuide?._error && (
          <div style={{ marginTop: 8, padding: '8px 12px', background: 'rgba(180,35,24,0.05)', border: `1px solid rgba(180,35,24,0.2)`, borderRadius: 8, fontSize: 12, color: P.error }}>
            Failed to generate study guide: {studyGuide._error}
          </div>
        )}
      </div>

      {/* Back button pinned at bottom */}
      <div style={{ padding: '10px 12px', borderTop: `1px solid ${P.border}` }}>
        <button onClick={startNewSearch} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%',
          padding: '8px', border: `1px solid ${P.accentHover}`, borderRadius: 9,
          background: `radial-gradient(ellipse at top left, rgba(255,255,255,0.22) 0%, transparent 55%), radial-gradient(ellipse at top right, rgba(255,255,255,0.22) 0%, transparent 55%), radial-gradient(ellipse at bottom left, rgba(255,255,255,0.22) 0%, transparent 55%), radial-gradient(ellipse at bottom right, rgba(255,255,255,0.22) 0%, transparent 55%), ${P.accent}`,
          cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#fff', transition: 'all 0.15s',
        }}
          onMouseEnter={e => { e.currentTarget.style.background = `radial-gradient(ellipse at top left, rgba(255,255,255,0.3) 0%, transparent 55%), radial-gradient(ellipse at top right, rgba(255,255,255,0.3) 0%, transparent 55%), radial-gradient(ellipse at bottom left, rgba(255,255,255,0.3) 0%, transparent 55%), radial-gradient(ellipse at bottom right, rgba(255,255,255,0.3) 0%, transparent 55%), ${P.accentHover}`; }}
          onMouseLeave={e => { e.currentTarget.style.background = `radial-gradient(ellipse at top left, rgba(255,255,255,0.22) 0%, transparent 55%), radial-gradient(ellipse at top right, rgba(255,255,255,0.22) 0%, transparent 55%), radial-gradient(ellipse at bottom left, rgba(255,255,255,0.22) 0%, transparent 55%), radial-gradient(ellipse at bottom right, rgba(255,255,255,0.22) 0%, transparent 55%), ${P.accent}`; }}
        >
          <ChevronIcon dir="left" size={11} /> New search
        </button>
      </div>
    </>
  );

  const showTopNavbar = !isMobile || !transcript;
  const topChromeOffset = showTopNavbar ? ((showBookmarkBanner && !isMobile) ? 97 : 56) : 0;
  const hasMobileBottomNav = isMobile && transcript && !hideMobileNavForFooter && !isMobileKeyboardOpen;
  const mobileBottomNavHeight = hasMobileBottomNav ? (MOBILE_BOTTOM_NAV_HEIGHT + MOBILE_BOTTOM_NAV_LIFT) : 0;
  const mobileBottomNavInsetExpr = hasMobileBottomNav
    ? `${mobileBottomNavHeight}px + env(safe-area-inset-bottom, 0px)`
    : '0px';
  const mobileBottomNavCssHeight = hasMobileBottomNav
    ? `calc(${mobileBottomNavInsetExpr})`
    : '0px';
  const mobileKeyboardInsetCss = (isMobileKeyboardOpen && mobileKeyboardInset > 0)
    ? `${mobileKeyboardInset}px`
    : '0px';
  const mobilePanelBottomInsetCss = (isMobile && isMobileKeyboardOpen)
    ? mobileKeyboardInsetCss
    : mobileBottomNavCssHeight;

  return (
    <>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
        @keyframes bounce { 0%,80%,100% { transform: scale(0.6); opacity:0.4; } 40% { transform: scale(1); opacity:1; } }
        @keyframes pulse { 0%,100% { opacity:1; } 50% { opacity:0.5; } }
        @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(200%); } }
        @keyframes dot-flicker { 0%,80%,100% { opacity:0.2; transform:scale(0.8); } 40% { opacity:1; transform:scale(1); } }
        @keyframes ss-wave-draw { 0%,2% { stroke-dasharray:0 1; } 88%,100% { stroke-dasharray:1 0; } }
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes logoFlipOut { 0% { transform: perspective(300px) rotateX(0deg); opacity:1; } 100% { transform: perspective(300px) rotateX(-80deg); opacity:0; } }
        @keyframes logoFlipIn  { 0% { transform: perspective(300px) rotateX(80deg);  opacity:0; } 100% { transform: perspective(300px) rotateX(0deg);   opacity:1; } }
        @keyframes tabHighlight { 0% { box-shadow: 0 0 0 0 rgba(60,140,255,0); } 30% { box-shadow: 0 0 0 3px rgba(60,140,255,0.34), 0 0 12px rgba(60,140,255,0.22); } 100% { box-shadow: 0 0 0 0 rgba(60,140,255,0); } }
        @keyframes modeSwapIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
        .fade-up { animation: fadeUp 0.3s ease forwards; }
        .mode-panel { animation: modeSwapIn 0.18s ease; }
        .left-rail { position: relative; width: 64px; flex-shrink: 0; z-index: 15; overflow: visible; }
        .left-rail-inner { position: absolute; top: 0; left: 0; bottom: 0; width: 64px; overflow: hidden; display: flex; flex-direction: column; transition: width 0.22s cubic-bezier(0.4,0,0.2,1), box-shadow 0.22s; }
        .left-rail:hover .left-rail-inner { width: 284px; box-shadow: 4px 0 24px rgba(29,29,31,0.09); }
        .lr-collapsed { display: flex; flex-direction: column; align-items: center; flex: 1; transition: opacity 0.09s; }
        .left-rail:hover .lr-collapsed { opacity: 0; pointer-events: none; }
        .lr-expanded { opacity: 0; pointer-events: none; position: absolute; inset: 0; display: flex; flex-direction: column; transition: opacity 0.14s 0.07s; width: 284px; overflow-y: auto; }
        .left-rail:hover .lr-expanded { opacity: 1; pointer-events: auto; }
        .marquee-track { animation: marquee 28s linear infinite; }
        .marquee-track:hover { animation-play-state: paused; }
        .rc-card:hover { box-shadow: 0 6px 24px rgba(29,29,31,0.11); border-color: rgba(60,140,255,0.3) !important; transform: translateY(-2px); }
        .rc-card:hover .rc-overlay { opacity: 1 !important; }
        .rc-card:hover .rc-delete { opacity: 1 !important; }
        * { box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        body { margin: 0; background: ${P.paper}; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: #EEEBE4; border-radius: 4px; }
        ::-webkit-scrollbar-thumb { background: #C4BBAD; border-radius: 4px; transition: background 0.2s; }
        ::-webkit-scrollbar-thumb:hover { background: #A89C8E; }
        * { scrollbar-width: thin; scrollbar-color: #C4BBAD #EEEBE4; }
        input, select, textarea { font-family: inherit; }
        .hero-grad {
          position: relative;
          overflow: hidden;
          isolation: isolate;
          background: radial-gradient(
            ellipse 124% 80% at 50% -22%,
            rgba(178,194,214,0.22) 0%,
            rgba(178,194,214,0.14) 42%,
            rgba(178,194,214,0.06) 68%,
            rgba(178,194,214,0) 100%
          );
        }
        .hero-inner {
          position: relative;
          z-index: 1;
        }

        /* ── Hero ambient decorations ─────────────────────────────────────── */
        @keyframes planetFloat {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          33%       { transform: translateY(-18px) rotate(1.5deg); }
          66%       { transform: translateY(10px) rotate(-1deg); }
        }
        @keyframes orbitFloat {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50%       { transform: translateY(14px) rotate(2deg); }
        }
        @keyframes starTwinkle {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.1; transform: scale(0.6); }
        }
        .hero-cosmos {
          position: absolute;
          inset: 0;
          pointer-events: none;
          overflow: hidden;
          z-index: 0;
        }
        .hero-orb-1 {
          position: absolute;
          width: 260px;
          height: 260px;
          left: -80px;
          top: 20px;
          border-radius: 50%;
          background: radial-gradient(circle at 38% 38%, rgba(60,140,255,0.45), rgba(100,170,255,0.15) 55%, transparent 80%);
          filter: blur(32px);
          opacity: 0.18;
          animation: planetFloat 26s ease-in-out infinite;
        }
        .hero-orb-2 {
          position: absolute;
          width: 160px;
          height: 160px;
          right: 4%;
          top: 15%;
          border-radius: 50%;
          background: radial-gradient(circle at 42% 42%, rgba(123,211,255,0.5), rgba(178,194,214,0.15) 58%, transparent 80%);
          filter: blur(24px);
          opacity: 0.15;
          animation: orbitFloat 20s ease-in-out infinite;
          animation-delay: -5s;
        }
        .hero-orb-3 {
          position: absolute;
          width: 100px;
          height: 100px;
          left: 30%;
          bottom: 5%;
          border-radius: 50%;
          background: radial-gradient(circle at 40% 40%, rgba(60,140,255,0.3), transparent 70%);
          filter: blur(20px);
          opacity: 0.1;
          animation: orbitFloat 16s ease-in-out infinite;
          animation-delay: -8s;
        }
        .hero-star {
          position: absolute;
          border-radius: 50%;
          background: rgba(60,140,255,0.55);
          animation-name: starTwinkle;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
        }
        .hero-trust-columns {
          position: absolute;
          inset: 0;
          pointer-events: none;
          z-index: 1;
        }
        .hero-trust-column {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          display: flex;
          flex-direction: column;
          gap: 9px;
          padding: 14px 0;
          -webkit-mask-image: linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%);
          mask-image: linear-gradient(to bottom, transparent 0%, black 18%, black 82%, transparent 100%);
        }
        .hero-trust-column.left {
          left: max(8px, calc(50% - 748px));
        }
        .hero-trust-column.right {
          right: max(8px, calc(50% - 748px));
        }
        .hero-trust-logo-chip {
          width: 34px;
          height: 34px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          border: 1px solid rgba(73,112,182,0.14);
          background: rgba(255,255,255,0.56);
          box-shadow: 0 3px 10px rgba(29,29,31,0.06);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
        }
        .hero-trust-logo-icon {
          display: inline-flex;
          width: 14px;
          height: 14px;
          align-items: center;
          justify-content: center;
          filter: grayscale(0.35) saturate(0.7);
          opacity: 0.8;
          flex-shrink: 0;
        }
        @media (max-width: 1100px) {
          .hero-trust-columns {
            display: none;
          }
        }

        /* ── Feature section cosmos ───────────────────────────────────────── */
        .feat-cosmos {
          position: absolute;
          inset: 0;
          pointer-events: none;
          overflow: hidden;
          z-index: 0;
        }
        .feat-orb-1 {
          position: absolute;
          width: 420px;
          height: 420px;
          right: -60px;
          top: -60px;
          border-radius: 50%;
          background: radial-gradient(circle at 42% 42%, rgba(123,90,255,0.55), rgba(100,140,255,0.18) 50%, transparent 75%);
          filter: blur(40px);
          opacity: 0.28;
          animation: orbitFloat 22s ease-in-out infinite;
          animation-delay: -6s;
        }
        .feat-orb-2 {
          position: absolute;
          width: 280px;
          height: 280px;
          left: -50px;
          bottom: 8%;
          border-radius: 50%;
          background: radial-gradient(circle at 40% 40%, rgba(60,140,255,0.6), rgba(123,211,255,0.15) 55%, transparent 78%);
          filter: blur(32px);
          opacity: 0.24;
          animation: planetFloat 18s ease-in-out infinite;
          animation-delay: -9s;
        }
        .feat-orb-3 {
          position: absolute;
          width: 160px;
          height: 160px;
          left: 44%;
          bottom: -20px;
          border-radius: 50%;
          background: radial-gradient(circle at 38% 38%, rgba(90,60,220,0.5), transparent 68%);
          filter: blur(24px);
          opacity: 0.18;
          animation: orbitFloat 14s ease-in-out infinite;
          animation-delay: -3s;
        }
        .feat-star {
          position: absolute;
          border-radius: 50%;
          background: rgba(110,80,255,0.85);
          animation-name: starTwinkle;
          animation-timing-function: ease-in-out;
          animation-iteration-count: infinite;
        }

        .feature-workflow {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 28px;
        }
        .feature-flow-head {
          position: relative;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          padding-bottom: 2px;
        }
        .feature-flow-node {
          min-width: 206px;
          min-height: 36px;
          padding: 0 6px;
          border: none;
          background: transparent;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: ${P.ink};
          font-size: 16px;
          font-weight: 800;
          letter-spacing: -0.01em;
          box-shadow: none;
        }
        .feature-flow-node.input {
          color: rgba(29,29,31,0.72);
          background: rgba(255,255,255,0.75);
          border: 1px solid rgba(29,29,31,0.10);
          border-radius: 10px;
          padding: 8px 18px;
          font-weight: 600;
          font-size: 14px;
          gap: 7px;
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
        }
        .feature-flow-arrow {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 18px;
          color: rgba(29,29,31,0.44);
          font-size: 13px;
          line-height: 1;
        }
        .feature-flow-node.transcript {
          min-width: 350px;
          min-height: 72px;
          height: auto;
          padding: 0;
        }
        .feature-flow-transcript-preview {
          width: 100%;
          border-radius: 10px;
          border: 1px solid rgba(84,104,168,0.12);
          background: rgba(255,255,255,0.62);
          padding: 7px 7px 6px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .feature-flow-transcript-title {
          font-size: 7.2px;
          line-height: 1;
          font-weight: 800;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          color: #40548A;
          opacity: 0.8;
          font-family: ui-monospace,monospace;
        }
        .feature-flow-transcript-preview .feature-mock-ts-row {
          gap: 4px;
        }
        .feature-flow-transcript-preview .feature-mock-ts-badge {
          font-size: 6.8px;
          opacity: 0.78;
        }
        .feature-flow-transcript-preview .feature-mock-line {
          height: 7px;
          border-radius: 4px;
        }
        .feature-flow-branches {
          position: relative;
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 30px;
          padding-top: 12px;
        }
        .feature-flow-branches::before {
          content: none;
        }
        .feature-card {
          position: relative;
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 6px 0;
          border: none;
          background: transparent;
          box-shadow: none;
          isolation: isolate;
          transition: transform 0.18s ease;
        }
        .feature-card::before {
          content: none;
        }
        .feature-card:hover {
          transform: translateY(-2px);
        }
        .feature-card-top {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: flex-start;
        }
        .feature-card-num {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 30px;
          height: 20px;
          padding: 0 7px;
          border-radius: 999px;
          background: var(--card-gradient);
          color: #fff;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 0.07em;
          font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
          box-shadow: 0 4px 10px rgba(29,29,31,0.14);
        }
        .feature-card-icon {
          width: 34px;
          height: 34px;
          border-radius: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: var(--card-accent);
          background: rgba(255,255,255,0.9);
          border: 1px solid var(--card-pill-border);
          box-shadow: 0 2px 8px rgba(29,29,31,0.07);
          animation: iconPulse 3.6s ease-in-out infinite;
          animation-delay: calc(var(--card-index) * 0.24s);
        }
        .feature-card-label {
          position: relative;
          z-index: 1;
          margin: 0;
          font-size: 20px;
          line-height: 1.25;
          font-weight: 800;
          letter-spacing: -0.02em;
          color: ${P.ink};
        }
        .feature-card-desc {
          position: relative;
          z-index: 1;
          margin: 0;
          font-size: 14px;
          line-height: 1.55;
          color: ${P.muted};
        }
        .feature-card-visual {
          position: relative;
          overflow: hidden;
          z-index: 1;
          margin: 0 0 6px;
          padding: 11px;
          border-radius: 14px;
          background: rgba(255,255,255,0.52);
          border: 1px solid rgba(29,29,31,0.13);
          box-shadow: 0 2px 12px rgba(29,29,31,0.07);
          display: flex;
          flex-direction: column;
          gap: 9px;
        }
        .feature-mock-shell {
          border-radius: 12px;
          border: 1px solid rgba(29,29,31,0.16);
          background: rgba(255,255,255,0.96);
          overflow: hidden;
          box-shadow: 0 3px 10px rgba(29,29,31,0.09), inset 0 1px 0 rgba(255,255,255,0.9);
        }
        .feature-mock-topbar {
          height: 24px;
          padding: 0 8px;
          border-bottom: 1px solid rgba(29,29,31,0.09);
          background: linear-gradient(180deg, rgba(29,29,31,0.045) 0%, rgba(29,29,31,0.02) 100%);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .feature-mock-dots {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .feature-mock-dot {
          width: 5px;
          height: 5px;
          border-radius: 999px;
          background: rgba(29,29,31,0.3);
        }
        .feature-mock-chip {
          border: 1px solid var(--card-pill-border);
          background: rgba(255,255,255,0.88);
          color: var(--card-accent);
          border-radius: 999px;
          font-size: 9.5px;
          font-weight: 700;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          padding: 2px 7px;
          line-height: 1;
        }
        .feature-mock-body {
          min-height: 100px;
          padding: 9px;
          display: flex;
          gap: 9px;
        }
        .feature-mock-timeline {
          width: 16px;
          display: flex;
          flex-direction: column;
          gap: 5px;
          padding-top: 3px;
        }
        .feature-mock-timeline-bar {
          height: 7px;
          border-radius: 999px;
          background: rgba(29,29,31,0.13);
        }
        .feature-mock-timeline-bar.active {
          background: var(--card-accent);
          opacity: 0.85;
        }
        .feature-mock-lines {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        .feature-mock-line {
          height: 7px;
          border-radius: 4px;
          background: rgba(29,29,31,0.11);
        }
        .feature-mock-line.active {
          background: linear-gradient(90deg, rgba(123,211,255,0.28) 0%, rgba(255,255,255,0.88) 100%);
          border: 1px solid var(--card-pill-border);
          height: 8px;
        }
        .feature-mock-tools-nav {
          width: 56px;
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .feature-mock-tools-tab {
          height: 11px;
          border-radius: 4px;
          background: rgba(29,29,31,0.12);
        }
        .feature-mock-tools-tab.active {
          background: var(--card-accent);
          opacity: 0.8;
        }
        .feature-mock-tools-main {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        .feature-mock-title {
          height: 8px;
          width: 66%;
          border-radius: 4px;
          background: rgba(29,29,31,0.2);
        }
        .feature-mock-bullet {
          height: 6px;
          border-radius: 4px;
          background: rgba(29,29,31,0.11);
        }
        .feature-mock-summary-stack {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        .feature-mock-summary-text {
          flex: 1;
          min-width: 0;
          font-size: 7.2px;
          line-height: 1.35;
          color: rgba(29,29,31,0.58);
          letter-spacing: 0.01em;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .feature-mock-summary-metrics {
          display: flex;
          gap: 4px;
          margin-top: 2px;
          flex-wrap: wrap;
        }
        .feature-mock-summary-metric {
          height: 13px;
          padding: 0 5px;
          border-radius: 999px;
          border: 1px solid var(--card-pill-border);
          background: linear-gradient(180deg, rgba(255,255,255,0.9) 0%, var(--card-pill-bg) 100%);
          color: var(--card-accent);
          font-size: 6.8px;
          font-weight: 700;
          letter-spacing: 0.02em;
          display: inline-flex;
          align-items: center;
          line-height: 1;
        }
        .feature-mock-mini-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 4px;
          margin-top: auto;
        }
        .feature-mock-mini-card {
          height: 17px;
          border-radius: 5px;
          background: var(--card-pill-bg);
          border: 1px solid var(--card-pill-border);
        }
        .feature-mock-chat {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        .feature-mock-bubble {
          height: 15px;
          border-radius: 8px;
        }
        .feature-mock-bubble.question {
          width: 63%;
          background: rgba(29,29,31,0.11);
        }
        .feature-mock-bubble.answer {
          width: 78%;
          align-self: flex-end;
          background: var(--card-pill-bg);
          border: 1px solid var(--card-pill-border);
        }
        .feature-mock-source-row {
          display: flex;
          gap: 4px;
          margin-top: 1px;
        }
        .feature-mock-source {
          width: 22px;
          height: 8px;
          border-radius: 4px;
          border: 1px solid var(--card-pill-border);
          background: rgba(255,255,255,0.9);
        }
        .feature-mock-meter {
          margin-top: 2px;
          height: 5px;
          border-radius: 999px;
          background: rgba(29,29,31,0.1);
          overflow: hidden;
        }
        .feature-mock-meter-fill {
          width: 76%;
          height: 100%;
          border-radius: 999px;
          background: linear-gradient(90deg, #7BD3FF 0%, var(--card-accent) 58%, #1F6BFF 100%);
        }
        .feature-mock-kpi-row {
          display: flex;
          gap: 4px;
          margin-top: auto;
        }
        .feature-mock-kpi {
          height: 13px;
          border-radius: 5px;
          border: 1px solid var(--card-pill-border);
          background: linear-gradient(180deg, rgba(255,255,255,0.88) 0%, var(--card-pill-bg) 100%);
        }
        .feature-mock-refs {
          display: flex;
          gap: 4px;
          margin-top: auto;
          flex-wrap: wrap;
        }
        .feature-mock-ref {
          height: 11px;
          border-radius: 999px;
          border: 1px solid var(--card-pill-border);
          background: rgba(255,255,255,0.92);
        }
        .feature-card-tags {
          position: relative;
          z-index: 1;
          margin-top: 1px;
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
        }
        .feature-card-tag {
          display: inline-flex;
          align-items: center;
          height: 21px;
          padding: 0 9px;
          border-radius: 999px;
          border: 1px solid var(--card-pill-border);
          background: rgba(255,255,255,0.74);
          color: var(--card-accent);
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.025em;
          line-height: 1;
          white-space: nowrap;
        }
        .feature-card-meta {
          position: relative;
          z-index: 1;
          margin-top: 0;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 10.5px;
          font-weight: 700;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          color: var(--card-accent);
        }
        .feature-card-meta::before {
          content: '';
          width: 16px;
          height: 2px;
          border-radius: 999px;
          background: var(--card-accent);
          opacity: 0.72;
        }
        @media (max-width: 1220px) {
          .feature-flow-branches {
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 18px;
          }
          .feature-flow-branches::before {
            left: 14%;
            right: 14%;
          }
        }
        @media (max-width: 1023px) {
          .hero-cosmos {
            display: none;
          }
        }
        @media (max-width: 920px) {
          .feature-flow-branches {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 14px;
          }
          .feature-flow-branches::before {
            left: 20%;
            right: 20%;
          }
        }
        @media (max-width: 740px) {
          .feature-flow-head {
            gap: 2px;
            padding-bottom: 2px;
          }
          .feature-flow-head::after,
          .feature-flow-branches::before,
          .feature-card::before {
            display: none;
          }
          .feature-flow-node {
            min-width: 130px;
            min-height: 28px;
            font-size: 12.8px;
          }
          .feature-flow-node.transcript {
            min-width: 100%;
            min-height: 56px;
            padding: 0;
          }
          .feature-flow-branches {
            grid-template-columns: 1fr;
            gap: 10px;
            padding-top: 0;
          }
          .feature-flow-arrow {
            width: 18px;
            height: 14px;
            font-size: 11px;
          }
          .feature-card-icon {
            width: 26px;
            height: 26px;
            border-radius: 8px;
          }
          .feature-card-label { font-size: 16px; }
          .feature-card-desc { font-size: 12px; }
          .feature-card-tag { font-size: 9px; height: 18px; }
          .feature-card-meta { font-size: 10px; }
        }
        @keyframes cursorBlink { 0%,100% { opacity:1; } 50% { opacity:0; } }
        @keyframes shimmerSweep { 0% { background-position:110% center; } 100% { background-position:-110% center; } }
        @keyframes bulletReveal { 0% { opacity:0.12; transform:translateX(-4px); } 18%,68% { opacity:1; transform:translateX(0); } 85%,100% { opacity:0.12; transform:translateX(0); } }
        @keyframes cardFlip3D {
          0% { transform:rotateY(0deg); }
          14% { transform:rotateY(180deg); }
          50% { transform:rotateY(180deg); }
          64% { transform:rotateY(360deg); }
          100% { transform:rotateY(360deg); }
        }
        @keyframes dotBounce { 0%,60%,100% { transform:translateY(0); opacity:0.35; } 30% { transform:translateY(-3px); opacity:0.85; } }
        @keyframes chatTurnIn { 0% { opacity:0; transform:translateY(3px); } 100% { opacity:1; transform:translateY(0); } }
        @keyframes iconPulse { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-2px); } }
        @keyframes starTwinkle { 0%,100% { opacity:0.2; transform:scale(0.9); } 50% { opacity:0.62; transform:scale(1.15); } }
        @keyframes planetFloat { 0%,100% { transform:translateY(0px); } 50% { transform:translateY(7px); } }
        .feature-mock-url-bar { display:flex; align-items:center; gap:5px; height:20px; padding:0 7px; border-radius:5px; background:rgba(255,255,255,0.88); border:1px solid rgba(29,29,31,0.11); font-size:8px; color:rgba(29,29,31,0.42); font-family:ui-monospace,monospace; letter-spacing:0.01em; overflow:hidden; }
        .feature-mock-cursor { width:1.5px; height:9px; background:var(--card-accent); border-radius:1px; flex-shrink:0; animation:cursorBlink 0.9s step-end infinite; }
        .feature-mock-ts-row { display:flex; align-items:center; gap:5px; }
        .feature-mock-ts-badge { font-size:7px; font-weight:700; color:var(--card-accent); font-family:ui-monospace,monospace; opacity:0.7; flex-shrink:0; white-space:nowrap; }
        .feature-mock-line.shimmer { background:linear-gradient(90deg, rgba(29,29,31,0.09) 0%, rgba(29,29,31,0.09) 20%, rgba(255,255,255,0.88) 50%, rgba(29,29,31,0.09) 80%, rgba(29,29,31,0.09) 100%); background-size:300% 100%; background-position:110% center; animation:none; }
        .feature-flow-node.transcript:hover .feature-mock-line.shimmer,
        .feature-card.is-hovered .feature-mock-line.shimmer { animation:shimmerSweep 2s ease-in-out infinite; }
        .feature-mock-bullet-row { display:flex; align-items:center; gap:5px; }
        .feature-mock-bullet-dot { width:4px; height:4px; border-radius:50%; flex-shrink:0; background:var(--card-accent); opacity:0.7; }
        .feature-mock-bullet-row.anim-1,
        .feature-mock-bullet-row.anim-2,
        .feature-mock-bullet-row.anim-3 { animation:none; opacity:1; transform:none; }
        .feature-card.is-hovered .feature-mock-bullet-row.anim-1 { animation:bulletReveal 3.6s ease-in-out infinite; animation-fill-mode:both; }
        .feature-card.is-hovered .feature-mock-bullet-row.anim-2 { animation:bulletReveal 3.6s ease-in-out infinite; animation-fill-mode:both; animation-delay:0.65s; }
        .feature-card.is-hovered .feature-mock-bullet-row.anim-3 { animation:bulletReveal 3.6s ease-in-out infinite; animation-fill-mode:both; animation-delay:1.3s; }
        .feature-mock-flip-scene { perspective:500px; height:58px; margin:2px 0; }
        .feature-mock-flip-card { width:100%; height:100%; position:relative; transform-style:preserve-3d; animation:none; will-change:transform; }
        .feature-card.is-hovered .feature-mock-flip-card { animation:cardFlip3D 3.1s cubic-bezier(0.26,0.78,0.24,1) infinite; }
        .feature-mock-flip-front, .feature-mock-flip-back { position:absolute; inset:0; border-radius:8px; backface-visibility:hidden; -webkit-backface-visibility:hidden; display:flex; flex-direction:column; align-items:flex-start; justify-content:flex-start; padding:5px 7px; gap:2px; overflow:hidden; }
        .feature-mock-flip-front { background:rgba(255,255,255,0.92); border:1px solid var(--card-pill-border); }
        .feature-mock-flip-back { background:var(--card-pill-bg); border:1px solid var(--card-pill-border); transform:rotateY(180deg); }
        .feature-mock-flip-label { font-size:6.2px; font-weight:800; color:var(--card-accent); text-transform:uppercase; letter-spacing:0.05em; font-family:ui-monospace,monospace; flex-shrink:0; opacity:0.9; }
        .feature-mock-flip-topic { font-size:6.1px; font-weight:700; color:var(--card-accent); text-transform:uppercase; letter-spacing:0.04em; border:1px solid var(--card-pill-border); background:rgba(255,255,255,0.84); border-radius:999px; padding:1px 4px; line-height:1; max-width:100%; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .feature-mock-flip-text { font-size:6.7px; line-height:1.28; color:rgba(29,29,31,0.72); letter-spacing:0.01em; max-width:100%; display:-webkit-box; -webkit-line-clamp:3; -webkit-box-orient:vertical; overflow:hidden; }
        .feature-mock-flip-line { height:6px; flex:1; border-radius:4px; background:rgba(29,29,31,0.12); }
        .feature-mock-typing { display:flex; align-items:center; gap:3px; padding:5px 8px; background:rgba(255,255,255,0.88); border:1px solid rgba(29,29,31,0.1); border-radius:8px; align-self:flex-start; width:fit-content; margin-top:1px; }
        .feature-mock-typing-dot { width:4px; height:4px; border-radius:50%; background:rgba(29,29,31,0.38); animation:dotBounce 1.1s ease-in-out infinite; }
        .feature-mock-chat-panel { border:1px solid rgba(29,29,31,0.11); border-radius:9px; background:rgba(255,255,255,0.95); padding:6px; display:flex; flex-direction:column; gap:5px; }
        .feature-mock-chat-head { display:flex; align-items:center; gap:5px; }
        .feature-mock-chat-head-avatar { width:13px; height:13px; border-radius:4px; flex-shrink:0; border:1px solid rgba(60,140,255,0.2); background:linear-gradient(135deg, rgba(60,140,255,0.14) 0%, rgba(60,140,255,0.06) 100%); display:flex; align-items:center; justify-content:center; }
        .feature-mock-chat-head-avatar img { width:8px; height:8px; display:block; }
        .feature-mock-chat-head-lines { flex:1; display:flex; flex-direction:column; gap:2px; min-width:0; }
        .feature-mock-chat-head-title { font-size:6.7px; font-weight:700; color:rgba(29,29,31,0.74); line-height:1.2; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .feature-mock-chat-head-sub { font-size:6px; color:rgba(29,29,31,0.52); line-height:1.2; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .feature-mock-chat-stream { display:flex; flex-direction:column; gap:3px; min-height:58px; }
        .feature-mock-chat-row { display:flex; align-items:flex-start; gap:4px; }
        .feature-mock-chat-row.user { justify-content:flex-end; }
        .feature-mock-chat-avatar { width:12px; height:12px; border-radius:4px; flex-shrink:0; border:1px solid rgba(60,140,255,0.18); background:linear-gradient(135deg, rgba(60,140,255,0.14) 0%, rgba(60,140,255,0.06) 100%); display:flex; align-items:center; justify-content:center; }
        .feature-mock-chat-avatar img { width:7px; height:7px; display:block; }
        .feature-mock-chat-message { max-width:100%; border-radius:7px; padding:4px 6px; display:flex; flex-direction:column; gap:2px; font-size:6.8px; line-height:1.3; letter-spacing:0.01em; }
        .feature-mock-chat-message.user { margin-left:auto; max-width:79%; background:linear-gradient(135deg, #5ba4f5 0%, #3C8CFF 100%); border:1px solid rgba(31,107,255,0.26); color:rgba(255,255,255,0.96); }
        .feature-mock-chat-message.ai { max-width:88%; background:rgba(255,255,255,0.95); border:1px solid rgba(29,29,31,0.12); color:rgba(29,29,31,0.74); }
        .feature-mock-chat-source { display:flex; align-items:center; gap:3px; font-size:5.8px; font-weight:700; color:rgba(29,29,31,0.48); text-transform:uppercase; letter-spacing:0.04em; }
        .feature-mock-chat-timestamp { color:#1F6BFF; font-size:6.3px; font-weight:800; text-decoration:underline; text-underline-offset:1px; cursor:pointer; letter-spacing:0.01em; padding:0; border:none; background:none; line-height:1; font-family:inherit; }
        .feature-mock-chat-turn { opacity:1; animation:none; }
        .feature-card.is-hovered .feature-mock-chat-turn { opacity:0; animation:chatTurnIn 0.55s cubic-bezier(0.22,0.74,0.2,1) both; }
        .feature-card.is-hovered .feature-mock-chat-turn.turn-1 { animation-delay:0.08s; }
        .feature-card.is-hovered .feature-mock-chat-turn.turn-2 { animation-delay:0.86s; }
        .feature-card.is-hovered .feature-mock-chat-turn.turn-3 { animation-delay:2.02s; }
        .feature-card.is-hovered .feature-mock-chat-turn.turn-4 { animation-delay:2.84s; }
        .feature-mock-chat-composer { display:flex; align-items:center; gap:4px; border:1px solid rgba(29,29,31,0.11); border-radius:7px; padding:2px 3px; background:#fff; }
        .feature-mock-chat-input-text { flex:1; min-width:0; font-size:6.2px; color:rgba(29,29,31,0.42); line-height:1; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .feature-mock-chat-send { width:8px; height:8px; border-radius:50%; background:var(--card-accent); opacity:0.88; flex-shrink:0; }
        .feature-mock-guide-stack { display:flex; flex-direction:column; gap:4px; }
        .feature-mock-guide-section { border:1px solid var(--card-pill-border); border-radius:7px; background:rgba(255,255,255,0.94); padding:4px 5px; display:flex; flex-direction:column; gap:2px; opacity:1; animation:none; }
        .feature-card.is-hovered .feature-mock-guide-section { opacity:0; animation:chatTurnIn 0.55s cubic-bezier(0.22,0.74,0.2,1) both; }
        .feature-card.is-hovered .feature-mock-guide-section.step-1 { animation-delay:0.08s; }
        .feature-card.is-hovered .feature-mock-guide-section.step-2 { animation-delay:0.6s; }
        .feature-card.is-hovered .feature-mock-guide-section.step-3 { animation-delay:1.12s; }
        .feature-mock-guide-label { font-size:5.8px; font-weight:800; color:var(--card-accent); letter-spacing:0.05em; text-transform:uppercase; line-height:1; }
        .feature-mock-guide-line { font-size:6.3px; color:rgba(29,29,31,0.68); line-height:1.3; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .feature-mock-guide-line.subtle { color:rgba(29,29,31,0.58); }
        .feature-mock-academic-stack { display:flex; flex-direction:column; gap:4px; }
        .feature-mock-academic-section { border:1px solid var(--card-pill-border); border-radius:7px; background:rgba(255,255,255,0.94); padding:4px 5px; display:flex; flex-direction:column; gap:2px; opacity:1; animation:none; }
        .feature-card.is-hovered .feature-mock-academic-section { opacity:0; animation:chatTurnIn 0.55s cubic-bezier(0.22,0.74,0.2,1) both; }
        .feature-card.is-hovered .feature-mock-academic-section.step-1 { animation-delay:0.08s; }
        .feature-card.is-hovered .feature-mock-academic-section.step-2 { animation-delay:0.6s; }
        .feature-card.is-hovered .feature-mock-academic-section.step-3 { animation-delay:1.12s; }
        .feature-mock-academic-label { font-size:5.8px; font-weight:800; color:var(--card-accent); letter-spacing:0.05em; text-transform:uppercase; line-height:1; }
        .feature-mock-academic-line { font-size:6.4px; color:rgba(29,29,31,0.68); line-height:1.3; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .feature-mock-academic-tags { display:flex; align-items:center; gap:3px; flex-wrap:wrap; }
        .feature-mock-academic-tag { display:inline-flex; align-items:center; height:10px; padding:0 4px; border-radius:999px; border:1px solid var(--card-pill-border); background:var(--card-pill-bg); color:var(--card-accent); font-size:5.7px; font-weight:700; letter-spacing:0.02em; opacity:1; animation:none; }
        .feature-card.is-hovered .feature-mock-academic-tag { opacity:0; animation:chatTurnIn 0.45s cubic-bezier(0.22,0.74,0.2,1) both; }
        .feature-card.is-hovered .feature-mock-academic-tag.term-1 { animation-delay:1.22s; }
        .feature-card.is-hovered .feature-mock-academic-tag.term-2 { animation-delay:1.34s; }
        .feature-card.is-hovered .feature-mock-academic-tag.term-3 { animation-delay:1.46s; }
        .chip-btn { transition: all 0.15s; }
        .chip-btn:hover { border-color: ${P.accent} !important; color: ${P.accent} !important; background: rgba(60,140,255,0.06) !important; }
        @keyframes slideDown { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
        .bookmark-banner { animation: slideDown 0.3s ease forwards; }
        @keyframes slideInLeft { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        .chip-btn { transition: all 0.15s; }
        .chip-btn:hover { border-color: ${P.accent} !important; color: ${P.accent} !important; background: rgba(60,140,255,0.06) !important; }
        @keyframes slideDown { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
        .bookmark-banner { animation: slideDown 0.3s ease forwards; }
        @keyframes slideInLeft { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        .no-scrollbar::-webkit-scrollbar { display: none; }
      `}</style>

      {showTopNavbar && (
        <Navbar
          onAskAI={onNavAskAI}
          hasTranscript={!!transcript}
          credits={credits}
          user={user}
          onSignIn={(tab = 'signin') => { setAuthInitialTab(tab); setShowAuthModal(true); }}
          onSignOut={handleSignOut}
          onDashboard={() => setView('dashboard')}
          onHome={goHome}
          onShowReferralPromo={() => setShowReferralPromo(true)}
          isMobile={isMobile}
          onReportBug={() => setShowBugModal(true)}
        />
      )}

      {showAuthModal && (
        <AuthModal
          onClose={() => setShowAuthModal(false)}
          onAuthSuccess={(u) => setUser(u)}
          initialTab={authInitialTab}
        />
      )}

      {showPasswordReset && (
        <PasswordResetModal onClose={() => setShowPasswordReset(false)} />
      )}

      {showReferralBanner && !user && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 200, width: 'calc(100% - 32px)', maxWidth: 420,
          background: '#fff', borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0,0,0,0.14), 0 2px 8px rgba(0,0,0,0.08)',
          border: '1px solid rgba(60,140,255,0.15)',
          overflow: 'hidden', animation: 'fadeUp 0.35s ease',
        }}>
          <div style={{ height: 4, background: 'linear-gradient(90deg, #3C8CFF, #5B9FFF)' }} />
          <div style={{ padding: '16px 18px 18px', display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{
              flexShrink: 0, width: 40, height: 40, borderRadius: 12,
              background: 'rgba(60,140,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3C8CFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 12 20 22 4 22 4 12"/>
                <rect x="2" y="7" width="20" height="5"/>
                <line x1="12" y1="22" x2="12" y2="7"/>
                <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>
                <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
              </svg>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: '0 0 3px', fontWeight: 700, fontSize: 15, color: '#1a1a2e' }}>
                You've been invited!
              </p>
              <p style={{ margin: '0 0 14px', fontSize: 13, color: '#6b7280', lineHeight: 1.45 }}>
                Sign up now and <strong style={{ color: '#3C8CFF' }}>both you and your friend get +3 free credits</strong> automatically.
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => { setShowReferralBanner(false); setAuthInitialTab('signup'); setShowAuthModal(true); }}
                  style={{
                    flex: 1, padding: '9px 0', borderRadius: 10, border: 'none',
                    background: '#3C8CFF', color: '#fff', fontWeight: 600, fontSize: 13,
                    cursor: 'pointer', transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#1F6BFF'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#3C8CFF'; }}
                >
                  Sign up free →
                </button>
                <button
                  onClick={() => { setShowReferralBanner(false); setAuthInitialTab('signin'); setShowAuthModal(true); }}
                  style={{
                    padding: '9px 14px', borderRadius: 10,
                    border: '1px solid rgba(60,140,255,0.25)', background: 'none',
                    color: '#3C8CFF', fontWeight: 600, fontSize: 13,
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(60,140,255,0.07)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
                >
                  Sign in
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowReferralBanner(false)}
              style={{
                flexShrink: 0, width: 24, height: 24, borderRadius: 6,
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#9ca3af', fontSize: 18, lineHeight: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = '#374151'; }}
              onMouseLeave={e => { e.currentTarget.style.color = '#9ca3af'; }}
              aria-label="Dismiss"
            >×</button>
          </div>
        </div>
      )}

      {showReferralPromo && user && (
        <ReferralPromoModal user={user} onClose={() => setShowReferralPromo(false)} />
      )}

      {showBookmarkBanner && !isMobile && (
        <div className="bookmark-banner" style={{
          position: 'fixed', top: 56, left: 0, right: 0, zIndex: 90,
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          padding: '9px 16px',
          background: P.ink, color: 'rgba(255,255,255,0.92)',
          fontSize: 13,
        }}>
          <span>
            Like this tool? Press{' '}
            <kbd style={{
              display: 'inline-flex', alignItems: 'center',
              padding: '1px 6px', borderRadius: 5,
              background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.2)',
              fontSize: 12, fontFamily: 'inherit', fontWeight: 600, letterSpacing: '0.01em',
            }}>
              {/Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent) ? '⌘ D' : 'Ctrl D'}
            </kbd>
            {' '}to bookmark us and always have quick access to your transcripts!
          </span>
          <button
            onClick={dismissBookmarkBanner}
            style={{
              marginLeft: 8, flexShrink: 0,
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'rgba(255,255,255,0.5)', fontSize: 18, lineHeight: 1, padding: '0 2px',
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = 'white'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'rgba(255,255,255,0.5)'; }}
            aria-label="Dismiss"
          >×</button>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* DASHBOARD VIEW */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {view === 'dashboard' && user && (
        <Dashboard
          user={user}
          credits={credits}
          history={history}
          setHistory={setHistory}
          lang={lang}
          setLang={setLang}
          onBack={() => setView('app')}
          onSignOut={handleSignOut}
          onLoadTranscript={loadFromHistory}
        />
      )}

      <div style={{ minHeight: '100vh', paddingTop: topChromeOffset, background: P.paper, transition: 'padding-top 0.3s ease', display: view === 'dashboard' ? 'none' : 'block' }}>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* LANDING VIEW */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {!transcript && (
          <div style={{ animation: 'fadeUp 0.4s ease', paddingBottom: 64 }}>

            {/* Hero */}
            <div className="hero-grad" style={{
              minHeight: '100vh',
              paddingBottom: isMobile ? 42 : 56,
            }}>

            {/* Wavy animated background canvas */}
            <WavyBackground
              containerClassName="absolute inset-0 h-full"
              backgroundFill="#F6F2EA"
              colors={['rgba(60,140,255,0.55)', 'rgba(100,170,255,0.45)', 'rgba(120,90,255,0.35)', 'rgba(178,194,214,0.6)', 'rgba(60,140,255,0.45)']}
              waveWidth={80}
              waveOpacity={isMobile ? 0.2 : 0.35}
              blur={14}
              speed="slow"
            />

            {/* Force-field gradient mask — fades waves toward center where input card sits */}
            <div aria-hidden="true" style={{
              position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
              background: isMobile
                ? 'radial-gradient(ellipse 86% 62% at 50% 56%, rgba(246,242,234,0.995) 0%, rgba(246,242,234,0.94) 44%, rgba(246,242,234,0.62) 70%, rgba(246,242,234,0.14) 90%, transparent 100%)'
                : 'radial-gradient(ellipse 55% 45% at 50% 58%, rgba(246,242,234,0.96) 0%, rgba(246,242,234,0.72) 38%, rgba(246,242,234,0.18) 62%, transparent 78%)',
            }} />

            {/* Grain texture overlay */}
            <svg aria-hidden="true" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: isMobile ? 0.02 : 0.045, pointerEvents: 'none', zIndex: 1 }}>
              <filter id="hero-grain">
                <feTurbulence type="fractalNoise" baseFrequency="0.68" numOctaves="4" stitchTiles="stitch" />
                <feColorMatrix type="saturate" values="0" />
              </filter>
              <rect width="100%" height="100%" filter="url(#hero-grain)" />
            </svg>

            {/* Ambient cosmos decorations */}
            <div className="hero-cosmos" aria-hidden="true">
              <div className="hero-orb-1" />
              <div className="hero-orb-2" />
              <div className="hero-orb-3" />
              {/* Stars — tiny twinkling dots at the periphery */}
              <span className="hero-star" style={{ top: '9%',  left: '7%',   width: 2, height: 2, opacity: 0.38, animationDuration: '3.8s', animationDelay: '0s'    }} />
              <span className="hero-star" style={{ top: '14%', right: '11%', width: 3, height: 3, opacity: 0.42, animationDuration: '4.8s', animationDelay: '-1s'   }} />
              <span className="hero-star" style={{ top: '34%', left: '4%',   width: 2, height: 2, opacity: 0.30, animationDuration: '3.2s', animationDelay: '-0.5s' }} />
              <span className="hero-star" style={{ top: '58%', left: '10%',  width: 3, height: 3, opacity: 0.35, animationDuration: '5.5s', animationDelay: '-2s'   }} />
              <span className="hero-star" style={{ top: '72%', right: '7%',  width: 2, height: 2, opacity: 0.30, animationDuration: '3.6s', animationDelay: '-1.5s' }} />
              <span className="hero-star" style={{ top: '11%', right: '26%', width: 2, height: 2, opacity: 0.25, animationDuration: '4.4s', animationDelay: '-0.8s' }} />
              <span className="hero-star" style={{ bottom:'18%',left: '24%', width: 3, height: 3, opacity: 0.28, animationDuration: '4.0s', animationDelay: '-1.3s' }} />
              <span className="hero-star" style={{ top: '44%', right: '3%',  width: 2, height: 2, opacity: 0.33, animationDuration: '5.2s', animationDelay: '-2.4s' }} />
            </div>


            <div className="hero-inner" style={{
              maxWidth: 700, margin: '0 auto', padding: isMobile ? '32px 16px 24px' : '72px 24px 40px',
              textAlign: 'center',
            }}>

              <h1 style={{
                fontSize: isMobile ? 'clamp(34px, 11vw, 46px)' : 'clamp(38px, 6.5vw, 62px)', fontWeight: 800, color: P.ink,
                letterSpacing: '-0.045em', lineHeight: 1.06, margin: isMobile ? '0 0 14px' : '0 0 22px',
                textShadow: isMobile ? '0 1px 0 rgba(246,242,234,0.95)' : 'none',
              }}>
                Watch less.<br />
                <span style={{ position: 'relative', display: 'inline-block', color: P.accent }}>
                  Know more.
                  <svg aria-hidden="true" viewBox="0 0 310 18" preserveAspectRatio="none" style={{ position: 'absolute', left: '-5%', bottom: '-10px', width: '110%', height: '14px', pointerEvents: 'none', overflow: 'visible' }}>
                    <path d="M 4 13 C 55 4, 115 17, 175 10 C 235 3, 278 16, 306 11" stroke={P.accent} strokeWidth="4.5" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.9" />
                    <path d="M 4 13 C 55 5, 115 16, 175 10 C 235 4, 278 15, 306 11" stroke={P.accent} strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.4" />
                  </svg>
                </span>
              </h1>
              <p style={{
                fontSize: isMobile ? 15.5 : 16,
                color: isMobile ? 'rgba(29,29,31,0.78)' : P.muted,
                margin: isMobile ? '0 0 32px' : '0 0 40px',
                lineHeight: isMobile ? 1.68 : 1.75,
                maxWidth: 500,
                marginLeft: 'auto',
                marginRight: 'auto',
              }}>
                Paste any video URL or upload your own video/audio file to get a full transcript in seconds, then use AI summaries, flashcards, study guides, and chat.
              </p>

              {/* Input card */}
              <div style={{
                position: 'relative', overflow: 'hidden',
                background: P.surface, border: `1px solid ${P.border}`,
                borderRadius: isMobile ? 20 : 26, boxShadow: '0 12px 56px rgba(29,29,31,0.13)',
                padding: isMobile ? 20 : 28,
                display: 'flex', flexDirection: 'column', gap: isMobile ? 14 : 18,
              }}>
                <BorderBeam size={280} duration={10} colorFrom="rgba(60,140,255,0.7)" colorTo="rgba(123,211,255,0)" borderWidth={1.5} />

                {/* ── URL row ── */}
                {inputMode === 'url' && <div className="mode-panel" style={{ display: 'flex', gap: isMobile ? 10 : 12, flexDirection: isMobile ? 'column' : 'row' }}>
                  <div style={{
                    flex: 1, display: 'flex', alignItems: 'center', gap: 12,
                    padding: isMobile ? '16px 18px' : '20px 22px',
                    background: P.paper, borderRadius: isMobile ? 14 : 16,
                    border: `1px solid ${P.border}`,
                  }}>
                    {(() => {
                      const platform = parseVideoUrl(videoUrl)?.platform;
                      if (platform) return <PlatformIcon platform={platform} />;
                      const flipStyle = {
                        display: 'inline-flex', flexShrink: 0,
                        animation: logoFlip === 'out'
                          ? 'logoFlipOut 0.22s ease-in forwards'
                          : logoFlip === 'in'
                          ? 'logoFlipIn 0.22s ease-out forwards'
                          : 'none',
                      };
                      return <span style={flipStyle}><PlatformIcon platform={activeLogo} /></span>;
                    })()}
                    <input
                      ref={urlInputRef}
                      type="text"
                      value={videoUrl}
                      onChange={e => setVideoUrl(e.target.value)}
                      onFocus={handleInputFocus}
                      onKeyDown={e => e.key === 'Enter' && !loading && getTranscript()}
                      placeholder="Paste a YouTube, TikTok, Loom, Vimeo, Dailymotion URL…"
                      style={{
                        flex: 1, border: 'none', background: 'transparent', outline: 'none',
                        fontSize: isMobile ? 16 : 17, color: P.ink,
                        minHeight: isMobile ? 26 : undefined,
                      }}
                    />
                  </div>
                  {(() => {
                    const _used = credits?.used ?? 0;
                    const _max = credits?.tierMax ?? (user ? CREDITS_MAX : CREDITS_FREE);
                    const outOfCredits = _used >= _max;
                    const btnDisabled = loading || outOfCredits;
                    return (
                      <ShimmerButton
                        onClick={() => { setInputMode('url'); getTranscript(); }}
                        disabled={btnDisabled}
                        background={outOfCredits ? 'rgba(220,38,38,0.1)' : btnDisabled ? 'rgba(60,140,255,0.5)' : P.accent}
                        hoverBackground={outOfCredits ? 'rgba(220,38,38,0.15)' : P.accentHover}
                        style={{
                          flexShrink: 0,
                          padding: isMobile ? '17px 24px' : '0 34px',
                          borderRadius: isMobile ? 14 : 16,
                          color: outOfCredits ? P.error : 'white',
                          fontSize: isMobile ? 16 : 17, fontWeight: 700,
                          cursor: btnDisabled ? 'not-allowed' : 'pointer',
                          gap: 8, whiteSpace: 'nowrap',
                          width: isMobile ? '100%' : undefined,
                          minWidth: isMobile ? undefined : 164,
                          height: isMobile ? undefined : 62,
                        }}
                      >
                        {loading && inputMode === 'url' ? <SpinnerIcon /> : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={outOfCredits ? P.error : 'currentColor'} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polygon points="5 3 19 12 5 21 5 3"/>
                          </svg>
                        )}
                        {loading && inputMode === 'url' ? 'Extracting…' : outOfCredits ? 'No Credits' : 'Extract'}
                      </ShimmerButton>
                    );
                  })()}
                </div>}

                {/* ── Upload zone ── */}
                {inputMode === 'upload' && <div className="mode-panel" style={{ display: 'flex', gap: isMobile ? 10 : 12, flexDirection: isMobile ? 'column' : 'row' }}>
                  {/* Hidden native file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".mp3,.mp4,.m4a,.wav,.webm,.ogg,.opus,.flac,.mpeg,.mpga,.mov,.avi,.mkv,.wmv,.3gp,audio/*,video/*"
                    style={{ display: 'none' }}
                    onChange={e => {
                      const f = e.target.files?.[0];
                      if (f) setUploadFile(f);
                      e.target.value = '';
                    }}
                  />

                  {/* Drop zone */}
                  <div
                    onClick={() => !loading && fileInputRef.current?.click()}
                    onDragOver={e => { e.preventDefault(); setUploadDragOver(true); }}
                    onDragLeave={() => setUploadDragOver(false)}
                    onDrop={e => {
                      e.preventDefault(); setUploadDragOver(false);
                      const f = e.dataTransfer.files?.[0];
                      if (f) setUploadFile(f);
                    }}
                    style={{
                      flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 12,
                      padding: isMobile ? '18px 18px' : '22px 22px',
                      background: uploadDragOver ? 'rgba(60,140,255,0.05)' : P.paper,
                      border: `1.5px dashed ${uploadDragOver ? P.accent : uploadFile ? P.accent : P.border}`,
                      borderRadius: isMobile ? 14 : 16,
                      cursor: loading ? 'default' : 'pointer',
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                  >
                    {uploadFile ? (
                      <>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={P.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                          <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
                        </svg>
                        <span style={{ flex: 1, fontSize: 14, color: P.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 500 }}>
                          {uploadFile.name}
                        </span>
                        <span style={{ fontSize: 11, color: P.muted, flexShrink: 0 }}>
                          {(uploadFile.size / (1024 * 1024)).toFixed(1)} MB
                        </span>
                        <button
                          onClick={e => { e.stopPropagation(); setUploadFile(null); }}
                          style={{ flexShrink: 0, background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: P.muted, display: 'flex', alignItems: 'center', borderRadius: 6 }}
                          onMouseEnter={e => e.currentTarget.style.color = P.ink}
                          onMouseLeave={e => e.currentTarget.style.color = P.muted}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={P.muted} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                          <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                        </svg>
                        <span style={{ fontSize: 14, color: P.muted }}>
                          {isMobile ? 'Tap to choose a file' : 'Drop a file here, or click to browse'}
                        </span>
                        {!isMobile && (
                          <span style={{ fontSize: 11, color: P.muted, marginLeft: 'auto', flexShrink: 0, opacity: 0.7 }}>
                            mp4 · mov · mp3 · wav · max 500 MB
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  {/* Transcribe button */}
                  {(() => {
                    const _used = credits?.used ?? 0;
                    const _max  = credits?.tierMax ?? (user ? CREDITS_MAX : CREDITS_FREE);
                    const outOfCredits = _used >= _max;
                    const btnDisabled  = loading || outOfCredits || !uploadFile;
                    const btnBg = btnDisabled ? (outOfCredits ? 'rgba(220,38,38,0.1)' : `rgba(60,140,255,${uploadFile ? '0.5' : '0.28'})`) : P.accent;
                    return (
                      <button
                        onClick={() => { setInputMode('upload'); getTranscriptFromUpload(uploadFile); }}
                        disabled={btnDisabled}
                        style={{
                          flexShrink: 0, padding: isMobile ? '17px 24px' : '0 30px',
                          borderRadius: isMobile ? 14 : 16, border: 'none',
                          background: btnBg, color: outOfCredits ? P.error : 'white',
                          fontSize: isMobile ? 16 : 17, fontWeight: 700,
                          cursor: btnDisabled ? 'default' : 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                          whiteSpace: 'nowrap', transition: 'background 0.15s',
                          width: isMobile ? '100%' : undefined, minWidth: isMobile ? undefined : 164,
                          height: isMobile ? undefined : 62,
                        }}
                        onMouseEnter={e => { if (!btnDisabled) e.currentTarget.style.background = P.accentHover; }}
                        onMouseLeave={e => { e.currentTarget.style.background = btnBg; }}
                      >
                        {loading && inputMode === 'upload' ? <SpinnerIcon /> : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                            stroke={outOfCredits ? P.error : 'currentColor'}
                            strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                          </svg>
                        )}
                        {loading && inputMode === 'upload' ? 'Transcribing…' : outOfCredits ? 'No Credits' : 'Transcribe'}
                      </button>
                    );
                  })()}
                </div>}

                {/* Mobile format hint */}
                {inputMode === 'upload' && isMobile && (
                  <p style={{ margin: 0, fontSize: 11, color: P.muted, textAlign: 'center', opacity: 0.8 }}>
                    mp4 · mov · mp3 · wav · m4a · max 500 MB
                  </p>
                )}

                {/* ── Segmented toggle ── */}
                <div style={{
                  display: 'flex', width: '100%',
                  background: P.paper, border: `1px solid ${P.border}`,
                  borderRadius: 10, padding: 3, gap: 3,
                }}>
                  {[
                    {
                      id: 'url',
                      icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
                      label: 'Paste URL',
                    },
                    {
                      id: 'upload',
                      icon: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
                      label: isMobile ? 'Upload' : 'Upload File',
                    },
                  ].map(({ id, icon, label }) => {
                    const active = inputMode === id;
                    return (
                      <button
                        key={id}
                        onClick={() => setInputMode(id)}
                        style={{
                          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          gap: 6, padding: '8px 12px',
                          borderRadius: 8, border: 'none',
                          background: active ? P.accent : 'transparent',
                          boxShadow: active ? '0 4px 12px rgba(60,140,255,0.28), inset 0 0 0 1px rgba(255,255,255,0.28)' : 'none',
                          color: active ? 'white' : P.muted,
                          fontSize: isMobile ? 13 : 13.5, fontWeight: active ? 700 : 600,
                          cursor: 'pointer', transition: 'background 0.16s, color 0.16s, box-shadow 0.16s, transform 0.16s',
                          transform: active ? 'translateY(-1px)' : 'none',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(29,29,31,0.04)'; }}
                        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                      >
                        {icon}{label}
                      </button>
                    );
                  })}
                </div>

              </div>

              {/* Credits exhausted banner */}
              {(() => {
                const _used = credits?.used ?? 0;
                const _max = credits?.tierMax ?? (user ? CREDITS_MAX : CREDITS_FREE);
                if (_used < _max) return null;
                const daysLeft = Math.max(0, Math.ceil(((credits?.resetAt ?? Date.now()) - Date.now()) / 86400000));
                const resetLabel = daysLeft === 0 ? 'less than a day' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''}`;
                return (
                  <div className="fade-up" style={{
                    marginTop: 12, padding: '12px 16px', borderRadius: 12,
                    background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.18)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={P.error} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                      <span style={{ fontSize: 13, fontWeight: 600, color: P.error }}>
                        No credits left — resets in {resetLabel}
                      </span>
                    </div>
                    {user ? (
                      <button
                        onClick={() => setShowReferralPromo(true)}
                        style={{
                          fontSize: 12, fontWeight: 700, padding: '5px 12px', borderRadius: 8,
                          background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.2)',
                          color: P.error, cursor: 'pointer', whiteSpace: 'nowrap',
                        }}
                      >
                        Get +3 credits — invite a friend
                      </button>
                    ) : (
                      <span style={{ fontSize: 12, color: P.muted, whiteSpace: 'nowrap' }}>
                        For more credits,{' '}
                        <button
                          onClick={() => { setAuthInitialTab('signup'); setShowAuthModal(true); }}
                          style={{
                            background: 'none', border: 'none', padding: 0,
                            fontSize: 12, fontWeight: 700, color: P.accent,
                            cursor: 'pointer', textDecoration: 'underline',
                          }}
                        >
                          create an account
                        </button>
                      </span>
                    )}
                  </div>
                );
              })()}

              {/* Loading */}
              {loading && (
                <div className="fade-up" style={{ marginTop: 18 }}>
                  {/* Segmented pill progress bar */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 5 }}>

                    {/* Left anchor pill — animated ScribeSnap wave */}
                    <div style={{
                      width: isMobile ? 48 : 58, height: 30, borderRadius: 999, flexShrink: 0,
                      background: 'rgba(60,140,255,0.1)',
                      border: '1px solid rgba(60,140,255,0.22)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      overflow: 'hidden',
                    }}>
                      <ScribeSnapWaveLoader width={isMobile ? 34 : 42} />
                    </div>

                    {/* 5 segment pills — filled by loadingPercent */}
                    {Array.from({ length: 5 }).map((_, i) => {
                      const segStart = i * 20;
                      const segEnd   = (i + 1) * 20;
                      const isComplete = loadingPercent >= segEnd;
                      const isActive   = loadingPercent > segStart && !isComplete;
                      const fillPct    = isActive ? ((loadingPercent - segStart) / 20) * 100 : 0;
                      return (
                        <div key={i} style={{
                          flex: 1, height: 30, borderRadius: 999, overflow: 'hidden', position: 'relative',
                          background: isComplete ? `linear-gradient(90deg, ${P.accent}, #1f6bff)` : 'rgba(29,29,31,0.08)',
                          transition: 'background 0.3s ease',
                        }}>
                          {isActive && (
                            <div style={{
                              position: 'absolute', left: 0, top: 0, height: '100%',
                              width: `${fillPct}%`,
                              background: `linear-gradient(90deg, #7bd3ff, ${P.accent})`,
                              borderRadius: 999,
                              transition: 'width 0.55s cubic-bezier(0.4,0,0.2,1)',
                            }} />
                          )}
                          {/* shimmer sweep on active segment */}
                          {isActive && (
                            <div style={{
                              position: 'absolute', inset: 0,
                              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent)',
                              animation: 'shimmer 1.6s infinite',
                              pointerEvents: 'none',
                            }} />
                          )}
                        </div>
                      );
                    })}

                    {/* Right anchor pill — percent */}
                    <div style={{
                      width: isMobile ? 42 : 50, height: 30, borderRadius: 999, flexShrink: 0,
                      background: loadingPercent >= 100 ? `linear-gradient(90deg, ${P.accent}, #1f6bff)` : 'rgba(29,29,31,0.06)',
                      border: loadingPercent >= 100 ? 'none' : `1px solid ${P.border}`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      transition: 'all 0.3s ease',
                    }}>
                      <span style={{
                        fontSize: 11, fontWeight: 700, letterSpacing: '0.02em',
                        color: loadingPercent >= 100 ? '#fff' : P.muted,
                      }}>
                        {loadingPercent > 0 ? `${loadingPercent}%` : '···'}
                      </span>
                    </div>
                  </div>

                  {/* Status message */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 9 }}>
                    <span style={{ fontSize: 12, color: P.muted }}>{loadingMsg || 'Fetching transcript'}</span>
                    <span style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                      {[0, 1, 2].map(i => (
                        <span key={i} style={{ width: 3, height: 3, borderRadius: '50%', background: P.muted, display: 'inline-block', animation: `dot-flicker 1.2s ease-in-out ${i * 0.2}s infinite` }} />
                      ))}
                    </span>
                  </div>
                </div>
              )}

                            {/* Error */}
              {error && (
                <div className="fade-up" style={{
                  marginTop: 14, padding: '11px 14px', textAlign: 'left',
                  background: 'rgba(180,35,24,0.06)', border: `1px solid rgba(180,35,24,0.2)`,
                  borderRadius: 10, fontSize: 13, color: P.error,
                  display: 'flex', gap: 10, alignItems: 'flex-start',
                }}>
                  <svg style={{ flexShrink: 0, marginTop: 1 }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  <span style={{ flex: 1 }}>{error}</span>
                  <button onClick={() => inputMode === 'upload' ? getTranscriptFromUpload(uploadFile) : getTranscript()} style={{ flexShrink: 0, border: `1px solid rgba(180,35,24,0.25)`, background: 'white', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600, color: P.error, cursor: 'pointer' }}>Retry</button>
                </div>
              )}

              {/* Supported formats hint */}
              <div style={{
                marginTop: 14,
                display: 'flex',
                flexWrap: 'nowrap',
                gap: isMobile ? 6 : 8,
                justifyContent: 'center',
                alignItems: 'center',
                whiteSpace: 'nowrap',
                overflowX: isMobile ? 'hidden' : 'auto',
              }}>
                {(isMobile
                  ? (windowWidth < 390
                    ? ['Your own uploads', 'AI chat']
                    : ['Your own uploads', 'AI chat', 'Flashcards'])
                  : ['Instant extraction', 'Your own uploads', 'AI summaries', 'Flashcards', 'Study guides', 'AI chat']
                ).map((item, i) => (
                  <span key={item} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: isMobile ? 11 : 12, color: P.muted, flexShrink: 0 }}>
                    {i > 0 && <span style={{ opacity: 0.3, marginRight: 2 }}>·</span>}
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: P.border, display: 'inline-block', flexShrink: 0 }} />
                    {item}
                  </span>
                ))}
              </div>

              {/* Trusted by Silicon Valley — styled social proof */}
              <div style={{
                marginTop: isMobile ? 20 : 42,
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? 9 : 14,
              }}>
                {/* Avatar stack — real photos */}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {['/avatar1.jpg','/avatar2.jpg','/avatar3.jpg','/avatar4.jpg','/avatar5.jpg'].map((src, i) => (
                    <img key={i} src={src} alt="" style={{
                      width: isMobile ? 30 : 'clamp(34px, 2.5vw, 38px)',
                      height: isMobile ? 30 : 'clamp(34px, 2.5vw, 38px)',
                      borderRadius: '50%',
                      border: `2.5px solid ${P.paper}`,
                      marginLeft: i === 0 ? 0 : (isMobile ? -8 : -10),
                      flexShrink: 0, position: 'relative', zIndex: 5 - i,
                      boxShadow: '0 1px 5px rgba(0,0,0,0.13)',
                      objectFit: 'cover', display: 'block',
                    }} />
                  ))}
                </div>
                <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
                  <div style={{
                    fontSize: isMobile ? 13 : 'clamp(14px, 1.15vw, 16px)',
                    fontWeight: 700,
                    color: P.ink,
                    letterSpacing: '-0.01em',
                    lineHeight: 1.3,
                  }}>
                    Trusted by Silicon Valley teams
                  </div>
                  <div style={{
                    fontSize: isMobile ? 12 : 'clamp(12px, 1vw, 14px)',
                    color: P.muted,
                    marginTop: isMobile ? 2 : 3,
                    lineHeight: 1.35,
                  }}>
                    10,000+ researchers, founders &amp; creators
                  </div>
                </div>
              </div>
            </div>
            </div>{/* end hero-grad */}

            {/* Features */}
            {/* Capability cards */}
            <BlurFade delay={0.05} yOffset={32}>
            <div style={{
              position: 'relative',
              backgroundImage: `linear-gradient(rgba(60,80,160,0.045) 1px, transparent 1px), linear-gradient(90deg, rgba(60,80,160,0.045) 1px, transparent 1px)`,
              backgroundSize: '40px 40px',
            }}>
            {/* Fade from hero into section */}
            <div aria-hidden="true" style={{
              position: 'absolute', top: 0, left: 0, right: 0, height: 100,
              background: `linear-gradient(to bottom, ${P.paper}, transparent)`,
              pointerEvents: 'none', zIndex: 1,
            }} />
            <div style={{
              position: 'relative', zIndex: 2,
              maxWidth: 1500, margin: '0 auto', padding: isMobile ? '52px 16px 44px' : '160px 36px 68px',
            }}>
              <div style={{ textAlign: 'center', marginBottom: isMobile ? 32 : 52 }}>
                <div style={{
                  display: 'inline-block',
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.11em', textTransform: 'uppercase',
                  color: P.accent, marginBottom: 18,
                  padding: '5px 14px', borderRadius: 999,
                  background: 'rgba(60,140,255,0.07)', border: '1px solid rgba(60,140,255,0.15)',
                }}>
                  What you get
                </div>
                <h2 style={{ margin: 0, letterSpacing: '-0.03em', lineHeight: 1.1 }}>
                  <span style={{
                    display: 'block',
                    fontSize: isMobile ? 'clamp(18px, 5vw, 22px)' : 'clamp(20px, 2.2vw, 26px)',
                    fontWeight: 400, color: P.muted, letterSpacing: '-0.01em',
                  }}>One transcript.</span>
                  <span style={{
                    display: 'block',
                    fontSize: isMobile ? 'clamp(30px, 9vw, 40px)' : 'clamp(38px, 4.5vw, 54px)',
                    fontWeight: 900, letterSpacing: '-0.04em',
                    background: 'linear-gradient(135deg, #3C8CFF 0%, #7B5FFF 100%)',
                    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}>Every workflow.</span>
                </h2>
                <p style={{ margin: '18px auto 0', fontSize: 15, color: P.muted, lineHeight: 1.7, maxWidth: 400 }}>
                  Extract once, then let AI branch it into anything you need — in seconds.
                </p>
              </div>
              <div className="feature-workflow">
                <div className="feature-flow-head">
                  <span className="feature-flow-node input">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{opacity:0.55,flexShrink:0}}>
                      <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/>
                      <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/>
                    </svg>
                    Add Video
                  </span>
                  <span className="feature-flow-arrow" aria-hidden="true">↓</span>
                  <span className="feature-flow-node transcript">
                    <span className="feature-flow-transcript-preview">
                      <span className="feature-flow-transcript-title">Transcript</span>
                      <span className="feature-mock-ts-row">
                        <span className="feature-mock-ts-badge">0:12</span>
                        <span className="feature-mock-line shimmer" style={{ flex: 1, animationDelay: '0s' }} />
                      </span>
                      <span className="feature-mock-ts-row">
                        <span className="feature-mock-ts-badge">0:38</span>
                        <span className="feature-mock-line shimmer" style={{ flex: 1, animationDelay: '0.22s' }} />
                      </span>
                      <span className="feature-mock-ts-row">
                        <span className="feature-mock-ts-badge">1:04</span>
                        <span className="feature-mock-line shimmer" style={{ flex: 1, animationDelay: '0.44s' }} />
                      </span>
                    </span>
                  </span>
                </div>
                <div className="feature-flow-branches">
                {[
                  {
                    label: 'AI Summaries',
                    desc: 'Create bullet point summaries and chapter breakdowns on demand from any transcript.',
                    meta: '',
                    tags: [],
                    accent: '#8A6640',
                    gradient: 'linear-gradient(100deg, #D4BC98 0%, #B09070 55%, #8A6640 100%)',
                    bg: 'rgba(138,102,64,0.08)',
                    panel: 'rgba(138,102,64,0.06)',
                    panelBorder: 'rgba(138,102,64,0.15)',
                    border: 'rgba(138,102,64,0.16)',
                    pillBg: 'rgba(138,102,64,0.10)',
                    pillBorder: 'rgba(138,102,64,0.18)',
                    glow: 'rgba(180,140,90,0.22)',
                    type: 'summaries',
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="7" y1="7" x2="17" y2="7"/><line x1="7" y1="12" x2="17" y2="12"/><line x1="7" y1="17" x2="13" y2="17"/><circle cx="18" cy="17" r="2"/>
                      </svg>
                    ),
                  },
                  {
                    label: 'Flash Cards',
                    desc: 'Generate smart Q&A flash cards with flip mode for active recall practice.',
                    meta: '',
                    tags: [],
                    accent: '#3A7A6A',
                    gradient: 'linear-gradient(100deg, #8EC8BC 0%, #5AA090 55%, #3A7A6A 100%)',
                    bg: 'rgba(58,122,106,0.08)',
                    panel: 'rgba(58,122,106,0.06)',
                    panelBorder: 'rgba(58,122,106,0.15)',
                    border: 'rgba(58,122,106,0.16)',
                    pillBg: 'rgba(58,122,106,0.10)',
                    pillBorder: 'rgba(58,122,106,0.18)',
                    glow: 'rgba(58,122,106,0.22)',
                    type: 'flashcards',
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="4" y="6" width="16" height="12" rx="2"/><path d="M9 10h6"/><path d="M8 14h8"/>
                      </svg>
                    ),
                  },
                  {
                    label: 'Study Guide',
                    desc: 'Create a structured study guide with overview, objectives, concepts, and review prompts.',
                    meta: '',
                    tags: [],
                    accent: '#2E6F86',
                    gradient: 'linear-gradient(100deg, #92C2D3 0%, #5A98AE 55%, #2E6F86 100%)',
                    bg: 'rgba(46,111,134,0.08)',
                    panel: 'rgba(46,111,134,0.06)',
                    panelBorder: 'rgba(46,111,134,0.15)',
                    border: 'rgba(46,111,134,0.16)',
                    pillBg: 'rgba(46,111,134,0.10)',
                    pillBorder: 'rgba(46,111,134,0.18)',
                    glow: 'rgba(46,111,134,0.22)',
                    type: 'study-guide',
                    icon: (
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
                      </svg>
                    ),
                  },
                  {
                    label: 'AI Chat Q&A',
                    desc: 'Ask transcript-grounded questions, then drill into follow-ups with cited answers.',
                    meta: '',
                    tags: [],
                    accent: '#6B4E90',
                    gradient: 'linear-gradient(100deg, #C0A8DC 0%, #9070BC 55%, #6B4E90 100%)',
                    bg: 'rgba(107,78,144,0.08)',
                    panel: 'rgba(107,78,144,0.06)',
                    panelBorder: 'rgba(107,78,144,0.15)',
                    border: 'rgba(107,78,144,0.16)',
                    pillBg: 'rgba(107,78,144,0.10)',
                    pillBorder: 'rgba(107,78,144,0.18)',
                    glow: 'rgba(107,78,144,0.22)',
                    type: 'evidence',
                    icon: (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M8 7h12"/><path d="M8 12h12"/><path d="M8 17h8"/><circle cx="4" cy="7" r="1"/><circle cx="4" cy="12" r="1"/><circle cx="4" cy="17" r="1"/>
                      </svg>
                    ),
                  },
                  {
                    label: 'Academic',
                    desc: 'Surface references, extract claims, and auto-build a glossary from transcript content.',
                    meta: '',
                    tags: [],
                    accent: '#365A8B',
                    gradient: 'linear-gradient(100deg, #A9C2E0 0%, #6F8FB8 55%, #365A8B 100%)',
                    bg: 'rgba(54,90,139,0.08)',
                    panel: 'rgba(54,90,139,0.06)',
                    panelBorder: 'rgba(54,90,139,0.15)',
                    border: 'rgba(54,90,139,0.16)',
                    pillBg: 'rgba(54,90,139,0.10)',
                    pillBorder: 'rgba(54,90,139,0.18)',
                    glow: 'rgba(54,90,139,0.22)',
                    type: 'academic',
                    icon: (
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 6h16"/><path d="M4 12h10"/><path d="M4 18h7"/><circle cx="18" cy="12" r="2"/><circle cx="14" cy="18" r="2"/>
                      </svg>
                    ),
                  },
                ].map((card, cardIndex) => (
                  <div
                    key={card.label}
                    className={`feature-card ${workflowHoverType === card.type ? 'is-hovered' : ''}`}
                    onMouseEnter={() => setWorkflowHoverType(card.type)}
                    onMouseLeave={() => setWorkflowHoverType('')}
                    style={{
                      '--card-index': cardIndex,
                      '--card-accent': card.accent,
                      '--card-rail': card.border,
                      '--card-glow': card.glow,
                      '--card-pill-bg': card.pillBg,
                      '--card-pill-border': card.pillBorder,
                      '--card-gradient': card.gradient,
                      '--card-panel-bg': card.panel,
                      '--card-panel-border': card.panelBorder,
                    }}
                  >
                    <div className="feature-card-top">
                      <span className="feature-card-icon">{card.icon}</span>
                    </div>
                    <h3 className="feature-card-label">{card.label}</h3>
                    <div className="feature-card-visual">
                      <BorderBeam size={220} duration={10} delay={cardIndex * 1.5} colorFrom="rgba(60,140,255,0.5)" colorTo="rgba(123,211,255,0)" borderWidth={1.2} />
                      {card.type === 'transcript' && (
                        <div className="feature-mock-shell">
                          <div className="feature-mock-topbar">
                            <span className="feature-mock-dots">
                              <span className="feature-mock-dot" />
                              <span className="feature-mock-dot" />
                              <span className="feature-mock-dot" />
                            </span>
                            <span className="feature-mock-chip">Transcript</span>
                          </div>
                          <div className="feature-mock-body" style={{ flexDirection: 'column', gap: 5 }}>
                            <div className="feature-mock-url-bar">
                              <span style={{ opacity: 0.45, fontSize: 10 }}>⌕</span>
                              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>youtube.com/watch?v=dQw4w9</span>
                              <span className="feature-mock-cursor" />
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                              <div className="feature-mock-ts-row">
                                <span className="feature-mock-ts-badge">0:12</span>
                                <span className="feature-mock-line shimmer" style={{ flex: 1, animationDelay: '0s' }} />
                              </div>
                              <div className="feature-mock-ts-row">
                                <span className="feature-mock-ts-badge">0:38</span>
                                <span className="feature-mock-line shimmer" style={{ flex: 1, animationDelay: '0.22s' }} />
                              </div>
                              <div className="feature-mock-ts-row">
                                <span className="feature-mock-ts-badge">1:04</span>
                                <span className="feature-mock-line shimmer" style={{ flex: 1, animationDelay: '0.44s' }} />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {card.type === 'summaries' && (
                        <div className="feature-mock-shell">
                          <div className="feature-mock-topbar">
                            <span className="feature-mock-dots">
                              <span className="feature-mock-dot" />
                              <span className="feature-mock-dot" />
                              <span className="feature-mock-dot" />
                            </span>
                            <span className="feature-mock-chip">Summaries</span>
                          </div>
                          <div className="feature-mock-body" style={{ flexDirection: 'column', gap: 5 }}>
                            <span className="feature-mock-title" style={{ width: '66%' }} />
                            <div key={`summary-preview-${summaryDemoIdx}`} className="feature-mock-summary-stack">
                              {summaryPreview.bullets.map((text, idx) => (
                                <div key={`summary-bullet-${idx}`} className={`feature-mock-bullet-row anim-${idx + 1}`}>
                                  <span className="feature-mock-bullet-dot" />
                                  <span className="feature-mock-summary-text">{text}</span>
                                </div>
                              ))}
                            </div>
                            <div className="feature-mock-summary-metrics">
                              {summaryPreview.metrics.map((m) => (
                                <span key={m} className="feature-mock-summary-metric">{m}</span>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                      {card.type === 'flashcards' && (
                        <div className="feature-mock-shell">
                          <div className="feature-mock-topbar">
                            <span className="feature-mock-dots">
                              <span className="feature-mock-dot" />
                              <span className="feature-mock-dot" />
                              <span className="feature-mock-dot" />
                            </span>
                            <span className="feature-mock-chip">Study Mode</span>
                          </div>
                          <div className="feature-mock-body" style={{ flexDirection: 'column', gap: 6 }}>
                            <div className="feature-mock-flip-scene">
                              <div className="feature-mock-flip-card">
                                <div className="feature-mock-flip-front">
                                  <span className="feature-mock-flip-label">Flashcard</span>
                                  <span className="feature-mock-flip-topic">{flashcardPreview.topic}</span>
                                  <span className="feature-mock-flip-text">{flashcardPreview.question}</span>
                                </div>
                                <div className="feature-mock-flip-back">
                                  <span className="feature-mock-flip-label">Answer</span>
                                  <span className="feature-mock-flip-text">{flashcardPreview.answer}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {card.type === 'study-guide' && (
                        <div className="feature-mock-shell">
                          <div className="feature-mock-topbar">
                            <span className="feature-mock-dots">
                              <span className="feature-mock-dot" />
                              <span className="feature-mock-dot" />
                              <span className="feature-mock-dot" />
                            </span>
                            <span className="feature-mock-chip">Study Guide</span>
                          </div>
                          <div className="feature-mock-body" style={{ flexDirection: 'column', gap: 5 }}>
                            <div key={`study-guide-preview-${studyGuideDemoIdx}`} className="feature-mock-guide-stack">
                              <div className="feature-mock-guide-section step-1">
                                <span className="feature-mock-guide-label">Overview</span>
                                <span className="feature-mock-guide-line">{studyGuidePreview.overview}</span>
                              </div>
                              <div className="feature-mock-guide-section step-2">
                                <span className="feature-mock-guide-label">Objectives</span>
                                <span className="feature-mock-guide-line">{studyGuidePreview.objectives[0]}</span>
                                <span className="feature-mock-guide-line subtle">{studyGuidePreview.objectives[1]}</span>
                              </div>
                              <div className="feature-mock-guide-section step-3">
                                <span className="feature-mock-guide-label">Review Prompt</span>
                                <span className="feature-mock-guide-line">{studyGuidePreview.reviewQuestion}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {card.type === 'evidence' && (
                        <div className="feature-mock-shell">
                          <div className="feature-mock-topbar">
                            <span className="feature-mock-dots">
                              <span className="feature-mock-dot" />
                              <span className="feature-mock-dot" />
                              <span className="feature-mock-dot" />
                            </span>
                            <span className="feature-mock-chip">AI Chat Q&amp;A</span>
                          </div>
                          <div className="feature-mock-body" style={{ flexDirection: 'column', gap: 6 }}>
                            <div className="feature-mock-chat-panel">
                              <div className="feature-mock-chat-head">
                                <span className="feature-mock-chat-head-avatar">
                                  <img src="/scribesnap_icon_wave.svg" alt="" />
                                </span>
                                <span className="feature-mock-chat-head-lines">
                                  <span className="feature-mock-chat-head-title">Transcript-grounded Q&amp;A</span>
                                  <span className="feature-mock-chat-head-sub">Ask, cite, and jump to source moments</span>
                                </span>
                              </div>
                              <div key={`chat-preview-${chatDemoIdx}`} className="feature-mock-chat-stream">
                                <div className="feature-mock-chat-row user">
                                  <span className="feature-mock-chat-message user feature-mock-chat-turn turn-1">
                                    {chatPreview.questionOne}
                                  </span>
                                </div>
                                <div className="feature-mock-chat-row ai">
                                  <span className="feature-mock-chat-avatar">
                                    <img src="/scribesnap_icon_wave.svg" alt="" />
                                  </span>
                                  <span className="feature-mock-chat-message ai feature-mock-chat-turn turn-2">
                                    <span>{chatPreview.answerOne}</span>
                                    <span className="feature-mock-chat-source">
                                      Source
                                      <button type="button" className="feature-mock-chat-timestamp" aria-label={`Jump to ${chatPreview.timestampOne}`}>
                                        {chatPreview.timestampOne}
                                      </button>
                                    </span>
                                  </span>
                                </div>
                                <div className="feature-mock-chat-row user">
                                  <span className="feature-mock-chat-message user feature-mock-chat-turn turn-3">
                                    {chatPreview.questionTwo}
                                  </span>
                                </div>
                                <div className="feature-mock-chat-row ai">
                                  <span className="feature-mock-chat-avatar">
                                    <img src="/scribesnap_icon_wave.svg" alt="" />
                                  </span>
                                  <span className="feature-mock-chat-message ai feature-mock-chat-turn turn-4">
                                    <span>{chatPreview.answerTwo}</span>
                                    <span className="feature-mock-chat-source">
                                      Source
                                      <button type="button" className="feature-mock-chat-timestamp" aria-label={`Jump to ${chatPreview.timestampTwo}`}>
                                        {chatPreview.timestampTwo}
                                      </button>
                                    </span>
                                  </span>
                                </div>
                              </div>
                              <div className="feature-mock-chat-composer">
                                <span className="feature-mock-chat-head-avatar">
                                  <img src="/scribesnap_icon_wave.svg" alt="" />
                                </span>
                                <span className="feature-mock-chat-input-text">Ask a follow-up about this clip…</span>
                                <span className="feature-mock-chat-send" />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {card.type === 'academic' && (
                        <div className="feature-mock-shell">
                          <div className="feature-mock-topbar">
                            <span className="feature-mock-dots">
                              <span className="feature-mock-dot" />
                              <span className="feature-mock-dot" />
                              <span className="feature-mock-dot" />
                            </span>
                            <span className="feature-mock-chip">Academic</span>
                          </div>
                          <div className="feature-mock-body" style={{ flexDirection: 'column', gap: 5 }}>
                            <div key={`academic-preview-${academicDemoIdx}`} className="feature-mock-academic-stack">
                              <div className="feature-mock-academic-section step-1">
                                <span className="feature-mock-academic-label">References</span>
                                <span className="feature-mock-academic-line">{academicPreview.reference}</span>
                              </div>
                              <div className="feature-mock-academic-section step-2">
                                <span className="feature-mock-academic-label">Claim</span>
                                <span className="feature-mock-academic-line">{academicPreview.claim}</span>
                              </div>
                              <div className="feature-mock-academic-section step-3">
                                <span className="feature-mock-academic-label">Glossary</span>
                                <div className="feature-mock-academic-tags">
                                  {academicPreview.glossary.map((term, idx) => (
                                    <span key={term} className={`feature-mock-academic-tag term-${idx + 1}`}>{term}</span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="feature-card-desc">{card.desc}</p>
                    {card.tags.length > 0 && (
                      <div className="feature-card-tags">
                        {card.tags.map(tag => (
                          <span key={`${card.label}-${tag}`} className="feature-card-tag">{tag}</span>
                        ))}
                      </div>
                    )}
                    {card.meta && <div className="feature-card-meta">{card.meta}</div>}
                  </div>
                ))}
              </div>
            </div>
            </div>
            </div>
            </BlurFade>
            {/* Testimonials */}
            <BlurFade delay={0.15} yOffset={20}>
            <div style={{ maxWidth: 820, margin: '0 auto', padding: isMobile ? '0 16px 48px' : '0 24px 64px' }}>
              <div style={{ textAlign: 'center', marginBottom: 28 }}>
                <div style={{
                  display: 'inline-block',
                  fontSize: 11, fontWeight: 700, letterSpacing: '0.11em', textTransform: 'uppercase',
                  color: P.accent, marginBottom: 18,
                  padding: '5px 14px', borderRadius: 999,
                  background: 'rgba(60,140,255,0.07)', border: '1px solid rgba(60,140,255,0.15)',
                }}>What people say</div>
                <h2 style={{ fontSize: isMobile ? 22 : 26, fontWeight: 800, color: P.ink, letterSpacing: '-0.03em', margin: 0 }}>Loved by Researchers &amp; Builders</h2>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
                {/* Founder */}
                <div style={{ position: 'relative', overflow: 'hidden', background: P.surface, border: `1px solid ${P.border}`, borderRadius: 18, padding: '26px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <BorderBeam size={300} duration={12} colorFrom="rgba(60,140,255,0.45)" colorTo="rgba(123,211,255,0)" borderWidth={1.2} />
                  <div style={{ display: 'flex', gap: 3 }}>
                    {[0,1,2,3,4].map(i => (
                      <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill={P.accent} stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                    ))}
                  </div>
                  <p style={{ fontSize: 14.5, color: P.ink, lineHeight: 1.75, margin: 0, fontStyle: 'italic' }}>
                    "This is the tool I didn't know I needed. We use it every week to pull takeaways from founder interviews and VC talks — it cut our research time in half."
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 'auto' }}>
                    <img src="/review-founder.jpg" alt="" style={{ width: 46, height: 46, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${P.border}`, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: P.ink }}>Startup Founder</div>
                      <div style={{ fontSize: 11.5, color: P.muted, marginTop: 2 }}>Series A · San Francisco</div>
                    </div>
                  </div>
                </div>
                {/* PhD Student */}
                <div style={{ position: 'relative', overflow: 'hidden', background: P.surface, border: `1px solid ${P.border}`, borderRadius: 18, padding: '26px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <BorderBeam size={300} duration={12} delay={4} colorFrom="rgba(60,140,255,0.45)" colorTo="rgba(123,211,255,0)" borderWidth={1.2} />
                  <div style={{ display: 'flex', gap: 3 }}>
                    {[0,1,2,3,4].map(i => (
                      <svg key={i} width="14" height="14" viewBox="0 0 24 24" fill={P.accent} stroke="none"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                    ))}
                  </div>
                  <p style={{ fontSize: 14.5, color: P.ink, lineHeight: 1.75, margin: 0, fontStyle: 'italic' }}>
                    "I spend hours watching academic lectures and conference talks. ScribeSnap gives me the full transcript plus a study guide in seconds. The flashcard feature alone is worth it."
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 'auto' }}>
                    <img src="/review-phd.jpg" alt="" style={{ width: 46, height: 46, borderRadius: '50%', objectFit: 'cover', border: `2px solid ${P.border}`, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: P.ink }}>PhD Student</div>
                      <div style={{ fontSize: 11.5, color: P.muted, marginTop: 2 }}>Neuroscience · Stanford</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </BlurFade>

            {/* Recent transcripts */}
            {history.length > 0 && (
            <BlurFade delay={0.2} yOffset={20}>
              <div style={{ maxWidth: 960, margin: '0 auto', padding: '0 24px 56px' }}>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: P.ink, letterSpacing: '-0.01em' }}>Recent transcripts</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: P.muted, background: 'rgba(29,29,31,0.06)', padding: '2px 8px', borderRadius: 999, border: `1px solid ${P.border}` }}>{history.length}</span>
                  </div>
                  <button
                    onClick={() => { setHistory([]); localStorage.removeItem('yte_history'); }}
                    style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, color: P.muted, fontWeight: 500, padding: '4px 8px', borderRadius: 6, transition: 'all 0.12s', fontFamily: 'inherit' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(180,35,24,0.07)'; e.currentTarget.style.color = P.error; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = P.muted; }}
                  >
                    Clear all
                  </button>
                </div>
                {/* Thumbnail-first card grid */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: isMobile ? 10 : 14 }}>
                  {history.map((h) => {
                    const wc = h.transcript ? h.transcript.trim().split(/\s+/).length : 0;
                    const displayTitle = h.title || h.id;
                    const displayChannel = h.channel || (h.platform === 'vimeo' ? 'Vimeo' : 'YouTube');
                    const platformTag = h.source === 'whisper' ? 'AI' : (h.platform || 'youtube').slice(0, 3).toUpperCase();
                    return (
                      <div key={h.id} className="rc-card" onClick={() => loadFromHistory(h)} style={{ borderRadius: 12, border: `1px solid ${P.border}`, background: P.surface, cursor: 'pointer', overflow: 'hidden', transition: 'all 0.18s', position: 'relative' }}>
                        <BorderBeam size={180} duration={8} colorFrom="rgba(60,140,255,0.45)" colorTo="rgba(123,211,255,0)" borderWidth={1} />
                        {/* Thumbnail */}
                        <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', background: 'rgba(29,29,31,0.06)', overflow: 'hidden' }}>
                          {h.thumbnail && (
                            <img src={h.thumbnail} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                              onError={e => { e.target.style.display = 'none'; }} />
                          )}
                          <div style={{ position: 'absolute', bottom: 6, left: 6, background: 'rgba(29,29,31,0.78)', color: 'white', fontSize: 8, fontWeight: 700, fontFamily: 'monospace', padding: '2px 5px', borderRadius: 4 }}>
                            {platformTag}
                          </div>
                          <div className="rc-overlay" style={{ position: 'absolute', inset: 0, background: 'rgba(29,29,31,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.18s' }}>
                            <div style={{ background: 'white', color: P.ink, fontSize: 11, fontWeight: 700, padding: '6px 14px', borderRadius: 999 }}>Open</div>
                          </div>
                        </div>
                        {/* Info */}
                        <div style={{ padding: '10px 12px 12px' }}>
                          <div style={{ fontSize: 12, fontWeight: 600, color: P.ink, lineHeight: 1.4, marginBottom: 5, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                            {displayTitle}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ fontSize: 10, color: P.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                              {displayChannel} · {wc.toLocaleString()}w
                            </div>
                            <div style={{ fontSize: 10, color: P.muted, flexShrink: 0, marginLeft: 6 }}>{timeAgo(h.date)}</div>
                          </div>
                        </div>
                        {/* Delete — reveals on card hover */}
                        <button
                          className="rc-delete"
                          onClick={(e) => deleteFromHistory(h.id, e)}
                          style={{ position: 'absolute', top: 6, right: 6, width: 26, height: 26, borderRadius: 999, border: 'none', background: 'rgba(29,29,31,0.7)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', opacity: 0, transition: 'opacity 0.15s, background 0.12s', fontFamily: 'inherit' }}
                          onMouseEnter={e => { e.stopPropagation(); e.currentTarget.style.background = 'rgba(180,35,24,0.9)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(29,29,31,0.7)'; }}
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </BlurFade>
            )}
          </div>
        )}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* TRANSCRIPT VIEW — app shell 3-column layout                     */}
        {/* ══════════════════════════════════════════���════════════════════════ */}
        {transcript && (
          <>
          <div className="fade-up" style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : isTablet ? '1fr 300px' : '64px 1fr 360px',
            gridTemplateRows: '1fr',
            height: isMobile ? `calc(100dvh - (${mobileBottomNavInsetExpr}))` : 'calc(100dvh - 56px)',
            minHeight: isMobile ? `calc(100vh - (${mobileBottomNavInsetExpr}))` : 'calc(100vh - 56px)',
            overflow: 'hidden',
          }}>

            {/* ── LEFT SIDEBAR — col 1 (desktop only, hover-to-expand rail) ─── */}
            {isDesktop && (
              <div className="left-rail" style={{ gridColumn: 1, gridRow: 1 }}>
                <div className="left-rail-inner" style={{ background: P.paper, borderRight: `1px solid ${P.border}` }}>

                  {/* ── COLLAPSED: icon rail ── */}
                  <div className="lr-collapsed" style={{ padding: '14px 0 12px', gap: 4 }}>
                    {[
                      { title: 'AI Summaries', color: P.accent,
                        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
                        onClick: summary ? () => setActiveTab('summary') : summarize, active: !!summary, loading: summarizing },
                      { title: 'Flash Cards', color: P.warning,
                        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
                        onClick: flashcards.length > 0 ? () => setActiveTab('flashcards') : generateFlashcards, active: flashcards.length > 0, loading: flashcardsLoading },
                      { title: 'Study Guide', color: P.success,
                        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>,
                        onClick: studyGuide && !studyGuide._error ? () => setActiveTab('study-guide') : generateStudyGuide, active: !!studyGuide && !studyGuide._error, loading: studyGuideLoading },
                      { title: 'Academic', color: '#7C3AED',
                        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>,
                        onClick: academicInsights && !academicInsights._error ? () => setActiveTab('academic') : generateAcademicInsights, active: !!academicInsights && !academicInsights._error, loading: academicInsightsLoading },
                      { title: 'Discover', color: '#C2410C',
                        icon: <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>,
                        onClick: discover && !discover._error ? () => setActiveTab('discover') : generateDiscover, active: !!discover && !discover._error, loading: discoverLoading },
                    ].map(item => (
                      <button key={item.title} onClick={item.loading ? undefined : item.onClick} title={item.title} style={{
                        width: 40, height: 40, borderRadius: 11, border: 'none', cursor: item.loading ? 'default' : 'pointer',
                        background: item.active ? item.color + '22' : 'transparent',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: item.active ? item.color : P.muted, transition: 'background 0.12s', flexShrink: 0,
                      }}
                        onMouseEnter={e => { if (!item.active) e.currentTarget.style.background = P.surface; }}
                        onMouseLeave={e => { if (!item.active) e.currentTarget.style.background = 'transparent'; }}
                      >
                        {item.loading ? <SpinnerIcon size={14} /> : item.icon}
                      </button>
                    ))}
                    <div style={{ height: 1, background: P.border, width: 32, margin: '4px 0', flexShrink: 0 }} />
                    <button title="Recent" style={{ width: 40, height: 40, borderRadius: 11, border: 'none', cursor: 'pointer', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.muted, transition: 'background 0.12s', position: 'relative', flexShrink: 0 }}
                      onMouseEnter={e => { e.currentTarget.style.background = P.surface; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      {history.length > 0 && <span style={{ position: 'absolute', top: 7, right: 7, width: 6, height: 6, borderRadius: '50%', background: P.accent }} />}
                    </button>
                    <div style={{ flex: 1 }} />
                    <button onClick={startNewSearch} title="New search" style={{ width: 40, height: 40, borderRadius: 11, border: 'none', cursor: 'pointer', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.muted, flexShrink: 0, marginBottom: 8 }}
                      onMouseEnter={e => { e.currentTarget.style.background = P.surface; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                    </button>
                  </div>

                  {/* ── EXPANDED: full sidebar content ── */}
                  <div className="lr-expanded">
                    <div style={{ padding: '18px 18px 6px', flexShrink: 0 }}>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: P.muted, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Insights</div>
                    </div>
                    <div style={{ padding: '0 10px', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
                      {[
                        { title: 'AI Summaries', sub: 'Bullet point summaries', color: P.accent, bg: 'rgba(60,140,255,0.1)',
                          icon: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
                          onClick: summary ? () => setActiveTab('summary') : summarize, active: !!summary, loading: summarizing },
                        { title: 'Flash Cards', sub: 'Q&A cards with flip mode', color: P.warning, bg: 'rgba(180,83,9,0.1)',
                          icon: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
                          onClick: flashcards.length > 0 ? () => setActiveTab('flashcards') : generateFlashcards, active: flashcards.length > 0, loading: flashcardsLoading },
                        { title: 'Study Guide', sub: 'Objectives & review', color: P.success, bg: 'rgba(15,118,110,0.1)',
                          icon: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>,
                          onClick: studyGuide && !studyGuide._error ? () => setActiveTab('study-guide') : generateStudyGuide, active: !!studyGuide && !studyGuide._error, loading: studyGuideLoading },
                          { title: 'Academic', sub: 'References & glossary', color: '#7C3AED', bg: 'rgba(124,58,237,0.1)',
                          icon: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>,
                          onClick: academicInsights && !academicInsights._error ? () => setActiveTab('academic') : generateAcademicInsights, active: !!academicInsights && !academicInsights._error, loading: academicInsightsLoading },
                        { title: 'Discover', sub: 'Related videos & papers', color: '#C2410C', bg: 'rgba(194,65,12,0.1)',
                          icon: <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>,
                          onClick: discover && !discover._error ? () => setActiveTab('discover') : generateDiscover, active: !!discover && !discover._error, loading: discoverLoading },
                      ].map(item => (
                        <div key={item.title} onClick={item.loading ? undefined : item.onClick}
                          style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '13px 10px', borderRadius: 12, cursor: 'pointer', transition: 'background 0.12s', marginBottom: 2 }}
                          onMouseEnter={e => { e.currentTarget.style.background = P.surface; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                        >
                          <div style={{ width: 46, height: 46, borderRadius: 13, background: item.active ? item.bg : item.bg.replace('0.1', '0.06'), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: item.color }}>
                            {item.loading ? <SpinnerIcon size={15} /> : item.icon}
                          </div>
                          <div style={{ minWidth: 0, flex: 1 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, color: item.active ? item.color : P.ink, whiteSpace: 'nowrap' }}>{item.title}</div>
                            <div style={{ fontSize: 11.5, color: P.muted, marginTop: 1, whiteSpace: 'nowrap' }}>{item.sub}</div>
                          </div>
                          {item.active
                            ? <div style={{ width: 7, height: 7, borderRadius: '50%', background: item.color, flexShrink: 0 }} />
                            : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={P.muted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, opacity: 0.35 }}><polyline points="9 18 15 12 9 6"/></svg>
                          }
                        </div>
                      ))}
                      {studyGuide?._error && (
                        <div style={{ margin: '2px 0 6px', padding: '7px 10px', background: 'rgba(180,35,24,0.05)', border: '1px solid rgba(180,35,24,0.2)', borderRadius: 8, fontSize: 11.5, color: P.error }}>
                          Failed to generate study guide.
                        </div>
                      )}
                    </div>

                    {/* ── Discover results — above Recent ── */}
                    {(discover && !discover._error || discoverLoading) && (
                      <div style={{ borderTop: `1px solid ${P.border}`, flexShrink: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 14px 7px' }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#C2410C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>
                          <span style={{ fontSize: 13, fontWeight: 600, color: P.ink }}>Related</span>
                          {discoverLoading && <SpinnerIcon size={10} />}
                          {discover && !discover._error && (
                            <button onClick={() => setDiscover(null)} style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer', color: P.muted, fontSize: 18, lineHeight: 1, padding: '0 2px', fontFamily: 'inherit' }}
                              onMouseEnter={e => e.currentTarget.style.color = P.ink}
                              onMouseLeave={e => e.currentTarget.style.color = P.muted}
                            >×</button>
                          )}
                        </div>
                        {discoverLoading ? (
                          <div style={{ padding: '6px 14px 14px', fontSize: 12, color: P.muted }}>Finding related content…</div>
                        ) : discover && !discover._error ? (
                          <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 8 }}>
                            {discover.videos?.length > 0 && (
                              <>
                                <div style={{ padding: '2px 14px 5px', fontSize: 11.5, fontWeight: 500, color: P.muted }}>Videos</div>
                                {discover.videos.map((v, i) => (
                                  <a key={i} href={`https://www.youtube.com/watch?v=${v.id}`} target="_blank" rel="noopener noreferrer"
                                    style={{ display: 'flex', gap: 9, alignItems: 'center', padding: '6px 14px', textDecoration: 'none', transition: 'background 0.12s', borderRadius: 8 }}
                                    onMouseEnter={e => e.currentTarget.style.background = P.paper}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                  >
                                    {v.thumbnail
                                      ? <img src={v.thumbnail} alt="" style={{ width: 52, height: 30, borderRadius: 5, objectFit: 'cover', flexShrink: 0 }} />
                                      : <div style={{ width: 52, height: 30, borderRadius: 5, background: P.border, flexShrink: 0 }} />
                                    }
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                      <div style={{ fontSize: 12, fontWeight: 600, color: P.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.35 }}>{v.title}</div>
                                      <div style={{ fontSize: 11, color: P.muted, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.channel}</div>
                                    </div>
                                  </a>
                                ))}
                              </>
                            )}
                            {discover.papers?.length > 0 && (
                              <>
                                <div style={{ padding: '10px 14px 5px', fontSize: 11.5, fontWeight: 500, color: P.muted }}>Papers</div>
                                {discover.papers.map((p, i) => (
                                  <div key={i} style={{ padding: '6px 14px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: P.ink, lineHeight: 1.4 }}>{p.title}</div>
                                    <div style={{ fontSize: 11, color: P.muted }}>
                                      {p.authors?.[0]}{p.authors?.length > 1 ? ' et al.' : ''}{p.year ? ` · ${p.year}` : ''}
                                    </div>
                                    <div style={{ display: 'flex', gap: 8, marginTop: 1 }}>
                                      {p.pdfUrl && <a href={p.pdfUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, fontWeight: 600, color: P.accent, textDecoration: 'none' }}>PDF ↗</a>}
                                      {p.doi && <a href={`https://doi.org/${p.doi}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, fontWeight: 500, color: P.muted, textDecoration: 'none' }}>DOI ↗</a>}
                                      {!p.pdfUrl && !p.doi && p.paperId && <a href={`https://www.semanticscholar.org/paper/${p.paperId}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, fontWeight: 500, color: P.muted, textDecoration: 'none' }}>View ↗</a>}
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                            {!discover.videos?.length && !discover.papers?.length && (
                              <div style={{ padding: '6px 14px 10px', fontSize: 12, color: P.muted }}>No related content found.</div>
                            )}
                          </div>
                        ) : null}
                      </div>
                    )}

                    {/* Recent — collapsible */}
                    <div style={{ margin: '8px 10px 0', borderTop: `1px solid ${P.border}`, flexShrink: 0 }}>
                      <button onClick={() => setRecentOpen(v => !v)} style={{
                        display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '10px 10px 8px',
                        border: 'none', background: 'none', cursor: 'pointer', textAlign: 'left',
                      }}>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={P.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                        <span style={{ fontSize: 11, fontWeight: 700, color: P.muted, letterSpacing: '0.07em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Recent</span>
                        {history.length > 0 && <span style={{ fontSize: 10, fontWeight: 700, color: P.muted, background: P.surface, padding: '1px 6px', borderRadius: 99, marginLeft: 2 }}>{history.length}</span>}
                        <div style={{ flex: 1 }} />
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={P.muted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: recentOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.18s', flexShrink: 0 }}><polyline points="6 9 12 15 18 9"/></svg>
                      </button>
                      {recentOpen && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingBottom: 8 }}>
                          {history.length === 0 ? (
                            <div style={{ padding: '8px 10px', fontSize: 12, color: P.muted, textAlign: 'center' }}>No recent transcripts</div>
                          ) : history.slice(0, 6).map((entry) => {
                            const hTitle = entry.title || entry.id;
                            const isActive = entry.id === currentVideoId;
                            return (
                              <button key={entry.id} onClick={() => loadFromHistory(entry)} style={{
                                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                                borderRadius: 8, border: `1px solid ${isActive ? 'rgba(60,140,255,0.18)' : 'transparent'}`,
                                background: isActive ? P.accentLight : 'transparent',
                                cursor: 'pointer', textAlign: 'left', width: '100%', transition: 'background 0.1s',
                              }}
                                onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = 'rgba(29,29,31,0.05)'; e.currentTarget.style.borderColor = P.border; }}}
                                onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; }}}
                              >
                                {entry.thumbnail
                                  ? <img src={entry.thumbnail} alt="" style={{ width: 44, height: 28, objectFit: 'cover', borderRadius: 5, display: 'block', flexShrink: 0 }} onError={e => { e.target.style.display = 'none'; }} />
                                  : <div style={{ width: 44, height: 28, borderRadius: 5, background: P.border, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><PlatformIcon platform={entry.platform || 'youtube'} size={10} /></div>
                                }
                                <div style={{ minWidth: 0, flex: 1 }}>
                                  <div style={{ fontSize: 11.5, fontWeight: 600, color: isActive ? P.accent : P.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{hTitle}</div>
                                  <div style={{ fontSize: 10, color: P.muted }}>{timeAgo(entry.date)}</div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* New search pinned at bottom */}
                    <div style={{ marginTop: 'auto', padding: '12px 12px', borderTop: `1px solid ${P.border}`, flexShrink: 0 }}>
                      <button onClick={startNewSearch} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%',
                        padding: '9px', border: `1px solid ${P.accentHover}`, borderRadius: 9,
                        background: P.accent, cursor: 'pointer', fontSize: 12.5, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap',
                      }}
                        onMouseEnter={e => { e.currentTarget.style.background = P.accentHover; }}
                        onMouseLeave={e => { e.currentTarget.style.background = P.accent; }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                        New search
                      </button>
                    </div>
                  </div>

                </div>
              </div>
            )}

            {/* ── CENTER — col 2 desktop / col 1 tablet+mobile ─────────────────── */}
            <div style={{
              gridColumn: isDesktop ? 2 : 1,
              gridRow: 1,
              display: isMobile ? ((mobilePanel === 'transcript' || mobilePanel === 'discover') ? 'flex' : 'none') : 'flex',
              flexDirection: 'column', overflow: 'hidden', background: P.paper,
              borderRight: !isMobile ? `1px solid ${P.border}` : 'none',
            }}>

              {/* Video player card */}
              {currentVideoId && (
                <div style={{ flexShrink: 0, borderRadius: 16, overflow: 'hidden', border: `1px solid ${P.border}`, background: P.paper }}>
                  {/* Centered 16:9 player — max 391×220, no black bars, paper sides */}
                  <div style={{ display: 'flex', justifyContent: 'center', background: P.paper }}>
                    <div style={{ width: 'min(100%, 411px)', flexShrink: 0, borderRadius: 0, overflow: 'hidden', ...(isMobile ? { maxHeight: 200 } : {}) }}>
                      {currentPlatform === 'vimeo' ? (
                        <iframe
                          ref={playerRef}
                          src={`https://player.vimeo.com/video/${currentVideoId}?api=1`}
                          style={{ width: '100%', aspectRatio: '16/9', border: 'none', display: 'block', ...(isMobile ? { maxHeight: 200 } : {}) }}
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          title="Video player"
                        />
                      ) : currentPlatform === 'youtube' ? (
                        <div ref={ytPlayerDivRef} style={{ width: '100%', aspectRatio: '16/9', display: 'block', ...(isMobile ? { maxHeight: 200 } : {}) }} />
                      ) : currentPlatform === 'tiktok' ? (
                        <iframe
                          src={`https://www.tiktok.com/embed/v2/${currentVideoId}`}
                          style={{ width: '100%', aspectRatio: '9/16', maxHeight: isMobile ? 320 : 480, border: 'none', display: 'block' }}
                          allow="autoplay; encrypted-media; fullscreen"
                          allowFullScreen
                          title="TikTok player"
                        />
                      ) : currentPlatform === 'dailymotion' ? (
                        <iframe
                          src={`https://www.dailymotion.com/embed/video/${currentVideoId}?autoplay=0&ui-logo=0`}
                          style={{ width: '100%', aspectRatio: '16/9', border: 'none', display: 'block', ...(isMobile ? { maxHeight: 200 } : {}) }}
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          title="Dailymotion player"
                        />
                      ) : currentPlatform === 'loom' ? (
                        <iframe
                          src={`https://www.loom.com/embed/${currentVideoId}`}
                          style={{ width: '100%', aspectRatio: '16/9', border: 'none', display: 'block', ...(isMobile ? { maxHeight: 200 } : {}) }}
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          title="Loom player"
                        />
                      ) : currentPlatform === 'upload' ? (
                        // Local uploaded file — no embeddable player
                        <div style={{ width: '100%', aspectRatio: '16/9', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, background: `linear-gradient(135deg, ${P.surface} 0%, rgba(60,140,255,0.06) 100%)` }}>
                          {/* Headphones icon */}
                          <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'rgba(60,140,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid rgba(60,140,255,0.2)' }}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={P.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 18v-6a9 9 0 0 1 18 0v6"/>
                              <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3z"/>
                              <path d="M3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/>
                            </svg>
                          </div>
                          <div style={{ textAlign: 'center', padding: '0 20px' }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: P.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 260 }}>{currentTitle || 'Local file'}</div>
                            <div style={{ fontSize: 11, color: P.muted, marginTop: 3 }}>Local recording · Whisper AI transcription</div>
                          </div>
                        </div>
                      ) : (
                        // Generic platform: show thumbnail + open link
                        <a href={currentVideoId} target="_blank" rel="noopener noreferrer" style={{ display: 'block', position: 'relative', width: '100%', aspectRatio: '16/9', background: '#000', textDecoration: 'none' }}>
                          {currentThumbnail
                            ? <img src={currentThumbnail} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                            : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: P.surface }}><PlatformIcon platform={currentPlatform} size={48} /></div>
                          }
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)' }}>
                            <div style={{ background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 12, fontWeight: 600, padding: '6px 14px', borderRadius: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                              Watch on {currentPlatform.charAt(0).toUpperCase() + currentPlatform.slice(1)}
                            </div>
                          </div>
                        </a>
                      )}
                    </div>
                  </div>
                  {/* Slim meta bar */}
                  <div style={{ padding: isMobile ? '6px 12px' : '9px 14px', display: 'flex', alignItems: 'center', gap: 10, background: '#FFFFFF' }}>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: P.ink, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                        {currentTitle || currentChannel || currentVideoId}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, flexWrap: 'wrap' }}>
                        {currentChannel && <span style={{ fontSize: 11, color: P.muted }}>{currentChannel}</span>}
                        {segments.length > 0 && (
                          <>
                            {currentChannel && <span style={{ fontSize: 11, color: P.border }}>·</span>}
                            <span style={{ fontSize: 11, color: P.muted }}>{formatVideoDuration(segments[segments.length - 1].seconds)}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <button onClick={copyToClipboard} style={{
                        display: 'flex', alignItems: 'center', gap: 5, padding: isMobile ? '8px 12px' : '5px 11px', minHeight: isMobile ? 44 : undefined, borderRadius: 20,
                        border: `1px solid ${P.border}`, background: 'transparent', cursor: 'pointer',
                        fontSize: 12, fontWeight: 600, color: P.muted, transition: 'all 0.15s',
                      }}
                        onMouseEnter={e => { e.currentTarget.style.background = P.accentLight; e.currentTarget.style.color = P.accent; e.currentTarget.style.borderColor = 'rgba(60,140,255,0.3)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = P.muted; e.currentTarget.style.borderColor = P.border; }}>
                        {copied ? <CheckIcon /> : <CopyIcon />} {copied ? 'Copied' : 'Copy'}
                      </button>
                      <button onClick={downloadTxt} style={{
                        display: 'flex', alignItems: 'center', gap: 5, padding: isMobile ? '8px 12px' : '5px 11px', minHeight: isMobile ? 44 : undefined, borderRadius: 20,
                        border: `1px solid ${P.border}`, background: 'transparent', cursor: 'pointer',
                        fontSize: 12, fontWeight: 600, color: P.muted, transition: 'all 0.15s',
                      }}
                        onMouseEnter={e => { e.currentTarget.style.background = P.accentLight; e.currentTarget.style.color = P.accent; e.currentTarget.style.borderColor = 'rgba(60,140,255,0.3)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = P.muted; e.currentTarget.style.borderColor = P.border; }}>
                        <DownloadIcon size={12} /> Download
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Browser-style tab bar — between video and content */}
              <div className="no-scrollbar" style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-end', gap: 2, padding: '6px 10px 0', background: P.paper, borderBottom: `1px solid ${P.border}`, overflowX: isMobile ? 'auto' : 'visible', overflowY: 'hidden', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {isTablet && (
                  <button onClick={() => setHistoryDrawerOpen(v => !v)} style={{
                    display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', marginRight: 4, flexShrink: 0,
                    border: `1px solid ${P.border}`, borderRadius: 7, background: historyDrawerOpen ? P.accentLight : 'transparent',
                    cursor: 'pointer', fontSize: 11, fontWeight: 600, color: historyDrawerOpen ? P.accent : P.muted,
                  }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
                    History
                  </button>
                )}
                {[
                  { key: 'transcript', label: 'Transcript', closeable: false, icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg> },
                  ...(!isMobile ? [{ key: 'editor', label: 'Editor', closeable: false, icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg> }] : []),
                  ...(!isMobile && (flashcards.length > 0 || flashcardsExhausted) ? [{ key: 'flashcards', label: 'Flashcards', closeable: true, onClose: () => { setFlashcards([]); setFlashcardsExhausted(false); setFlashcardsExhaustedReason(''); if (activeTab === 'flashcards') setActiveTab('transcript'); }, icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg> }] : []),
                  ...(!isMobile && summary ? [{ key: 'summary', label: 'Summary', closeable: true, onClose: () => { setSummary(''); setSummarizing(false); if (activeTab === 'summary') setActiveTab('transcript'); }, icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> }] : []),
                  ...(!isMobile && studyGuide && !studyGuide._error ? [{ key: 'study-guide', label: 'Study Guide', closeable: true, onClose: () => { setStudyGuide(null); if (activeTab === 'study-guide') setActiveTab('transcript'); }, icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg> }] : []),
                  ...(!isMobile && academicInsights && !academicInsights._error ? [{ key: 'academic', label: 'Academic', closeable: true, onClose: () => { setAcademicInsights(null); if (activeTab === 'academic') setActiveTab('transcript'); }, icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg> }] : []),
                  ...(!isMobile && discover && !discover._error ? [{ key: 'discover', label: 'Discover', closeable: true, onClose: () => { setDiscover(null); setDiscoverLoading(false); if (activeTab === 'discover') setActiveTab('transcript'); }, icon: <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg> }] : []),
                ].sort((a, b) => {
                  const ai = activeTabOrder.indexOf(a.key);
                  const bi = activeTabOrder.indexOf(b.key);
                  return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                }).map(tab => {
                  const isActive = activeTab === tab.key;
                  const isOver = dragOverKey === tab.key && dragTabKey !== tab.key;
                  return (
                    <button
                      key={tab.key}
                      draggable
                      onDragStart={() => setDragTabKey(tab.key)}
                      onDragOver={e => { e.preventDefault(); setDragOverKey(tab.key); }}
                      onDrop={e => {
                        e.preventDefault();
                        if (dragTabKey && dragTabKey !== tab.key) {
                          setActiveTabOrder(prev => {
                            const arr = [...prev];
                            const fi = arr.indexOf(dragTabKey), ti = arr.indexOf(tab.key);
                            if (fi !== -1 && ti !== -1) { arr.splice(fi, 1); arr.splice(ti, 0, dragTabKey); }
                            return arr;
                          });
                        }
                        setDragTabKey(null); setDragOverKey(null);
                      }}
                      onDragEnd={() => { setDragTabKey(null); setDragOverKey(null); }}
                      onClick={() => setActiveTab(tab.key)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        padding: tab.closeable ? (isMobile ? '8px 8px 8px 12px' : '5px 6px 5px 12px') : (isMobile ? '8px 12px' : '5px 12px'),
                        minHeight: isMobile ? 44 : undefined, fontSize: 12,
                        fontWeight: isActive ? 600 : 500,
                        border: isActive ? '1px solid rgba(60,140,255,0.25)' : isOver ? `1px solid ${P.accent}` : '1px solid transparent',
                        borderBottom: isActive ? '1px solid #FFFFFF' : '1px solid transparent',
                        borderRadius: '7px 7px 0 0', marginBottom: '-1px',
                        background: isActive ? P.accentLight : isOver ? 'rgba(60,140,255,0.06)' : 'transparent',
                        color: isActive ? P.accent : P.muted,
                        cursor: 'grab', transition: 'all 0.15s', whiteSpace: 'nowrap',
                        opacity: dragTabKey === tab.key ? 0.4 : 1,
                        animation: tab.key === 'flashcards' && flashcardsTabPulse ? 'tabHighlight 1.2s ease-out forwards' : undefined,
                      }}
                      onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = 'rgba(29,29,31,0.05)'; e.currentTarget.style.color = P.ink; }}}
                      onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = isOver ? 'rgba(60,140,255,0.06)' : 'transparent'; e.currentTarget.style.color = P.muted; }}}
                    >
                      {tab.icon}
                      {tab.label}
                      {tab.closeable && (
                        <span
                          onClick={e => { e.stopPropagation(); tab.onClose(); }}
                          style={{ marginLeft: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: 3, flexShrink: 0, opacity: 0.5 }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = P.error; e.currentTarget.style.background = 'rgba(180,35,24,0.1)'; }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.color = ''; e.currentTarget.style.background = 'transparent'; }}
                        >
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Transcript card */}
              <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: '#FFFFFF', borderRadius: '0 0 16px 16px', boxShadow: '0 2px 12px rgba(29,29,31,0.07)', border: `1px solid ${P.border}`, borderTop: 'none', overflow: 'hidden' }}>

                {/* Transcript tab content */}
                {activeTab === 'transcript' && (
                  <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    {/* Search bar */}
                    <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8, padding: isMobile ? '10px 12px' : '8px 16px', borderBottom: `1px solid ${P.border}`, background: '#FFFFFF' }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={P.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search transcript…"
                        style={{ flex: 1, minHeight: isMobile ? 44 : 28, border: 'none', background: 'transparent', outline: 'none', fontSize: isMobile ? 14 : 13, color: P.ink }} />
                      {search && matchCount > 0 && <span style={{ fontSize: 11, color: P.muted }}>{matchCount} match{matchCount !== 1 ? 'es' : ''}</span>}
                      {search && <button onClick={() => setSearch('')} style={{ border: 'none', background: 'none', cursor: 'pointer', color: P.muted, fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>}
                      {segments.length > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, borderLeft: `1px solid ${P.border}`, paddingLeft: 10, flexShrink: 0 }}>
                          {/* Timestamps toggle */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ fontSize: 11, color: P.muted, whiteSpace: 'nowrap' }}>Timestamps</span>
                            <button
                              type="button"
                              aria-label={showTimestamps ? 'Hide timestamps' : 'Show timestamps'}
                              onClick={() => setShowTimestamps(v => !v)}
                              style={{ width: 44, height: 44, border: 'none', background: 'transparent', cursor: 'pointer', position: 'relative', flexShrink: 0, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <div style={{ width: 40, height: 24, borderRadius: 999, background: showTimestamps ? P.accent : P.border, position: 'relative', transition: 'background 0.2s' }}>
                                <div style={{ position: 'absolute', top: 3, left: showTimestamps ? 19 : 3, width: 18, height: 18, borderRadius: '50%', background: 'white', transition: 'left 0.2s' }} />
                              </div>
                            </button>
                          </div>
                          {/* Topics toggle — desktop/tablet only */}
                          {!isMobile && <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <span style={{ fontSize: 11, color: P.muted, whiteSpace: 'nowrap' }}>Topics</span>
                            <button
                              type="button"
                              aria-label={showTopics ? 'Hide topics' : 'Show topics'}
                              onClick={() => {
                              const next = !showTopics;
                              setShowTopics(next);
                              if (next && !timeline && !timelineLoading) generateTimeline();
                            }}
                              style={{ width: 44, height: 44, border: 'none', background: 'transparent', cursor: 'pointer', position: 'relative', flexShrink: 0, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <div style={{ width: 40, height: 24, borderRadius: 999, background: showTopics ? '#7C3AED' : P.border, position: 'relative', transition: 'background 0.2s' }}>
                                <div style={{ position: 'absolute', top: 3, left: showTopics ? 19 : 3, width: 18, height: 18, borderRadius: '50%', background: 'white', transition: 'left 0.2s' }} />
                              </div>
                            </button>
                          </div>}
                          {/* Language pill — ON ICE, desktop/tablet only */}
                          {!isMobile && <div title="Language translation coming soon" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 9px 3px 7px', borderRadius: 20, border: `1px solid ${P.border}`, background: P.paper, cursor: 'not-allowed', position: 'relative', opacity: 0.45 }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={P.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                            <span style={{ fontSize: 10.5, fontWeight: 600, color: P.ink, letterSpacing: '0.03em' }}>ENG</span>
                            <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={P.muted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                          </div>}
                        </div>
                      )}
                    </div>
                    {/* Translation notice — disabled (on ice) */}
                    {/* Transcript list — 2-column grid: timestamp | text */}
                    <div ref={transcriptListRef} style={{ flex: 1, overflowY: 'auto', background: '#FFFFFF', position: 'relative' }}>
                      {langRefetching && (
                        <div style={{ position: 'absolute', inset: 0, zIndex: 10, background: 'rgba(246,243,238,0.7)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            {[0,1,2].map(i => <span key={i} style={{ width: 7, height: 7, borderRadius: '50%', background: P.accent, display: 'inline-block', animation: `dot-flicker 1.2s ease-in-out ${i * 0.2}s infinite` }} />)}
                          </div>
                          <span style={{ fontSize: 12, color: P.muted, fontWeight: 500 }}>{langRefetchMsg || `Loading in ${LANGUAGES.find(l => l.code === lang)?.label}…`}</span>
                        </div>
                      )}
                      {segments.length > 0 && showTimestamps ? (
                        (() => {
                          // Pre-compute which segment index starts each topic section
                          const sectionBreaks = (showTopics && timeline && !timeline[0]?._error)
                            ? timeline.map(sec => segments.findIndex(s => s.seconds >= sec.startSeconds)).filter(idx => idx >= 0)
                            : [];
                          return segments.map((seg, i) => {
                            const secIdx = sectionBreaks.indexOf(i);
                            const section = secIdx >= 0 ? timeline[secIdx] : null;
                            return (
                              <React.Fragment key={i}>
                                {section && (
                                  <div
                                    onClick={() => seekToTime(section.startSeconds)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 14px 7px 10px', background: 'rgba(124,58,237,0.06)', borderBottom: `1px solid rgba(124,58,237,0.15)`, borderTop: i > 0 ? `1px solid rgba(124,58,237,0.15)` : 'none', cursor: 'pointer' }}
                                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.11)'}
                                    onMouseLeave={e => e.currentTarget.style.background = 'rgba(124,58,237,0.06)'}
                                  >
                                    <span style={{ fontFamily: 'monospace', fontSize: 10, fontWeight: 700, color: '#7C3AED', flexShrink: 0 }}>{formatTime(section.startSeconds)}</span>
                                    <span style={{ fontSize: 11.5, fontWeight: 700, color: '#5B21B6', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{section.title}</span>
                                    {timelineLoading && secIdx === 0 && <span style={{ fontSize: 10, color: '#7C3AED', opacity: 0.6 }}>loading…</span>}
                                  </div>
                                )}
                                <div
                                  ref={el => { segmentRefs.current[i] = el; }}
                                  onClick={() => { setSelectedSegment(selectedSegment === i ? null : i); seekToTime(seg.seconds); }}
                                  style={{
                                    display: 'grid', gridTemplateColumns: '54px 1fr',
                                    gap: 0, padding: '0',
                                    background: selectedSegment === i ? 'rgba(60,140,255,0.08)' : playingSegment === i ? 'rgba(91,155,213,0.13)' : (i % 2 === 0 ? '#FFFFFF' : 'rgba(246,243,238,0.5)'),
                                    cursor: 'pointer', transition: 'background 0.15s',
                                    borderLeft: playingSegment === i ? `3px solid ${P.accent}` : '3px solid transparent',
                                    borderBottom: `1px solid ${selectedSegment === i ? 'rgba(60,140,255,0.15)' : P.border}`,
                                  }}
                                  onMouseEnter={e => { if (selectedSegment !== i && playingSegment !== i) e.currentTarget.style.background = 'rgba(60,140,255,0.03)'; }}
                                  onMouseLeave={e => { if (selectedSegment !== i) e.currentTarget.style.background = playingSegment === i ? 'rgba(60,140,255,0.05)' : (i % 2 === 0 ? '#FFFFFF' : 'rgba(246,243,238,0.5)'); }}
                                >
                                  <button
                                    onClick={e => { e.stopPropagation(); seekToTime(seg.seconds); setSelectedSegment(i); }}
                                    style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', minHeight: 44, padding: '10px 6px 10px 0', background: 'none', border: 'none', cursor: 'pointer', color: selectedSegment === i ? P.accent : playingSegment === i ? '#5B9BD5' : '#999', fontWeight: playingSegment === i ? 700 : 600, fontSize: 10.5, fontFamily: 'monospace', flexShrink: 0 }}>
                                    {formatTime(seg.seconds)}
                                  </button>
                                  <div style={{ padding: '10px 14px 10px 8px', fontSize: 13.5, lineHeight: 1.7, color: P.ink, fontWeight: playingSegment === i ? 600 : selectedSegment === i ? 500 : 400 }}>
                                    {highlightText(seg.text)}
                                  </div>
                                </div>
                              </React.Fragment>
                            );
                          });
                        })()
                      ) : (
                        <div style={{ padding: '20px', fontSize: 14, lineHeight: 1.85, color: P.ink }}>
                          {highlightText(transcript)}
                        </div>
                      )}
                    </div>
                    {/* Bottom info bar */}
                    <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', flexWrap: isMobile ? 'wrap' : 'nowrap', gap: 12, rowGap: isMobile ? 6 : undefined, padding: '8px 16px', background: P.paper, borderTop: `1px solid ${P.border}` }}>
                      <span style={{ fontSize: 11, color: P.muted }}>Word Count: <strong style={{ color: P.ink }}>{wordCount.toLocaleString()}</strong></span>
                      <span style={{ color: P.border }}>·</span>
                      <span style={{ fontSize: 11, color: P.muted }}>Character Count: <strong style={{ color: P.ink }}>{charCount.toLocaleString()}</strong></span>
                      <span style={{ color: P.border }}>·</span>
                      <span style={{ fontSize: 11, color: P.muted }}>~<strong style={{ color: P.ink }}>{readingMins}</strong> min read</span>
                    </div>
                  </div>
                )}

                {/* Editor tab */}
                {activeTab === 'editor' && (
                  <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px' }}>
                    <textarea defaultValue={transcript} style={{
                      width: '100%', minHeight: 400, border: `1px solid ${P.border}`, borderRadius: 10,
                      padding: '16px', fontSize: 13.5, lineHeight: 1.85, color: P.ink, background: P.paper,
                      outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
                    }}
                      onFocus={e => { e.target.style.borderColor = P.accent; }}
                      onBlur={e => { e.target.style.borderColor = P.border; }}
                    />
                    <div style={{ marginTop: 8, fontSize: 11, color: P.muted }}>Edit the transcript above. Changes are local only.</div>
                  </div>
                )}

                {/* Flashcards tab */}
                {activeTab === 'flashcards' && (
                  <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 24px' }}>
                    {flashcardsLoading && (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '60px 0', color: P.muted }}>
                        <div style={{ width: 32, height: 32, border: `3px solid ${P.border}`, borderTopColor: P.accent, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        <div style={{ fontSize: 13, fontWeight: 500 }}>Generating flashcards…</div>
                      </div>
                    )}
                    {!flashcardsLoading && <>
                    {/* Top bar */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: P.ink }}>Flashcards</div>
                        <div style={{ fontSize: 11.5, color: P.muted }}>{flashcardKnown.size} / {flashcards.length} known</div>
                      </div>
                      {flashcards.length > 0 && (
                        <button
                          onClick={() => { setFlashcardIndex(0); setFlashcardFlipped(false); setShowFlashcardModal(true); }}
                          style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 8, border: 'none', background: P.accent, color: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s' }}
                          onMouseEnter={e => { e.currentTarget.style.background = P.accentHover; }}
                          onMouseLeave={e => { e.currentTarget.style.background = P.accent; }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                          Study in Modal
                        </button>
                      )}
                    </div>
                    {/* Progress bar */}
                    <div style={{ height: 6, borderRadius: 3, background: P.border, marginBottom: 18, overflow: 'hidden' }}>
                      <div style={{ height: '100%', borderRadius: 3, background: P.success, width: `${flashcards.length > 0 ? (flashcardKnown.size / flashcards.length) * 100 : 0}%`, transition: 'width 0.4s' }} />
                    </div>
                    {/* Card list */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {flashcards.map((card, i) => {
                        const isExpanded = expandedCards.has(i);
                        const isKnown = flashcardKnown.has(i);
                        return (
                          <div key={i} style={{ borderRadius: 10, border: `1px solid ${isKnown ? 'rgba(15,118,110,0.3)' : P.border}`, background: isKnown ? 'rgba(15,118,110,0.04)' : '#fff', overflow: 'hidden', transition: 'border-color 0.2s' }}>
                            {/* Card header row */}
                            <div
                              onClick={() => setExpandedCards(prev => { const next = new Set(prev); if (next.has(i)) next.delete(i); else next.add(i); return next; })}
                              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', cursor: 'pointer', userSelect: 'none' }}
                            >
                              <div style={{ width: 24, height: 24, borderRadius: 6, background: isKnown ? 'rgba(15,118,110,0.12)' : P.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <span style={{ fontSize: 10, fontWeight: 700, color: isKnown ? P.success : P.muted }}>{i + 1}</span>
                              </div>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 12.5, fontWeight: 600, color: P.ink, lineHeight: 1.45, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isExpanded ? 'normal' : 'nowrap' }}>{card.question}</div>
                                {card.topic && !isExpanded && (
                                  <div style={{ fontSize: 10.5, color: '#D97706', fontWeight: 600, marginTop: 2 }}>{card.topic}</div>
                                )}
                              </div>
                              {isKnown && (
                                <div style={{ fontSize: 10, fontWeight: 700, color: P.success, background: 'rgba(15,118,110,0.1)', padding: '2px 8px', borderRadius: 4, flexShrink: 0, letterSpacing: '0.04em' }}>KNOWN</div>
                              )}
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={P.muted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                            </div>
                            {/* Expanded answer */}
                            {isExpanded && (
                              <div style={{ borderTop: `1px solid ${P.border}`, padding: '12px 14px', background: P.paper }}>
                                {card.topic && (
                                  <div style={{ fontSize: 10.5, fontWeight: 700, color: '#D97706', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{card.topic}</div>
                                )}
                                <div style={{ fontSize: 13, lineHeight: 1.65, color: P.ink }}>{card.answer}</div>
                                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                                  <button
                                    onClick={e => { e.stopPropagation(); setFlashcardKnown(prev => { const next = new Set(prev); if (isKnown) next.delete(i); else next.add(i); return next; }); }}
                                    style={{ flex: 1, padding: '6px 0', borderRadius: 7, border: `1px solid ${isKnown ? 'rgba(15,118,110,0.4)' : P.border}`, background: isKnown ? 'rgba(15,118,110,0.08)' : 'none', color: isKnown ? P.success : P.muted, fontSize: 11.5, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}
                                  >{isKnown ? '✓ Known' : 'Mark as Known'}</button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {/* Generate More / exhausted */}
                    <div style={{ marginTop: 14 }}>
                      {flashcardsExhausted ? (
                        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', borderRadius: 10, border: '1px solid rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.07)' }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                          <div>
                            <div style={{ fontSize: 12.5, fontWeight: 700, color: '#D97706', marginBottom: 3 }}>No more flashcards available</div>
                            <div style={{ fontSize: 12, color: P.muted, lineHeight: 1.55 }}>{flashcardsExhaustedReason}</div>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={generateMoreFlashcards}
                          disabled={flashcardsMoreLoading}
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', padding: '8px 0', borderRadius: 8, border: `1.5px dashed ${P.border}`, background: 'none', color: flashcardsMoreLoading ? P.muted : P.ink, fontSize: 12.5, fontWeight: 600, cursor: flashcardsMoreLoading ? 'default' : 'pointer', transition: 'all 0.15s', opacity: flashcardsMoreLoading ? 0.6 : 1 }}
                          onMouseEnter={e => { if (!flashcardsMoreLoading) { e.currentTarget.style.borderColor = P.accent; e.currentTarget.style.color = P.accent; } }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = P.border; e.currentTarget.style.color = flashcardsMoreLoading ? P.muted : P.ink; }}
                        >
                          {flashcardsMoreLoading ? (
                            <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>Generating more…</>
                          ) : (
                            <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>Generate More Flashcards</>
                          )}
                        </button>
                      )}
                    </div>
                    {/* Reset progress */}
                    {flashcardKnown.size > 0 && (
                      <button
                        onClick={() => setFlashcardKnown(new Set())}
                        style={{ marginTop: 10, display: 'block', width: '100%', padding: '8px 0', borderRadius: 8, border: `1px solid ${P.border}`, background: 'none', color: P.muted, fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}
                        onMouseEnter={e => { e.currentTarget.style.color = P.ink; e.currentTarget.style.borderColor = P.ink; }}
                        onMouseLeave={e => { e.currentTarget.style.color = P.muted; e.currentTarget.style.borderColor = P.border; }}
                      >Reset Progress</button>
                    )}
                    </>}
                  </div>
                )}

                {/* Summary tab */}
                {activeTab === 'summary' && (
                  <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 24px' }}>
                    {summarizing ? (
                      <div style={{ padding: '60px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: P.muted, fontSize: 13 }}>
                        <SpinnerIcon size={14} /> Summarizing…
                      </div>
                    ) : summary ? (
                      <div style={{ maxWidth: 700, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 34, height: 34, borderRadius: 10, background: P.accentLight, display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.accent }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                            </div>
                            <div>
                              <div style={{ fontSize: 15, fontWeight: 700, color: P.ink }}>Summary</div>
                              <div style={{ fontSize: 11.5, color: P.muted }}>AI-generated from transcript</div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => { navigator.clipboard.writeText(summary).then(() => { setSummaryCopied(true); setTimeout(() => setSummaryCopied(false), 2000); }); }}
                              style={{ display: 'flex', alignItems: 'center', gap: 4, border: `1px solid ${P.border}`, background: 'none', cursor: 'pointer', borderRadius: 6, padding: '4px 10px', fontSize: 11, fontWeight: 600, color: summaryCopied ? P.success : P.muted, transition: 'all 0.15s' }}>
                              {summaryCopied ? <CheckIcon /> : <CopyIcon />} {summaryCopied ? 'Copied!' : 'Copy'}
                            </button>
                            <button onClick={() => { setSummary(''); setActiveTab('transcript'); }}
                              style={{ border: `1px solid ${P.border}`, background: 'none', cursor: 'pointer', color: P.muted, fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 6, transition: 'all 0.15s' }}
                              onMouseEnter={e => { e.currentTarget.style.background = P.paper; e.currentTarget.style.color = P.ink; }}
                              onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = P.muted; }}
                            >Clear</button>
                          </div>
                        </div>
                        <div style={{ padding: '18px 20px', background: '#fff', borderRadius: 12, border: `1px solid ${P.border}`, boxShadow: '0 1px 4px rgba(29,29,31,0.04)', fontSize: 14, lineHeight: 1.8, color: P.ink, whiteSpace: 'pre-wrap' }}>{summary}</div>
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Study Guide tab */}
                {activeTab === 'study-guide' && (
                  <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 24px' }}>
                    {studyGuideLoading ? (
                      <div style={{ padding: '60px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: P.muted, fontSize: 13 }}>
                        <SpinnerIcon size={14} /> Generating study guide…
                      </div>
                    ) : studyGuide && !studyGuide._error ? (() => {
                      const sgBody = (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 700, margin: '0 auto' }}>
                          {/* Header */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                              <div style={{ fontSize: 15, fontWeight: 700, color: P.ink }}>Study Guide</div>
                              <div style={{ fontSize: 11.5, color: P.muted }}>AI-generated from transcript</div>
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => setStudyGuideFull(true)}
                                title="Full screen"
                                style={{ border: `1px solid ${P.border}`, background: 'none', cursor: 'pointer', color: P.muted, fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6, transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 5 }}
                                onMouseEnter={e => { e.currentTarget.style.background = P.paper; e.currentTarget.style.color = P.ink; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = P.muted; }}
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                                Full Screen
                              </button>
                              <button onClick={() => { setStudyGuide(null); setSgMessages([]); setActiveTab('transcript'); }}
                                style={{ border: `1px solid ${P.border}`, background: 'none', cursor: 'pointer', color: P.muted, fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 6, transition: 'all 0.15s' }}
                                onMouseEnter={e => { e.currentTarget.style.background = P.paper; e.currentTarget.style.color = P.ink; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = P.muted; }}
                              >Clear</button>
                            </div>
                          </div>

                          {/* Overview */}
                          <div style={{ paddingTop: 16, borderTop: `3px solid ${P.success}` }}>
                            <div style={{ fontSize: 10.5, fontWeight: 700, color: P.success, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 10 }}>Overview</div>
                            <div style={{ fontSize: 14.5, lineHeight: 1.75, color: P.ink }}>{studyGuide.overview}</div>
                          </div>

                          {/* Learning Objectives */}
                          {studyGuide.objectives?.length > 0 && (
                            <div style={{ padding: '20px 22px', background: '#fff', borderRadius: 12, border: `1px solid ${P.border}`, boxShadow: '0 1px 4px rgba(29,29,31,0.04)' }}>
                              <div style={{ fontSize: 10.5, fontWeight: 700, color: P.success, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 12 }}>Learning Objectives</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {studyGuide.objectives.map((obj, i) => (
                                  <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                                    <div style={{ width: 22, height: 22, borderRadius: 6, background: 'rgba(15,118,110,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                                      <span style={{ fontSize: 10, fontWeight: 700, color: P.success }}>{i + 1}</span>
                                    </div>
                                    <div style={{ fontSize: 13.5, lineHeight: 1.6, color: P.ink }}>{obj}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Key Concepts */}
                          {studyGuide.keyConcepts?.length > 0 && (
                            <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${P.border}`, boxShadow: '0 1px 4px rgba(29,29,31,0.04)', overflow: 'hidden' }}>
                              <div style={{ padding: '14px 20px 12px', borderBottom: `1px solid ${P.border}` }}>
                                <div style={{ fontSize: 10.5, fontWeight: 700, color: P.success, letterSpacing: '0.07em', textTransform: 'uppercase' }}>Key Concepts</div>
                              </div>
                              {studyGuide.keyConcepts.map((kc, i) => (
                                <div key={i} style={{ display: 'flex', gap: 0, padding: '12px 20px', borderBottom: i < studyGuide.keyConcepts.length - 1 ? `1px solid ${P.border}` : 'none', alignItems: 'flex-start' }}>
                                  <div style={{ minWidth: 140, flexShrink: 0, fontSize: 13, fontWeight: 700, color: P.success, paddingRight: 16, paddingTop: 1 }}>{kc.term}</div>
                                  <div style={{ fontSize: 13.5, color: P.ink, lineHeight: 1.65 }}>{kc.definition}</div>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Sections */}
                          {studyGuide.sections?.length > 0 && (
                            <div style={{ padding: '20px 22px', background: '#fff', borderRadius: 12, border: `1px solid ${P.border}`, boxShadow: '0 1px 4px rgba(29,29,31,0.04)' }}>
                              <div style={{ fontSize: 10.5, fontWeight: 700, color: P.success, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 12 }}>Sections</div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {studyGuide.sections.map((sec, i) => (
                                  <div key={i} style={{ paddingBottom: i < studyGuide.sections.length - 1 ? 16 : 0, borderBottom: i < studyGuide.sections.length - 1 ? `1px solid ${P.border}` : 'none' }}>
                                    <div style={{ fontSize: 14, fontWeight: 700, color: P.ink, marginBottom: 6 }}>{sec.title}</div>
                                    <div style={{ fontSize: 13, lineHeight: 1.65, color: P.muted, marginBottom: sec.keyPoints?.length ? 10 : 0 }}>{sec.summary}</div>
                                    {sec.keyPoints?.length > 0 && (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                                        {sec.keyPoints.map((pt, j) => (
                                          <div key={j} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                            <div style={{ width: 5, height: 5, borderRadius: '50%', background: P.success, flexShrink: 0, marginTop: 6 }} />
                                            <div style={{ fontSize: 13, lineHeight: 1.6, color: P.ink }}>{pt}</div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Review Questions */}
                          {studyGuide.reviewQuestions?.length > 0 && (
                            <div style={{ background: '#fff', borderRadius: 12, border: `1px solid ${P.border}`, boxShadow: '0 1px 4px rgba(29,29,31,0.04)', overflow: 'hidden' }}>
                              <div style={{ padding: '14px 20px 12px', borderBottom: `1px solid ${P.border}` }}>
                                <div style={{ fontSize: 10.5, fontWeight: 700, color: P.success, letterSpacing: '0.07em', textTransform: 'uppercase' }}>Review Questions</div>
                              </div>
                              {studyGuide.reviewQuestions.map((q, i) => (
                                <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '13px 20px', borderBottom: i < studyGuide.reviewQuestions.length - 1 ? `1px solid ${P.border}` : 'none', borderLeft: `3px solid rgba(15,118,110,0.3)` }}>
                                  <span style={{ fontSize: 11, fontWeight: 700, color: P.success, flexShrink: 0, marginTop: 2, letterSpacing: '0.03em' }}>Q{i + 1}</span>
                                  <span style={{ fontSize: 13.5, lineHeight: 1.65, color: P.ink }}>{q}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Ask ScribeSnap AI */}
                          <div style={{ padding: '16px 18px', background: '#fff', borderRadius: 12, border: `1.5px solid rgba(60,140,255,0.18)`, boxShadow: '0 1px 4px rgba(29,29,31,0.04)', marginBottom: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                              <div style={{ width: 26, height: 26, borderRadius: 7, background: 'linear-gradient(135deg, rgba(60,140,255,0.13) 0%, rgba(60,140,255,0.05) 100%)', border: '1.5px solid rgba(60,140,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <img src="/scribesnap_icon_wave.svg" alt="AI" style={{ width: 14, height: 14 }} />
                              </div>
                              <span style={{ fontSize: 12.5, fontWeight: 700, color: P.accent }}>Ask ScribeSnap AI</span>
                              <span style={{ fontSize: 11.5, color: P.muted, marginLeft: 2 }}>— deeper questions about this video</span>
                              {sgMessages.length > 0 && (
                                <button onClick={() => setSgMessages([])} style={{ marginLeft: 'auto', border: `1px solid ${P.border}`, background: 'none', cursor: 'pointer', color: P.muted, fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 5 }}>Clear</button>
                              )}
                            </div>

                            {/* Chat messages */}
                            {sgMessages.length > 0 && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
                                {sgMessages.map((msg, i) => (
                                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                                    {msg.role === 'ai' ? (
                                      <div style={{ maxWidth: '90%', padding: '9px 13px', borderRadius: '3px 12px 12px 12px', background: P.paper, border: `1px solid ${P.border}`, fontSize: 13, lineHeight: 1.65, color: msg.isError ? P.error : P.ink }}>
                                        {msg.text.split('\n').map((line, li, arr) => <React.Fragment key={li}>{renderChatLine(line)}{li < arr.length - 1 && <br />}</React.Fragment>)}
                                      </div>
                                    ) : (
                                      <div style={{ maxWidth: '86%', padding: '9px 13px', borderRadius: '12px 12px 3px 12px', background: P.accent, fontSize: 13, lineHeight: 1.6, color: 'white' }}>{msg.text}</div>
                                    )}
                                  </div>
                                ))}
                                {sgLoading && (
                                  <div style={{ display: 'flex', gap: 5, padding: '10px 13px', background: P.paper, border: `1px solid ${P.border}`, borderRadius: '3px 12px 12px 12px', width: 'fit-content' }}>
                                    {[0,1,2].map(d => <div key={d} style={{ width: 5, height: 5, borderRadius: '50%', background: P.accent, opacity: 0.6, animation: `bounce 1.2s ease-in-out ${d * 0.2}s infinite` }} />)}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Input */}
                            <div style={{ display: 'flex', alignItems: 'center', background: P.paper, border: `1.5px solid ${P.border}`, borderRadius: 12, padding: '6px 6px 6px 14px', transition: 'border-color 0.2s' }}
                              onFocus={() => {}} onBlur={() => {}}
                            >
                              <input
                                value={sgQuestion}
                                onChange={e => setSgQuestion(e.target.value)}
                                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && askSgQuestion()}
                                placeholder="Ask a question about this video…"
                                disabled={sgLoading}
                                style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: 13, color: P.ink, padding: '3px 0' }}
                                onFocus={e => { e.currentTarget.parentElement.style.borderColor = P.accent; e.currentTarget.parentElement.style.boxShadow = '0 0 0 3px rgba(60,140,255,0.1)'; }}
                                onBlur={e => { e.currentTarget.parentElement.style.borderColor = P.border; e.currentTarget.parentElement.style.boxShadow = 'none'; }}
                              />
                              <button
                                onClick={() => askSgQuestion()}
                                disabled={!sgQuestion.trim() || sgLoading}
                                style={{ flexShrink: 0, width: 34, height: 34, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: 'none', background: sgQuestion.trim() && !sgLoading ? 'linear-gradient(135deg, #5ba4f5 0%, #3C8CFF 100%)' : 'rgba(29,29,31,0.05)', color: sgQuestion.trim() && !sgLoading ? 'white' : P.muted, cursor: sgQuestion.trim() && !sgLoading ? 'pointer' : 'default', transition: 'all 0.2s' }}
                              >
                                {sgLoading ? <SpinnerIcon size={12} /> : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                      return sgBody;
                    })() : null}
                  </div>
                )}

                {/* ── Academic Insights tab — desktop only ── */}
                {activeTab === 'academic' && (
                  <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 24px' }}>
                    {academicInsightsLoading ? (
                      <div style={{ padding: '60px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
                        <div style={{ width: 44, height: 44, borderRadius: 14, background: 'rgba(124,58,237,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <SpinnerIcon size={18} style={{ color: '#7C3AED' }} />
                        </div>
                        <div style={{ fontSize: 13, color: P.muted }}>Extracting academic insights…</div>
                      </div>
                    ) : academicInsights && !academicInsights._error ? (() => {
                      const ai = academicInsights;
                      const supportedCount = ai.claims?.filter(c => c.supported).length || 0;
                      const unsupportedCount = (ai.claims?.length || 0) - supportedCount;
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 720, margin: '0 auto' }}>
                          {/* Header */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                              <div style={{ width: 36, height: 36, borderRadius: 11, background: 'rgba(124,58,237,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7C3AED' }}>
                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
                              </div>
                              <div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: P.ink }}>Academic Insights</div>
                                <div style={{ fontSize: 11.5, color: P.muted }}>AI-extracted from transcript</div>
                              </div>
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => setAcademicInsightsFull(true)}
                                style={{ border: `1px solid ${P.border}`, background: 'none', cursor: 'pointer', color: P.muted, fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6, transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 5 }}
                                onMouseEnter={e => { e.currentTarget.style.background = P.paper; e.currentTarget.style.color = P.ink; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = P.muted; }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/><line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>
                                Full Screen
                              </button>
                              <button onClick={() => { setAcademicInsights(null); setActiveTab('transcript'); }}
                                style={{ border: `1px solid ${P.border}`, background: 'none', cursor: 'pointer', color: P.muted, fontSize: 11, fontWeight: 600, padding: '4px 12px', borderRadius: 6, transition: 'all 0.15s' }}
                                onMouseEnter={e => { e.currentTarget.style.background = P.paper; e.currentTarget.style.color = P.ink; }}
                                onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = P.muted; }}>Clear</button>
                            </div>
                          </div>

                          {/* Stats bar */}
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            {ai.references?.length > 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', background: 'rgba(124,58,237,0.07)', borderRadius: 99, border: '1px solid rgba(124,58,237,0.15)' }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                                <span style={{ fontSize: 11.5, fontWeight: 600, color: '#7C3AED' }}>{ai.references.length} References</span>
                              </div>
                            )}
                            {ai.claims?.length > 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 11px', background: 'rgba(29,29,31,0.04)', borderRadius: 99, border: `1px solid ${P.border}` }}>
                                <span style={{ fontSize: 11.5, fontWeight: 600, color: P.success }}>{supportedCount} supported</span>
                                <span style={{ width: 1, height: 10, background: P.border }} />
                                <span style={{ fontSize: 11.5, fontWeight: 600, color: unsupportedCount > 0 ? P.error : P.muted }}>{unsupportedCount} unsupported</span>
                              </div>
                            )}
                            {ai.glossary?.length > 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', background: 'rgba(124,58,237,0.07)', borderRadius: 99, border: '1px solid rgba(124,58,237,0.15)' }}>
                                <span style={{ fontSize: 11.5, fontWeight: 600, color: '#7C3AED' }}>{ai.glossary.length} Terms</span>
                              </div>
                            )}
                            {ai.researchGaps?.length > 0 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', background: 'rgba(124,58,237,0.07)', borderRadius: 99, border: '1px solid rgba(124,58,237,0.15)' }}>
                                <span style={{ fontSize: 11.5, fontWeight: 600, color: '#7C3AED' }}>{ai.researchGaps.length} Open Questions</span>
                              </div>
                            )}
                          </div>

                          {/* References */}
                          {ai.references?.length > 0 && (
                            <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${P.border}`, boxShadow: '0 1px 4px rgba(29,29,31,0.04)', overflow: 'hidden' }}>
                              <div style={{ padding: '14px 18px 12px', borderBottom: `1px solid ${P.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#7C3AED', letterSpacing: '0.07em', textTransform: 'uppercase' }}>References Mentioned</span>
                                <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#7C3AED', background: 'rgba(124,58,237,0.1)', padding: '1px 7px', borderRadius: 99 }}>{ai.references.length}</span>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {ai.references.map((ref, i) => (
                                  <div key={i} style={{ display: 'flex', gap: 13, alignItems: 'center', padding: '12px 18px', borderBottom: i < ai.references.length - 1 ? `1px solid ${P.border}` : 'none', transition: 'background 0.12s' }}
                                    onMouseEnter={e => e.currentTarget.style.background = P.paper}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                    <div style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(124,58,237,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#7C3AED' }}>
                                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                                    </div>
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                      <div style={{ fontSize: 13.5, fontWeight: 600, color: P.ink, lineHeight: 1.35 }}>{ref.title || ref.author}</div>
                                      <div style={{ fontSize: 12, color: P.muted, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                                        {[ref.author, ref.year].filter(Boolean).join(' · ')}
                                        {ref.type && ref.type !== 'other' && <span style={{ fontSize: 10, fontWeight: 700, color: '#7C3AED', background: 'rgba(124,58,237,0.08)', padding: '1px 6px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{ref.type}</span>}
                                      </div>
                                    </div>
                                    <a href={`https://scholar.google.com/scholar?q=${encodeURIComponent([ref.title, ref.author].filter(Boolean).join(' '))}`} target="_blank" rel="noopener noreferrer"
                                      style={{ flexShrink: 0, fontSize: 11, fontWeight: 600, color: '#7C3AED', background: 'rgba(124,58,237,0.07)', border: '1px solid rgba(124,58,237,0.2)', padding: '5px 11px', borderRadius: 7, textDecoration: 'none', whiteSpace: 'nowrap', transition: 'all 0.12s' }}
                                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.14)'; }}
                                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.07)'; }}
                                      onClick={e => e.stopPropagation()}>
                                      Search →
                                    </a>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Key Claims */}
                          {ai.claims?.length > 0 && (
                            <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${P.border}`, boxShadow: '0 1px 4px rgba(29,29,31,0.04)', overflow: 'hidden' }}>
                              <div style={{ padding: '14px 18px 12px', borderBottom: `1px solid ${P.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#7C3AED', letterSpacing: '0.07em', textTransform: 'uppercase' }}>Key Claims</span>
                                <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                                  {supportedCount > 0 && <span style={{ fontSize: 10.5, fontWeight: 700, color: P.success, background: 'rgba(15,118,110,0.08)', padding: '2px 8px', borderRadius: 99 }}>✓ {supportedCount}</span>}
                                  {unsupportedCount > 0 && <span style={{ fontSize: 10.5, fontWeight: 700, color: P.error, background: 'rgba(180,35,24,0.08)', padding: '2px 8px', borderRadius: 99 }}>⚠ {unsupportedCount}</span>}
                                </div>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                                {ai.claims.map((c, i) => (
                                  <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '13px 18px', borderBottom: i < ai.claims.length - 1 ? `1px solid ${P.border}` : 'none', borderLeft: `3px solid ${c.supported ? P.success : P.error}` }}>
                                    <div style={{ flexShrink: 0, marginTop: 3, width: 18, height: 18, borderRadius: '50%', background: c.supported ? 'rgba(15,118,110,0.1)' : 'rgba(180,35,24,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      {c.supported
                                        ? <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={P.success} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                        : <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke={P.error} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                      }
                                    </div>
                                    <div style={{ minWidth: 0 }}>
                                      <div style={{ fontSize: 13.5, lineHeight: 1.6, color: P.ink, fontWeight: 500 }}>{c.claim}</div>
                                      {c.evidence && <div style={{ fontSize: 12, color: P.muted, marginTop: 5, lineHeight: 1.55, paddingTop: 5, borderTop: `1px solid ${P.border}` }}>
                                        <span style={{ fontWeight: 600, color: P.ink }}>Evidence: </span>{c.evidence}
                                      </div>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Glossary */}
                          {ai.glossary?.length > 0 && (
                            <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${P.border}`, boxShadow: '0 1px 4px rgba(29,29,31,0.04)', overflow: 'hidden' }}>
                              <div style={{ padding: '14px 18px 12px', borderBottom: `1px solid ${P.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#7C3AED', letterSpacing: '0.07em', textTransform: 'uppercase' }}>Glossary</span>
                                <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#7C3AED', background: 'rgba(124,58,237,0.1)', padding: '1px 7px', borderRadius: 99 }}>{ai.glossary.length} terms</span>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {ai.glossary.map((g, i) => (
                                  <div key={i} style={{ display: 'flex', gap: 0, padding: '12px 18px', borderBottom: i < ai.glossary.length - 1 ? `1px solid ${P.border}` : 'none', transition: 'background 0.12s' }}
                                    onMouseEnter={e => e.currentTarget.style.background = P.paper}
                                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                                    <div style={{ minWidth: 160, flexShrink: 0, fontSize: 13.5, fontWeight: 700, color: '#7C3AED', lineHeight: 1.5, paddingRight: 16 }}>{g.term}</div>
                                    <div style={{ fontSize: 13.5, color: P.ink, lineHeight: 1.65 }}>{g.definition}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Research Gaps */}
                          {ai.researchGaps?.length > 0 && (
                            <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${P.border}`, boxShadow: '0 1px 4px rgba(29,29,31,0.04)', overflow: 'hidden' }}>
                              <div style={{ padding: '14px 18px 12px', borderBottom: `1px solid ${P.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#7C3AED', letterSpacing: '0.07em', textTransform: 'uppercase' }}>Open Questions & Research Gaps</span>
                                <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#7C3AED', background: 'rgba(124,58,237,0.1)', padding: '1px 7px', borderRadius: 99 }}>{ai.researchGaps.length}</span>
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column' }}>
                                {ai.researchGaps.map((gap, i) => (
                                  <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '13px 18px', borderBottom: i < ai.researchGaps.length - 1 ? `1px solid ${P.border}` : 'none', borderLeft: '3px solid rgba(124,58,237,0.35)' }}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2, opacity: 0.6 }}><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                                    <div style={{ fontSize: 13.5, lineHeight: 1.65, color: P.ink }}>{gap}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Empty state */}
                          {!ai.references?.length && !ai.claims?.length && !ai.glossary?.length && !ai.researchGaps?.length && (
                            <div style={{ padding: '48px 0', textAlign: 'center', color: P.muted, fontSize: 13 }}>No academic content detected in this transcript.</div>
                          )}
                        </div>
                      );
                    })() : academicInsights?._error ? (
                      <div style={{ padding: '32px 24px', color: P.error, fontSize: 13 }}>Failed to generate: {academicInsights._error}</div>
                    ) : null}
                  </div>
                )}

                {/* ── DISCOVER TAB ── */}
                {activeTab === 'discover' && (
                  <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                    {discoverLoading ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 16, padding: '80px 24px' }}>
                        <div style={{ width: 52, height: 52, borderRadius: 16, background: 'rgba(194,65,12,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C2410C' }}>
                          <div style={{ width: 24, height: 24, border: '2.5px solid rgba(194,65,12,0.25)', borderTopColor: '#C2410C', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: 14, fontWeight: 600, color: P.ink, marginBottom: 4 }}>Discovering related content…</div>
                          <div style={{ fontSize: 12.5, color: P.muted }}>Finding videos and academic papers for you</div>
                        </div>
                      </div>
                    ) : discover && !discover._error ? (
                      <div style={{ padding: isMobile ? '16px 14px 32px' : '22px 26px 40px', display: 'flex', flexDirection: 'column', gap: 24 }}>

                        {/* ── Hero banner ── */}
                        <div style={{ borderRadius: 16, background: 'linear-gradient(135deg, rgba(194,65,12,0.08) 0%, rgba(234,88,12,0.05) 100%)', border: '1px solid rgba(194,65,12,0.14)', padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 16 }}>
                          <div style={{ width: 48, height: 48, borderRadius: 14, background: 'rgba(194,65,12,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C2410C', flexShrink: 0 }}>
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 15, fontWeight: 700, color: P.ink, letterSpacing: '-0.01em' }}>Discover</div>
                            <div style={{ fontSize: 12.5, color: P.muted, marginTop: 2 }}>Related videos &amp; academic papers based on this transcript</div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                            {(discover.videos?.length > 0 || discover.papers?.length > 0) && (
                              <div style={{ display: 'flex', gap: 6 }}>
                                {discover.videos?.length > 0 && (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999, background: 'rgba(194,65,12,0.1)', color: '#C2410C', fontSize: 11, fontWeight: 700, letterSpacing: '0.02em' }}>
                                    <svg width="9" height="9" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                                    {discover.videos.length}
                                  </span>
                                )}
                                {discover.papers?.length > 0 && (
                                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999, background: P.accentLight, color: P.accent, fontSize: 11, fontWeight: 700, letterSpacing: '0.02em' }}>
                                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                    {discover.papers.length}
                                  </span>
                                )}
                              </div>
                            )}
                            <button
                              onClick={() => { setDiscover(null); setDiscoverLoading(false); setActiveTab('transcript'); }}
                              style={{ padding: '5px 10px', borderRadius: 8, border: `1px solid ${P.border}`, background: 'transparent', color: P.muted, fontSize: 12, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap' }}
                              onMouseEnter={e => { e.currentTarget.style.color = P.error; e.currentTarget.style.borderColor = P.error; e.currentTarget.style.background = 'rgba(180,35,24,0.06)'; }}
                              onMouseLeave={e => { e.currentTarget.style.color = P.muted; e.currentTarget.style.borderColor = P.border; e.currentTarget.style.background = 'transparent'; }}
                            >Clear</button>
                          </div>
                        </div>

                        {/* ── Keywords searched ── */}
                        {discover.keywords?.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                            <span style={{ fontSize: 11, fontWeight: 700, color: P.muted, letterSpacing: '0.05em', textTransform: 'uppercase', marginRight: 4 }}>Topics</span>
                            {discover.keywords.map((kw, i) => (
                              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 11px', borderRadius: 999, background: 'rgba(194,65,12,0.07)', color: '#C2410C', fontSize: 12, fontWeight: 600, letterSpacing: '0.01em', border: '1px solid rgba(194,65,12,0.15)' }}>{kw}</span>
                            ))}
                          </div>
                        )}

                        {/* ── Related Videos ── */}
                        {discover.videos?.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ width: 26, height: 26, borderRadius: 8, background: 'rgba(255,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="11" height="11" viewBox="0 0 24 24" fill="#FF0000"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 700, color: P.ink, letterSpacing: '-0.01em' }}>Related Videos</span>
                              <span style={{ fontSize: 11, color: P.muted, fontWeight: 500 }}>{discover.videos.length} found</span>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
                              {discover.videos.map((v, i) => (
                                <a key={i} href={`https://www.youtube.com/watch?v=${v.id}`} target="_blank" rel="noopener noreferrer"
                                  style={{ display: 'flex', flexDirection: 'column', borderRadius: 14, border: `1px solid ${P.border}`, background: P.surface, textDecoration: 'none', overflow: 'hidden', transition: 'all 0.18s', boxShadow: '0 1px 4px rgba(29,29,31,0.05)' }}
                                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(29,29,31,0.12)'; e.currentTarget.style.borderColor = 'rgba(194,65,12,0.3)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(29,29,31,0.05)'; e.currentTarget.style.borderColor = P.border; e.currentTarget.style.transform = 'none'; }}
                                >
                                  {v.thumbnail ? (
                                    <div style={{ position: 'relative', paddingTop: '56.25%', background: '#000', flexShrink: 0 }}>
                                      <img src={v.thumbnail} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
                                      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.3) 0%, transparent 50%)' }} />
                                      <div style={{ position: 'absolute', bottom: 8, right: 8, width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' }}>
                                        <svg width="10" height="10" viewBox="0 0 24 24" fill="#fff"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                                      </div>
                                    </div>
                                  ) : (
                                    <div style={{ height: 80, background: 'rgba(194,65,12,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="rgba(194,65,12,0.4)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>
                                    </div>
                                  )}
                                  <div style={{ padding: '10px 12px 12px', flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: P.ink, lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>{v.title}</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={P.muted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                      <span style={{ fontSize: 11.5, color: P.muted, fontWeight: 500 }}>{v.channel}</span>
                                    </div>
                                  </div>
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* ── Academic Papers ── */}
                        {discover.papers?.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ width: 26, height: 26, borderRadius: 8, background: P.accentLight, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={P.accent} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 700, color: P.ink, letterSpacing: '-0.01em' }}>Academic Papers</span>
                              <span style={{ fontSize: 11, color: P.muted, fontWeight: 500 }}>{discover.papers.length} found</span>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                              {discover.papers.map((p, i) => (
                                <div key={i} style={{ borderRadius: 14, border: `1px solid ${P.border}`, background: P.surface, overflow: 'hidden', transition: 'box-shadow 0.18s, border-color 0.18s', boxShadow: '0 1px 4px rgba(29,29,31,0.05)' }}
                                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(29,29,31,0.1)'; e.currentTarget.style.borderColor = 'rgba(60,140,255,0.25)'; }}
                                  onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(29,29,31,0.05)'; e.currentTarget.style.borderColor = P.border; }}
                                >
                                  {/* Paper accent strip */}
                                  <div style={{ height: 3, background: 'linear-gradient(90deg, rgba(60,140,255,0.6), rgba(60,140,255,0.2))' }} />
                                  <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    <div style={{ fontSize: 13.5, fontWeight: 700, color: P.ink, lineHeight: 1.45, letterSpacing: '-0.01em' }}>{p.title}</div>
                                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                                      {p.authors?.length > 0 && (
                                        <span style={{ fontSize: 12, color: P.muted, fontStyle: 'italic' }}>{p.authors.slice(0, 3).join(', ')}{p.authors.length > 3 ? ' et al.' : ''}</span>
                                      )}
                                      {p.year && (
                                        <span style={{ fontSize: 11, fontWeight: 700, color: P.accent, background: P.accentLight, padding: '2px 8px', borderRadius: 99 }}>{p.year}</span>
                                      )}
                                    </div>
                                    {p.abstract && (
                                      <div style={{ fontSize: 12.5, color: P.muted, lineHeight: 1.65, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>{p.abstract}</div>
                                    )}
                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', paddingTop: 2, borderTop: `1px solid ${P.border}`, marginTop: 2 }}>
                                      {p.pdfUrl && (
                                        <a href={p.pdfUrl} target="_blank" rel="noopener noreferrer"
                                          style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 8, border: 'none', background: P.accent, color: '#fff', fontSize: 12, fontWeight: 600, textDecoration: 'none', transition: 'background 0.15s', marginTop: 4 }}
                                          onMouseEnter={e => e.currentTarget.style.background = P.accentHover}
                                          onMouseLeave={e => e.currentTarget.style.background = P.accent}
                                        >
                                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                                          PDF
                                        </a>
                                      )}
                                      {p.doi && (
                                        <a href={`https://doi.org/${p.doi}`} target="_blank" rel="noopener noreferrer"
                                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 11px', borderRadius: 8, border: `1px solid ${P.border}`, background: 'transparent', color: P.muted, fontSize: 12, fontWeight: 500, textDecoration: 'none', transition: 'all 0.15s', marginTop: 4 }}
                                          onMouseEnter={e => { e.currentTarget.style.color = P.ink; e.currentTarget.style.borderColor = P.ink; }}
                                          onMouseLeave={e => { e.currentTarget.style.color = P.muted; e.currentTarget.style.borderColor = P.border; }}
                                        >DOI ↗</a>
                                      )}
                                      {!p.pdfUrl && !p.doi && p.paperId && (
                                        <a href={`https://www.semanticscholar.org/paper/${p.paperId}`} target="_blank" rel="noopener noreferrer"
                                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '5px 11px', borderRadius: 8, border: `1px solid ${P.border}`, background: 'transparent', color: P.muted, fontSize: 12, fontWeight: 500, textDecoration: 'none', transition: 'all 0.15s', marginTop: 4 }}
                                          onMouseEnter={e => { e.currentTarget.style.color = P.ink; e.currentTarget.style.borderColor = P.ink; }}
                                          onMouseLeave={e => { e.currentTarget.style.color = P.muted; e.currentTarget.style.borderColor = P.border; }}
                                        >View Paper ↗</a>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Empty state */}
                        {!discover.videos?.length && !discover.papers?.length && (
                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 24px', gap: 12, textAlign: 'center' }}>
                            <div style={{ width: 48, height: 48, borderRadius: 14, background: P.paper, border: `1px solid ${P.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.muted }}>
                              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                            </div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: P.ink }}>No related content found</div>
                            <div style={{ fontSize: 12.5, color: P.muted, maxWidth: 260 }}>This transcript may be too niche or we couldn't find matching content.</div>
                          </div>
                        )}
                      </div>
                    ) : discover?._error ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 24px', gap: 12, textAlign: 'center' }}>
                        <div style={{ width: 48, height: 48, borderRadius: 14, background: 'rgba(180,35,24,0.07)', border: '1px solid rgba(180,35,24,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.error }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: P.error }}>Discovery failed</div>
                        <div style={{ fontSize: 12.5, color: P.muted }}>{discover._error}</div>
                        <button onClick={generateDiscover} style={{ marginTop: 4, padding: '8px 16px', borderRadius: 9, border: 'none', background: P.accent, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.background = P.accentHover}
                          onMouseLeave={e => e.currentTarget.style.background = P.accent}
                        >Try again</button>
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            </div>

            {/* ── RIGHT SIDEBAR — col 3 desktop / col 2 tablet / col 1 mobile ─── */}
            <div style={{
              gridColumn: isDesktop ? 3 : isTablet ? 2 : 1,
              gridRow: 1,
              position: isMobile ? 'fixed' : 'relative',
              top: isMobile ? topChromeOffset : undefined,
              left: isMobile ? 0 : undefined,
              right: isMobile ? 0 : undefined,
              bottom: isMobile ? mobilePanelBottomInsetCss : undefined,
              zIndex: isMobile ? 50 : undefined,
              display: isMobile ? ((mobilePanel === 'ai' || mobilePanel === 'insights') ? 'flex' : 'none') : 'flex',
              flexDirection: 'column', overflowY: isDesktop ? 'auto' : 'hidden', background: '#FFFFFF',
            }}>
              {/* ── Sidebar tab bar — tablet only (mobile uses bottom nav) ── */}
              {isTablet && <div style={{ flexShrink: 0, display: 'flex', alignItems: 'flex-end', gap: 2, padding: '6px 10px 0', background: P.paper, borderBottom: `1px solid ${P.border}`, overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                {[
                  { key: 'ai',         label: 'AI Chat',     closeable: false, icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> },
                  { key: 'insights',   label: 'Insights',    closeable: false, icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg> },
                  ...(summary || summarizing ? [{ key: 'summary', label: 'Summary', closeable: true, onClose: () => { setSummary(''); setSummarizing(false); if (sidebarTab === 'summary') setSidebarTab('ai'); }, icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg> }] : []),
                  ...(flashcards.length > 0 || flashcardsExhausted || flashcardsLoading ? [{ key: 'flashcards', label: 'Flashcards', closeable: true, onClose: () => { setFlashcards([]); setFlashcardsExhausted(false); setFlashcardsExhaustedReason(''); if (sidebarTab === 'flashcards') setSidebarTab('ai'); }, icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg> }] : []),
                  ...(studyGuide || studyGuideLoading ? [{ key: 'study-guide', label: 'Study Guide', closeable: true, onClose: () => { setStudyGuide(null); setStudyGuideLoading(false); if (sidebarTab === 'study-guide') setSidebarTab('ai'); }, icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg> }] : []),
                  ...(academicInsights || academicInsightsLoading ? [{ key: 'academic', label: 'Academic', closeable: true, onClose: () => { setAcademicInsights(null); setAcademicInsightsLoading(false); if (sidebarTab === 'academic') setSidebarTab('ai'); }, icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg> }] : []),
                  ...(discover && !discover._error || discoverLoading ? [{ key: 'discover', label: 'Discover', closeable: true, onClose: () => { setDiscover(null); setDiscoverLoading(false); if (sidebarTab === 'discover') setSidebarTab('ai'); }, icon: <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg> }] : []),
                ].sort((a, b) => {
                  const ai = sidebarTabOrder.indexOf(a.key);
                  const bi = sidebarTabOrder.indexOf(b.key);
                  return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                }).map(tab => {
                  const isAct = sidebarTab === tab.key;
                  const isOver = dragOverKey === tab.key && dragTabKey !== tab.key;
                  return (
                    <button
                      key={tab.key}
                      draggable
                      onDragStart={() => setDragTabKey(tab.key)}
                      onDragOver={e => { e.preventDefault(); setDragOverKey(tab.key); }}
                      onDrop={e => {
                        e.preventDefault();
                        if (dragTabKey && dragTabKey !== tab.key) {
                          setSidebarTabOrder(prev => {
                            const arr = [...prev];
                            const fi = arr.indexOf(dragTabKey), ti = arr.indexOf(tab.key);
                            if (fi !== -1 && ti !== -1) { arr.splice(fi, 1); arr.splice(ti, 0, dragTabKey); }
                            return arr;
                          });
                        }
                        setDragTabKey(null); setDragOverKey(null);
                      }}
                      onDragEnd={() => { setDragTabKey(null); setDragOverKey(null); }}
                      onClick={() => { setSidebarTab(tab.key); if (isMobile) setMobilePanel(tab.key === 'ai' ? 'ai' : 'insights'); }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: tab.closeable ? '5px 6px 5px 10px' : '5px 10px', fontSize: 11.5,
                        fontWeight: isAct ? 600 : 500,
                        border: isAct ? '1px solid rgba(60,140,255,0.25)' : isOver ? `1px solid ${P.accent}` : '1px solid transparent',
                        borderBottom: isAct ? '1px solid #FFFFFF' : '1px solid transparent',
                        borderRadius: '7px 7px 0 0', marginBottom: '-1px',
                        background: isAct ? P.accentLight : isOver ? 'rgba(60,140,255,0.06)' : 'transparent',
                        color: isAct ? P.accent : P.muted,
                        cursor: 'grab', transition: 'all 0.15s', whiteSpace: 'nowrap',
                        opacity: dragTabKey === tab.key ? 0.4 : 1,
                      }}
                      onMouseEnter={e => { if (!isAct) { e.currentTarget.style.background = 'rgba(29,29,31,0.05)'; e.currentTarget.style.color = P.ink; }}}
                      onMouseLeave={e => { if (!isAct) { e.currentTarget.style.background = isOver ? 'rgba(60,140,255,0.06)' : 'transparent'; e.currentTarget.style.color = P.muted; }}}
                    >
                      {tab.icon}{tab.label}
                      {tab.closeable && (
                        <span
                          onClick={e => { e.stopPropagation(); tab.onClose(); }}
                          style={{ marginLeft: 3, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 16, height: 16, borderRadius: 3, flexShrink: 0, opacity: 0.5 }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = P.error; e.currentTarget.style.background = 'rgba(180,35,24,0.1)'; }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.color = ''; e.currentTarget.style.background = 'transparent'; }}
                        >
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>}

              {/* ScribeSnap AI Chat — always on desktop, tab-gated on mobile */}
              {(isDesktop || sidebarTab === 'ai') && <div ref={qaRef} style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }}>

                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '13px 18px 11px', borderBottom: `1px solid ${P.border}` }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 9, flexShrink: 0,
                    background: 'linear-gradient(135deg, rgba(60,140,255,0.13) 0%, rgba(60,140,255,0.05) 100%)',
                    border: `1.5px solid rgba(60,140,255,0.2)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <img src="/scribesnap_icon_wave.svg" alt="ScribeSnap AI" style={{ width: 19, height: 19 }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 13.5, fontWeight: 700, color: P.ink, lineHeight: 1.25 }}>ScribeSnap AI</div>
                    <div style={{ fontSize: 10.5, color: P.muted, lineHeight: 1 }}>Ask anything about this video</div>
                  </div>
                  {qaMessages.length > 0 && (
                    <button onClick={() => setQaMessages([])} style={{
                      marginLeft: 'auto', border: `1px solid ${P.border}`, background: 'none',
                      cursor: 'pointer', color: P.muted, fontSize: 11, fontWeight: 600,
                      padding: '3px 9px', borderRadius: 6, transition: 'all 0.15s',
                    }}
                      onMouseEnter={e => { e.currentTarget.style.background = P.paper; e.currentTarget.style.color = P.ink; e.currentTarget.style.borderColor = P.ink; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = P.muted; e.currentTarget.style.borderColor = P.border; }}
                    >Clear</button>
                  )}
                </div>

                {/* Empty state — intentional, not a bug */}
                {qaMessages.length === 0 && (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '20px 18px 16px', background: 'linear-gradient(180deg, rgba(60,140,255,0.025) 0%, transparent 50%)' }}>
                    {/* Hero */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginBottom: 20, paddingTop: 4 }}>
                      <div style={{
                        width: 54, height: 54, borderRadius: 17,
                        background: 'linear-gradient(135deg, rgba(60,140,255,0.14) 0%, rgba(60,140,255,0.05) 100%)',
                        border: '1.5px solid rgba(60,140,255,0.2)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 4px 16px rgba(60,140,255,0.1)',
                      }}>
                        <img src="/scribesnap_icon_wave.svg" alt="" style={{ width: 32, height: 32 }} />
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: P.ink, marginBottom: 3 }}>Ask anything</div>
                        <div style={{ fontSize: 11.5, color: P.muted, lineHeight: 1.4 }}>Questions answered from the video transcript</div>
                      </div>
                    </div>
                    {/* Suggestions */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 7 }}>
                      {DEMO_CHIPS.map((chip, i) => {
                        const chipIcons = [
                          <svg key="sum" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
                          <svg key="kp" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>,
                          <svg key="q" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
                          <svg key="arg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>,
                        ];
                        return (
                          <button key={chip} onClick={() => askQuestion(chip)} style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                            gap: 7, padding: '13px 10px 12px', borderRadius: 11, border: `1px solid ${P.border}`,
                            background: '#fff', fontSize: 11.5, color: P.ink, fontWeight: 500,
                            cursor: 'pointer', transition: 'all 0.15s', textAlign: 'center', lineHeight: 1.4,
                            boxShadow: '0 1px 3px rgba(29,29,31,0.04)',
                          }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = P.accent; e.currentTarget.style.background = P.accentLight; e.currentTarget.style.color = P.accent; e.currentTarget.style.boxShadow = '0 2px 8px rgba(60,140,255,0.1)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = P.border; e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = P.ink; e.currentTarget.style.boxShadow = '0 1px 3px rgba(29,29,31,0.04)'; }}
                          >
                            <span style={{ opacity: 0.45 }}>{chipIcons[i]}</span>
                            {chip}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Chat messages */}
                {qaMessages.length > 0 && (
                  <div ref={chatMessagesRef} style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12, padding: '14px 18px 8px' }}>
                    {qaMessages.map((msg, i) => (
                      <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                        {msg.role === 'ai' ? (
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, maxWidth: '93%' }}>
                            <AiBubbleAvatar />
                            <div style={{
                              padding: '9px 13px',
                              borderRadius: '3px 12px 12px 12px',
                              background: msg.isError ? 'rgba(180,35,24,0.05)' : P.paper,
                              border: `1px solid ${msg.isError ? 'rgba(180,35,24,0.2)' : P.border}`,
                              fontSize: 13, lineHeight: 1.65,
                              color: msg.isError ? P.error : P.ink,
                            }}>
                              {msg.text.split('\n').map((line, li, arr) => (
                                <React.Fragment key={li}>{renderChatLine(line)}{li < arr.length - 1 && <br />}</React.Fragment>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div style={{
                            maxWidth: '86%', padding: '9px 13px',
                            borderRadius: '12px 12px 3px 12px',
                            background: P.accent, fontSize: 13, lineHeight: 1.6, color: 'white',
                          }}>{msg.text}</div>
                        )}
                      </div>
                    ))}
                    {qaLoading && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <AiBubbleAvatar />
                        <div style={{ padding: '11px 15px', borderRadius: '3px 12px 12px 12px', background: P.paper, border: `1px solid ${P.border}`, display: 'flex', gap: 4, alignItems: 'center' }}>
                          {[0, 1, 2].map(d => <div key={d} style={{ width: 5, height: 5, borderRadius: '50%', background: P.accent, opacity: 0.6, animation: `bounce 1.2s ease-in-out ${d * 0.2}s infinite` }} />)}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Composer — at the BOTTOM, above footer */}
                <div style={{
                  paddingTop: 11,
                  paddingLeft: isMobile ? 14 : 16,
                  paddingRight: isMobile ? 14 : 16,
                  paddingBottom: isMobile ? 18 : 12,
                  marginBottom: isMobile ? 12 : 0,
                  borderTop: `1px solid ${P.border}`,
                  background: 'linear-gradient(0deg, rgba(60,140,255,0.04) 0%, transparent 100%)',
                }}>
                  <div
                    data-composer="true"
                    style={{
                      display: 'flex', alignItems: 'center',
                      background: '#fff',
                      border: `1.5px solid ${P.border}`,
                      borderRadius: 14,
                      padding: '6px 6px 6px 8px',
                      transition: 'border-color 0.2s, box-shadow 0.2s',
                      boxShadow: '0 1px 4px rgba(29,29,31,0.06)',
                    }}
                    onClick={() => qaInputRef.current?.focus()}
                  >
                    <AiBubbleAvatar size={24} iconSize={13} radius={7} />
                    <input
                      ref={qaInputRef}
                      value={qaQuestion}
                      onChange={e => setQaQuestion(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && !e.shiftKey && askQuestion()}
                      placeholder={qaMessages.length === 0 ? 'Ask anything about this video…' : 'Ask a follow-up…'}
                      disabled={qaLoading}
                      style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: 13.5, color: P.ink, padding: '4px 0', marginLeft: 9 }}
                      onFocus={e => {
                        const w = e.currentTarget.closest('[data-composer]');
                        if (w) { w.style.borderColor = P.accent; w.style.boxShadow = '0 0 0 3px rgba(60,140,255,0.12)'; }
                      }}
                      onBlur={e => {
                        const w = e.currentTarget.closest('[data-composer]');
                        if (w) { w.style.borderColor = P.border; w.style.boxShadow = '0 1px 4px rgba(29,29,31,0.06)'; }
                      }}
                    />
                    <button
                      onClick={e => { e.stopPropagation(); askQuestion(); }}
                      disabled={!qaQuestion.trim() || qaLoading}
                      style={{
                        flexShrink: 0, width: 42, height: 42,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: 10, border: 'none',
                        background: qaQuestion.trim() && !qaLoading
                          ? 'linear-gradient(135deg, #5ba4f5 0%, #3C8CFF 100%)'
                          : 'rgba(29,29,31,0.05)',
                        color: qaQuestion.trim() && !qaLoading ? 'white' : P.muted,
                        cursor: qaQuestion.trim() && !qaLoading ? 'pointer' : 'default',
                        transition: 'all 0.2s',
                        boxShadow: qaQuestion.trim() && !qaLoading ? '0 2px 8px rgba(60,140,255,0.3)' : 'none',
                      }}
                      onMouseEnter={e => {
                        if (qaQuestion.trim() && !qaLoading) {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #6bbcff 0%, #1F6BFF 100%)';
                          e.currentTarget.style.boxShadow = '0 3px 12px rgba(60,140,255,0.4)';
                        }
                      }}
                      onMouseLeave={e => {
                        if (qaQuestion.trim() && !qaLoading) {
                          e.currentTarget.style.background = 'linear-gradient(135deg, #5ba4f5 0%, #3C8CFF 100%)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(60,140,255,0.3)';
                        }
                      }}
                    >
                      {qaLoading
                        ? <SpinnerIcon size={13} />
                        : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                      }
                    </button>
                  </div>
                  {qaQuestion.trim() && (
                    <div style={{ fontSize: 10.5, color: P.muted, marginTop: 5, textAlign: 'right', paddingRight: 2 }}>↵ to send</div>
                  )}
                </div>

              </div>}


              {/* Insights — tab-gated
              {/* Insights — tab-gated on mobile/tablet (rich cards) */}
              {!isDesktop && sidebarTab === 'insights' && (
                /* ── Mobile: rich card design ── */
                  <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 14px 20px' }}>
                    <div style={{ marginBottom: 10 }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: P.ink, letterSpacing: '-0.01em', marginBottom: 2 }}>AI Insights</div>
                      <div style={{ fontSize: 11.5, color: P.muted, lineHeight: 1.4 }}>Generate smart study tools in seconds.</div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {[
                        { title: 'Summary', sub: 'Key points & bullet overview', color: P.accent,
                          bg: 'linear-gradient(135deg,rgba(60,140,255,0.12),rgba(60,140,255,0.06))', border: 'rgba(60,140,255,0.2)',
                          icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
                          label: summary ? 'View' : summarizing ? 'Generating…' : 'Generate',
                          onClick: summary ? () => isMobile ? setMobilePanel('summary') : setSidebarTab('summary') : summarize,
                          active: !!summary, loading: summarizing, badge: summary ? 'Ready' : null },
                        { title: 'Flashcards', sub: 'Q&A cards — flip to reveal', color: '#D97706',
                          bg: 'linear-gradient(135deg,rgba(217,119,6,0.12),rgba(217,119,6,0.06))', border: 'rgba(217,119,6,0.2)',
                          icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>,
                          label: flashcards.length > 0 ? 'View' : flashcardsLoading ? 'Generating…' : 'Generate',
                          onClick: flashcards.length > 0 ? () => isMobile ? setMobilePanel('flashcards') : setSidebarTab('flashcards') : generateFlashcards,
                          active: flashcards.length > 0, loading: flashcardsLoading, badge: flashcards.length > 0 ? `${flashcards.length} cards` : null },
                        { title: 'Study Guide', sub: 'Objectives, concepts & review', color: P.success,
                          bg: 'linear-gradient(135deg,rgba(15,118,110,0.12),rgba(15,118,110,0.06))', border: 'rgba(15,118,110,0.2)',
                          icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>,
                          label: (studyGuide && !studyGuide._error) ? 'View' : studyGuideLoading ? 'Generating…' : 'Generate',
                          onClick: (studyGuide && !studyGuide._error) ? () => isMobile ? setMobilePanel('study-guide') : setSidebarTab('study-guide') : generateStudyGuide,
                          active: !!(studyGuide && !studyGuide._error), loading: studyGuideLoading, badge: (studyGuide && !studyGuide._error) ? 'Ready' : null },
                        { title: 'Academic', sub: 'References, claims & glossary', color: '#7C3AED',
                          bg: 'linear-gradient(135deg,rgba(124,58,237,0.12),rgba(124,58,237,0.06))', border: 'rgba(124,58,237,0.2)',
                          icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/><line x1="9" y1="9" x2="15" y2="9"/><line x1="9" y1="13" x2="13" y2="13"/></svg>,
                          label: (academicInsights && !academicInsights._error) ? 'View' : academicInsightsLoading ? 'Generating…' : 'Generate',
                          onClick: (academicInsights && !academicInsights._error) ? () => isMobile ? setMobilePanel('academic') : setSidebarTab('academic') : generateAcademicInsights,
                          active: !!(academicInsights && !academicInsights._error), loading: academicInsightsLoading, badge: (academicInsights && !academicInsights._error) ? 'Ready' : null },
                        { title: 'Discover', sub: 'Related videos & papers', color: '#C2410C',
                          bg: 'linear-gradient(135deg,rgba(194,65,12,0.12),rgba(194,65,12,0.06))', border: 'rgba(194,65,12,0.2)',
                          icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>,
                          label: (discover && !discover._error) ? 'View' : discoverLoading ? 'Finding…' : 'Discover',
                          onClick: (discover && !discover._error) ? () => setSidebarTab('discover') : generateDiscover,
                          active: !!(discover && !discover._error), loading: discoverLoading, badge: discover && !discover._error ? `${(discover.videos?.length||0) + (discover.papers?.length||0)} results` : null },
                      ].map(item => (
                        <div key={item.title} onClick={item.loading ? undefined : item.onClick}
                          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 13px', borderRadius: 14, cursor: item.loading ? 'default' : 'pointer', background: item.active ? item.bg : '#fff', border: `1.5px solid ${item.active ? item.border : P.border}`, boxShadow: item.active ? `0 2px 10px ${item.border}` : '0 1px 3px rgba(29,29,31,0.05)', transition: 'all 0.18s' }}>
                          <div style={{ width: 42, height: 42, borderRadius: 12, flexShrink: 0, background: item.active ? item.bg : 'rgba(0,0,0,0.04)', border: `1.5px solid ${item.active ? item.border : 'transparent'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: item.active ? item.color : P.muted }}>
                            {item.loading ? <SpinnerIcon size={14} /> : item.icon}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                              <span style={{ fontSize: 13.5, fontWeight: 700, color: item.active ? item.color : P.ink }}>{item.title}</span>
                              {item.badge && <span style={{ fontSize: 9.5, fontWeight: 700, color: item.color, background: item.bg, border: `1px solid ${item.border}`, padding: '1px 6px', borderRadius: 99 }}>{item.badge}</span>}
                            </div>
                            <div style={{ fontSize: 11.5, color: P.muted, lineHeight: 1.3 }}>{item.sub}</div>
                          </div>
                          <div style={{ flexShrink: 0, padding: '5px 11px', borderRadius: 8, fontSize: 11.5, fontWeight: 600, border: `1.5px solid ${item.active ? item.border : P.border}`, background: item.active ? item.bg : 'transparent', color: item.active ? item.color : P.muted, whiteSpace: 'nowrap' }}>
                            {item.label}
                          </div>
                        </div>
                      ))}
                    </div>
                    {studyGuide?._error && (
                      <div style={{ marginTop: 12, padding: '10px 14px', background: 'rgba(180,35,24,0.05)', border: `1px solid rgba(180,35,24,0.2)`, borderRadius: 10, fontSize: 12.5, color: P.error }}>
                        Failed to generate study guide: {studyGuide._error}
                      </div>
                    )}
                  </div>
              )}

              {/* Summary sidebar tab

              {/* Summary sidebar tab — mobile/tablet only */}
              {!isDesktop && sidebarTab === 'summary' && (
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px 18px' }}>
                  {summarizing ? (
                    <div style={{ padding: '60px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: P.muted, fontSize: 13 }}>
                      <SpinnerIcon size={14} /> Summarizing…
                    </div>
                  ) : summary ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: P.ink }}>AI-generated summary</div>
                        <div style={{ display: 'flex', gap: 5 }}>
                          <button onClick={() => { navigator.clipboard.writeText(summary).then(() => { setSummaryCopied(true); setTimeout(() => setSummaryCopied(false), 2000); }); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, border: `1px solid ${P.border}`, background: 'none', cursor: 'pointer', borderRadius: 6, padding: '4px 9px', fontSize: 11, fontWeight: 600, color: summaryCopied ? P.success : P.muted }}>
                            {summaryCopied ? <CheckIcon /> : <CopyIcon />} {summaryCopied ? 'Copied!' : 'Copy'}
                          </button>
                          <button onClick={() => { setSummary(''); setSidebarTab('insights'); }}
                            style={{ border: `1px solid ${P.border}`, background: 'none', cursor: 'pointer', color: P.muted, fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6 }}>Clear</button>
                        </div>
                      </div>
                      <div style={{ padding: '16px 18px', background: '#fff', borderRadius: 12, border: `1px solid ${P.border}`, boxShadow: '0 1px 4px rgba(29,29,31,0.04)', fontSize: 13.5, lineHeight: 1.75, color: P.ink, whiteSpace: 'pre-wrap' }}>{summary}</div>
                    </div>
                  ) : null}
                </div>
              )}

              {/* Flashcards sidebar tab — mobile/tablet only */}
              {!isDesktop && sidebarTab === 'flashcards' && (
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
                  {flashcardsLoading ? (
                    <div style={{ padding: '60px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: P.muted, fontSize: 13 }}>
                      <SpinnerIcon size={14} /> Generating flashcards…
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: P.ink }}>{flashcards.length} cards · {flashcardKnown.size} known</div>
                        {flashcards.length > 0 && (
                          <button onClick={() => { setFlashcardIndex(0); setFlashcardFlipped(false); setShowFlashcardModal(true); }}
                            style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 7, border: 'none', background: P.accent, color: 'white', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                            Study
                          </button>
                        )}
                      </div>
                      {flashcards.length > 0 && (
                        <div style={{ height: 5, borderRadius: 3, background: P.border, marginBottom: 14, overflow: 'hidden' }}>
                          <div style={{ height: '100%', borderRadius: 3, background: P.success, width: `${(flashcardKnown.size / flashcards.length) * 100}%`, transition: 'width 0.4s' }} />
                        </div>
                      )}
                      {flashcardsExhausted && (
                        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.07)', marginBottom: 10 }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#D97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                          <div style={{ fontSize: 12, color: P.muted, lineHeight: 1.5 }}>{flashcardsExhaustedReason}</div>
                        </div>
                      )}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {flashcards.map((card, i) => {
                          const isExpanded = expandedCards.has(i);
                          const isKnown = flashcardKnown.has(i);
                          return (
                            <div key={i} style={{ borderRadius: 9, border: `1px solid ${isKnown ? 'rgba(15,118,110,0.3)' : P.border}`, background: isKnown ? 'rgba(15,118,110,0.04)' : '#fff', overflow: 'hidden' }}>
                              <div onClick={() => setExpandedCards(prev => { const next = new Set(prev); if (next.has(i)) next.delete(i); else next.add(i); return next; })}
                                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', cursor: 'pointer', userSelect: 'none' }}>
                                <div style={{ width: 22, height: 22, borderRadius: 5, background: isKnown ? 'rgba(15,118,110,0.12)' : P.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <span style={{ fontSize: 9.5, fontWeight: 700, color: isKnown ? P.success : P.muted }}>{i + 1}</span>
                                </div>
                                <div style={{ flex: 1, minWidth: 0, fontSize: 12, fontWeight: 600, color: P.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isExpanded ? 'normal' : 'nowrap' }}>{card.question}</div>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={P.muted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                              </div>
                              {isExpanded && (
                                <div style={{ borderTop: `1px solid ${P.border}`, padding: '10px 12px', background: P.paper }}>
                                  {card.topic && <div style={{ fontSize: 10, fontWeight: 700, color: '#D97706', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{card.topic}</div>}
                                  <div style={{ fontSize: 12.5, lineHeight: 1.6, color: P.ink }}>{card.answer}</div>
                                  <button onClick={e => { e.stopPropagation(); setFlashcardKnown(prev => { const next = new Set(prev); if (isKnown) next.delete(i); else next.add(i); return next; }); }}
                                    style={{ marginTop: 8, width: '100%', padding: '5px 0', borderRadius: 6, border: `1px solid ${isKnown ? 'rgba(15,118,110,0.4)' : P.border}`, background: isKnown ? 'rgba(15,118,110,0.08)' : 'none', color: isKnown ? P.success : P.muted, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                                    {isKnown ? '✓ Known' : 'Mark as Known'}
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {!flashcardsExhausted && (
                        <button onClick={generateMoreFlashcards} disabled={flashcardsMoreLoading}
                          style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', padding: '7px 0', borderRadius: 7, border: `1.5px dashed ${P.border}`, background: 'none', color: P.ink, fontSize: 12, fontWeight: 600, cursor: flashcardsMoreLoading ? 'default' : 'pointer', opacity: flashcardsMoreLoading ? 0.6 : 1 }}>
                          {flashcardsMoreLoading ? <><SpinnerIcon size={11} />Generating…</> : <>+ More Cards</>}
                        </button>
                      )}
                      {flashcardKnown.size > 0 && (
                        <button onClick={() => setFlashcardKnown(new Set())}
                          style={{ marginTop: 6, display: 'block', width: '100%', padding: '7px 0', borderRadius: 7, border: `1px solid ${P.border}`, background: 'none', color: P.muted, fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }}>
                          Reset Progress
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Academic Insights sidebar tab — mobile/tablet only */}
              {!isDesktop && sidebarTab === 'academic' && (
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
                  {academicInsightsLoading ? (
                    <div style={{ padding: '60px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#7C3AED', fontSize: 13 }}>
                      <SpinnerIcon size={14} /> Generating academic insights…
                    </div>
                  ) : academicInsights && !academicInsights._error ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {academicInsights.references?.length > 0 && (
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#7C3AED', marginBottom: 8 }}>References ({academicInsights.references.length})</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {academicInsights.references.map((ref, i) => (
                              <div key={i} style={{ padding: '10px 12px', background: '#fff', borderRadius: 9, border: `1px solid ${P.border}` }}>
                                <div style={{ fontSize: 12.5, fontWeight: 600, color: P.ink }}>{ref.title || ref.author}</div>
                                <div style={{ fontSize: 11.5, color: P.muted, marginTop: 2 }}>{[ref.author, ref.year].filter(Boolean).join(' · ')}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {academicInsights.claims?.length > 0 && (
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#7C3AED', marginBottom: 8 }}>Key Claims</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {academicInsights.claims.map((c, i) => (
                              <div key={i} style={{ padding: '9px 11px', background: c.supported ? 'rgba(15,118,110,0.05)' : 'rgba(180,35,24,0.05)', borderRadius: 8, border: `1px solid ${c.supported ? 'rgba(15,118,110,0.15)' : 'rgba(180,35,24,0.15)'}`, fontSize: 12.5, color: P.ink, lineHeight: 1.5 }}>
                                <span style={{ color: c.supported ? P.success : P.error, fontWeight: 700, marginRight: 5 }}>{c.supported ? '✓' : '⚠'}</span>{c.claim}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {academicInsights.glossary?.length > 0 && (
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#7C3AED', marginBottom: 8 }}>Glossary</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {academicInsights.glossary.map((g, i) => (
                              <div key={i} style={{ padding: '10px 12px', background: '#fff', borderRadius: 9, border: `1px solid ${P.border}` }}>
                                <div style={{ fontSize: 12.5, fontWeight: 700, color: '#7C3AED', marginBottom: 3 }}>{g.term}</div>
                                <div style={{ fontSize: 12, color: P.muted, lineHeight: 1.5 }}>{g.definition}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {academicInsights.researchGaps?.length > 0 && (
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#7C3AED', marginBottom: 8 }}>Research Gaps</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {academicInsights.researchGaps.map((gap, i) => (
                              <div key={i} style={{ padding: '9px 11px', background: 'rgba(124,58,237,0.05)', borderRadius: 8, border: '1px solid rgba(124,58,237,0.12)', fontSize: 12.5, color: P.ink, lineHeight: 1.5 }}>{gap}</div>
                            ))}
                          </div>
                        </div>
                      )}
                      <button onClick={() => { setAcademicInsights(null); setSidebarTab('insights'); }}
                        style={{ marginTop: 4, border: `1px solid ${P.border}`, background: 'none', cursor: 'pointer', color: P.muted, fontSize: 11.5, fontWeight: 600, padding: '6px 14px', borderRadius: 7 }}>
                        Clear
                      </button>
                    </div>
                  ) : null}
                </div>
              )}

              {/* Study Guide sidebar tab — mobile/tablet only */}
              {!isDesktop && sidebarTab === 'study-guide' && (
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px' }}>
                  {studyGuideLoading ? (
                    <div style={{ padding: '60px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: P.muted, fontSize: 13 }}>
                      <SpinnerIcon size={14} /> Generating study guide…
                    </div>
                  ) : studyGuide && !studyGuide._error ? (() => {
                    const sg = studyGuide;
                    return (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {sg.overview && (
                          <div style={{ padding: '14px 16px', background: P.accentLight, borderRadius: 12, border: `1px solid rgba(60,140,255,0.15)` }}>
                            <div style={{ fontSize: 11, fontWeight: 700, color: P.accent, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Overview</div>
                            <div style={{ fontSize: 13, lineHeight: 1.65, color: P.ink }}>{sg.overview}</div>
                          </div>
                        )}
                        {sg.objectives && sg.objectives.length > 0 && (
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: P.ink, marginBottom: 8 }}>Learning Objectives</div>
                            <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 5 }}>
                              {sg.objectives.map((obj, i) => <li key={i} style={{ fontSize: 12.5, color: P.ink, lineHeight: 1.55 }}>{obj}</li>)}
                            </ul>
                          </div>
                        )}
                        {sg.keyConcepts && sg.keyConcepts.length > 0 && (
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: P.ink, marginBottom: 8 }}>Key Concepts</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                              {sg.keyConcepts.map((kc, i) => (
                                <div key={i} style={{ padding: '10px 12px', background: '#fff', borderRadius: 9, border: `1px solid ${P.border}` }}>
                                  <div style={{ fontSize: 12.5, fontWeight: 700, color: P.ink, marginBottom: 3 }}>{kc.concept}</div>
                                  <div style={{ fontSize: 12, color: P.muted, lineHeight: 1.5 }}>{kc.explanation}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {sg.sections && sg.sections.length > 0 && (
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: P.ink, marginBottom: 8 }}>Sections</div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                              {sg.sections.map((sec, i) => (
                                <div key={i} style={{ padding: '12px 14px', background: '#fff', borderRadius: 10, border: `1px solid ${P.border}` }}>
                                  <div style={{ fontSize: 13, fontWeight: 700, color: P.ink, marginBottom: 5 }}>{sec.title}</div>
                                  {sec.points && sec.points.length > 0 && (
                                    <ul style={{ margin: 0, paddingLeft: 16, display: 'flex', flexDirection: 'column', gap: 3 }}>
                                      {sec.points.map((pt, j) => <li key={j} style={{ fontSize: 12, color: P.ink, lineHeight: 1.5 }}>{pt}</li>)}
                                    </ul>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {sg.reviewQuestions && sg.reviewQuestions.length > 0 && (
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 700, color: P.ink, marginBottom: 8 }}>Review Questions</div>
                            <ol style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 5 }}>
                              {sg.reviewQuestions.map((q, i) => <li key={i} style={{ fontSize: 12.5, color: P.ink, lineHeight: 1.55 }}>{q}</li>)}
                            </ol>
                          </div>
                        )}
                        <button onClick={() => { setStudyGuide(null); setSidebarTab('insights'); }}
                          style={{ marginTop: 4, border: `1px solid ${P.border}`, background: 'none', cursor: 'pointer', color: P.muted, fontSize: 11.5, fontWeight: 600, padding: '6px 14px', borderRadius: 7 }}>
                          Clear Study Guide
                        </button>
                      </div>
                    );
                  })() : null}
                </div>
              )}

              {/* Discover sidebar tab — mobile/tablet only */}
              {!isDesktop && sidebarTab === 'discover' && (
                <div style={{ flex: 1, overflowY: 'auto', padding: '16px 18px 32px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {discoverLoading ? (
                    <div style={{ padding: '60px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: P.muted, fontSize: 13 }}>
                      <SpinnerIcon size={14} /> Finding related content…
                    </div>
                  ) : discover && !discover._error ? (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 34, height: 34, borderRadius: 10, background: 'rgba(194,65,12,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#C2410C', flexShrink: 0 }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/></svg>
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 700, color: P.ink }}>Discover</div>
                          <div style={{ fontSize: 11.5, color: P.muted }}>Related videos &amp; papers</div>
                        </div>
                        <button onClick={() => { setDiscover(null); setSidebarTab('insights'); }}
                          style={{ border: `1px solid ${P.border}`, background: 'none', cursor: 'pointer', color: P.muted, fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 6 }}>Clear</button>
                      </div>
                      {discover.keywords?.length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                          {discover.keywords.map((kw, i) => (
                            <span key={i} style={{ fontSize: 11, fontWeight: 600, color: '#C2410C', background: 'rgba(194,65,12,0.08)', padding: '3px 8px', borderRadius: 99 }}>{kw}</span>
                          ))}
                        </div>
                      )}
                      {discover.videos?.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: P.muted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Videos · {discover.videos.length}</div>
                          {discover.videos.map((v, i) => (
                            <a key={i} href={`https://www.youtube.com/watch?v=${v.id}`} target="_blank" rel="noopener noreferrer"
                              style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '10px 12px', borderRadius: 10, border: `1px solid ${P.border}`, background: P.surface, textDecoration: 'none', transition: 'all 0.15s' }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor = P.accent; e.currentTarget.style.background = P.paper; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor = P.border; e.currentTarget.style.background = P.surface; }}
                            >
                              {v.thumbnail && <img src={v.thumbnail} alt="" style={{ width: 60, height: 34, borderRadius: 5, objectFit: 'cover', flexShrink: 0 }} />}
                              <div style={{ minWidth: 0, flex: 1 }}>
                                <div style={{ fontSize: 12.5, fontWeight: 600, color: P.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.title}</div>
                                <div style={{ fontSize: 11, color: P.muted, marginTop: 2 }}>{v.channel}</div>
                              </div>
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={P.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                            </a>
                          ))}
                        </div>
                      )}
                      {discover.papers?.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: P.muted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Papers · {discover.papers.length}</div>
                          {discover.papers.map((p, i) => (
                            <div key={i} style={{ padding: '12px 14px', borderRadius: 10, border: `1px solid ${P.border}`, background: P.surface, display: 'flex', flexDirection: 'column', gap: 5 }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: P.ink, lineHeight: 1.4 }}>{p.title}</div>
                              <div style={{ fontSize: 11, color: P.muted }}>{p.authors?.[0]}{p.authors?.length > 1 ? ' et al.' : ''}{p.year ? ` · ${p.year}` : ''}</div>
                              {p.abstract && <div style={{ fontSize: 12, color: P.muted, lineHeight: 1.55, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>{p.abstract}</div>}
                              <div style={{ display: 'flex', gap: 6, marginTop: 2 }}>
                                {p.pdfUrl && <a href={p.pdfUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, fontWeight: 600, color: P.accent, background: P.accentLight, padding: '3px 9px', borderRadius: 6, textDecoration: 'none' }}>PDF</a>}
                                {p.doi && <a href={`https://doi.org/${p.doi}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, fontWeight: 500, color: P.muted, textDecoration: 'none' }}>DOI ↗</a>}
                                {!p.pdfUrl && !p.doi && p.paperId && <a href={`https://www.semanticscholar.org/paper/${p.paperId}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, fontWeight: 500, color: P.muted, textDecoration: 'none' }}>View ↗</a>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {!discover.videos?.length && !discover.papers?.length && (
                        <div style={{ padding: '32px 0', textAlign: 'center', color: P.muted, fontSize: 13 }}>No related content found.</div>
                      )}
                    </>
                  ) : discover?._error ? (
                    <div style={{ color: P.error, fontSize: 13 }}>Failed: {discover._error}</div>
                  ) : null}
                </div>
              )}

            </div>
          </div>

          {/* ── MOBILE BOTTOM NAV ─────────────────────────────────────────────── */}
          {isMobile && (() => {
            const mobileTabs = [
              { key: 'transcript', label: 'Transcript', icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10.5 12 3l9 7.5"/><path d="M5 9.5V20h14V9.5"/></svg> },
              { key: 'ai', label: 'AI Chat', icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 6h8a3 3 0 0 1 3 3v5a3 3 0 0 1-3 3h-4l-3.5 2.5V17H7a3 3 0 0 1-3-3V9a3 3 0 0 1 3-3z"/><path d="M17.8 4.6v2.2"/><path d="M16.7 5.7h2.2"/><circle cx="9.5" cy="11.7" r="0.8" fill="currentColor" stroke="none"/><circle cx="12" cy="11.7" r="0.8" fill="currentColor" stroke="none"/><circle cx="14.5" cy="11.7" r="0.8" fill="currentColor" stroke="none"/></svg> },
              { key: 'insights', label: 'Insights', icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.8 8.6a5.1 5.1 0 0 0-8.8-3.5 5.1 5.1 0 0 0-8.8 3.5c0 5.1 8.8 10.6 8.8 10.6s8.8-5.5 8.8-10.6z"/></svg> },
              { key: 'new-search', label: 'New Search', icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg> },
            ];
            const hasDynamic = mobileTabs.length > 4;
            const insightPanels = new Set(['insights', 'summary', 'flashcards', 'study-guide', 'academic']);
            return (
              <div ref={mobileNavRef} className="no-scrollbar" style={{
                position: 'fixed', bottom: MOBILE_BOTTOM_NAV_LIFT, left: 0, right: 0, zIndex: 100,
                height: MOBILE_BOTTOM_NAV_HEIGHT,
                background: isMobileKeyboardOpen ? 'transparent' : '#FFFFFF',
                borderTop: isMobileKeyboardOpen ? '1px solid transparent' : '1px solid rgba(29,29,31,0.12)',
                borderRadius: '22px 22px 0 0',
                boxShadow: isMobileKeyboardOpen ? 'none' : '0 -6px 18px rgba(29,29,31,0.08)',
                display: showFlashcardModal || studyGuideFull || academicInsightsFull || hideMobileNavForFooter || isMobileKeyboardOpen ? 'none' : 'flex',
                alignItems: 'center',
                justifyContent: hasDynamic ? 'flex-start' : 'center',
                overflowX: hasDynamic ? 'auto' : 'visible',
                scrollbarWidth: 'none', msOverflowStyle: 'none',
                padding: '0 10px env(safe-area-inset-bottom, 0px)',
                gap: 4,
                transition: 'opacity 0.12s linear, background 0.12s linear, box-shadow 0.12s linear',
              }}>
                {mobileTabs.map(tab => {
                  const isNewSearch = tab.key === 'new-search';
                  const isAct = !isNewSearch && (mobilePanel === tab.key || (tab.key === 'insights' && insightPanels.has(mobilePanel)));
                  return (
                    <button key={tab.key} data-nav-key={tab.key} title={tab.label} aria-label={tab.label} onClick={() => {
                      if (isNewSearch) {
                        startNewSearch();
                        return;
                      }
                      setMobilePanel(tab.key);
                      if (tab.key === 'ai') setSidebarTab('ai');
                      if (tab.key === 'insights') setSidebarTab('insights');
                      const el = mobileNavRef.current?.querySelector(`[data-nav-key="${tab.key}"]`);
                      el?.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'center' });
                    }} style={{
                      flex: hasDynamic ? '0 0 auto' : '1',
                      minWidth: hasDynamic ? 56 : 0,
                      maxWidth: hasDynamic ? 70 : undefined,
                      position: 'relative',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      border: 'none', cursor: 'pointer',
                      padding: 0,
                      height: 48,
                      borderRadius: 14,
                      background: isAct ? 'rgba(29,29,31,0.06)' : 'transparent',
                      color: isAct ? '#1D1D1F' : '#B4B7BE',
                      transition: 'background 0.1s, color 0.1s',
                    }}>
                      <div style={{ color: isAct ? '#1D1D1F' : '#B4B7BE', transition: 'color 0.18s' }}>{tab.icon}</div>
                    </button>
                  );
                })}
              </div>
            );
          })()}

          {/* ── TABLET HISTORY DRAWER ─────────────────────────��───────────────── */}
          {isTablet && historyDrawerOpen && (
            <>
              <div onClick={() => setHistoryDrawerOpen(false)} style={{
                position: 'fixed', inset: 0, zIndex: 200,
                background: 'rgba(29,29,31,0.4)',
                backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)',
              }} />
              <div style={{
                position: 'fixed', top: 56, left: 0, bottom: 0,
                width: 300, zIndex: 201,
                background: P.paper, borderRight: `1px solid ${P.border}`,
                boxShadow: '4px 0 24px rgba(29,29,31,0.12)',
                animation: 'slideInLeft 0.22s ease',
                display: 'flex', flexDirection: 'column', overflowY: 'auto',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: `1px solid ${P.border}`, flexShrink: 0 }}>
                  <span style={{ fontSize: 15, fontWeight: 700, color: P.ink }}>History & Export</span>
                  <button onClick={() => setHistoryDrawerOpen(false)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: P.muted, padding: 4, display: 'flex', borderRadius: 6 }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
                {renderSidebarContent()}
              </div>
            </>
          )}

          {/* ── MOBILE SUMMARY PANEL ──────────────────────────────────────────── */}
          {isMobile && mobilePanel === 'summary' && (
            <div style={{ position: 'fixed', top: topChromeOffset, left: 0, right: 0, bottom: mobilePanelBottomInsetCss, zIndex: 50, background: P.paper, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
              {/* Sticky back header */}
              <div style={{ position: 'sticky', top: 0, zIndex: 1, background: P.paper, borderBottom: `1px solid ${P.border}`, padding: '11px 16px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <button onClick={() => { setMobilePanel('insights'); setSidebarTab('insights'); }} style={{ display: 'flex', alignItems: 'center', gap: 4, border: 'none', background: 'none', color: P.muted, cursor: 'pointer', padding: '4px 8px 4px 0', fontSize: 13, fontWeight: 600 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  Back
                </button>
                <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: P.ink }}>Summary</div>
                {summary && (
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={() => { navigator.clipboard.writeText(summary).then(() => { setSummaryCopied(true); setTimeout(() => setSummaryCopied(false), 2000); }); }}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, border: `1px solid ${P.border}`, background: 'none', cursor: 'pointer', borderRadius: 6, padding: '5px 10px', fontSize: 12, fontWeight: 600, color: summaryCopied ? P.success : P.muted }}>
                      {summaryCopied ? <CheckIcon /> : <CopyIcon />} {summaryCopied ? 'Copied!' : 'Copy'}
                    </button>
                    <button onClick={() => { setSummary(''); setMobilePanel('transcript'); }}
                      style={{ border: `1px solid ${P.border}`, background: 'none', cursor: 'pointer', color: P.muted, fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 6 }}>Clear</button>
                  </div>
                )}
              </div>
              <div style={{ padding: '16px 18px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
                {summarizing ? (
                  <div style={{ padding: '60px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: P.muted, fontSize: 13 }}>
                    <SpinnerIcon size={14} /> Summarizing…
                  </div>
                ) : summary ? (
                  <div style={{ padding: '16px 18px', background: '#fff', borderRadius: 12, border: `1px solid ${P.border}`, fontSize: 14, lineHeight: 1.8, color: P.ink, whiteSpace: 'pre-wrap' }}>{summary}</div>
                ) : null}
              </div>
            </div>
          )}

          {/* ── MOBILE FLASHCARDS PANEL ───────────────────────────────────────── */}
          {isMobile && mobilePanel === 'flashcards' && (
            <div style={{ position: 'fixed', top: topChromeOffset, left: 0, right: 0, bottom: mobilePanelBottomInsetCss, zIndex: 50, background: P.paper, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
              {/* Sticky back header */}
              <div style={{ position: 'sticky', top: 0, zIndex: 1, background: P.paper, borderBottom: `1px solid ${P.border}`, padding: '11px 16px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <button onClick={() => { setMobilePanel('insights'); setSidebarTab('insights'); }} style={{ display: 'flex', alignItems: 'center', gap: 4, border: 'none', background: 'none', color: P.muted, cursor: 'pointer', padding: '4px 8px 4px 0', fontSize: 13, fontWeight: 600 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  Back
                </button>
                <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: P.ink }}>Flashcards · {flashcards.length} cards</div>
                {flashcards.length > 0 && (
                  <button onClick={() => { setFlashcardIndex(0); setFlashcardFlipped(false); setShowFlashcardModal(true); }}
                    style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 8, border: 'none', background: P.accent, color: 'white', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                    Study
                  </button>
                )}
              </div>
              <div style={{ padding: '16px 18px 24px' }}>
                {flashcardsLoading ? (
                  <div style={{ padding: '60px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: P.muted, fontSize: 13 }}>
                    <SpinnerIcon size={14} /> Generating flashcards…
                  </div>
                ) : (
                  <>
                    {flashcards.length > 0 && (
                      <div style={{ height: 5, borderRadius: 3, background: P.border, marginBottom: 14, overflow: 'hidden' }}>
                        <div style={{ height: '100%', borderRadius: 3, background: P.success, width: `${(flashcardKnown.size / flashcards.length) * 100}%`, transition: 'width 0.4s' }} />
                      </div>
                    )}
                    {flashcardsExhausted && (
                      <div style={{ display: 'flex', gap: 10, padding: '10px 12px', borderRadius: 10, border: '1px solid rgba(245,158,11,0.3)', background: 'rgba(245,158,11,0.07)', marginBottom: 12, fontSize: 12.5, color: P.muted, lineHeight: 1.5 }}>
                        {flashcardsExhaustedReason}
                      </div>
                    )}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                      {flashcards.map((card, i) => {
                        const isExpanded = expandedCards.has(i);
                        const isKnown = flashcardKnown.has(i);
                        return (
                          <div key={i} style={{ borderRadius: 10, border: `1px solid ${isKnown ? 'rgba(15,118,110,0.3)' : P.border}`, background: isKnown ? 'rgba(15,118,110,0.04)' : '#fff', overflow: 'hidden' }}>
                            <div onClick={() => setExpandedCards(prev => { const next = new Set(prev); if (next.has(i)) next.delete(i); else next.add(i); return next; })}
                              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', cursor: 'pointer', userSelect: 'none' }}>
                              <div style={{ width: 24, height: 24, borderRadius: 6, background: isKnown ? 'rgba(15,118,110,0.12)' : P.surface, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <span style={{ fontSize: 10, fontWeight: 700, color: isKnown ? P.success : P.muted }}>{i + 1}</span>
                              </div>
                              <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: P.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: isExpanded ? 'normal' : 'nowrap' }}>{card.question}</div>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={P.muted} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}><polyline points="6 9 12 15 18 9"/></svg>
                            </div>
                            {isExpanded && (
                              <div style={{ borderTop: `1px solid ${P.border}`, padding: '12px 14px', background: P.paper }}>
                                {card.topic && <div style={{ fontSize: 10.5, fontWeight: 700, color: '#D97706', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{card.topic}</div>}
                                <div style={{ fontSize: 13.5, lineHeight: 1.65, color: P.ink }}>{card.answer}</div>
                                <button onClick={e => { e.stopPropagation(); setFlashcardKnown(prev => { const next = new Set(prev); if (isKnown) next.delete(i); else next.add(i); return next; }); }}
                                  style={{ marginTop: 10, width: '100%', padding: '7px 0', borderRadius: 7, border: `1px solid ${isKnown ? 'rgba(15,118,110,0.4)' : P.border}`, background: isKnown ? 'rgba(15,118,110,0.08)' : 'none', color: isKnown ? P.success : P.muted, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                                  {isKnown ? '✓ Known' : 'Mark as Known'}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {!flashcardsExhausted && flashcards.length > 0 && (
                      <button onClick={generateMoreFlashcards} disabled={flashcardsMoreLoading}
                        style={{ marginTop: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, width: '100%', padding: '9px 0', borderRadius: 9, border: `1.5px dashed ${P.border}`, background: 'none', color: P.ink, fontSize: 13, fontWeight: 600, cursor: flashcardsMoreLoading ? 'default' : 'pointer', opacity: flashcardsMoreLoading ? 0.6 : 1 }}>
                        {flashcardsMoreLoading ? <><SpinnerIcon size={12} /> Generating…</> : <>+ More Cards</>}
                      </button>
                    )}
                    {flashcardKnown.size > 0 && (
                      <button onClick={() => setFlashcardKnown(new Set())}
                        style={{ marginTop: 8, display: 'block', width: '100%', padding: '8px 0', borderRadius: 8, border: `1px solid ${P.border}`, background: 'none', color: P.muted, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }}>
                        Reset Progress
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* ── MOBILE ACADEMIC INSIGHTS PANEL ───────────────────────────────── */}
          {isMobile && mobilePanel === 'academic' && (
            <div style={{ position: 'fixed', top: topChromeOffset, left: 0, right: 0, bottom: mobilePanelBottomInsetCss, zIndex: 50, background: P.paper, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
              {/* Sticky back header */}
              <div style={{ position: 'sticky', top: 0, zIndex: 1, background: P.paper, borderBottom: `1px solid ${P.border}`, padding: '11px 16px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <button onClick={() => { setMobilePanel('insights'); setSidebarTab('insights'); }} style={{ display: 'flex', alignItems: 'center', gap: 4, border: 'none', background: 'none', color: P.muted, cursor: 'pointer', padding: '4px 8px 4px 0', fontSize: 13, fontWeight: 600 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  Back
                </button>
                <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: P.ink }}>Academic Insights</div>
                {academicInsights && !academicInsights._error && (
                  <button onClick={() => { setAcademicInsights(null); setMobilePanel('transcript'); }}
                    style={{ border: `1px solid ${P.border}`, background: 'none', cursor: 'pointer', color: P.muted, fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 6 }}>Clear</button>
                )}
              </div>
              <div style={{ padding: '16px 18px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {academicInsightsLoading ? (
                  <div style={{ padding: '60px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: '#7C3AED', fontSize: 13 }}>
                    <SpinnerIcon size={14} /> Generating academic insights…
                  </div>
                ) : academicInsights && !academicInsights._error ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {academicInsights.references?.length > 0 && (
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#7C3AED', marginBottom: 8 }}>References ({academicInsights.references.length})</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {academicInsights.references.map((ref, i) => (
                            <div key={i} style={{ padding: '12px 14px', background: '#fff', borderRadius: 10, border: `1px solid ${P.border}` }}>
                              <div style={{ fontSize: 13.5, fontWeight: 600, color: P.ink }}>{ref.title || ref.author}</div>
                              <div style={{ fontSize: 12, color: P.muted, marginTop: 2 }}>{[ref.author, ref.year].filter(Boolean).join(' · ')}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {academicInsights.claims?.length > 0 && (
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#7C3AED', marginBottom: 8 }}>Key Claims</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {academicInsights.claims.map((c, i) => (
                            <div key={i} style={{ padding: '12px 14px', background: c.supported ? 'rgba(15,118,110,0.05)' : 'rgba(180,35,24,0.05)', borderRadius: 10, border: `1px solid ${c.supported ? 'rgba(15,118,110,0.2)' : 'rgba(180,35,24,0.2)'}` }}>
                              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                                <span style={{ color: c.supported ? P.success : P.error, fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{c.supported ? '✓' : '⚠'}</span>
                                <div>
                                  <div style={{ fontSize: 13.5, color: P.ink, lineHeight: 1.6 }}>{c.claim}</div>
                                  {c.evidence && <div style={{ fontSize: 12, color: P.muted, marginTop: 4 }}>{c.evidence}</div>}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {academicInsights.glossary?.length > 0 && (
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#7C3AED', marginBottom: 8 }}>Glossary</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {academicInsights.glossary.map((g, i) => (
                            <div key={i} style={{ padding: '12px 14px', background: '#fff', borderRadius: 10, border: `1px solid ${P.border}` }}>
                              <div style={{ fontSize: 13.5, fontWeight: 700, color: '#7C3AED', marginBottom: 4 }}>{g.term}</div>
                              <div style={{ fontSize: 13, color: P.muted, lineHeight: 1.6 }}>{g.definition}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {academicInsights.researchGaps?.length > 0 && (
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#7C3AED', marginBottom: 8 }}>Research Gaps</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {academicInsights.researchGaps.map((gap, i) => (
                            <div key={i} style={{ padding: '12px 14px', background: 'rgba(124,58,237,0.05)', borderRadius: 10, border: '1px solid rgba(124,58,237,0.15)', fontSize: 13.5, color: P.ink, lineHeight: 1.6 }}>{gap}</div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {/* ── MOBILE STUDY GUIDE PANEL ──────────────────────────────────────── */}
          {isMobile && mobilePanel === 'study-guide' && (
            <div style={{ position: 'fixed', top: topChromeOffset, left: 0, right: 0, bottom: mobilePanelBottomInsetCss, zIndex: 50, background: P.paper, display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
              {/* Sticky back header */}
              <div style={{ position: 'sticky', top: 0, zIndex: 1, background: P.paper, borderBottom: `1px solid ${P.border}`, padding: '11px 16px', display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                <button onClick={() => { setMobilePanel('insights'); setSidebarTab('insights'); }} style={{ display: 'flex', alignItems: 'center', gap: 4, border: 'none', background: 'none', color: P.muted, cursor: 'pointer', padding: '4px 8px 4px 0', fontSize: 13, fontWeight: 600 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  Back
                </button>
                <div style={{ flex: 1, fontSize: 15, fontWeight: 700, color: P.ink }}>Study Guide</div>
              </div>
              <div style={{ padding: '16px 18px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                {studyGuideLoading ? (
                  <div style={{ padding: '60px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, color: P.muted, fontSize: 13 }}>
                    <SpinnerIcon size={14} /> Generating study guide…
                  </div>
                ) : studyGuide && !studyGuide._error ? (() => {
                  const sg = studyGuide;
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {sg.overview && (
                        <div style={{ padding: '14px 16px', background: P.accentLight, borderRadius: 12, border: `1px solid rgba(60,140,255,0.15)` }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: P.accent, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Overview</div>
                          <div style={{ fontSize: 13.5, lineHeight: 1.7, color: P.ink }}>{sg.overview}</div>
                        </div>
                      )}
                      {sg.objectives && sg.objectives.length > 0 && (
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: P.ink, marginBottom: 8 }}>Learning Objectives</div>
                          <ul style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {sg.objectives.map((obj, i) => <li key={i} style={{ fontSize: 13.5, color: P.ink, lineHeight: 1.6 }}>{obj}</li>)}
                          </ul>
                        </div>
                      )}
                      {sg.keyConcepts && sg.keyConcepts.length > 0 && (
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: P.ink, marginBottom: 8 }}>Key Concepts</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {sg.keyConcepts.map((kc, i) => (
                              <div key={i} style={{ padding: '12px 14px', background: '#fff', borderRadius: 10, border: `1px solid ${P.border}` }}>
                                <div style={{ fontSize: 13.5, fontWeight: 700, color: P.ink, marginBottom: 4 }}>{kc.concept}</div>
                                <div style={{ fontSize: 13, color: P.muted, lineHeight: 1.6 }}>{kc.explanation}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {sg.sections && sg.sections.length > 0 && (
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: P.ink, marginBottom: 8 }}>Sections</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {sg.sections.map((sec, i) => (
                              <div key={i} style={{ padding: '14px 16px', background: '#fff', borderRadius: 10, border: `1px solid ${P.border}` }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: P.ink, marginBottom: 6 }}>{sec.title}</div>
                                {sec.points && sec.points.length > 0 && (
                                  <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                    {sec.points.map((pt, j) => <li key={j} style={{ fontSize: 13, color: P.ink, lineHeight: 1.6 }}>{pt}</li>)}
                                  </ul>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {sg.reviewQuestions && sg.reviewQuestions.length > 0 && (
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: P.ink, marginBottom: 8 }}>Review Questions</div>
                          <ol style={{ margin: 0, paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {sg.reviewQuestions.map((q, i) => <li key={i} style={{ fontSize: 13.5, color: P.ink, lineHeight: 1.6 }}>{q}</li>)}
                          </ol>
                        </div>
                      )}
                      <button onClick={() => { setStudyGuide(null); setMobilePanel('transcript'); }}
                        style={{ marginTop: 4, border: `1px solid ${P.border}`, background: 'none', cursor: 'pointer', color: P.muted, fontSize: 13, fontWeight: 600, padding: '8px 16px', borderRadius: 8 }}>
                        Clear Study Guide
                      </button>
                    </div>
                  );
                })() : null}
              </div>
            </div>
          )}
          </>
        )}
      </div>

      {/* Footer */}
      <footer ref={footerRef} style={{
        background: P.surface, borderTop: `1px solid ${P.border}`,
        padding: isMobile
          ? `34px 20px ${hasMobileBottomNav ? `calc(${mobileBottomNavHeight + 30}px + env(safe-area-inset-bottom, 0px))` : '28px'}`
          : '40px 24px 32px',
        marginTop: 24,
      }}>
        <div style={{ maxWidth: 820, margin: '0 auto' }}>
          {/* Top row */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 32, marginBottom: 32 }}>
            {/* Brand */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
                <img
                  src={FOOTER_LOGO_SRC}
                  alt={BRAND_NAME}
                  style={{ height: 44, width: 'auto', display: 'block' }}
                />
              </div>
              <p style={{ fontSize: 13, color: P.muted, lineHeight: 1.6, maxWidth: 260, margin: 0 }}>
                Extract transcripts from YouTube, Vimeo, TikTok, X, Instagram, Dailymotion, Facebook, Loom, Wistia, and more — free, no account needed.
              </p>
            </div>

            {/* Links */}
            <div style={{ display: 'flex', gap: 48, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: P.ink, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>Product</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {[
                    { label: 'Extract Transcript', href: '#' },
                    { label: 'AI Summaries', href: '#' },
                    { label: 'Q&A', href: '#' },
                  ].map(l => (
                    <a key={l.label} href={l.href} style={{ fontSize: 13, color: P.muted, textDecoration: 'none', transition: 'color 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.color = P.ink; }}
                      onMouseLeave={e => { e.currentTarget.style.color = P.muted; }}
                    >{l.label}</a>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: P.ink, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>Follow Us</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {[
                    { label: 'LinkedIn', href: 'https://www.linkedin.com/company/scribesnap-ai/' },
                    { label: 'Instagram', href: 'https://www.instagram.com/scribesnap.ai/' },
                    { label: 'Watch Intro ▶', href: 'https://www.youtube.com/watch?v=0CO9lSEXG3U' },
                  ].map(l => (
                    <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 13, color: P.muted, textDecoration: 'none', transition: 'color 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.color = P.ink; }}
                      onMouseLeave={e => { e.currentTarget.style.color = P.muted; }}
                    >{l.label}</a>
                  ))}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: P.ink, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 12 }}>Connect</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {[
                    { label: 'GitHub', href: 'https://github.com/JoelMoyal' },
                    { label: 'joelmoyal.com', href: 'https://joelmoyal.com' },
                  ].map(l => (
                    <a key={l.label} href={l.href} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 13, color: P.muted, textDecoration: 'none', transition: 'color 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.color = P.ink; }}
                      onMouseLeave={e => { e.currentTarget.style.color = P.muted; }}
                    >{l.label}</a>
                  ))}
                  <button onClick={() => setShowBugModal(true)}
                    style={{ fontSize: 13, color: P.muted, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left', transition: 'color 0.15s', display: 'flex', alignItems: 'center', gap: 5 }}
                    onMouseEnter={e => { e.currentTarget.style.color = P.ink; }}
                    onMouseLeave={e => { e.currentTarget.style.color = P.muted; }}
                  ><BugIcon size={12} /> Report a Bug</button>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div style={{ height: 1, background: P.border, marginBottom: 20 }} />

          {/* Bottom row */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <span style={{ fontSize: 12, color: P.muted }}>
              © {new Date().getFullYear()} {BRAND_NAME} · Built by{' '}
              <a href="https://joelmoyal.com" target="_blank" rel="noopener noreferrer"
                style={{ color: P.ink, fontWeight: 600, textDecoration: 'none' }}
                onMouseEnter={e => { e.currentTarget.style.color = P.accent; }}
                onMouseLeave={e => { e.currentTarget.style.color = P.ink; }}
              >Joël Moyal</a>
              {' '}· <a href="/privacy" style={{ color: P.muted, textDecoration: 'none' }}
                onMouseEnter={e => { e.currentTarget.style.color = P.ink; }}
                onMouseLeave={e => { e.currentTarget.style.color = P.muted; }}
              >Privacy Policy</a>
            </span>
          </div>
        </div>
      </footer>

      {/* ── Bug Report Modal ── */}
      {showBugModal && (() => {
        const closeBugModal = () => { if (bugSubmitting) return; setShowBugModal(false); setBugStatus(null); };
        return (
          <div
            onClick={e => { if (e.target === e.currentTarget) closeBugModal(); }}
            style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(29,29,31,0.5)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          >
            <div style={{ background: P.surface, borderRadius: 16, padding: isMobile ? 20 : 28, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', gap: 16, maxHeight: '90vh', overflowY: 'auto' }}>
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <BugIcon size={18} />
                  <span style={{ fontSize: 15, fontWeight: 700, color: P.ink }}>Report a Bug</span>
                </div>
                <button onClick={closeBugModal} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: P.muted, padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.color = P.ink} onMouseLeave={e => e.currentTarget.style.color = P.muted}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>

              {bugStatus === 'success' ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '20px 0' }}>
                  <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(15,118,110,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={P.success} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  </div>
                  <p style={{ fontSize: 14, fontWeight: 600, color: P.ink, margin: 0 }}>Thanks! We'll look into it.</p>
                  <p style={{ fontSize: 13, color: P.muted, margin: 0, textAlign: 'center' }}>Your report has been sent successfully.</p>
                </div>
              ) : (
                <>
                  {/* Category */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: P.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Type</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {[{ value: 'bug', label: '🐛 Bug' }, { value: 'feature', label: '✨ Feature Request' }, { value: 'other', label: '💬 Other' }].map(opt => (
                        <button key={opt.value} onClick={() => setBugForm(f => ({ ...f, category: opt.value }))}
                          style={{ flex: 1, padding: '7px 6px', borderRadius: 8, border: `1.5px solid ${bugForm.category === opt.value ? P.accent : P.border}`, background: bugForm.category === opt.value ? P.accentLight : 'transparent', color: bugForm.category === opt.value ? P.accent : P.muted, fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.12s' }}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Description */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: P.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Description <span style={{ color: P.error }}>*</span></label>
                    <textarea value={bugForm.description} onChange={e => setBugForm(f => ({ ...f, description: e.target.value }))} placeholder="What happened?" rows={3}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${P.border}`, background: P.paper, fontSize: 14, color: P.ink, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>

                  {/* Steps */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: P.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Steps to Reproduce <span style={{ color: P.muted, fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
                    <textarea value={bugForm.steps} onChange={e => setBugForm(f => ({ ...f, steps: e.target.value }))} placeholder="1. Go to… 2. Click… 3. See error" rows={2}
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${P.border}`, background: P.paper, fontSize: 14, color: P.ink, outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>

                  {/* Email */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: P.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Your Email <span style={{ color: P.muted, fontWeight: 400, textTransform: 'none' }}>(optional)</span></label>
                    <input type="email" value={bugForm.email} onChange={e => setBugForm(f => ({ ...f, email: e.target.value }))} placeholder="So we can follow up"
                      style={{ width: '100%', padding: '10px 12px', borderRadius: 10, border: `1px solid ${P.border}`, background: P.paper, fontSize: 14, color: P.ink, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' }} />
                  </div>

                  {/* Error */}
                  {bugStatus === 'error' && (
                    <p style={{ fontSize: 13, color: P.error, margin: 0 }}>Something went wrong. Please try again.</p>
                  )}

                  {/* Submit */}
                  <button onClick={submitBugReport} disabled={!bugForm.description.trim() || bugSubmitting}
                    style={{ padding: '11px 16px', borderRadius: 10, border: 'none', background: (!bugForm.description.trim() || bugSubmitting) ? P.border : P.accent, color: (!bugForm.description.trim() || bugSubmitting) ? P.muted : '#fff', fontWeight: 600, fontSize: 14, cursor: (!bugForm.description.trim() || bugSubmitting) ? 'not-allowed' : 'pointer', transition: 'background 0.15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                    onMouseEnter={e => { if (bugForm.description.trim() && !bugSubmitting) e.currentTarget.style.background = P.accentHover; }}
                    onMouseLeave={e => { if (bugForm.description.trim() && !bugSubmitting) e.currentTarget.style.background = P.accent; }}>
                    {bugSubmitting ? <><SpinnerIcon size={14} /> Sending…</> : 'Send Report'}
                  </button>
                </>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Study Guide Fullscreen Overlay ──────────────────────────────── */}
      {studyGuideFull && studyGuide && !studyGuide._error && (
        <div style={{ position: 'fixed', inset: 0, background: '#FAFAF8', zIndex: 9998, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Toolbar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: isMobile ? '12px 16px' : '14px 24px', borderBottom: `1px solid ${P.border}`, background: '#fff', flexShrink: 0 }}>
            <div style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(15,118,110,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.success, flexShrink: 0 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>
            </div>
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: P.ink, lineHeight: 1.2 }}>Study Guide</div>
              <div style={{ fontSize: 11, color: P.muted, marginTop: 1 }}>{currentTitle || 'Full study guide'}</div>
            </div>
            <button
              onClick={() => setStudyGuideFull(false)}
              style={{
                marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 7,
                padding: '9px 18px', borderRadius: 10,
                border: `1px solid ${P.border}`, background: P.paper,
                cursor: 'pointer', color: P.muted, fontSize: 13, fontWeight: 600,
                transition: 'all 0.15s', flexShrink: 0,
              }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(180,35,24,0.06)'; e.currentTarget.style.borderColor = 'rgba(180,35,24,0.28)'; e.currentTarget.style.color = P.error; }}
              onMouseLeave={e => { e.currentTarget.style.background = P.paper; e.currentTarget.style.borderColor = P.border; e.currentTarget.style.color = P.muted; }}
              title="Close (Esc)"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
              Close
              <span style={{ fontSize: 10, opacity: 0.5, fontWeight: 500, marginLeft: 2 }}>Esc</span>
            </button>
          </div>
          {/* Scrollable content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px 16px' : '28px 40px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 22, maxWidth: 800, margin: '0 auto' }}>
              {studyGuide.overview && (
                <div style={{ padding: '18px 22px', background: '#fff', borderRadius: 14, border: `1px solid ${P.border}`, boxShadow: '0 1px 6px rgba(29,29,31,0.05)' }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: P.success, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 10 }}>Overview</div>
                  <div style={{ fontSize: 15, lineHeight: 1.75, color: P.ink }}>{studyGuide.overview}</div>
                </div>
              )}
              {studyGuide.objectives?.length > 0 && (
                <div style={{ padding: '18px 22px', background: '#fff', borderRadius: 14, border: `1px solid ${P.border}`, boxShadow: '0 1px 6px rgba(29,29,31,0.05)' }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: P.success, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 14 }}>Learning Objectives</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {studyGuide.objectives.map((obj, i) => (
                      <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                        <div style={{ width: 26, height: 26, borderRadius: 7, background: 'rgba(15,118,110,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: P.success }}>{i + 1}</span>
                        </div>
                        <div style={{ fontSize: 14.5, lineHeight: 1.65, color: P.ink }}>{obj}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {studyGuide.keyConcepts?.length > 0 && (
                <div style={{ padding: '18px 22px', background: '#fff', borderRadius: 14, border: `1px solid ${P.border}`, boxShadow: '0 1px 6px rgba(29,29,31,0.05)' }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: P.success, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 14 }}>Key Concepts</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {studyGuide.keyConcepts.map((kc, i) => (
                      <div key={i} style={{ paddingBottom: i < studyGuide.keyConcepts.length - 1 ? 12 : 0, borderBottom: i < studyGuide.keyConcepts.length - 1 ? `1px solid ${P.border}` : 'none' }}>
                        <span style={{ fontSize: 14.5, fontWeight: 700, color: P.success }}>{kc.term}</span>
                        <span style={{ fontSize: 14.5, color: P.muted }}> — </span>
                        <span style={{ fontSize: 14.5, color: P.ink, lineHeight: 1.65 }}>{kc.definition}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {studyGuide.sections?.length > 0 && (
                <div style={{ padding: '18px 22px', background: '#fff', borderRadius: 14, border: `1px solid ${P.border}`, boxShadow: '0 1px 6px rgba(29,29,31,0.05)' }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: P.success, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 14 }}>Sections</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                    {studyGuide.sections.map((sec, i) => (
                      <div key={i} style={{ paddingBottom: i < studyGuide.sections.length - 1 ? 18 : 0, borderBottom: i < studyGuide.sections.length - 1 ? `1px solid ${P.border}` : 'none' }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: P.ink, marginBottom: 7 }}>{sec.title}</div>
                        <div style={{ fontSize: 13.5, lineHeight: 1.7, color: P.muted, marginBottom: sec.keyPoints?.length ? 12 : 0 }}>{sec.summary}</div>
                        {sec.keyPoints?.length > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                            {sec.keyPoints.map((pt, j) => (
                              <div key={j} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                <div style={{ width: 6, height: 6, borderRadius: '50%', background: P.success, flexShrink: 0, marginTop: 7 }} />
                                <div style={{ fontSize: 13.5, lineHeight: 1.65, color: P.ink }}>{pt}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {studyGuide.reviewQuestions?.length > 0 && (
                <div style={{ padding: '18px 22px', background: '#fff', borderRadius: 14, border: `1px solid ${P.border}`, boxShadow: '0 1px 6px rgba(29,29,31,0.05)' }}>
                  <div style={{ fontSize: 10.5, fontWeight: 700, color: P.success, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 14 }}>Review Questions</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {studyGuide.reviewQuestions.map((q, i) => (
                      <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '12px 16px', background: P.paper, borderRadius: 10 }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: P.success, flexShrink: 0, marginTop: 1 }}>Q{i + 1}</span>
                        <span style={{ fontSize: 14.5, lineHeight: 1.65, color: P.ink }}>{q}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Ask ScribeSnap AI — fullscreen version */}
              <div style={{ padding: '18px 22px', background: '#fff', borderRadius: 14, border: `1.5px solid rgba(60,140,255,0.18)`, boxShadow: '0 1px 6px rgba(29,29,31,0.05)', marginBottom: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg, rgba(60,140,255,0.13) 0%, rgba(60,140,255,0.05) 100%)', border: '1.5px solid rgba(60,140,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img src="/scribesnap_icon_wave.svg" alt="AI" style={{ width: 16, height: 16 }} />
                  </div>
                  <span style={{ fontSize: 14, fontWeight: 700, color: P.accent }}>Ask ScribeSnap AI</span>
                  <span style={{ fontSize: 12.5, color: P.muted }}>— deeper questions about this video</span>
                  {sgMessages.length > 0 && <button onClick={() => setSgMessages([])} style={{ marginLeft: 'auto', border: `1px solid ${P.border}`, background: 'none', cursor: 'pointer', color: P.muted, fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 6 }}>Clear</button>}
                </div>
                {sgMessages.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 14 }}>
                    {sgMessages.map((msg, i) => (
                      <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
                        {msg.role === 'ai'
                          ? <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, maxWidth: '90%' }}>
                              <AiBubbleAvatar size={28} iconSize={16} radius={8} />
                              <div style={{ padding: '10px 16px', borderRadius: '3px 14px 14px 14px', background: P.paper, border: `1px solid ${P.border}`, fontSize: 14, lineHeight: 1.7, color: msg.isError ? P.error : P.ink }}>{msg.text.split('\n').map((l, li, a) => <React.Fragment key={li}>{renderChatLine(l)}{li < a.length - 1 && <br />}</React.Fragment>)}</div>
                            </div>
                          : <div style={{ maxWidth: '80%', padding: '10px 16px', borderRadius: '14px 14px 3px 14px', background: P.accent, fontSize: 14, lineHeight: 1.65, color: 'white' }}>{msg.text}</div>
                        }
                      </div>
                    ))}
                    {sgLoading && (
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                        <AiBubbleAvatar size={28} iconSize={16} radius={8} />
                        <div style={{ display: 'flex', gap: 5, padding: '10px 14px', background: P.paper, border: `1px solid ${P.border}`, borderRadius: '3px 14px 14px 14px', width: 'fit-content' }}>{[0,1,2].map(d => <div key={d} style={{ width: 6, height: 6, borderRadius: '50%', background: P.accent, opacity: 0.6, animation: `bounce 1.2s ease-in-out ${d * 0.2}s infinite` }} />)}</div>
                      </div>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', background: P.paper, border: `1.5px solid ${P.border}`, borderRadius: 14, padding: '8px 8px 8px 10px' }}>
                  <AiBubbleAvatar size={24} iconSize={13} radius={7} />
                  <input value={sgQuestion} onChange={e => setSgQuestion(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && askSgQuestion()} placeholder="Ask a question about this video…" disabled={sgLoading}
                    style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: 14, color: P.ink, padding: '4px 0' }}
                    onFocus={e => { e.currentTarget.parentElement.style.borderColor = P.accent; e.currentTarget.parentElement.style.boxShadow = '0 0 0 3px rgba(60,140,255,0.1)'; }}
                    onBlur={e => { e.currentTarget.parentElement.style.borderColor = P.border; e.currentTarget.parentElement.style.boxShadow = 'none'; }}
                  />
                  <button onClick={() => askSgQuestion()} disabled={!sgQuestion.trim() || sgLoading}
                    style={{ flexShrink: 0, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 10, border: 'none', background: sgQuestion.trim() && !sgLoading ? 'linear-gradient(135deg, #5ba4f5 0%, #3C8CFF 100%)' : 'rgba(29,29,31,0.05)', color: sgQuestion.trim() && !sgLoading ? 'white' : P.muted, cursor: sgQuestion.trim() && !sgLoading ? 'pointer' : 'default', transition: 'all 0.2s' }}>
                    {sgLoading ? <SpinnerIcon size={13} /> : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Academic Insights Fullscreen ────────────────────────────────── */}
      {academicInsightsFull && academicInsights && !academicInsights._error && (() => {
        const ai = academicInsights;
        const supportedCount = ai.claims?.filter(c => c.supported).length || 0;
        const unsupportedCount = (ai.claims?.length || 0) - supportedCount;
        return (
          <div style={{ position: 'fixed', inset: 0, background: '#FAFAF8', zIndex: 9998, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: isMobile ? '12px 16px' : '14px 24px', borderBottom: `1px solid ${P.border}`, background: '#fff', flexShrink: 0 }}>
              <div style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(124,58,237,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#7C3AED', flexShrink: 0 }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg>
              </div>
              <div>
                <div style={{ fontSize: 14.5, fontWeight: 700, color: P.ink, lineHeight: 1.2 }}>Academic Insights</div>
                <div style={{ fontSize: 11, color: P.muted, marginTop: 1 }}>{currentTitle || 'Extracted from transcript'}</div>
              </div>
              {/* Stats */}
              <div style={{ display: 'flex', gap: 8, marginLeft: 16, flexWrap: 'wrap' }}>
                {ai.references?.length > 0 && <span style={{ fontSize: 11, fontWeight: 600, color: '#7C3AED', background: 'rgba(124,58,237,0.08)', padding: '3px 9px', borderRadius: 99 }}>{ai.references.length} References</span>}
                {ai.claims?.length > 0 && <span style={{ fontSize: 11, fontWeight: 600, color: P.success, background: 'rgba(15,118,110,0.08)', padding: '3px 9px', borderRadius: 99 }}>✓ {supportedCount}</span>}
                {unsupportedCount > 0 && <span style={{ fontSize: 11, fontWeight: 600, color: P.error, background: 'rgba(180,35,24,0.08)', padding: '3px 9px', borderRadius: 99 }}>⚠ {unsupportedCount}</span>}
                {ai.glossary?.length > 0 && <span style={{ fontSize: 11, fontWeight: 600, color: '#7C3AED', background: 'rgba(124,58,237,0.08)', padding: '3px 9px', borderRadius: 99 }}>{ai.glossary.length} Terms</span>}
              </div>
              <button onClick={() => setAcademicInsightsFull(false)}
                style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 10, border: `1px solid ${P.border}`, background: P.paper, cursor: 'pointer', color: P.muted, fontSize: 13, fontWeight: 600, transition: 'all 0.15s', flexShrink: 0 }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(180,35,24,0.06)'; e.currentTarget.style.borderColor = 'rgba(180,35,24,0.28)'; e.currentTarget.style.color = P.error; }}
                onMouseLeave={e => { e.currentTarget.style.background = P.paper; e.currentTarget.style.borderColor = P.border; e.currentTarget.style.color = P.muted; }}
                title="Close (Esc)">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                Close <span style={{ fontSize: 10, opacity: 0.5, fontWeight: 500, marginLeft: 2 }}>Esc</span>
              </button>
            </div>
            {/* Scrollable content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px 16px' : '28px 40px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 22, maxWidth: 800, margin: '0 auto' }}>
                {/* References */}
                {ai.references?.length > 0 && (
                  <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${P.border}`, boxShadow: '0 1px 6px rgba(29,29,31,0.05)', overflow: 'hidden' }}>
                    <div style={{ padding: '14px 22px 12px', borderBottom: `1px solid ${P.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#7C3AED', letterSpacing: '0.07em', textTransform: 'uppercase' }}>References Mentioned</span>
                      <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#7C3AED', background: 'rgba(124,58,237,0.1)', padding: '1px 7px', borderRadius: 99 }}>{ai.references.length}</span>
                    </div>
                    {ai.references.map((ref, i) => (
                      <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '14px 22px', borderBottom: i < ai.references.length - 1 ? `1px solid ${P.border}` : 'none', transition: 'background 0.12s' }}
                        onMouseEnter={e => e.currentTarget.style.background = P.paper}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(124,58,237,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#7C3AED' }}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                        </div>
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 14.5, fontWeight: 600, color: P.ink, lineHeight: 1.35 }}>{ref.title || ref.author}</div>
                          <div style={{ fontSize: 12.5, color: P.muted, marginTop: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
                            {[ref.author, ref.year].filter(Boolean).join(' · ')}
                            {ref.type && ref.type !== 'other' && <span style={{ fontSize: 10, fontWeight: 700, color: '#7C3AED', background: 'rgba(124,58,237,0.08)', padding: '1px 7px', borderRadius: 4, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{ref.type}</span>}
                          </div>
                        </div>
                        <a href={`https://scholar.google.com/scholar?q=${encodeURIComponent([ref.title, ref.author].filter(Boolean).join(' '))}`} target="_blank" rel="noopener noreferrer"
                          style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, color: '#7C3AED', background: 'rgba(124,58,237,0.07)', border: '1px solid rgba(124,58,237,0.2)', padding: '7px 14px', borderRadius: 8, textDecoration: 'none', whiteSpace: 'nowrap', transition: 'all 0.12s' }}
                          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.14)'; }}
                          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.07)'; }}>
                          Search →
                        </a>
                      </div>
                    ))}
                  </div>
                )}
                {/* Key Claims */}
                {ai.claims?.length > 0 && (
                  <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${P.border}`, boxShadow: '0 1px 6px rgba(29,29,31,0.05)', overflow: 'hidden' }}>
                    <div style={{ padding: '14px 22px 12px', borderBottom: `1px solid ${P.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#7C3AED', letterSpacing: '0.07em', textTransform: 'uppercase' }}>Key Claims</span>
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                        {supportedCount > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: P.success, background: 'rgba(15,118,110,0.08)', padding: '2px 9px', borderRadius: 99 }}>✓ {supportedCount} supported</span>}
                        {unsupportedCount > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: P.error, background: 'rgba(180,35,24,0.08)', padding: '2px 9px', borderRadius: 99 }}>⚠ {unsupportedCount} unsupported</span>}
                      </div>
                    </div>
                    {ai.claims.map((c, i) => (
                      <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '15px 22px', borderBottom: i < ai.claims.length - 1 ? `1px solid ${P.border}` : 'none', borderLeft: `3px solid ${c.supported ? P.success : P.error}` }}>
                        <div style={{ flexShrink: 0, marginTop: 3, width: 20, height: 20, borderRadius: '50%', background: c.supported ? 'rgba(15,118,110,0.1)' : 'rgba(180,35,24,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {c.supported
                            ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={P.success} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                            : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={P.error} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                          }
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 14.5, lineHeight: 1.65, color: P.ink, fontWeight: 500 }}>{c.claim}</div>
                          {c.evidence && <div style={{ fontSize: 13, color: P.muted, marginTop: 6, lineHeight: 1.6, paddingTop: 6, borderTop: `1px solid ${P.border}` }}><span style={{ fontWeight: 600, color: P.ink }}>Evidence: </span>{c.evidence}</div>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Glossary */}
                {ai.glossary?.length > 0 && (
                  <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${P.border}`, boxShadow: '0 1px 6px rgba(29,29,31,0.05)', overflow: 'hidden' }}>
                    <div style={{ padding: '14px 22px 12px', borderBottom: `1px solid ${P.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#7C3AED', letterSpacing: '0.07em', textTransform: 'uppercase' }}>Glossary</span>
                      <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#7C3AED', background: 'rgba(124,58,237,0.1)', padding: '1px 7px', borderRadius: 99 }}>{ai.glossary.length} terms</span>
                    </div>
                    {ai.glossary.map((g, i) => (
                      <div key={i} style={{ display: 'flex', padding: '13px 22px', borderBottom: i < ai.glossary.length - 1 ? `1px solid ${P.border}` : 'none', transition: 'background 0.12s' }}
                        onMouseEnter={e => e.currentTarget.style.background = P.paper}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <div style={{ minWidth: 190, flexShrink: 0, fontSize: 14.5, fontWeight: 700, color: '#7C3AED', lineHeight: 1.5, paddingRight: 20 }}>{g.term}</div>
                        <div style={{ fontSize: 14.5, color: P.ink, lineHeight: 1.7 }}>{g.definition}</div>
                      </div>
                    ))}
                  </div>
                )}
                {/* Research Gaps */}
                {ai.researchGaps?.length > 0 && (
                  <div style={{ background: '#fff', borderRadius: 14, border: `1px solid ${P.border}`, boxShadow: '0 1px 6px rgba(29,29,31,0.05)', overflow: 'hidden', marginBottom: 24 }}>
                    <div style={{ padding: '14px 22px 12px', borderBottom: `1px solid ${P.border}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#7C3AED', letterSpacing: '0.07em', textTransform: 'uppercase' }}>Open Questions & Research Gaps</span>
                      <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: '#7C3AED', background: 'rgba(124,58,237,0.1)', padding: '1px 7px', borderRadius: 99 }}>{ai.researchGaps.length}</span>
                    </div>
                    {ai.researchGaps.map((gap, i) => (
                      <div key={i} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '15px 22px', borderBottom: i < ai.researchGaps.length - 1 ? `1px solid ${P.border}` : 'none', borderLeft: '3px solid rgba(124,58,237,0.35)' }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2, opacity: 0.6 }}><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                        <div style={{ fontSize: 14.5, lineHeight: 1.7, color: P.ink }}>{gap}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Flashcard Modal ─────────────────────────────────────────────── */}
      {showFlashcardModal && flashcards.length > 0 && (() => {
        const card = flashcards[flashcardIndex];
        const isLast = flashcardIndex === flashcards.length - 1;
        const allReviewed = flashcardKnown.size + (flashcards.length - flashcardKnown.size) === flashcards.length && flashcardIndex === isLast;
        const progressPct = Math.round((flashcardKnown.size / flashcards.length) * 100);
        return (
          <div
            onClick={(e) => { if (e.target === e.currentTarget) closeFlashcardModal(); }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(29,29,31,0.72)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', zIndex: 9999, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: isMobile ? 12 : 24 }}
          >
            {/* Header */}
            <div style={{ width: '100%', maxWidth: 560, display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <div style={{ display: 'flex', align: 'center', gap: 10 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.01em' }}>
                  Flashcards
                </span>
                <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)', marginLeft: 8 }}>
                  {flashcardIndex + 1} / {flashcards.length}
                </span>
              </div>
              <button
                onClick={closeFlashcardModal}
                style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, color: 'rgba(255,255,255,0.7)', cursor: 'pointer', fontSize: 13, fontWeight: 600, padding: '5px 12px', transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
              >✕ Close</button>
            </div>

            {/* Progress bar */}
            <div style={{ width: '100%', maxWidth: 560, marginBottom: 20 }}>
              <div style={{ height: 4, background: 'rgba(255,255,255,0.12)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${progressPct}%`, background: '#0F766E', borderRadius: 4, transition: 'width 0.4s ease' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5 }}>
                <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.4)' }}>{flashcardKnown.size} known</span>
                <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.4)' }}>{flashcards.length - flashcardKnown.size} to review</span>
              </div>
            </div>

            {/* Card */}
            <div
              style={{ width: '100%', maxWidth: 560, height: isMobile ? 220 : 280, perspective: '1200px', cursor: 'pointer', marginBottom: 20 }}
              onClick={() => setFlashcardFlipped(f => !f)}
            >
              <div style={{
                width: '100%', height: '100%', position: 'relative',
                transformStyle: 'preserve-3d',
                transform: flashcardFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
                transition: 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)',
              }}>
                {/* Front — Question */}
                <div style={{
                  position: 'absolute', inset: 0,
                  backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
                  background: P.surface, borderRadius: 18,
                  border: `1.5px solid ${P.border}`,
                  boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  padding: isMobile ? '16px 20px' : '28px 32px', textAlign: 'center',
                }}>
                  {card.topic && (
                    <div style={{ fontSize: 10, fontWeight: 700, color: P.warning, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14, padding: '3px 10px', background: 'rgba(180,83,9,0.08)', borderRadius: 20 }}>
                      {card.topic}
                    </div>
                  )}
                  <div style={{ fontSize: 18, fontWeight: 600, color: P.ink, lineHeight: 1.5, maxHeight: 160, overflow: 'auto' }}>{card.question}</div>
                  <div style={{ marginTop: 20, fontSize: 11, color: P.muted, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/></svg>
                    Tap to reveal answer · Space
                  </div>
                </div>
                {/* Back — Answer */}
                <div style={{
                  position: 'absolute', inset: 0,
                  backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)',
                  background: 'linear-gradient(135deg, #0F766E 0%, #0d5e57 100%)', borderRadius: 18,
                  boxShadow: '0 20px 60px rgba(0,0,0,0.35)',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  padding: isMobile ? '16px 20px' : '28px 32px', textAlign: 'center',
                }}>
                  {card.topic && (
                    <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.6)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 14 }}>
                      {card.topic}
                    </div>
                  )}
                  <div style={{ fontSize: 16, fontWeight: 500, color: '#fff', lineHeight: 1.65, maxHeight: 180, overflow: 'auto' }}>{card.answer}</div>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            {flashcardFlipped ? (
              <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                <button
                  onClick={() => fcMarkKnown(false)}
                  style={{ padding: '10px 22px', borderRadius: 12, border: '1.5px solid rgba(180,35,24,0.5)', background: 'rgba(180,35,24,0.12)', color: '#ff8f86', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(180,35,24,0.22)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(180,35,24,0.12)'; }}
                >✗ Still Learning</button>
                <button
                  onClick={() => fcMarkKnown(true)}
                  style={{ padding: '10px 22px', borderRadius: 12, border: '1.5px solid rgba(15,118,110,0.5)', background: 'rgba(15,118,110,0.18)', color: '#5ee8d2', fontSize: 13.5, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(15,118,110,0.28)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(15,118,110,0.18)'; }}
                >✓ I Know This</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                <button
                  onClick={fcPrev}
                  disabled={flashcardIndex === 0}
                  style={{ padding: '9px 18px', borderRadius: 10, border: '1.5px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', color: flashcardIndex === 0 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 600, cursor: flashcardIndex === 0 ? 'default' : 'pointer', transition: 'all 0.15s' }}
                >← Prev</button>
                <button
                  onClick={() => setFlashcardFlipped(true)}
                  style={{ padding: '9px 24px', borderRadius: 10, border: '1.5px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.18)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                >Reveal Answer</button>
                <button
                  onClick={fcNext}
                  disabled={isLast}
                  style={{ padding: '9px 18px', borderRadius: 10, border: '1.5px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.06)', color: isLast ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 600, cursor: isLast ? 'default' : 'pointer', transition: 'all 0.15s' }}
                >Next →</button>
              </div>
            )}

            {/* Generate More / exhausted */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              {flashcardsExhausted ? (
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 16px', borderRadius: 12, border: '1px solid rgba(245,158,11,0.35)', background: 'rgba(245,158,11,0.1)', maxWidth: 420 }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#FBBF24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontSize: 12.5, fontWeight: 700, color: '#FBBF24', marginBottom: 3 }}>No more flashcards available</div>
                    <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.55)', lineHeight: 1.55 }}>{flashcardsExhaustedReason}</div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={generateMoreFlashcards}
                  disabled={flashcardsMoreLoading}
                  style={{ padding: '8px 20px', borderRadius: 10, border: '1.5px solid rgba(255,255,255,0.18)', background: flashcardsMoreLoading ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.08)', color: flashcardsMoreLoading ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.6)', fontSize: 12.5, fontWeight: 600, cursor: flashcardsMoreLoading ? 'default' : 'pointer', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 6 }}
                  onMouseEnter={e => { if (!flashcardsMoreLoading) e.currentTarget.style.background = 'rgba(255,255,255,0.14)'; }}
                  onMouseLeave={e => { if (!flashcardsMoreLoading) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; }}
                >
                  {flashcardsMoreLoading ? (
                    <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>Generating…</>
                  ) : (
                    <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>Generate More</>
                  )}
                </button>
              )}
            </div>

            {/* Keyboard hint */}
            <div style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.28)', letterSpacing: '0.02em' }}>
              Space to flip · ← → to navigate · Esc to close
            </div>
          </div>
        );
      })()}


    </>
  );
};

export default App;
